import React, { useState, useCallback, useMemo, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import db from '../services/db';
import { seededShuffle, seededPick } from '../utils/random';
import RichTextSlideRenderer, { InlineAudioRenderer } from './RichTextSlideRenderer';
import { ImageLightboxPortal, useImageLightbox } from './ExpandableImageLightbox';
import {
    parseFillGapsStructuredBody,
    collectGapParts,
    normalizeFillGapsWordsParts,
    parseMultipleChoiceShortBody,
    parseMcOptionText,
    stripInlineMarkersPlain,
    parseFillGapsChoiceStructuredBody,
    collectMixedGapChoiceItems,
    parseChecklistStructuredBody,
    collectChecklistItems,
    isChecklistBoxChecked,
    isTableNoHeaderDirective,
    TABLE_NO_HEADER_MARKER,
} from '../utils/gapParsing.js';
import { computeAutoGrade, parseMatchingColumnsPairs, parseOrderLinesItems, getOrderLinesOrder, getPerGapRewardCaps, buildGapRewardsFromIsCorrect, sumGapRewards, isMaxGapReward, answerMarkEmoji, answerMarkTone, answerMarkColors, MARK_NEEDS_TEACHER } from '../utils/exerciseGrading.js';
import { normalizeAnswer } from '../utils/normalizeAnswer.js';
import { parseSortBoxesBody, parseErrorCorrectionBody, collectErrorCorrectionItems } from '../utils/sortBoxesAndErrorCorrection.js';
import { canUseAnswerPeek, canShowExerciseAnswers, exerciseHasAnswersHtml } from '../utils/exercisePeekAnswers.js';
import { AnswersView } from './AnswersEditor.jsx';
import ExerciseAnswerPeekButton from './ExerciseAnswerPeekButton.jsx';
import MixedNoteHighlight from './MixedNoteHighlight.jsx';
import { parsePowerpointBody } from '../utils/powerpointExercise';
import { parseTextSlidePages, isTextSlidesDifferentStudents, getTextSlideAudiences, findTextSlideLogicalStep, resolveTeacherLogicalPage, resolveStudentTextSlidePage } from '../utils/textSlides.js';
import { extractMediaStoragePath } from '../utils/writingCheckerImages.js';
import VoiceRecordInteractive from './VoiceRecordInteractive';
import PronunciationCompareInteractive from './PronunciationCompareInteractive';
import { sanitizeTeacherCommentHtml, teacherCommentToHtml } from './TeacherCommentField';
import ExerciseLead, {
    EXERCISE_CARD_PAD as CARD_PAD,
    instructionStyleForPad,
    notesStyleForPad,
} from './ExerciseLead';
import { aiService } from '../services/ai';
import {
    readFillGapsAiMeta,
    extractFillGapsAiAnswersOnly,
    collectFillGapsAiFeedbackItems,
    buildFillGapsAiAnswersPayload,
    buildFillGapsAiCheckSections,
    remapFillGapsAiBatchResults,
    areAllFillGapsAiSectionsChecked,
} from '../utils/fillGapsAi.js';
import { isExerciseHideEnabled, isExerciseHideForcedVisible } from '../utils/exerciseHide.js';

const PowerPointPresentationViewer = React.lazy(() => import('./PowerPointPresentationViewer'));
import { formatInline } from '../utils/inlineMarkdown.js';
import { matchNumberedListLine, stripNumberedListChromeMarkers } from '../utils/exerciseBodyMarkup.js';
import {
    parseWordlistExerciseBody,
    parseWordlistExerciseLine,
    formatIpaInBrackets,
    isWordlistHeadingRow,
    groupWordlistRowsBySubtheme,
} from '../utils/parseWordlistExerciseLines.js';
import {
    parseDisappearingTextBody,
    generateDisappearingPages,
    disappearingBlankForWord,
} from '../utils/disappearingText.js';
import { normalizeImageWidthPercent } from '../utils/imageEmbedSizes.js';
import { speakEnglish } from '../services/tts';
import {
    isThreeAttemptsEnabled,
    getGapAttemptResults,
    applyGapAttempt,
    setGapAttemptResults,
    THREE_ATTEMPTS_MAX,
} from '../utils/threeAttempts.js';
import './ExerciseGameUI.css';

/* ─── Persistent Sync Manager ──────────────────────────────────────────────── */
const SYNC_KEY = (id) => `ex_sync_${id}`;

function SyncStatus({ status }) {
    const icons = {
        'synced': { icon: '✅', label: 'Synced', color: '#059669' },
        'saving': { icon: '⏳', label: 'Saving...', color: '#4361ee' },
        'offline': { icon: '⚠️', label: 'Saved Locally (Offline)', color: '#ca8a04' },
        'error': { icon: '❌', label: 'Sync Error', color: '#dc2626' }
    };
    const s = icons[status] || icons.synced;


    return (
        <div style={{
            position: 'absolute', top: '10px', right: '10px', fontSize: '0.65rem', fontWeight: 800,
            display: 'flex', alignItems: 'center', gap: '4px', color: s.color,
            background: 'white', padding: '2px 8px', borderRadius: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
            pointerEvents: 'none', zIndex: 10, transition: 'all 0.3s'
        }}>
            <span>{s.icon}</span> <span>{s.label}</span>
        </div>
    );
}

function useExercisePersistence(assignmentId, currentState, onUpdate, readOnly, assignment, isOnlineLesson = false, updateSlideState, slideState) {
    const [syncStatus, setSyncStatus] = useState('synced');
    const [lastSaved, setLastSaved] = useState(Date.now());

    // NO-OP when in online lesson mode — sync is handled by the room's slide_state
    const noopGetLocal = useCallback(() => null, []);
    const noopPersist = useCallback(async () => {}, []);

    // Clear stale localStorage when assignment is reassigned or already completed
    // This prevents old answers from leaking into re-assigned exercises
    // and stops localStorage from conflicting with DB data on completed exercises
    useEffect(() => {
        if (isOnlineLesson) return;
        if (!assignmentId) return;
        if (assignment?.reassigned && assignment?.status === 'assigned') {
            localStorage.removeItem(SYNC_KEY(assignmentId));
        }
    }, [assignmentId, assignment?.reassigned, assignment?.status, isOnlineLesson]);

    // Load from local storage on mount
    const getLocalData = useCallback(() => {
        if (isOnlineLesson) return null;
        if (!assignmentId) return null;
        try {
            const raw = localStorage.getItem(SYNC_KEY(assignmentId));
            return raw ? JSON.parse(raw) : null;
        } catch(e) { return null; }
    }, [assignmentId, isOnlineLesson]);

    // Save to local storage and trigger update
    const persist = useCallback(async (data) => {
        if (isOnlineLesson || readOnly) return;

        // 1. Save to LocalStorage immediately when we have a key
        if (assignmentId) {
            try {
                localStorage.setItem(SYNC_KEY(assignmentId), JSON.stringify({
                    ...data,
                    timestamp: Date.now()
                }));
            } catch (e) {
                console.warn('[SyncManager] localStorage write failed:', e);
            }
        }

        if (!onUpdate && !assignmentId) return;

        setSyncStatus('saving');

        // 2. Try to sync to DB even if assignmentId is missing (block sub-exercises use onUpdate)
        try {
            if (onUpdate) {
                await onUpdate(data);
                setLastSaved(Date.now());
            }
            setSyncStatus('synced');
        } catch (err) {
            console.warn("[SyncManager] Save failed, data kept in local storage:", err);
            setSyncStatus(window.navigator.onLine ? 'error' : 'offline');
        }
    }, [assignmentId, onUpdate, readOnly, isOnlineLesson]);

    // AUTHORITATIVE SYNC logic for Online Lessons
    const onlinePersist = useCallback(async (data) => {
        if (!isOnlineLesson || !updateSlideState || readOnly) return;
        updateSlideState(data);
    }, [isOnlineLesson, updateSlideState, readOnly]);

    const onlineGetLocal = useCallback(() => {
        return null; // Don't return state as "local data" to avoid triggering sync loops
    }, []);

    if (isOnlineLesson) {
        return { syncStatus: 'synced', persist: onlinePersist, getLocalData: onlineGetLocal, lastSaved };
    }

    return { syncStatus, persist, getLocalData, lastSaved };
}

/** Nested under assignment.answers (jsonb) so no extra DB columns are required. */
const FILL_GAPS_SELF_CHECK_META = '__self_check_meta__';

function extractGapAnswersOnly(answersObj) {
    if (!answersObj || typeof answersObj !== 'object') return {};
    const { [FILL_GAPS_SELF_CHECK_META]: _m, ...rest } = answersObj;
    return rest;
}

function buildFillGapsSelfCheckAnswersPayload(gapAnswers, studentSelfCheck, suggestedAnswersSnapshot, existingAnswersObj) {
    const prevMeta = readFillGapsSelfCheckMeta(existingAnswersObj);
    const meta = { student_self_check: studentSelfCheck || {} };
    if (suggestedAnswersSnapshot && Object.keys(suggestedAnswersSnapshot).length > 0) {
        meta.suggested_answers = suggestedAnswersSnapshot;
    }
    if (prevMeta.ai_feedback) meta.ai_feedback = prevMeta.ai_feedback;
    if (prevMeta.ai_feedback_at) meta.ai_feedback_at = prevMeta.ai_feedback_at;
    return { ...gapAnswers, [FILL_GAPS_SELF_CHECK_META]: meta };
}

function readFillGapsSelfCheckMeta(answersObj) {
    return answersObj?.[FILL_GAPS_SELF_CHECK_META] || {};
}

function formatSuggestedForGap(gapPart) {
    if (!gapPart?.answers?.length) return '';
    return gapPart.answers.join(' / ');
}

function parseOrderWords(body, exerciseId) {
    return (body || '').split('\n').filter(l => l.trim()).map((line, idx) => {
        const m = line.match(/^(\d+)\.\s*(.+)/);
        const content = m ? m[2] : line;
        const num = m ? m[1] : (idx + 1);
        const words = content.split('/').map(w => w.trim()).filter(Boolean);
        const mapped = words.map((text, i) => ({ text, id: `${idx}-${i}` }));
        const shuffled = seededShuffle(mapped, `orderwords_${exerciseId || 'ex'}_${idx}`);
        return { num, original: words.join(' '), shuffled };
    });
}

/** Shuffled id list for Order (Lines); reshuffles once if still in correct order. */
function buildOrderLinesShuffle(items, exerciseId) {
    const ids = items.map((it) => it.id);
    if (ids.length <= 1) return ids;
    let shuffled = seededShuffle(ids, `orderlines_${exerciseId || 'ex'}`);
    if (shuffled.every((id, i) => id === i)) {
        shuffled = seededShuffle(ids, `orderlines_${exerciseId || 'ex'}_alt`);
    }
    return shuffled;
}

function renderBold(str, unblurredMap = {}, prefix = 'ex') {
    if (!str) return '';
    const html = formatInline(str, { unblurredMap: unblurredMap || {}, prefix: prefix || 'ex' });
    return <InlineAudioRenderer html={html} inline />;
}

function escapeNoteTitle(s) {
    return String(s || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function renderMarkdownToHtml(str, unblurredMap = {}, idPrefix = 'txt', isFragment = false) {
    if (!str) return '';
    const state = { counter: 0, prefix: idPrefix, unblurredMap };
    const inlineOpts = { inlineImages: isFragment };
    
    const lines = str.split('\n');
    const finalHtml = [];
    let tableBuffer = [];
    let noteBuffer = null; // { title, lines: string[] }
    let pendingNoHeader = false;

    const flushTable = () => {
        if (tableBuffer.length < 2) {
             if (pendingNoHeader) {
                 finalHtml.push(`<p>${formatInline(TABLE_NO_HEADER_MARKER, state, inlineOpts)}</p>`);
             }
             tableBuffer.forEach(l => finalHtml.push(`<p>${formatInline(l, state, inlineOpts)}</p>`));
             tableBuffer = [];
             pendingNoHeader = false;
             return;
        }
        
        // Basic markdown table parser: | Col1 | Col2 |
        const rawRows = tableBuffer.map(line => {
             const trimmed = line.trim();
             return trimmed
                .split('|')
                .filter((_, i, arr) => i > 0 && i < arr.length - 1)
                .map(c => c.trim());
        });
        
        // Remove separator row (like |---|---|)
        const filteredRows = rawRows.filter(r => !r.every(c => c.match(/^[-\s]+$/)));
        const header = !pendingNoHeader;
        pendingNoHeader = false;
        
        let html = '<div class="md-table-wrapper"><table class="md-table">';
        filteredRows.forEach((row, rowIndex) => {
             html += '<tr>';
             row.forEach(cell => {
                 const tag = (header && rowIndex === 0) ? 'th' : 'td';
                 html += `<${tag}>${formatInline(cell, state, inlineOpts)}</${tag}>`;
             });
             html += '</tr>';
        });
        html += '</table></div>';
        finalHtml.push(html);
        tableBuffer = [];
    };

    const flushNote = () => {
        if (!noteBuffer) return;
        const title = noteBuffer.title || 'Note';
        const bodyHtml = noteBuffer.lines
            .map((line) => {
                if (!line.trim()) return '<br/>';
                return `<p>${formatInline(line, state, inlineOpts)}</p>`;
            })
            .join('');
        finalHtml.push(
            `<aside class="ex-note" role="note"><div class="ex-note-label">${escapeNoteTitle(title)}</div><div class="ex-note-body">${bodyHtml}</div></aside>`
        );
        noteBuffer = null;
    };

    let numberedBuffer = null; // { num, text }[]

    const flushNumbered = () => {
        if (!numberedBuffer?.length) {
            numberedBuffer = null;
            return;
        }
        let html = '<ol class="ex-numbered-list">';
        numberedBuffer.forEach(({ num, text }) => {
            const safeNum = escapeNoteTitle(num);
            html +=
                `<li class="ex-numbered-item" value="${safeNum}">` +
                `<span class="ex-numbered-badge">${safeNum}</span>` +
                `<span class="ex-numbered-text">${formatInline(text, state, inlineOpts)}</span>` +
                `</li>`;
        });
        html += '</ol>';
        finalHtml.push(html);
        numberedBuffer = null;
    };

    const matchNumbered = (line) => matchNumberedListLine(line);

    lines.forEach((line, idx) => {
        const trimmed = line.trim();

        if (noteBuffer) {
            if (trimmed === ':::') {
                flushNote();
            } else {
                noteBuffer.lines.push(line);
            }
            return;
        }

        const noteOpen = trimmed.match(/^:::note(?:\s+(.*))?$/i);
        if (noteOpen) {
            flushTable();
            flushNumbered();
            noteBuffer = {
                title: (noteOpen[1] || 'Note').trim() || 'Note',
                lines: [],
            };
            return;
        }

        if (isTableNoHeaderDirective(trimmed) && tableBuffer.length === 0) {
            flushNumbered();
            pendingNoHeader = true;
            return;
        }
        if (pendingNoHeader && tableBuffer.length === 0 && trimmed === '') {
            return;
        }

        if ((trimmed.startsWith('|') && trimmed.endsWith('|')) || (trimmed.startsWith('|') && (tableBuffer.length > 0))) {
            flushNumbered();
            tableBuffer.push(line);
        } else {
            if (pendingNoHeader && tableBuffer.length === 0) {
                finalHtml.push(`<p>${formatInline(TABLE_NO_HEADER_MARKER, state, inlineOpts)}</p>`);
                pendingNoHeader = false;
            }
            flushTable();
            const numbered = matchNumbered(line);
            if (numbered) {
                if (!numberedBuffer) numberedBuffer = [];
                numberedBuffer.push(numbered);
                return;
            }
            flushNumbered();
            if (trimmed === '---') {
                finalHtml.push('<hr/>');
            } else {
                const content = formatInline(line, state, inlineOpts);
                if (isFragment) {
                    finalHtml.push(content + (idx < lines.length - 1 ? '<br/>' : ''));
                } else if (trimmed !== '') {
                    finalHtml.push(`<p>${content}</p>`);
                } else {
                    // Preserve blank lines from Exercise Content as vertical space (matches Visual editor).
                    finalHtml.push('<p class="ex-blank-line" aria-hidden="true"><br/></p>');
                }
            }
        }
    });
    flushTable();
    flushNumbered();
    flushNote();

    let result = finalHtml.join('');

    return result;
}

/** Flat render of fill-gap parts (no line splitting). */
function renderFillGapsPartList(parts, ctx) {
    const elements = [];
    parts.forEach((p, i) => {
        if (p.type === 'text') {
            elements.push(ctx.renderText(p, `${ctx.idPrefix}${i}`));
        } else if (p.type === 'note' && ctx.renderNote) {
            elements.push(ctx.renderNote(p, `${ctx.idPrefix}${i}`));
        } else if (p.type === 'gap') {
            const gi = ctx.nextGapIdx();
            elements.push(ctx.renderGap(p, gi, `${ctx.idPrefix}${i}`));
        } else if (p.type === 'choice' && ctx.renderChoice) {
            const gi = ctx.nextGapIdx();
            elements.push(ctx.renderChoice(p, gi, `${ctx.idPrefix}${i}`));
        } else if (p.type === 'check' && ctx.renderCheck) {
            const gi = ctx.nextGapIdx();
            elements.push(ctx.renderCheck(p, gi, `${ctx.idPrefix}${i}`));
        } else if (p.type === 'table') {
            elements.push(
                <div key={`${ctx.idPrefix}tbl-${i}`} className="md-table-wrapper">
                    <table className="md-table">
                        <tbody>
                            {p.rows.map((row, ri) => (
                                <tr key={ri}>
                                    {row.cells.map((cellParts, ci) => {
                                        const Tag = row.isHeader ? 'th' : 'td';
                                        return (
                                            <Tag key={ci}>
                                                {renderFillGapsStructuredParts(cellParts, {
                                                    ...ctx,
                                                    idPrefix: `${ctx.idPrefix}t${i}r${ri}c${ci}-`,
                                                })}
                                            </Tag>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            );
        }
    });
    return elements;
}

/**
 * Split fill-gap parts into visual lines (by \\n / \\r\\n / <br> in text). Tables stay atomic.
 * @returns {Array<{kind:'line', parts: object[]}|{kind:'table', part: object}>}
 */
function splitFillGapPartsIntoLines(parts) {
    const lines = [];
    let current = [];

    const endLine = () => {
        lines.push({ kind: 'line', parts: current });
        current = [];
    };

    (parts || []).forEach((p) => {
        if (p.type === 'table') {
            if (current.length) endLine();
            lines.push({ kind: 'table', part: p });
            return;
        }
        if (p.type !== 'text') {
            current.push(p);
            return;
        }
        const segs = String(p.text || '')
            .replace(/\r\n/g, '\n')
            .replace(/\r/g, '\n')
            .split(/\n|<br\s*\/?>/i);
        segs.forEach((seg, si) => {
            if (si > 0) endLine();
            if (seg !== '') current.push({ type: 'text', text: seg });
        });
    });
    if (current.length) endLine();
    return lines;
}

/** If a line starts with `1.` / `2` / …, strip the marker and return { num, parts }. */
function stripNumberedLinePrefix(lineParts) {
    if (!lineParts?.length || lineParts[0].type !== 'text') return null;
    const matched = matchNumberedListLine(lineParts[0].text);
    if (!matched) return null;
    const rest = lineParts.slice();
    if (matched.text === '') rest.shift();
    else rest[0] = { ...rest[0], text: matched.text };
    return { num: matched.num, parts: rest };
}

/** Render structured fill-gap parts (text, gaps, markdown tables with gaps in cells). */
function renderFillGapsStructuredParts(parts, ctx) {
    const lines = splitFillGapPartsIntoLines(parts);
    const elements = [];
    let li = 0;

    while (li < lines.length) {
        const row = lines[li];

        if (row.kind === 'table') {
            elements.push(...renderFillGapsPartList([row.part], { ...ctx, idPrefix: `${ctx.idPrefix}L${li}-` }));
            li += 1;
            continue;
        }

        const numbered = stripNumberedLinePrefix(row.parts);
        if (numbered) {
            const group = [];
            while (li < lines.length && lines[li].kind === 'line') {
                const item = stripNumberedLinePrefix(lines[li].parts);
                if (!item) break;
                group.push(item);
                li += 1;
            }
            elements.push(
                <ol
                    key={`${ctx.idPrefix}nl-${li}`}
                    className="ex-numbered-list ex-numbered-list--gaps"
                    style={{
                        listStyle: 'none',
                        margin: '0.75em 0',
                        padding: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.55em',
                    }}
                >
                    {group.map((g, gi) => (
                        <li
                            key={gi}
                            className="ex-numbered-item"
                            value={Number(g.num) || gi + 1}
                            style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '0.55rem',
                                margin: 0,
                                padding: 0,
                                listStyle: 'none',
                            }}
                        >
                            <span
                                className="ex-numbered-badge"
                                style={{
                                    flexShrink: 0,
                                    width: '1.75rem',
                                    height: '1.75rem',
                                    marginTop: '0.55em',
                                    borderRadius: '50%',
                                    background: 'linear-gradient(135deg, #4361ee 0%, #7c3aed 100%)',
                                    color: '#fff',
                                    fontWeight: 800,
                                    fontSize: '0.85rem',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    zIndex: 1,
                                    position: 'relative',
                                    lineHeight: 1,
                                }}
                            >
                                {g.num}
                            </span>
                            <span
                                className="ex-numbered-text"
                                style={{
                                    flex: 1,
                                    minWidth: 0,
                                    marginLeft: 0,
                                    padding: 0,
                                    background: 'transparent',
                                    borderRadius: 0,
                                    color: '#1e293b',
                                    fontWeight: 400,
                                    lineHeight: 2.2,
                                    display: 'block',
                                    boxSizing: 'border-box',
                                }}
                            >
                                {renderFillGapsPartList(
                                    g.parts.map((p) =>
                                        p.type === 'text'
                                            ? { ...p, text: stripNumberedListChromeMarkers(p.text) }
                                            : p
                                    ),
                                    {
                                        ...ctx,
                                        idPrefix: `${ctx.idPrefix}n${li}_${gi}_`,
                                    }
                                )}
                            </span>
                        </li>
                    ))}
                </ol>
            );
            continue;
        }

        if (!row.parts.length) {
            elements.push(<br key={`${ctx.idPrefix}br-${li}`} />);
        } else {
            elements.push(
                <span key={`${ctx.idPrefix}ln-${li}`} className="ex-gap-line" style={{ display: 'block' }}>
                    {renderFillGapsPartList(row.parts, { ...ctx, idPrefix: `${ctx.idPrefix}L${li}-` })}
                </span>
            );
        }
        li += 1;
    }

    return elements;
}

/* ─── Helper: check if assignment is already submitted ─── */
function isAlreadyDone(assignment) {
    if (!assignment) return false;
    const s = assignment.status;
    // Explicit in-progress wins over a stale completed_at (common on block sub-exercises).
    if (s === 'working' || s === 'assigned') return false;
    if (s === 'completed' || s === 'checked') return true;
    return !!assignment.completed_at;
}

/* ─── Shared UI tokens ───────────────────────────────────────────────────── */
const CARD_STYLE = {
    background: '#ffffff',
    borderRadius: '20px',
    padding: CARD_PAD,
    border: '1.5px solid #e2e8f0',
    maxWidth: '100%',
    overflow: 'hidden',
    width: '100%',
    boxSizing: 'border-box',
    color: '#0f172a',
};
const COMPLETE_BTN = 'exercise-game-btn exercise-game-btn--complete';
const COMPLETE_LABEL = 'Mark Done';
const COMPLETE_DONE_LABEL = 'Done ✅';
const COMPLETE_DONE_CLASS = 'exercise-game-btn exercise-game-btn--marked-done';

function ExerciseActionBar({ children, style }) {
    return (
        <div className="exercise-action-bar" style={style}>
            {children}
        </div>
    );
}

function ExerciseScoreBadge({ children }) {
    return (
        <div className="exercise-score-badge">
            <span className="exercise-score-badge__star" aria-hidden="true">⭐</span>
            <span>{children}</span>
        </div>
    );
}

function ExerciseStatusHint({ children, icon }) {
    return (
        <p className="exercise-status-hint" role="status">
            {icon ? <span className="exercise-status-hint__icon" aria-hidden="true">{icon}</span> : null}
            <span>{children}</span>
        </p>
    );
}

function AssignmentStatusPill({ assignment, completed, fallback, isOnlineLesson = false, completedLabel, practiceMode }) {
    // Public /open pages: no homework status chrome
    if (practiceMode === 'open') return null;

    if (assignment?.status === 'checked') {
        return (
            <div className="exercise-status-pill exercise-status-pill--checked">
                <span className="exercise-status-pill__icon" aria-hidden="true">✅</span>
                <span>Checked!</span>
            </div>
        );
    }

    if (completed) {
        return (
            <div className="exercise-status-pill exercise-status-pill--sent">
                <span className="exercise-status-pill__icon" aria-hidden="true">📬</span>
                <span>{completedLabel || 'Sent to teacher'}</span>
            </div>
        );
    }

    if (isOnlineLesson) return null;

    return <ExerciseStatusHint icon="🎯">{fallback || 'Keep going!'}</ExerciseStatusHint>;
}

function ExerciseScoreBox({ submitted, children }) {
    if (!submitted) {
        return <ExerciseStatusHint icon="🎯">{children || 'Keep going!'}</ExerciseStatusHint>;
    }
    return <ExerciseScoreBadge>{children}</ExerciseScoreBadge>;
}

function ScoreDisplay({ score, total, assignment, reviewMode, onUpdateAssignment, showPercentage = true }) {
    const percent = total > 0 ? Math.round((score / total) * 100) : 0;
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <ExerciseScoreBadge>
                Score: {score} / {total}
            </ExerciseScoreBadge>
            {showPercentage && (
                <div className="exercise-status-pill exercise-status-pill--checked" style={{ padding: '5px 10px', borderBottomWidth: '3px' }}>
                    <span>{percent}%</span>
                </div>
            )}
            <ConfirmationBadge assignment={assignment} reviewMode={reviewMode} onUpdateAssignment={onUpdateAssignment} />
        </div>
    );
}

/* ─── Teacher Comment / Feedback Block ────────────────────────────────── */
function TeacherComment({ exercise, assignment }) {
    const comment = String(assignment?.teacher_comment || '').trim();
    if (!comment) return null;
    // Standalone: after this assignment is checked.
    // Block sub-exercise: parent block may be checked while the sub status stays "completed".
    const visible = assignment?.status === 'checked' || assignment?.block_status === 'checked';
    if (!visible) return null;
    const html = sanitizeTeacherCommentHtml(teacherCommentToHtml(comment));
    return (
        <div style={{
            marginTop: '1rem', padding: '0.75rem 1rem', borderRadius: '12px',
            background: '#fffbeb', border: '1.5px solid #fde68a',
            display: 'flex', gap: '0.6rem', alignItems: 'flex-start',
        }}>
            <span style={{ fontSize: '1.1rem', flexShrink: 0, marginTop: '1px' }}>💬</span>
            <div>
                <div style={{ fontSize: '0.68rem', fontWeight: 800, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.25rem' }}>Teacher's Feedback</div>
                <div
                    style={{ fontSize: '0.88rem', color: '#713f12', lineHeight: 1.6 }}
                    dangerouslySetInnerHTML={{ __html: html }}
                />
            </div>
        </div>
    );
}

/* ─── Confirmation Status Badge (teacher view) ────────────────────────── */
function ConfirmationBadge({ assignment, reviewMode, onUpdateAssignment }) {
    if (!reviewMode || !assignment) return null;
    const isConfirmed = assignment.teacher_confirmed;
    return (
        <span
            onClick={() => {
                if (onUpdateAssignment && !isConfirmed) {
                    onUpdateAssignment(assignment.id, { teacher_confirmed: true });
                }
            }}
            style={{
                display: 'inline-block', padding: '0.3rem 0.7rem', borderRadius: '8px',
                fontSize: '0.72rem', fontWeight: 800, marginLeft: '0.5rem', cursor: isConfirmed ? 'default' : 'pointer',
                background: isConfirmed ? '#d1fae5' : '#fee2e2',
                color: isConfirmed ? '#065f46' : '#991b1b',
                border: isConfirmed ? '1.5px solid #6ee7b7' : '1.5px solid #fca5a5',
                transition: 'all 0.2s',
            }}
            title={isConfirmed ? 'Auto-check confirmed by teacher' : 'Click to confirm auto-check'}
        >
            {isConfirmed ? '✅ CONFIRMED' : '⚠️ NOT CONFIRMED'}
        </span>
    );
}

const GAP_SUFFIX_SUBMIT_PX = 30;
const GAP_SUFFIX_MARK_PX = 28;
const GAP_SHELL_BORDER_PX = 4;
const GAP_FIELD_MIN_HEIGHT_PX = 26;
/** Caret + rounding slack so the last typed glyph never sits under the border. */
const GAP_TEXT_SLACK_PX = 3;

function getGapFieldSuffixWidth(showSubmit, showMark) {
    if (showSubmit) return GAP_SUFFIX_SUBMIT_PX;
    if (showMark) return GAP_SUFFIX_MARK_PX;
    return 0;
}

/**
 * Single-line text width for gap sizing.
 * Measured in a detached probe with `white-space: pre` — reading `scrollWidth` off the
 * field itself returns the *wrapped* (min-content) width, which collapses gaps to one
 * character per line.
 */
let gapTextProbe = null;

function getGapTextProbe() {
    if (typeof document === 'undefined') return null;
    if (gapTextProbe && gapTextProbe.isConnected) return gapTextProbe;
    const probe = document.createElement('span');
    probe.setAttribute('aria-hidden', 'true');
    probe.style.cssText = [
        'position:absolute',
        'top:-9999px',
        'left:-9999px',
        'visibility:hidden',
        'white-space:pre',
        'pointer-events:none',
        'padding:0',
        'border:0',
    ].join(';');
    document.body.appendChild(probe);
    gapTextProbe = probe;
    return probe;
}

function measureSingleLineTextWidth(el, text) {
    const probe = getGapTextProbe();
    if (!probe || !el || typeof window === 'undefined') return 0;
    const cs = window.getComputedStyle(el);
    probe.style.fontFamily = cs.fontFamily;
    probe.style.fontSize = cs.fontSize;
    probe.style.fontWeight = cs.fontWeight;
    probe.style.fontStyle = cs.fontStyle;
    probe.style.letterSpacing = cs.letterSpacing;
    probe.style.textTransform = cs.textTransform;
    probe.textContent = text || '';
    return probe.getBoundingClientRect().width;
}

/**
 * Grow the gap to fit what the student typed, then clamp to the exercise card width
 * so long answers wrap instead of forcing the whole card wider than the screen.
 * (CSS `max-width: 100%` alone is not enough: an explicit pixel width on an inline-flex
 * shell contributes to min-content and can expand ancestors before the percentage resolves.)
 */
function getGapShellMaxWidthPx(shell) {
    if (!shell || typeof window === 'undefined') return Infinity;
    const host = shell.closest('.exercise-gap-content')
        || shell.closest('.exercise-card-shell')
        || shell.parentElement;
    if (!host) return Infinity;
    const cs = window.getComputedStyle(host);
    const padX = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
    return Math.max(40, host.clientWidth - padX);
}

function applyGapShellWidth(shell, inputEl, text) {
    if (!shell || !inputEl) return;
    const cs = window.getComputedStyle(inputEl);
    const padX = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
    const textPx = measureSingleLineTextWidth(inputEl, text || '');
    const desired = Math.ceil(textPx + padX + GAP_SHELL_BORDER_PX + GAP_TEXT_SLACK_PX);
    const maxW = getGapShellMaxWidthPx(shell);
    shell.style.width = `${Math.min(desired, maxW)}px`;
}

function useGapFieldAutoSize(shellRef, inputRef, value, placeholder) {
    const fitHeight = useCallback(() => {
        const el = inputRef.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${Math.max(el.scrollHeight, GAP_FIELD_MIN_HEIGHT_PX)}px`;
    }, [inputRef]);

    const fitWidth = useCallback(() => {
        applyGapShellWidth(shellRef.current, inputRef.current, value || placeholder || '');
    }, [shellRef, inputRef, value, placeholder]);

    useLayoutEffect(() => {
        fitWidth();
        fitHeight();
    }, [fitWidth, fitHeight]);

    // Container resize (or the max-width clamp) moves the wrap point — re-fit width + height.
    useEffect(() => {
        const el = inputRef.current;
        const shell = shellRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const host = shell?.closest('.exercise-gap-content')
            || shell?.closest('.exercise-card-shell')
            || shell?.parentElement
            || el;
        let lastHostW = host.getBoundingClientRect().width;
        let lastFieldW = el.getBoundingClientRect().width;
        const ro = new ResizeObserver(() => {
            const hostW = host.getBoundingClientRect().width;
            const fieldW = el.getBoundingClientRect().width;
            const hostChanged = Math.abs(hostW - lastHostW) >= 0.5;
            const fieldChanged = Math.abs(fieldW - lastFieldW) >= 0.5;
            if (!hostChanged && !fieldChanged) return;
            lastHostW = hostW;
            lastFieldW = fieldW;
            if (hostChanged) fitWidth();
            fitHeight();
        });
        ro.observe(host);
        if (host !== el) ro.observe(el);
        return () => ro.disconnect();
    }, [inputRef, shellRef, fitWidth, fitHeight]);
}

function GapAttemptDots({ results = [], visible = false }) {
    if (!visible) return null;
    const slots = Array.from({ length: THREE_ATTEMPTS_MAX }, (_, i) => {
        if (i < results.length) return results[i] === true ? 'ok' : 'bad';
        return 'idle';
    });
    return (
        <span className="exercise-gap-attempt-dots" aria-label={`Attempts: ${results.length} of ${THREE_ATTEMPTS_MAX}`} title={`${results.length}/${THREE_ATTEMPTS_MAX} attempts`}>
            {slots.map((state, i) => (
                <span key={i} className={`exercise-gap-attempt-dot exercise-gap-attempt-dot--${state}`} />
            ))}
        </span>
    );
}

function ExerciseGapField({
    value,
    onChange,
    onBlur,
    onKeyDown,
    placeholder,
    readOnly,
    borderColor,
    background,
    cursor,
    draggable,
    onDrop,
    onDragOver,
    onClick,
    className = '',
    showSubmit = false,
    onSubmit,
    showMark = false,
    markContent = null,
    onMarkClick,
    markClickable = false,
    markTitle = '',
    peekEnabled = false,
    peeking = false,
    onPeekToggle,
}) {
    const ref = useRef(null);
    const shellRef = useRef(null);
    useGapFieldAutoSize(shellRef, ref, value, placeholder || '...');
    const suffixPx = getGapFieldSuffixWidth(showSubmit, showMark);
    const hasSuffix = suffixPx > 0;

    return (
        <div
            ref={shellRef}
            className={`exercise-gap-field-shell${draggable ? ' exercise-gap-field-shell--draggable' : ''}${hasSuffix ? ' exercise-gap-field-shell--with-suffix' : ''}`}
            style={{
                borderColor,
                background,
            }}
        >
            <div className="exercise-gap-field-shell__main">
                <textarea
                    ref={ref}
                    rows={1}
                    value={value}
                    readOnly={readOnly}
                    tabIndex={readOnly ? -1 : undefined}
                    onChange={onChange}
                    onBlur={onBlur}
                    onKeyDown={onKeyDown}
                    placeholder={placeholder}
                    onDrop={onDrop}
                    onDragOver={onDragOver}
                    onClick={onClick}
                    className={`exercise-gap-field exercise-gap-field--embedded${hasSuffix ? ' exercise-gap-field--with-suffix' : ''} ${draggable ? 'exercise-gap-field--draggable' : ''} ${className}`.trim()}
                    style={{
                        cursor: cursor || (readOnly ? 'default' : 'text'),
                        // Draggable (Given Words) gaps are read-only for typing but must receive clicks.
                        pointerEvents: readOnly && !draggable ? 'none' : undefined,
                    }}
                />
                {showSubmit && (
                    <button
                        type="button"
                        className="exercise-gap-field-shell__submit"
                        title="Submit this answer"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={onSubmit}
                        aria-label="Submit this answer"
                    >
                        👆🏻
                    </button>
                )}
                {showMark && (
                    <span
                        className={`exercise-gap-field-shell__mark${markClickable ? ' exercise-gap-field-shell__mark--clickable' : ''}`}
                        onClick={onMarkClick}
                        title={markTitle}
                        role={markClickable ? 'button' : undefined}
                    >
                        {markContent}
                    </span>
                )}
            </div>
            {peekEnabled && (
                <div className="exercise-gap-field-shell__footer">
                    <ExerciseAnswerPeekButton
                        peeking={peeking}
                        onToggle={onPeekToggle}
                        className="exercise-answer-peek-btn--inline"
                    />
                </div>
            )}
        </div>
    );
}

function ExerciseChoiceField({
    value,
    onChange,
    options = [],
    disabled = false,
    placeholder = '...',
    borderColor = '#c7d2fe',
    background = '#fff',
    showSubmit = false,
    onSubmit,
    showMark = false,
    markContent = null,
    markClickable = false,
    onMarkClick,
    markTitle = '',
    peekEnabled = false,
    peeking = false,
    onPeekToggle,
}) {
    const displayText = value ? stripInlineMarkersPlain(value) : placeholder;
    const suffixPx = getGapFieldSuffixWidth(showSubmit, showMark);
    const hasSuffix = suffixPx > 0;
    const showChevron = !disabled && !hasSuffix;
    const selectRef = useRef(null);
    const shellRef = useRef(null);

    // A <select> cannot wrap, so size it to the chosen label and clamp to the card width.
    useLayoutEffect(() => {
        const el = selectRef.current;
        const shell = shellRef.current;
        if (!el || !shell) return;
        applyGapShellWidth(shell, el, displayText);
    }, [displayText, hasSuffix, showChevron]);

    return (
        <div
            ref={shellRef}
            className={`exercise-gap-field-shell exercise-choice-field-shell${hasSuffix ? ' exercise-gap-field-shell--with-suffix' : ''}`}
            style={{
                borderColor,
                background,
            }}
        >
            <div className="exercise-gap-field-shell__main">
                <div className="exercise-choice-field-shell__select-wrap">
                    <select
                        ref={selectRef}
                        value={value || ''}
                        onChange={onChange}
                        disabled={disabled}
                        className={`exercise-gap-field exercise-gap-field--embedded exercise-gap-field--select${hasSuffix ? ' exercise-gap-field--with-suffix' : ''}${showChevron ? ' exercise-gap-field--with-chevron' : ''}`}
                        style={{ cursor: disabled ? 'default' : 'pointer' }}
                    >
                        <option value="" disabled hidden>{placeholder}</option>
                        {options.map((opt, oi) => (
                            <option key={oi} value={opt.label}>{stripInlineMarkersPlain(opt.label)}</option>
                        ))}
                    </select>
                    {showChevron && (
                        <span className="exercise-choice-field-shell__chevron" aria-hidden>▾</span>
                    )}
                </div>
                {showSubmit && (
                    <button
                        type="button"
                        className="exercise-gap-field-shell__submit"
                        title="Check this answer"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={onSubmit}
                        aria-label="Check this answer"
                    >
                        👆🏻
                    </button>
                )}
                {showMark && (
                    <span
                        className={`exercise-gap-field-shell__mark${markClickable ? ' exercise-gap-field-shell__mark--clickable' : ''}`}
                        onClick={onMarkClick}
                        title={markTitle}
                        role={markClickable ? 'button' : undefined}
                    >
                        {markContent}
                    </span>
                )}
            </div>
            {peekEnabled && (
                <div className="exercise-gap-field-shell__footer">
                    <ExerciseAnswerPeekButton
                        peeking={peeking}
                        onToggle={onPeekToggle}
                        className="exercise-answer-peek-btn--inline"
                    />
                </div>
            )}
        </div>
    );
}

/** Teacher review: edit stars/coins for one gap (defaults from ✅=max / ❌=0). */
function GapRewardEditors({ maxStars = 0, maxCoins = 0, stars = 0, coins = 0, onChange }) {
    if (!(maxStars > 0 || maxCoins > 0)) return null;
    const starStep = Number.isInteger(maxStars) ? 1 : 0.5;
    return (
        <span
            className="exercise-gap-reward-editors"
            onClick={(e) => e.stopPropagation()}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                marginTop: 0,
                padding: '3px 6px',
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                fontSize: 11,
                fontWeight: 700,
                color: '#334155',
                flexShrink: 0,
                verticalAlign: 'middle',
            }}
        >
            {maxStars > 0 && (
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 3, margin: 0 }}>
                    <span aria-hidden>⭐</span>
                    <input
                        type="number"
                        min={0}
                        max={maxStars}
                        step={starStep}
                        value={Number(stars) || 0}
                        onChange={(e) => onChange?.({ stars: e.target.value, coins })}
                        title={`Stars for this item (max ${maxStars})`}
                        style={{
                            width: 48, padding: '2px 4px', borderRadius: 6,
                            border: '1px solid #cbd5e1', fontWeight: 800, fontSize: 12,
                        }}
                    />
                    <span style={{ color: '#94a3b8', fontWeight: 600 }}>/ {maxStars}</span>
                </label>
            )}
            {maxCoins > 0 && (
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 3, margin: 0 }}>
                    <span aria-hidden>🪙</span>
                    <input
                        type="number"
                        min={0}
                        max={maxCoins}
                        step={1}
                        value={Number(coins) || 0}
                        onChange={(e) => onChange?.({ stars, coins: e.target.value })}
                        title={`Coins for this item (max ${maxCoins})`}
                        style={{
                            width: 48, padding: '2px 4px', borderRadius: 6,
                            border: '1px solid #cbd5e1', fontWeight: 800, fontSize: 12,
                        }}
                    />
                    <span style={{ color: '#94a3b8', fontWeight: 600 }}>/ {maxCoins}</span>
                </label>
            )}
        </span>
    );
}

/** Flush typed gap text on unmount (mobile overlay close often skips blur). */
function useGapDraftFlush({ gapIdx, localVal, enabled, onDraftChange }) {
    const localValRef = useRef(localVal);
    localValRef.current = localVal;
    const onDraftChangeRef = useRef(onDraftChange);
    onDraftChangeRef.current = onDraftChange;
    const enabledRef = useRef(enabled);
    enabledRef.current = enabled;
    useEffect(() => () => {
        if (enabledRef.current && onDraftChangeRef.current) {
            onDraftChangeRef.current(gapIdx, localValRef.current);
        }
    }, [gapIdx]);
}

/* ─── Gap input component ─────────────────────────────────────────────────── */
function GapInput({ gapIdx, prompt, answers, effectiveAnswers, globalAnswers, onSubmit, locked, draggable, reviewMode, isCorrect, onToggleCorrect, readOnly, onDragDrop, onClick, showSolution, solutionValue, draftValue, onDraftChange, assignment, rewardStars, rewardCoins, maxStars, maxCoins, onRewardChange, attemptResults, showAttempts }) {
    'use no memo';
    const [peeking, setPeeking] = useState(false);
    const submittedVal = showSolution ? solutionValue : (globalAnswers[gapIdx] || '');
    const val = draftValue !== undefined ? draftValue : submittedVal;
    const isLocked = showSolution || locked[gapIdx] || readOnly;
    const answersToCheck = effectiveAnswers || answers;
    const hasAnswers = answersToCheck.some((a) => String(a || '').trim());

    const check = (v) => {
        if (!hasAnswers) return null;
        return answersToCheck.some(a => normalizeAnswer(a) === normalizeAnswer(v));
    };

    const storedMark = (isCorrect === true || isCorrect === false) ? isCorrect : null;
    const autoMark = (!reviewMode && isLocked) ? check(val) : null;
    const effectiveMark = storedMark !== null ? storedMark : autoMark;
    // Student view: items without a key never show as wrong from auto/legacy false
    const displayMark = (!hasAnswers && !reviewMode)
        ? (storedMark === true ? true : null)
        : effectiveMark;

    const tone = showSolution ? 'ok' : answerMarkTone(displayMark, { hasAutoKey: hasAnswers, reviewMode });
    const { bg: bgColor, border: borderColor } = answerMarkColors(
        (showSolution || isLocked || reviewMode) ? tone : 'idle',
        { locked: isLocked || reviewMode },
    );

    const handleDrop = draggable && !reviewMode && !readOnly ? (e) => {
        e.preventDefault();
        const word = e.dataTransfer.getData('text/plain');
        if (!isLocked && onSubmit) onSubmit(gapIdx, word);
    } : undefined;

    // For fill_gaps_words: show empty placeholder (no underscores)
    const isAnswerPrompt = !hasAnswers || (hasAnswers && answersToCheck.some(a => a.trim().toLowerCase() === prompt?.trim().toLowerCase()));
    const finalPlaceholder = draggable ? '' : (isAnswerPrompt ? '' : (prompt || ''));

    const [localVal, setLocalVal] = useState(val);
    const canDraft = !!(onDraftChange && !isLocked && !draggable && !reviewMode && !readOnly);

    // Sync local state when parent value changes (initial load or slide sync)
    useEffect(() => {
        setLocalVal(val);
    }, [val]);

    useGapDraftFlush({ gapIdx, localVal, enabled: canDraft, onDraftChange });

    const pushDraft = (v) => {
        if (canDraft) onDraftChange(gapIdx, v);
    };

    const answerKey = showSolution ? '' : (solutionValue || answersToCheck[0] || '');
    const peekEnabled = canUseAnswerPeek({
        assignment,
        reviewMode,
        isWrong: displayMark === false,
        hasKey: !!answerKey,
    });
    const displayVal = peekEnabled && peeking ? answerKey : localVal;

    const markContent = answerMarkEmoji(displayMark, { hasAutoKey: hasAnswers, reviewMode });
    const showMarkSlot = (isLocked || reviewMode) && (!!markContent);

    return (
        <span className="exercise-gap-row" style={{ display: 'inline-flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 6, verticalAlign: 'middle', maxWidth: '100%', minWidth: 0 }}>
            <span className="exercise-gap-row__field-line">
                <ExerciseGapField
                    value={displayVal}
                    readOnly={isLocked || draggable || reviewMode || readOnly}
                    onChange={!isLocked && !draggable && !reviewMode && !readOnly ? (e) => {
                        const v = e.target.value;
                        setLocalVal(v);
                        pushDraft(v);
                    } : undefined}
                    onBlur={() => {
                        if (canDraft) pushDraft(localVal);
                    }}
                    placeholder={finalPlaceholder || '...'}
                    draggable={draggable}
                    onDrop={handleDrop}
                    onDragOver={(draggable && !reviewMode && !readOnly) ? (e) => e.preventDefault() : undefined}
                    onClick={() => {
                        if (onClick) onClick();
                    }}
                    borderColor={borderColor}
                    background={bgColor}
                    cursor={readOnly ? 'default' : (draggable ? 'pointer' : 'text')}
                    showSubmit={!isLocked && !draggable && !reviewMode && !readOnly}
                    onSubmit={() => {
                        if (onSubmit) onSubmit(gapIdx, localVal);
                    }}
                    showMark={showMarkSlot}
                    markContent={markContent}
                    markClickable={!!reviewMode}
                    onMarkClick={() => reviewMode && onToggleCorrect && onToggleCorrect(gapIdx)}
                    markTitle={reviewMode ? 'Click to toggle mark' : (markContent === MARK_NEEDS_TEACHER ? 'Teacher will check this' : '')}
                    peekEnabled={peekEnabled}
                    peeking={peeking}
                    onPeekToggle={() => setPeeking((p) => !p)}
                />
                <GapAttemptDots results={attemptResults || []} visible={!!showAttempts} />
            </span>
            {reviewMode && (
                <GapRewardEditors
                    maxStars={maxStars}
                    maxCoins={maxCoins}
                    stars={rewardStars}
                    coins={rewardCoins}
                    onChange={(next) => onRewardChange?.(gapIdx, next)}
                />
            )}
        </span>
    );
}
GapInput.displayName = 'GapInput';

/** Inline multiple-choice bracket — same pill shell as text gaps (👆 check + ✅/❌ mark). */
function ChoiceInput({
    choiceIdx,
    options,
    selected,
    correctLabel,
    locked,
    isCorrect,
    showSolution,
    reviewMode,
    readOnly,
    alreadyDone,
    assignment,
    onSelect,
    onCheck,
    onToggleCorrect,
    peeking,
    onPeekToggle,
    attemptResults,
    showAttempts,
    rewardStars,
    rewardCoins,
    maxStars,
    maxCoins,
    onRewardChange,
}) {
    const isLocked = showSolution || locked || alreadyDone || readOnly || reviewMode;
    const hasAutoKey = !!String(correctLabel || '').trim();
    const computedCorrect = hasAutoKey
        ? normalizeAnswer(selected || '') === normalizeAnswer(correctLabel)
        : null;
    const storedMark = (isCorrect === true || isCorrect === false) ? isCorrect : null;
    const result = storedMark !== null ? storedMark : (isLocked && !reviewMode ? computedCorrect : null);
    const displayMark = (!hasAutoKey && !reviewMode)
        ? (storedMark === true ? true : null)
        : result;

    const mcPeekEnabled = canUseAnswerPeek({
        assignment,
        reviewMode,
        isWrong: displayMark === false,
        hasKey: hasAutoKey,
    });
    const displayValue = (showSolution ? correctLabel : (peeking && mcPeekEnabled ? correctLabel : selected)) || '';

    const tone = showSolution ? 'ok' : answerMarkTone(displayMark, { hasAutoKey, reviewMode });
    const { bg: bgColor, border: borderColor } = answerMarkColors(
        (showSolution || isLocked || reviewMode) ? tone : 'idle',
        { locked: isLocked || reviewMode },
    );

    const showSubmit = !isLocked && !reviewMode && !readOnly && selected && !showSolution;
    const markContent = answerMarkEmoji(displayMark, { hasAutoKey, reviewMode });
    const showMark = (isLocked || reviewMode) && !!markContent;

    return (
        <span className="exercise-gap-row" style={{ display: 'inline-flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 6, verticalAlign: 'middle', maxWidth: '100%', minWidth: 0 }}>
            <span className="exercise-gap-row__field-line">
                <ExerciseChoiceField
                    value={displayValue}
                    onChange={(e) => onSelect(choiceIdx, e.target.value)}
                    options={options}
                    disabled={showSolution || peeking || isLocked}
                    borderColor={borderColor}
                    background={bgColor}
                    showSubmit={showSubmit}
                    onSubmit={() => onCheck(choiceIdx)}
                    showMark={showMark}
                    markContent={markContent}
                    markClickable={!!reviewMode}
                    onMarkClick={() => reviewMode && onToggleCorrect?.(choiceIdx)}
                    markTitle={reviewMode ? 'Click to toggle mark' : (markContent === MARK_NEEDS_TEACHER ? 'Teacher will check this' : '')}
                    peekEnabled={mcPeekEnabled}
                    peeking={peeking}
                    onPeekToggle={onPeekToggle}
                />
                <GapAttemptDots results={attemptResults || []} visible={!!showAttempts} />
            </span>
            {reviewMode && (
                <GapRewardEditors
                    maxStars={maxStars}
                    maxCoins={maxCoins}
                    stars={rewardStars}
                    coins={rewardCoins}
                    onChange={(next) => onRewardChange?.(choiceIdx, next)}
                />
            )}
        </span>
    );
}
ChoiceInput.displayName = 'ChoiceInput';

/** Fill in the Gaps (Self-Check): one gap with reveal + student self-rating; teacher review panel. */
function GapSelfCheckBlock({
    gapIdx,
    gapPart,
    globalAnswers,
    onSubmitGap,
    lockedMap,
    selfRating,
    onSelfRate,
    reviewMode,
    teacherMark,
    onToggleCorrect,
    readOnly,
    showSolution,
    suggestedText,
    studentSelfCheckStored,
    draftValue,
    onDraftChange,
    aiFeedback,
    rewardStars,
    rewardCoins,
    maxStars,
    maxCoins,
    onRewardChange,
}) {
    'use no memo';
    const hasKey = gapPart?.answers?.length > 0;
    const solutionVal = gapPart?.answers?.[0] || '';
    const submittedVal = showSolution ? solutionVal : (globalAnswers[gapIdx] ?? '');
    const val = draftValue !== undefined ? draftValue : submittedVal;
    const locked = showSolution || lockedMap[gapIdx] || readOnly;
    const answersToCheck = gapPart?.answers || [];
    const isAnswerPrompt = !answersToCheck.length || (answersToCheck.some(a => a.trim().toLowerCase() === gapPart.prompt?.trim().toLowerCase()));
    const finalPlaceholder = isAnswerPrompt ? '' : (gapPart.prompt || '');

    const [localVal, setLocalVal] = useState(val);
    useEffect(() => { setLocalVal(val); }, [val]);
    const canDraft = !!(onDraftChange && !locked && !reviewMode);
    useGapDraftFlush({ gapIdx, localVal, enabled: canDraft, onDraftChange });
    const pushDraft = (v) => {
        if (canDraft) onDraftChange(gapIdx, v);
    };

    let inputBg;
    let inputBorder;
    if (showSolution) {
        inputBg = '#d1fae5';
        inputBorder = '#6ee7b7';
    } else if (reviewMode) {
        if (teacherMark === true) { inputBg = '#d1fae5'; inputBorder = '#6ee7b7'; }
        else if (teacherMark === false) { inputBg = '#fee2e2'; inputBorder = '#fca5a5'; }
        else { inputBg = '#f8fafc'; inputBorder = '#cbd5e1'; }
    } else if (locked) {
        if (selfRating === true) { inputBg = '#d1fae5'; inputBorder = '#6ee7b7'; }
        else if (selfRating === false) { inputBg = '#fee2e2'; inputBorder = '#fca5a5'; }
        else { inputBg = '#f8fafc'; inputBorder = '#cbd5e1'; }
    } else {
        inputBg = '#fff';
        inputBorder = '#c7d2fe';
    }

    const showRevealBlock = locked && !showSolution;

    const aiFeedbackBlock = aiFeedback ? (
        <span style={{
            display: 'block', marginTop: '6px', fontSize: '0.75rem', color: '#3730a3',
            padding: '8px 10px', background: '#eef2ff', borderRadius: '8px',
            border: '1px solid #c7d2fe', maxWidth: '340px', lineHeight: 1.5, whiteSpace: 'pre-wrap',
        }}>
            <span style={{ display: 'block', fontWeight: 700, marginBottom: '4px' }}>🤖 AI Feedback</span>
            {aiFeedback}
        </span>
    ) : null;

    return (
        <span style={{ display: 'inline', verticalAlign: 'middle' }}>
            <span className="exercise-gap-row" style={{ display: 'inline-flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
                <ExerciseGapField
                    value={localVal}
                    readOnly={locked || reviewMode}
                    onChange={!locked && !reviewMode ? (e) => {
                        const v = e.target.value;
                        setLocalVal(v);
                        pushDraft(v);
                    } : undefined}
                    onKeyDown={!locked && !reviewMode ? (e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            if (onSubmitGap) onSubmitGap(gapIdx, localVal);
                        }
                    } : undefined}
                    onBlur={() => {
                        if (canDraft) pushDraft(localVal);
                    }}
                    placeholder={finalPlaceholder || '...'}
                    borderColor={inputBorder}
                    background={inputBg}
                    cursor={locked || reviewMode ? 'default' : 'text'}
                    showSubmit={!locked && !reviewMode}
                    onSubmit={() => onSubmitGap && onSubmitGap(gapIdx, localVal)}
                    showMark={reviewMode}
                    markContent={teacherMark === true ? '✅' : teacherMark === false ? '❌' : MARK_NEEDS_TEACHER}
                    markClickable={!!onToggleCorrect}
                    onMarkClick={() => onToggleCorrect && onToggleCorrect(gapIdx)}
                    markTitle={onToggleCorrect ? 'Click to toggle teacher mark' : 'Teacher will check this'}
                />
                {reviewMode && (
                    <GapRewardEditors
                        maxStars={maxStars}
                        maxCoins={maxCoins}
                        stars={rewardStars}
                        coins={rewardCoins}
                        onChange={(next) => onRewardChange?.(gapIdx, next)}
                    />
                )}
            </span>
            {reviewMode && (
                <span style={{ display: 'block', marginTop: '6px', fontSize: '0.75rem', color: '#475569', maxWidth: '340px', lineHeight: 1.45, padding: '6px 8px', background: '#f1f5f9', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <span style={{ display: 'block' }}><strong>Suggested:</strong> {suggestedText || '—'}{!hasKey && ' (no answer key)'}</span>
                    <span style={{ display: 'block', marginTop: '4px' }}><strong>Student self-check:</strong> {studentSelfCheckStored === true ? '✅ Correct' : studentSelfCheckStored === false ? '❌ Not correct' : '—'}</span>
                    {aiFeedbackBlock}
                </span>
            )}
            {!reviewMode && showRevealBlock && (
                <span style={{ display: 'block', marginTop: '6px', fontSize: '0.78rem', color: '#1e40af', padding: '6px 8px', background: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe', maxWidth: '300px', lineHeight: 1.4 }}>
                    <span style={{ display: 'block', fontWeight: 400, color: '#64748b', marginBottom: '6px' }}>
                        Suggested answer:{' '}
                        <strong style={{ fontWeight: 700, color: '#64748b' }}>{suggestedText || '(no answer key)'}</strong>
                    </span>
                    {!readOnly && (
                        <span style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <button
                                type="button"
                                onClick={() => onSelfRate && onSelfRate(gapIdx, true)}
                                style={{
                                    padding: '4px 12px', borderRadius: '8px', border: '1.5px solid #6ee7b7',
                                    background: selfRating === true ? '#d1fae5' : 'white',
                                    fontWeight: 700, cursor: 'pointer', fontSize: '0.75rem',
                                }}
                            >Correct</button>
                            <button
                                type="button"
                                onClick={() => onSelfRate && onSelfRate(gapIdx, false)}
                                style={{
                                    padding: '4px 12px', borderRadius: '8px', border: '1.5px solid #fca5a5',
                                    background: selfRating === false ? '#fee2e2' : 'white',
                                    fontWeight: 700, cursor: 'pointer', fontSize: '0.75rem',
                                }}
                            >Not correct</button>
                        </span>
                    )}
                    {readOnly && (selfRating === true || selfRating === false) && (
                        <span style={{ display: 'block', marginTop: '6px', fontWeight: 600, color: '#334155' }}>
                            You marked: {selfRating ? 'Correct' : 'Not correct'}
                        </span>
                    )}
                    {aiFeedbackBlock}
                </span>
            )}
        </span>
    );
}
GapSelfCheckBlock.displayName = 'GapSelfCheckBlock';

/* ═══════════════════════════════════════════════════════════════════════════
   FILL IN THE GAPS
═══════════════════════════════════════════════════════════════════════════ */
function FillGapsInteractive({ exercise, assignment, preview, onComplete, reviewMode, onUpdateAssignment, onUpdate, readOnly, isOnlineLesson, updateSlideState, slideState, user, isTeacher, showSolution, practiceMode }) {
    'use no memo';
    const [parts, setParts] = useState([]);
    useEffect(() => {
        setParts(parseFillGapsStructuredBody(exercise.body || ''));
    }, [exercise.body]);
    const gaps = useMemo(() => collectGapParts(parts), [parts]);
    const alreadyDone = isAlreadyDone(assignment) && !reviewMode;
    const [answers, setAnswers] = useState(assignment?.answers || {});
    const [locked, setLocked] = useState(
        readOnly || reviewMode ? Object.fromEntries(gaps.map((_, i) => [i, true])) :
        alreadyDone ? Object.fromEntries(gaps.map((_, i) => [i, true])) : {}
    );
    const [isCorrectMap, setIsCorrectMap] = useState(assignment?.is_correct || {});
    const [gapAttempts, setGapAttempts] = useState(assignment?.gap_attempts || {});
    const [gapRewards, setGapRewards] = useState(() => (
        assignment?.gap_rewards
        || buildGapRewardsFromIsCorrect(exercise, assignment?.is_correct || {}, (assignment?.total || 0))
    ));
    const [completed, setCompleted] = useState(readOnly || reviewMode || alreadyDone);
    const rewardCaps = useMemo(() => getPerGapRewardCaps(exercise), [exercise]);
    const threeAttempts = isThreeAttemptsEnabled(exercise);
    const [localUnblurred, setLocalUnblurred] = useState({});
    const currentUnblurred = isOnlineLesson ? (slideState?.unblurred || {}) : localUnblurred;

    // Draft answers - unsaved typing state, persisted separately to prevent data loss
    const [draftAnswers, setDraftAnswers] = useState({});

    const activeAnswers = isOnlineLesson ? (slideState?.answers || answers) : answers;
    const activeLocked = isOnlineLesson ? (slideState?.locked || locked) : locked;
    const activeIsCorrect = isOnlineLesson ? (slideState?.is_correct || isCorrectMap) : isCorrectMap;
    const activeGapAttempts = isOnlineLesson ? (slideState?.gap_attempts || gapAttempts) : gapAttempts;
    // Merge submitted answers with drafts (drafts take precedence for display)
    const effectiveAnswers = useMemo(() => ({ ...activeAnswers, ...draftAnswers }), [activeAnswers, draftAnswers]);

    // Draft persistence key (separate from submitted answers)
    const DRAFT_KEY = useMemo(() => `ex_draft_${assignment?.id}`, [assignment?.id]);

    // Load draft answers from localStorage on mount
    useEffect(() => {
        if (isOnlineLesson || !assignment?.id || alreadyDone || readOnly) return;
        try {
            const raw = localStorage.getItem(DRAFT_KEY);
            if (raw) {
                const draft = JSON.parse(raw);
                if (draft && typeof draft === 'object') {
                    setDraftAnswers(draft);
                }
            }
        } catch (e) {
            console.warn('[FillGaps] Failed to load draft answers:', e);
        }
    }, [DRAFT_KEY, isOnlineLesson, assignment?.id, alreadyDone, readOnly]);

    const hasAutoCheck = gaps.some(g => g.answers.length > 0);
    const { syncStatus, persist, getLocalData } = useExercisePersistence(
        assignment?.id,
        {
            answers: activeAnswers,
            locked: activeLocked,
            status: completed ? 'completed' : 'working',
            is_correct: activeIsCorrect,
            ...(threeAttempts ? { gap_attempts: activeGapAttempts } : {}),
        },
        onUpdate,
        readOnly || alreadyDone,
        assignment,
        isOnlineLesson,
        updateSlideState,
        slideState,
    );

    const draftAnswersRef = useRef(draftAnswers);
    draftAnswersRef.current = draftAnswers;
    const activeAnswersRef = useRef(activeAnswers);
    activeAnswersRef.current = activeAnswers;
    const activeLockedRef = useRef(activeLocked);
    activeLockedRef.current = activeLocked;
    const isCorrectMapRef = useRef(activeIsCorrect);
    isCorrectMapRef.current = activeIsCorrect;

    // Persist drafts as the student types (not only on blur/submit)
    const handleDraftChange = useCallback((idx, val) => {
        const nextDrafts = { ...draftAnswersRef.current, [idx]: val };
        draftAnswersRef.current = nextDrafts;
        setDraftAnswers(nextDrafts);
        try {
            localStorage.setItem(DRAFT_KEY, JSON.stringify(nextDrafts));
        } catch (e) {
            console.warn('[FillGaps] Failed to save draft answers:', e);
        }

        const mergedAnswers = { ...activeAnswersRef.current };
        Object.entries(nextDrafts).forEach(([draftIdx, draftVal]) => {
            if (draftVal != null && String(draftVal).trim() !== '') {
                mergedAnswers[draftIdx] = draftVal;
            }
        });
        persist({ answers: mergedAnswers, locked: activeLockedRef.current, status: 'working', is_correct: isCorrectMapRef.current });
    }, [DRAFT_KEY, persist]);

    const handleSubmitAnswer = (idx, val) => {
        const gap = gaps[idx];
        const hasKey = Array.isArray(gap?.answers) && gap.answers.some((a) => String(a || '').trim());
        const ok = hasKey ? gap.answers.some(a => normalizeAnswer(a) === normalizeAnswer(val)) : null;

        let nextAnswers = { ...activeAnswers, [idx]: val };
        let nextLocked = { ...activeLocked, [idx]: true };
        let nextIsCorrect = { ...activeIsCorrect };
        if (ok === null) {
            delete nextIsCorrect[idx];
            delete nextIsCorrect[String(idx)];
        } else {
            nextIsCorrect = { ...activeIsCorrect, [idx]: ok, [String(idx)]: ok };
        }
        let nextGapAttempts = activeGapAttempts;

        if (threeAttempts && hasKey) {
            const applied = applyGapAttempt(getGapAttemptResults(activeGapAttempts, idx), ok);
            nextGapAttempts = setGapAttemptResults(activeGapAttempts, idx, applied.results);
            nextLocked = { ...activeLocked, [idx]: applied.lock };
            if (applied.isCorrect === null) {
                const { [idx]: _a, [String(idx)]: _b, ...rest } = nextIsCorrect;
                nextIsCorrect = rest;
            } else {
                nextIsCorrect = { ...activeIsCorrect, [idx]: applied.isCorrect, [String(idx)]: applied.isCorrect };
            }
            if (applied.clearAnswer) {
                nextAnswers = { ...activeAnswers, [idx]: '' };
            }
        }

        const nextDrafts = { ...draftAnswersRef.current };
        if (threeAttempts && nextAnswers[idx] === '') {
            nextDrafts[idx] = '';
        } else {
            delete nextDrafts[idx];
        }
        draftAnswersRef.current = nextDrafts;
        setDraftAnswers(nextDrafts);

        if (!isOnlineLesson) {
            setAnswers(nextAnswers);
            setLocked(nextLocked);
            setIsCorrectMap(nextIsCorrect);
            if (threeAttempts) setGapAttempts(nextGapAttempts);
        }

        try {
            localStorage.setItem(DRAFT_KEY, JSON.stringify(nextDrafts));
        } catch (e) {
            console.warn('[FillGaps] Failed to update draft answers:', e);
        }

        persist({
            answers: nextAnswers,
            locked: nextLocked,
            status: 'working',
            is_correct: nextIsCorrect,
            ...(threeAttempts ? { gap_attempts: nextGapAttempts } : {}),
        });
    };

    const setGlobalLocked = (idx, val) => {
        const next = { ...activeLocked, [idx]: val };
        if (!isOnlineLesson) setLocked(next);
        persist({ answers: activeAnswers, locked: next, status: 'working', is_correct: activeIsCorrect });
    };

    const handleToggleCorrect = (idx) => {
        if (!reviewMode || !onUpdateAssignment) return;
        const k = String(idx);
        const cur = isCorrectMap[idx] === true || isCorrectMap[k] === true;
        const nextOk = !cur;
        const newMap = { ...isCorrectMap, [idx]: nextOk, [k]: nextOk };
        const newRewards = {
            ...gapRewards,
            [k]: {
                stars: nextOk ? rewardCaps.maxStars : 0,
                coins: nextOk ? rewardCaps.maxCoins : 0,
            },
        };
        const score = Object.values(newMap).filter((v) => v === true).length;
        const rewards = sumGapRewards(newRewards, exercise);
        setIsCorrectMap(newMap);
        setGapRewards(newRewards);
        onUpdateAssignment(assignment.id, {
            is_correct: newMap,
            gap_rewards: newRewards,
            score,
            awarded_stars: rewards.awarded_stars,
            awarded_coins: rewards.awarded_coins,
        });
    };

    const handleGapRewardChange = (idx, partial) => {
        if (!reviewMode || !onUpdateAssignment) return;
        const k = String(idx);
        const prev = gapRewards[k] || gapRewards[idx] || { stars: 0, coins: 0 };
        let stars = partial.stars !== undefined ? Number(partial.stars) : (parseFloat(prev.stars) || 0);
        let coins = partial.coins !== undefined ? Number(partial.coins) : (parseInt(prev.coins, 10) || 0);
        if (Number.isNaN(stars)) stars = 0;
        if (Number.isNaN(coins)) coins = 0;
        if (rewardCaps.maxStars > 0) stars = Math.max(0, Math.min(rewardCaps.maxStars, stars));
        else stars = 0;
        if (rewardCaps.maxCoins > 0) coins = Math.max(0, Math.min(rewardCaps.maxCoins, Math.round(coins)));
        else coins = 0;
        const ok = isMaxGapReward(stars, coins, rewardCaps.maxStars, rewardCaps.maxCoins);
        const newMap = { ...isCorrectMap, [idx]: ok, [k]: ok };
        const newRewards = { ...gapRewards, [k]: { stars, coins } };
        const score = Object.values(newMap).filter((v) => v === true).length;
        const rewards = sumGapRewards(newRewards, exercise);
        setIsCorrectMap(newMap);
        setGapRewards(newRewards);
        onUpdateAssignment(assignment.id, {
            is_correct: newMap,
            gap_rewards: newRewards,
            score,
            awarded_stars: rewards.awarded_stars,
            awarded_coins: rewards.awarded_coins,
        });
    };

    const handleBlurClick = (e) => {
        const target = e.target;
        const blurTarget = target.closest('.blurred-text');
        if (blurTarget) {
            e.stopPropagation();
            const blurId = blurTarget.getAttribute('data-blur-id');
            const nextUnblurred = { ...currentUnblurred, [blurId]: !currentUnblurred[blurId] };
            if (isOnlineLesson && isTeacher && updateSlideState) {
                updateSlideState({ ...slideState, unblurred: nextUnblurred });
            } else if (isOnlineLesson && !isTeacher) {
                setLocalUnblurred(nextUnblurred);
            } else if (!isOnlineLesson) {
                setLocalUnblurred(nextUnblurred);
                persist({ answers: { ...activeAnswers, unblurred: nextUnblurred }, locked: activeLocked, status: 'working', is_correct: isCorrectMap });
            }
        }
    };

    useEffect(() => {
        if (!isOnlineLesson && (readOnly || reviewMode || alreadyDone) && assignment) {
            if (assignment.answers) setAnswers(assignment.answers);
            if (assignment.locked) setLocked(assignment.locked);
            if (assignment.gap_attempts) setGapAttempts(assignment.gap_attempts);
            if (assignment.status === 'completed' || assignment.status === 'checked') setCompleted(true);
        }
    }, [readOnly, reviewMode, alreadyDone, assignment, isOnlineLesson]);

    // Teacher review: sync ✅/❌ marks from assignment or auto-compute from answers
    useEffect(() => {
        if (!reviewMode || !assignment || !exercise) return;
        const stored = assignment.is_correct;
        let nextMap = stored && typeof stored === 'object' ? stored : {};
        if (stored && typeof stored === 'object' && Object.keys(stored).length > 0) {
            setIsCorrectMap(stored);
        } else if (assignment.answers) {
            const graded = computeAutoGrade(exercise, assignment.answers);
            nextMap = graded.is_correct;
            setIsCorrectMap(graded.is_correct);
        }
        if (assignment.gap_rewards && typeof assignment.gap_rewards === 'object' && Object.keys(assignment.gap_rewards).length > 0) {
            setGapRewards(assignment.gap_rewards);
        } else {
            setGapRewards(buildGapRewardsFromIsCorrect(exercise, nextMap || {}, gaps.length));
        }
    }, [reviewMode, assignment, exercise, gaps.length]);

    useEffect(() => {
        if (isOnlineLesson) return;
        const local = getLocalData();
        if (local && !alreadyDone && !readOnly) {
            if (local.answers) setAnswers(local.answers);
            if (local.locked) setLocked(local.locked);
        }
    }, [getLocalData, alreadyDone, readOnly, isOnlineLesson]);

    // Clear drafts when exercise is completed
    useEffect(() => {
        if (completed && !isOnlineLesson && assignment?.id) {
            try {
                localStorage.removeItem(DRAFT_KEY);
                setDraftAnswers({});
            } catch (e) {
                console.warn('[FillGaps] Failed to clear draft answers:', e);
            }
        }
    }, [completed, isOnlineLesson, assignment?.id, DRAFT_KEY]);

    const gapIdxRef = useRef(0);
    gapIdxRef.current = 0;
    return (
        <div className="exercise-card-shell" style={CARD_STYLE}>
            <ExerciseLead exercise={exercise} />
            <div style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                <div className="exercise-gap-content" onClick={handleBlurClick}>
                    {renderFillGapsStructuredParts(parts, {
                        idPrefix: 'fg',
                        nextGapIdx: () => gapIdxRef.current++,
                        renderText: (p, key) => (
                            <InlineAudioRenderer key={key} inline html={renderMarkdownToHtml(p.text, currentUnblurred, key, true)} />
                        ),
                        renderGap: (p, gi, key) => (
                            <GapInput key={key} gapIdx={gi} prompt={p.prompt} answers={p.answers}
                                globalAnswers={effectiveAnswers} onSubmit={handleSubmitAnswer}
                                locked={activeLocked} draggable={alreadyDone || readOnly}
                                reviewMode={reviewMode} isCorrect={activeIsCorrect[gi] ?? activeIsCorrect[String(gi)]}
                                onToggleCorrect={handleToggleCorrect}
                                readOnly={alreadyDone || readOnly}
                                showSolution={showSolution}
                                solutionValue={p.answers?.[0] || ''}
                                draftValue={draftAnswers[gi]}
                                onDraftChange={handleDraftChange}
                                assignment={assignment}
                                maxStars={rewardCaps.maxStars}
                                maxCoins={rewardCaps.maxCoins}
                                rewardStars={(gapRewards[String(gi)] || gapRewards[gi] || {}).stars ?? 0}
                                rewardCoins={(gapRewards[String(gi)] || gapRewards[gi] || {}).coins ?? 0}
                                onRewardChange={handleGapRewardChange}
                                showAttempts={threeAttempts}
                                attemptResults={getGapAttemptResults(activeGapAttempts, gi)}
                            />
                        ),
                    })}
                </div>
            </div>
            {!preview && (
                    <ExerciseActionBar>
                        {!readOnly && !reviewMode && (!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && <button type="button" className={(alreadyDone || completed) ? COMPLETE_DONE_CLASS : COMPLETE_BTN} disabled={!!(alreadyDone || completed)} onClick={(alreadyDone || completed) ? undefined : (() => {
                            const mergedAnswers = { ...activeAnswers };
                            Object.entries(draftAnswers || {}).forEach(([draftIdx, draftVal]) => {
                                if (draftVal != null && String(draftVal).trim() !== '') {
                                    mergedAnswers[draftIdx] = draftVal;
                                }
                            });
                            const nextLocked = Object.fromEntries(gaps.map((_, i) => [i, true]));
                            if (!isOnlineLesson) {
                                setAnswers(mergedAnswers);
                                setLocked(nextLocked);
                                setDraftAnswers({});
                                try { localStorage.removeItem(DRAFT_KEY); } catch (e) { /* ignore */ }
                            }
                            setCompleted(true);
                            const isCorrectMapFinal = Object.fromEntries(gaps.map((g, i) => {
                                const hasKey = (g.answers || []).some((a) => String(a || '').trim());
                                if (!hasKey) return [i, null];
                                return [i, g.answers.some(a => normalizeAnswer(a) === normalizeAnswer(mergedAnswers[i]))];
                            }));
                            const finalScore = Object.values(isCorrectMapFinal).filter((v) => v === true).length;
                            setIsCorrectMap(isCorrectMapFinal);
                            persist({ answers: mergedAnswers, locked: nextLocked, status: 'completed', is_correct: isCorrectMapFinal });
                            if (onComplete) {
                                onComplete({ answers: mergedAnswers, score: finalScore, total: gaps.length, is_correct: isCorrectMapFinal, status: 'completed' });
                            }
                        })}>{(alreadyDone || completed) ? COMPLETE_DONE_LABEL : COMPLETE_LABEL}</button>}
                        {(!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                            <>
                                {hasAutoCheck && !reviewMode ? (
                                    <ExerciseScoreBox submitted={completed}>
                                        Score: {Object.values(activeIsCorrect).filter(v => v === true).length} / {gaps.length}
                                    </ExerciseScoreBox>
                                ) : !hasAutoCheck ? (
                                    <AssignmentStatusPill assignment={assignment} completed={completed} isOnlineLesson={isOnlineLesson}  practiceMode={practiceMode} />
                                ) : null}
                            </>
                        )}
                    </ExerciseActionBar>
            )}
            <TeacherComment exercise={exercise} assignment={assignment} />
        </div>
    );
}
FillGapsInteractive.displayName = 'FillGapsInteractive';

/* ═══════════════════════════════════════════════════════════════════════════
   FILL IN THE GAPS (SELF-CHECK)
═══════════════════════════════════════════════════════════════════════════ */
function FillGapsSelfCheckInteractive({ exercise, assignment, preview, onComplete, reviewMode, onUpdateAssignment, onUpdate, readOnly, isOnlineLesson, updateSlideState, slideState, user, isTeacher, showSolution, practiceMode }) {
    'use no memo';
    const [parts, setParts] = useState([]);
    useEffect(() => {
        setParts(parseFillGapsStructuredBody(exercise.body || ''));
    }, [exercise.body]);
    const gaps = useMemo(() => collectGapParts(parts), [parts]);
    const alreadyDone = isAlreadyDone(assignment) && !reviewMode;

    const initialMeta = readFillGapsSelfCheckMeta(assignment?.answers || {});
    const [answers, setAnswers] = useState(extractGapAnswersOnly(assignment?.answers || {}));
    const [selfCheckMap, setSelfCheckMap] = useState(initialMeta.student_self_check || {});
    const [locked, setLocked] = useState(
        readOnly || reviewMode || alreadyDone ? gaps.reduce((acc, _, i) => { acc[i] = true; return acc; }, {}) : {}
    );
    const [isCorrectMap, setIsCorrectMap] = useState(assignment?.is_correct || {});
    const [gapRewards, setGapRewards] = useState(() => (
        assignment?.gap_rewards
        || buildGapRewardsFromIsCorrect(exercise, assignment?.is_correct || {}, gaps.length)
    ));
    const [completed, setCompleted] = useState(readOnly || reviewMode || alreadyDone);
    const rewardCaps = useMemo(() => getPerGapRewardCaps(exercise), [exercise]);
    const [localUnblurred, setLocalUnblurred] = useState({});
    const currentUnblurred = isOnlineLesson ? (slideState?.unblurred || {}) : localUnblurred;
    
    // Draft answers - unsaved typing state, persisted separately to prevent data loss
    const [draftAnswers, setDraftAnswers] = useState({});

    const activeAnswers = isOnlineLesson ? extractGapAnswersOnly(slideState?.answers || {}) : answers;
    const activeLocked = isOnlineLesson ? (slideState?.locked || locked) : locked;
    const activeSelfCheck = isOnlineLesson ? (readFillGapsSelfCheckMeta(slideState?.answers || {}).student_self_check || {}) : selfCheckMap;
    // Merge submitted answers with drafts (drafts take precedence for display)
    const effectiveAnswers = useMemo(() => ({ ...activeAnswers, ...draftAnswers }), [activeAnswers, draftAnswers]);

    // Draft persistence key (separate from submitted answers)
    const DRAFT_KEY = useMemo(() => `ex_draft_${assignment?.id}`, [assignment?.id]);
    
    // Load draft answers from localStorage on mount
    useEffect(() => {
        if (isOnlineLesson || !assignment?.id || alreadyDone || readOnly) return;
        try {
            const raw = localStorage.getItem(DRAFT_KEY);
            if (raw) {
                const draft = JSON.parse(raw);
                if (draft && typeof draft === 'object') {
                    setDraftAnswers(draft);
                }
            }
        } catch (e) {
            console.warn('[FillGapsSelfCheck] Failed to load draft answers:', e);
        }
    }, [DRAFT_KEY, isOnlineLesson, assignment?.id, alreadyDone, readOnly]);
    
    const { syncStatus, persist, getLocalData } = useExercisePersistence(
        assignment?.id,
        { answers: buildFillGapsSelfCheckAnswersPayload(activeAnswers, activeSelfCheck, undefined, assignment?.answers), locked: activeLocked, status: completed ? 'completed' : 'working', is_correct: isCorrectMap },
        onUpdate,
        readOnly || alreadyDone,
        assignment,
        isOnlineLesson,
        updateSlideState,
        slideState
    );

    const draftAnswersRef = useRef(draftAnswers);
    draftAnswersRef.current = draftAnswers;
    const activeAnswersRef = useRef(activeAnswers);
    activeAnswersRef.current = activeAnswers;
    const activeLockedRef = useRef(activeLocked);
    activeLockedRef.current = activeLocked;
    const isCorrectMapRef = useRef(isCorrectMap);
    isCorrectMapRef.current = isCorrectMap;
    const activeSelfCheckRef = useRef(activeSelfCheck);
    activeSelfCheckRef.current = activeSelfCheck;

    const handleDraftChange = useCallback((idx, val) => {
        const nextDrafts = { ...draftAnswersRef.current, [idx]: val };
        draftAnswersRef.current = nextDrafts;
        setDraftAnswers(nextDrafts);
        try {
            localStorage.setItem(DRAFT_KEY, JSON.stringify(nextDrafts));
        } catch (e) {
            console.warn('[FillGapsSelfCheck] Failed to save draft answers:', e);
        }

        const mergedAnswers = { ...activeAnswersRef.current };
        Object.entries(nextDrafts).forEach(([draftIdx, draftVal]) => {
            if (draftVal != null && String(draftVal).trim() !== '') {
                mergedAnswers[draftIdx] = draftVal;
            }
        });
        const payload = buildFillGapsSelfCheckAnswersPayload(mergedAnswers, activeSelfCheckRef.current, undefined, assignment?.answers);
        persist({ answers: payload, locked: activeLockedRef.current, status: 'working', is_correct: isCorrectMapRef.current });
    }, [DRAFT_KEY, persist, assignment?.answers]);

    const handleSubmitAnswer = (idx, val) => {
        const nextAnswers = { ...activeAnswers, [idx]: val };
        const nextLocked = { ...activeLocked, [idx]: true };
        // Clear draft for this gap since it's now submitted
        const nextDrafts = { ...draftAnswersRef.current };
        delete nextDrafts[idx];
        draftAnswersRef.current = nextDrafts;
        setDraftAnswers(nextDrafts);
        
        // Update localStorage drafts immediately on submit
        try {
            localStorage.setItem(DRAFT_KEY, JSON.stringify(nextDrafts));
        } catch (e) {
            console.warn('[FillGapsSelfCheck] Failed to update draft answers:', e);
        }
        
        if (!isOnlineLesson) {
            setAnswers(nextAnswers);
            setLocked(nextLocked);
            const payload = buildFillGapsSelfCheckAnswersPayload(nextAnswers, activeSelfCheck, undefined, assignment?.answers);
            persist({ answers: payload, locked: nextLocked, status: 'working', is_correct: isCorrectMap });
        } else if (updateSlideState) {
            const payload = buildFillGapsSelfCheckAnswersPayload(nextAnswers, activeSelfCheck, undefined, assignment?.answers);
            updateSlideState({ ...slideState, answers: payload, locked: nextLocked });
        }
    };

    const handleSelfRate = (idx, isCorrect) => {
        const nextSelf = { ...activeSelfCheck, [idx]: isCorrect };
        // Use ref to get latest answers (avoid stale closure from async state updates)
        const latestAnswers = answers || activeAnswers;
        if (!isOnlineLesson) {
            setSelfCheckMap(nextSelf);
            const payload = buildFillGapsSelfCheckAnswersPayload(latestAnswers, nextSelf, undefined, assignment?.answers);
            persist({ answers: payload, locked: activeLocked, status: 'working', is_correct: isCorrectMap });
        } else if (updateSlideState) {
            const payload = buildFillGapsSelfCheckAnswersPayload(latestAnswers, nextSelf, undefined, assignment?.answers);
            updateSlideState({ ...slideState, answers: payload, locked: activeLocked });
        }
    };

    const handleToggleCorrect = (idx) => {
        if (!reviewMode || !onUpdateAssignment) return;
        const cur = isCorrectMap[idx];
        const nextVal = cur === true ? false : true;
        const k = String(idx);
        const newMap = { ...isCorrectMap, [idx]: nextVal, [k]: nextVal };
        const newRewards = {
            ...gapRewards,
            [k]: {
                stars: nextVal ? rewardCaps.maxStars : 0,
                coins: nextVal ? rewardCaps.maxCoins : 0,
            },
        };
        const newScore = Object.values(newMap).filter(v => v === true).length;
        const rewards = sumGapRewards(newRewards, exercise);
        setIsCorrectMap(newMap);
        setGapRewards(newRewards);
        onUpdateAssignment(assignment.id, {
            is_correct: newMap,
            gap_rewards: newRewards,
            score: newScore,
            awarded_stars: rewards.awarded_stars,
            awarded_coins: rewards.awarded_coins,
        });
    };

    const handleGapRewardChange = (idx, partial) => {
        if (!reviewMode || !onUpdateAssignment) return;
        const k = String(idx);
        const prev = gapRewards[k] || gapRewards[idx] || { stars: 0, coins: 0 };
        let stars = partial.stars !== undefined ? Number(partial.stars) : (parseFloat(prev.stars) || 0);
        let coins = partial.coins !== undefined ? Number(partial.coins) : (parseInt(prev.coins, 10) || 0);
        if (Number.isNaN(stars)) stars = 0;
        if (Number.isNaN(coins)) coins = 0;
        if (rewardCaps.maxStars > 0) stars = Math.max(0, Math.min(rewardCaps.maxStars, stars));
        else stars = 0;
        if (rewardCaps.maxCoins > 0) coins = Math.max(0, Math.min(rewardCaps.maxCoins, Math.round(coins)));
        else coins = 0;
        const ok = isMaxGapReward(stars, coins, rewardCaps.maxStars, rewardCaps.maxCoins);
        const newMap = { ...isCorrectMap, [idx]: ok, [k]: ok };
        const newRewards = { ...gapRewards, [k]: { stars, coins } };
        const newScore = Object.values(newMap).filter(v => v === true).length;
        const rewards = sumGapRewards(newRewards, exercise);
        setIsCorrectMap(newMap);
        setGapRewards(newRewards);
        onUpdateAssignment(assignment.id, {
            is_correct: newMap,
            gap_rewards: newRewards,
            score: newScore,
            awarded_stars: rewards.awarded_stars,
            awarded_coins: rewards.awarded_coins,
        });
    };

    const handleBlurClick = (e) => {
        const target = e.target;
        const blurTarget = target.closest('.blurred-text');
        if (blurTarget) {
            e.stopPropagation();
            const blurId = blurTarget.getAttribute('data-blur-id');
            const nextUnblurred = { ...currentUnblurred, [blurId]: !currentUnblurred[blurId] };
            if (isOnlineLesson && isTeacher && updateSlideState) {
                updateSlideState({ ...slideState, unblurred: nextUnblurred });
            } else if (isOnlineLesson && !isTeacher) {
                setLocalUnblurred(nextUnblurred);
            } else if (!isOnlineLesson) {
                setLocalUnblurred(nextUnblurred);
            }
        }
    };

    useEffect(() => {
        if (!isOnlineLesson && (readOnly || reviewMode || alreadyDone) && assignment) {
            if (assignment.answers) {
                setAnswers(extractGapAnswersOnly(assignment.answers));
                const m = readFillGapsSelfCheckMeta(assignment.answers);
                setSelfCheckMap(m.student_self_check || {});
            }
            if (assignment.locked) setLocked(assignment.locked);
            if (assignment.status === 'completed' || assignment.status === 'checked') setCompleted(true);
        }
    }, [readOnly, reviewMode, alreadyDone, assignment, isOnlineLesson]);

    useEffect(() => {
        if (!reviewMode || !assignment || !exercise) return;
        const stored = assignment.is_correct;
        if (stored && typeof stored === 'object' && Object.keys(stored).length > 0) {
            setIsCorrectMap(stored);
            return;
        }
        if (assignment.answers) {
            const { is_correct } = computeAutoGrade(exercise, assignment.answers);
            setIsCorrectMap(is_correct);
        }
    }, [reviewMode, assignment, exercise]);

    useEffect(() => {
        if (isOnlineLesson) return;
        const local = getLocalData();
        if (local && !alreadyDone && !readOnly) {
            if (local.answers) {
                setAnswers(extractGapAnswersOnly(local.answers));
                const m = readFillGapsSelfCheckMeta(local.answers);
                if (m.student_self_check) setSelfCheckMap(m.student_self_check);
            }
            if (local.locked) setLocked(local.locked);
        }
    }, [getLocalData, alreadyDone, readOnly, isOnlineLesson]);

    // Clear drafts when exercise is completed
    useEffect(() => {
        if (completed && !isOnlineLesson && assignment?.id) {
            try {
                localStorage.removeItem(DRAFT_KEY);
                setDraftAnswers({});
            } catch (e) {
                console.warn('[FillGapsSelfCheck] Failed to clear draft answers:', e);
            }
        }
    }, [completed, isOnlineLesson, assignment?.id, DRAFT_KEY]);

    const suggestedSnapshot = useMemo(() => Object.fromEntries(gaps.map((g, i) => [String(i), formatSuggestedForGap(g)])), [gaps]);
    const aiFeedbackMap = useMemo(() => readFillGapsSelfCheckMeta(assignment?.answers || {}).ai_feedback || {}, [assignment?.answers]);

    const allGapsLocked = gaps.length > 0 && gaps.every((_, i) => activeLocked[i]);
    const allSelfRated = gaps.length > 0 && gaps.every((_, i) => activeSelfCheck[i] === true || activeSelfCheck[i] === false);
    const canComplete = allGapsLocked && allSelfRated;
    const selfScore = Object.values(activeSelfCheck).filter(v => v === true).length;

    const gapIdxRef = useRef(0);
    gapIdxRef.current = 0;
    return (
        <div className="exercise-card-shell" style={CARD_STYLE}>
            <ExerciseLead exercise={exercise} />
            <div className="exercise-gap-content" onClick={handleBlurClick}>
                {renderFillGapsStructuredParts(parts, {
                    idPrefix: 'fgsc',
                    nextGapIdx: () => gapIdxRef.current++,
                    renderText: (p, key) => (
                        <InlineAudioRenderer key={key} inline html={renderMarkdownToHtml(p.text, currentUnblurred, key, true)} />
                    ),
                    renderGap: (p, gi, key) => {
                        const meta = readFillGapsSelfCheckMeta(assignment?.answers || {});
                        const snap = meta.suggested_answers || {};
                        const suggestedText = (snap[String(gi)] !== undefined && snap[String(gi)] !== '') ? snap[String(gi)] : formatSuggestedForGap(p);
                        return (
                            <GapSelfCheckBlock
                                key={key}
                                gapIdx={gi}
                                gapPart={p}
                                globalAnswers={effectiveAnswers}
                                onSubmitGap={handleSubmitAnswer}
                                lockedMap={activeLocked}
                                selfRating={activeSelfCheck[gi]}
                                onSelfRate={handleSelfRate}
                                reviewMode={reviewMode}
                                teacherMark={isCorrectMap[gi] ?? isCorrectMap[String(gi)]}
                                onToggleCorrect={handleToggleCorrect}
                                readOnly={alreadyDone || readOnly}
                                showSolution={showSolution}
                                suggestedText={suggestedText}
                                studentSelfCheckStored={(meta.student_self_check || {})[gi]}
                                draftValue={draftAnswers[gi]}
                                onDraftChange={handleDraftChange}
                                aiFeedback={aiFeedbackMap[String(gi)] || aiFeedbackMap[gi] || ''}
                                maxStars={rewardCaps.maxStars}
                                maxCoins={rewardCaps.maxCoins}
                                rewardStars={(gapRewards[String(gi)] || gapRewards[gi] || {}).stars ?? 0}
                                rewardCoins={(gapRewards[String(gi)] || gapRewards[gi] || {}).coins ?? 0}
                                onRewardChange={handleGapRewardChange}
                            />
                        );
                    },
                })}
            </div>
            {!preview && (
                <ExerciseActionBar>
                    {!readOnly && !reviewMode && (!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                        <button
                            type="button"
                            className={(alreadyDone || completed) ? COMPLETE_DONE_CLASS : COMPLETE_BTN}
                            disabled={!!(alreadyDone || completed)} onClick={(alreadyDone || completed) ? undefined : (() => {
                                setCompleted(true);

                                // Ensure all gaps are locked (submitted)
                                const nextLocked = Object.fromEntries(gaps.map((_, i) => [i, true]));
                                if (!isOnlineLesson) setLocked(nextLocked);

                                // Flush drafts into submitted answers before scoring/complete
                                const mergedAnswers = { ...activeAnswers };
                                Object.entries(draftAnswers || {}).forEach(([draftIdx, draftVal]) => {
                                    if (draftVal != null && String(draftVal).trim() !== '') {
                                        mergedAnswers[draftIdx] = draftVal;
                                    }
                                });
                                if (!isOnlineLesson) {
                                    setAnswers(mergedAnswers);
                                    setDraftAnswers({});
                                    try { localStorage.removeItem(DRAFT_KEY); } catch (e) { /* ignore */ }
                                }

                                // Ensure all gaps have a self-check status (default to false if not set)
                                const finalSelfCheck = Object.fromEntries(gaps.map((_, idx) => [
                                    idx,
                                    (activeSelfCheck[idx] === true || activeSelfCheck[idx] === false)
                                        ? activeSelfCheck[idx]
                                        : false // Default to not correct if not self-checked
                                ]));
                                if (!isOnlineLesson) setSelfCheckMap(finalSelfCheck);
                                
                                const finalAnswers = buildFillGapsSelfCheckAnswersPayload(mergedAnswers, finalSelfCheck, suggestedSnapshot, assignment?.answers);
                                const isCorrectFinal = Object.fromEntries(gaps.map((_, idx) => [idx, finalSelfCheck[idx] === true]));
                                const finalScore = gaps.filter((_, idx) => finalSelfCheck[idx] === true).length;

                                if (onComplete) {
                                    onComplete({
                                        answers: finalAnswers,
                                        score: finalScore,
                                        total: gaps.length,
                                        is_correct: isCorrectFinal,
                                        status: 'completed',
                                    });
                                }

                                if (!isOnlineLesson) {
                                    persist({ answers: finalAnswers, locked: nextLocked, status: 'completed', is_correct: isCorrectFinal });
                                }

                                if (isOnlineLesson && updateSlideState) {
                                    updateSlideState({
                                        ...slideState,
                                        answers: finalAnswers,
                                        locked: nextLocked,
                                        status: 'completed',
                                        is_correct: isCorrectFinal,
                                    });
                                }
                            })}
                        >{(alreadyDone || completed) ? COMPLETE_DONE_LABEL : COMPLETE_LABEL}</button>
                    )}
                    {(!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && !reviewMode && (
                        <ExerciseScoreBox submitted={completed}>
                            Self-check score: {selfScore} / {gaps.length}
                        </ExerciseScoreBox>
                    )}
                </ExerciseActionBar>
            )}
            <TeacherComment exercise={exercise} assignment={assignment} />
        </div>
    );
}
FillGapsSelfCheckInteractive.displayName = 'FillGapsSelfCheckInteractive';

/* ═══════════════════════════════════════════════════════════════════════════
   FILL IN THE GAPS (AI) — student Check with AI, then Done
═══════════════════════════════════════════════════════════════════════════ */
function FillGapsAiInteractive({ exercise, assignment, preview, onComplete, reviewMode, onUpdateAssignment, onUpdate, readOnly, isOnlineLesson, updateSlideState, slideState, user, isTeacher, showSolution, practiceMode }) {
    'use no memo';
    const checkPlan = useMemo(() => buildFillGapsAiCheckSections(exercise.body || ''), [exercise.body]);
    const gaps = useMemo(() => {
        const all = [];
        checkPlan.pieces.forEach((piece) => {
            collectGapParts(parseFillGapsStructuredBody(piece.body || '')).forEach((g) => all.push(g));
        });
        return all;
    }, [checkPlan]);
    const alreadyDone = isAlreadyDone(assignment) && !reviewMode;

    const initialMeta = readFillGapsAiMeta(assignment?.answers || {});
    const [answers, setAnswers] = useState(extractFillGapsAiAnswersOnly(assignment?.answers || {}));
    const [aiFeedbackMap, setAiFeedbackMap] = useState(initialMeta.ai_feedback || {});
    const [sectionChecks, setSectionChecks] = useState(initialMeta.ai_section_checks || {});
    const [aiChecked, setAiChecked] = useState(
        areAllFillGapsAiSectionsChecked(checkPlan.scopes, initialMeta.ai_section_checks, initialMeta.ai_checked)
            || alreadyDone || reviewMode || readOnly
    );
    const [aiLoadingSection, setAiLoadingSection] = useState(null);
    const [aiError, setAiError] = useState('');
    const [locked, setLocked] = useState(() => {
        if (readOnly || reviewMode || alreadyDone) {
            return Object.fromEntries(gaps.map((_, i) => [i, true]));
        }
        const next = {};
        const checks = initialMeta.ai_section_checks || {};
        checkPlan.scopes.forEach((scope) => {
            if (checks[String(scope.sectionIndex)] || (checkPlan.scopes.length === 1 && initialMeta.ai_checked)) {
                scope.gapIndices.forEach((gi) => { next[gi] = true; });
            }
        });
        return next;
    });
    const [isCorrectMap, setIsCorrectMap] = useState(assignment?.is_correct || {});
    const [gapRewards, setGapRewards] = useState(() => (
        assignment?.gap_rewards
        || buildGapRewardsFromIsCorrect(exercise, assignment?.is_correct || {}, gaps.length)
    ));
    const [completed, setCompleted] = useState(readOnly || reviewMode || alreadyDone);
    const rewardCaps = useMemo(() => getPerGapRewardCaps(exercise), [exercise]);
    const [localUnblurred, setLocalUnblurred] = useState({});
    const currentUnblurred = isOnlineLesson ? (slideState?.unblurred || {}) : localUnblurred;
    const [draftAnswers, setDraftAnswers] = useState({});

    const slideMeta = isOnlineLesson ? readFillGapsAiMeta(slideState?.answers || {}) : null;
    const activeAnswers = isOnlineLesson ? extractFillGapsAiAnswersOnly(slideState?.answers || {}) : answers;
    const activeLocked = isOnlineLesson ? (slideState?.locked || locked) : locked;
    const activeIsCorrect = isOnlineLesson ? (slideState?.is_correct || isCorrectMap) : isCorrectMap;
    const activeAiFeedback = isOnlineLesson ? (slideMeta?.ai_feedback || {}) : aiFeedbackMap;
    const activeSectionChecks = isOnlineLesson ? (slideMeta?.ai_section_checks || {}) : sectionChecks;
    const activeAiChecked = isOnlineLesson
        ? areAllFillGapsAiSectionsChecked(checkPlan.scopes, slideMeta?.ai_section_checks, slideMeta?.ai_checked)
        : aiChecked;
    const effectiveAnswers = useMemo(() => ({ ...activeAnswers, ...draftAnswers }), [activeAnswers, draftAnswers]);

    const gapSectionChecked = useCallback((gi) => {
        const scope = checkPlan.scopes.find((s) => gi >= s.gapStart && gi < s.gapEnd);
        if (!scope) return !!activeAiChecked;
        return !!(activeSectionChecks[String(scope.sectionIndex)]
            || (checkPlan.scopes.length === 1 && activeAiChecked));
    }, [checkPlan.scopes, activeSectionChecks, activeAiChecked]);

    const DRAFT_KEY = useMemo(() => `ex_draft_${assignment?.id}`, [assignment?.id]);
    useEffect(() => {
        if (isOnlineLesson || !assignment?.id || alreadyDone || readOnly) return;
        try {
            const raw = localStorage.getItem(DRAFT_KEY);
            if (raw) {
                const draft = JSON.parse(raw);
                if (draft && typeof draft === 'object') setDraftAnswers(draft);
            }
        } catch (e) {
            console.warn('[FillGapsAi] Failed to load drafts:', e);
        }
    }, [DRAFT_KEY, isOnlineLesson, assignment?.id, alreadyDone, readOnly]);

    const buildPayload = useCallback((gapAns, metaPatch, existing) => (
        buildFillGapsAiAnswersPayload(gapAns, metaPatch, existing)
    ), []);

    const { syncStatus, persist, getLocalData } = useExercisePersistence(
        assignment?.id,
        {
            answers: buildPayload(activeAnswers, {
                ai_feedback: activeAiFeedback,
                ai_section_checks: activeSectionChecks,
                ai_checked: activeAiChecked,
                ai_feedback_at: readFillGapsAiMeta(assignment?.answers || {}).ai_feedback_at,
            }, assignment?.answers),
            locked: activeLocked,
            status: completed ? 'completed' : 'working',
            is_correct: activeIsCorrect,
        },
        onUpdate,
        readOnly || alreadyDone,
        assignment,
        isOnlineLesson,
        updateSlideState,
        slideState,
    );

    const draftAnswersRef = useRef(draftAnswers);
    draftAnswersRef.current = draftAnswers;
    const activeAnswersRef = useRef(activeAnswers);
    activeAnswersRef.current = activeAnswers;
    const activeLockedRef = useRef(activeLocked);
    activeLockedRef.current = activeLocked;
    const isCorrectMapRef = useRef(activeIsCorrect);
    isCorrectMapRef.current = activeIsCorrect;
    const aiFeedbackRef = useRef(activeAiFeedback);
    aiFeedbackRef.current = activeAiFeedback;
    const sectionChecksRef = useRef(activeSectionChecks);
    sectionChecksRef.current = activeSectionChecks;
    const aiCheckedRef = useRef(activeAiChecked);
    aiCheckedRef.current = activeAiChecked;
    const gapSectionCheckedRef = useRef(gapSectionChecked);
    gapSectionCheckedRef.current = gapSectionChecked;

    const handleDraftChange = useCallback((idx, val) => {
        if (gapSectionCheckedRef.current(idx)) return;
        const nextDrafts = { ...draftAnswersRef.current, [idx]: val };
        draftAnswersRef.current = nextDrafts;
        setDraftAnswers(nextDrafts);
        try { localStorage.setItem(DRAFT_KEY, JSON.stringify(nextDrafts)); } catch (e) { /* ignore */ }

        const merged = { ...activeAnswersRef.current };
        Object.entries(nextDrafts).forEach(([k, v]) => {
            if (v != null && String(v).trim() !== '') merged[k] = v;
        });
        const allDone = areAllFillGapsAiSectionsChecked(
            checkPlan.scopes,
            sectionChecksRef.current,
            false
        );
        const payload = buildPayload(merged, {
            ai_feedback: aiFeedbackRef.current,
            ai_section_checks: sectionChecksRef.current,
            ai_checked: allDone,
        }, assignment?.answers);
        persist({ answers: payload, locked: activeLockedRef.current, status: 'working', is_correct: isCorrectMapRef.current });
    }, [DRAFT_KEY, persist, assignment?.answers, buildPayload, checkPlan.scopes]);

    const handleSubmitAnswer = (idx, val) => {
        if (gapSectionChecked(idx) || completed || alreadyDone || readOnly) return;
        const nextAnswers = { ...activeAnswers, [idx]: val };
        const nextLocked = { ...activeLocked, [idx]: true };
        const nextDrafts = { ...draftAnswersRef.current };
        delete nextDrafts[idx];
        draftAnswersRef.current = nextDrafts;
        setDraftAnswers(nextDrafts);
        try { localStorage.setItem(DRAFT_KEY, JSON.stringify(nextDrafts)); } catch (e) { /* ignore */ }
        const allDone = areAllFillGapsAiSectionsChecked(checkPlan.scopes, activeSectionChecks, false);
        const payload = buildPayload(nextAnswers, {
            ai_feedback: aiFeedbackRef.current,
            ai_section_checks: activeSectionChecks,
            ai_checked: allDone,
        }, assignment?.answers);
        if (!isOnlineLesson) {
            setAnswers(nextAnswers);
            setLocked(nextLocked);
            persist({ answers: payload, locked: nextLocked, status: 'working', is_correct: activeIsCorrect });
        } else if (updateSlideState) {
            updateSlideState({ ...slideState, answers: payload, locked: nextLocked });
        }
    };

    const handleCheckWithAi = async (scope) => {
        if (aiLoadingSection != null || completed || alreadyDone || readOnly || reviewMode) return;
        if (!scope?.gapIndices?.length) return;
        if (activeSectionChecks[String(scope.sectionIndex)]) return;
        setAiError('');
        setAiLoadingSection(scope.sectionIndex);
        try {
            const mergedAnswers = { ...activeAnswers, ...draftAnswersRef.current };
            Object.entries(draftAnswersRef.current || {}).forEach(([k, v]) => {
                if (v != null && String(v).trim() !== '') mergedAnswers[k] = v;
            });
            const items = collectFillGapsAiFeedbackItems(exercise.body || '', mergedAnswers, {
                gapIndices: scope.gapIndices,
            });
            if (!items.length) {
                setAiError('No gaps found in this section.');
                return;
            }
            const piece = checkPlan.pieces[scope.pieceIndex];
            const batch = await aiService.generateFillGapsAiFeedback(items, exercise.course || '', {
                instruction: exercise.instruction || '',
                sectionBody: piece?.body || '',
            });
            const remapped = remapFillGapsAiBatchResults(items, batch);
            const nextFeedback = { ...activeAiFeedback, ...remapped.feedback };
            const nextCorrect = { ...activeIsCorrect, ...remapped.isCorrect };
            const nextSectionChecks = { ...activeSectionChecks, [String(scope.sectionIndex)]: true };
            const nextLocked = { ...activeLocked };
            scope.gapIndices.forEach((gi) => { nextLocked[gi] = true; });
            const allDone = areAllFillGapsAiSectionsChecked(checkPlan.scopes, nextSectionChecks, false);
            const payload = buildPayload(mergedAnswers, {
                ai_feedback: nextFeedback,
                ai_section_checks: nextSectionChecks,
                ai_checked: allDone,
                ai_feedback_at: new Date().toISOString(),
            }, assignment?.answers);
            const score = Object.values(nextCorrect).filter((v) => v === true).length;

            const clearedDrafts = { ...draftAnswersRef.current };
            scope.gapIndices.forEach((gi) => {
                delete clearedDrafts[gi];
                delete clearedDrafts[String(gi)];
            });
            draftAnswersRef.current = clearedDrafts;

            if (!isOnlineLesson) {
                setAnswers(mergedAnswers);
                setLocked(nextLocked);
                setAiFeedbackMap(nextFeedback);
                setSectionChecks(nextSectionChecks);
                setAiChecked(allDone);
                setIsCorrectMap(nextCorrect);
                setDraftAnswers(clearedDrafts);
                try { localStorage.setItem(DRAFT_KEY, JSON.stringify(clearedDrafts)); } catch (e) { /* ignore */ }
                persist({
                    answers: payload,
                    locked: nextLocked,
                    status: 'working',
                    is_correct: nextCorrect,
                    score,
                    total: gaps.length,
                });
            } else if (updateSlideState) {
                updateSlideState({
                    ...slideState,
                    answers: payload,
                    locked: nextLocked,
                    is_correct: nextCorrect,
                });
                setSectionChecks(nextSectionChecks);
                setAiChecked(allDone);
                setAiFeedbackMap(nextFeedback);
            }
        } catch (err) {
            console.error(err);
            setAiError(err?.message || 'AI check failed. Please try again.');
        } finally {
            setAiLoadingSection(null);
        }
    };

    const handleToggleCorrect = (idx) => {
        if (!reviewMode || !onUpdateAssignment) return;
        const k = String(idx);
        const cur = isCorrectMap[idx] === true || isCorrectMap[k] === true;
        const nextOk = !cur;
        const newMap = { ...isCorrectMap, [idx]: nextOk, [k]: nextOk };
        const newRewards = {
            ...gapRewards,
            [k]: { stars: nextOk ? rewardCaps.maxStars : 0, coins: nextOk ? rewardCaps.maxCoins : 0 },
        };
        const score = Object.values(newMap).filter((v) => v === true).length;
        const rewards = sumGapRewards(newRewards, exercise);
        setIsCorrectMap(newMap);
        setGapRewards(newRewards);
        onUpdateAssignment(assignment.id, {
            is_correct: newMap,
            gap_rewards: newRewards,
            score,
            awarded_stars: rewards.awarded_stars,
            awarded_coins: rewards.awarded_coins,
        });
    };

    const handleGapRewardChange = (idx, partial) => {
        if (!reviewMode || !onUpdateAssignment) return;
        const k = String(idx);
        const prev = gapRewards[k] || gapRewards[idx] || { stars: 0, coins: 0 };
        let stars = partial.stars !== undefined ? Number(partial.stars) : (parseFloat(prev.stars) || 0);
        let coins = partial.coins !== undefined ? Number(partial.coins) : (parseInt(prev.coins, 10) || 0);
        if (Number.isNaN(stars)) stars = 0;
        if (Number.isNaN(coins)) coins = 0;
        if (rewardCaps.maxStars > 0) stars = Math.max(0, Math.min(rewardCaps.maxStars, stars));
        else stars = 0;
        if (rewardCaps.maxCoins > 0) coins = Math.max(0, Math.min(rewardCaps.maxCoins, Math.round(coins)));
        else coins = 0;
        const ok = isMaxGapReward(stars, coins, rewardCaps.maxStars, rewardCaps.maxCoins);
        const newMap = { ...isCorrectMap, [idx]: ok, [k]: ok };
        const newRewards = { ...gapRewards, [k]: { stars, coins } };
        const score = Object.values(newMap).filter((v) => v === true).length;
        const rewards = sumGapRewards(newRewards, exercise);
        setIsCorrectMap(newMap);
        setGapRewards(newRewards);
        onUpdateAssignment(assignment.id, {
            is_correct: newMap, gap_rewards: newRewards, score,
            awarded_stars: rewards.awarded_stars, awarded_coins: rewards.awarded_coins,
        });
    };

    const handleBlurClick = (e) => {
        const blurTarget = e.target.closest('.blurred-text');
        if (!blurTarget) return;
        e.stopPropagation();
        const blurId = blurTarget.getAttribute('data-blur-id');
        const nextUnblurred = { ...currentUnblurred, [blurId]: !currentUnblurred[blurId] };
        if (isOnlineLesson && isTeacher && updateSlideState) updateSlideState({ ...slideState, unblurred: nextUnblurred });
        else setLocalUnblurred(nextUnblurred);
    };

    useEffect(() => {
        if (!isOnlineLesson && (readOnly || reviewMode || alreadyDone) && assignment) {
            if (assignment.answers) {
                setAnswers(extractFillGapsAiAnswersOnly(assignment.answers));
                const m = readFillGapsAiMeta(assignment.answers);
                setAiFeedbackMap(m.ai_feedback || {});
                setSectionChecks(m.ai_section_checks || {});
                setAiChecked(areAllFillGapsAiSectionsChecked(
                    checkPlan.scopes,
                    m.ai_section_checks,
                    m.ai_checked
                ));
            }
            if (assignment.locked) setLocked(assignment.locked);
            if (assignment.is_correct) setIsCorrectMap(assignment.is_correct);
            if (assignment.status === 'completed' || assignment.status === 'checked') setCompleted(true);
        }
    }, [readOnly, reviewMode, alreadyDone, assignment, isOnlineLesson, checkPlan.scopes]);

    useEffect(() => {
        if (isOnlineLesson) return;
        const local = getLocalData();
        if (local && !alreadyDone && !readOnly) {
            if (local.answers) {
                setAnswers(extractFillGapsAiAnswersOnly(local.answers));
                const m = readFillGapsAiMeta(local.answers);
                if (m.ai_feedback) setAiFeedbackMap(m.ai_feedback);
                if (m.ai_section_checks) setSectionChecks(m.ai_section_checks);
                if (m.ai_checked || m.ai_section_checks) {
                    setAiChecked(areAllFillGapsAiSectionsChecked(
                        checkPlan.scopes,
                        m.ai_section_checks,
                        m.ai_checked
                    ));
                }
            }
            if (local.locked) setLocked(local.locked);
            if (local.is_correct) setIsCorrectMap(local.is_correct);
        }
    }, [getLocalData, alreadyDone, readOnly, isOnlineLesson, checkPlan.scopes]);

    const canUseAiActions = !completed && !alreadyDone && !readOnly && !reviewMode
        && (!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open'));
    const canComplete = activeAiChecked && canUseAiActions;
    const score = Object.values(activeIsCorrect).filter((v) => v === true).length;

    const scopeForPiece = useCallback((pieceIndex) => (
        checkPlan.scopes.find((s) => s.pieceIndex === pieceIndex) || null
    ), [checkPlan.scopes]);

    const renderAiCheckButton = (scope) => {
        if (!scope || preview) return null;
        const checked = !!(activeSectionChecks[String(scope.sectionIndex)]
            || (checkPlan.scopes.length === 1 && activeAiChecked));
        if (checked || !canUseAiActions) {
            if (checked) {
                return (
                    <div key={`ai-check-done-${scope.sectionIndex}`} style={{ margin: '1rem 0', fontSize: '0.85rem', fontWeight: 700, color: '#4338ca' }}>
                        ✓ AI check done for this section
                    </div>
                );
            }
            return null;
        }
        const loading = aiLoadingSection === scope.sectionIndex;
        return (
            <div key={`ai-check-${scope.sectionIndex}`} style={{ margin: '1rem 0' }}>
                <button
                    type="button"
                    className={COMPLETE_BTN}
                    onClick={() => handleCheckWithAi(scope)}
                    disabled={aiLoadingSection != null}
                    style={{
                        background: loading ? '#e2e8f0' : 'linear-gradient(135deg,#6366f1,#7c3aed)',
                        color: loading ? '#64748b' : '#fff',
                        cursor: loading ? 'wait' : 'pointer',
                    }}
                >
                    {loading ? '⏳ Checking…' : '🤖 Check with AI'}
                </button>
            </div>
        );
    };

    return (
        <div className="exercise-card-shell" style={CARD_STYLE}>
            {!readOnly && !preview && !isOnlineLesson && <SyncStatus status={syncStatus} />}
            <ExerciseLead exercise={exercise} />
            <div className="exercise-gap-content" onClick={handleBlurClick}>
                {checkPlan.pieces.map((piece, pieceIndex) => {
                    const parts = parseFillGapsStructuredBody(piece.body || '');
                    let localGap = 0;
                    return (
                        <React.Fragment key={`fgai-piece-${pieceIndex}`}>
                            {renderFillGapsStructuredParts(parts, {
                                idPrefix: `fgai-${pieceIndex}`,
                                nextGapIdx: () => piece.gapStart + (localGap++),
                                renderText: (p, key) => (
                                    <InlineAudioRenderer key={key} inline html={renderMarkdownToHtml(p.text, currentUnblurred, key, true)} />
                                ),
                                renderGap: (p, gi, key) => {
                                    const sectionDone = gapSectionChecked(gi);
                                    const fb = activeAiFeedback[String(gi)] || activeAiFeedback[gi] || '';
                                    const mark = activeIsCorrect[gi] ?? activeIsCorrect[String(gi)];
                                    return (
                                        <span key={key} style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', verticalAlign: 'middle', maxWidth: '100%', minWidth: 0 }}>
                                            <GapInput
                                                gapIdx={gi}
                                                prompt={p.prompt}
                                                answers={sectionDone || reviewMode || showSolution ? (p.answers || []) : []}
                                                globalAnswers={effectiveAnswers}
                                                onSubmit={handleSubmitAnswer}
                                                locked={activeLocked}
                                                reviewMode={reviewMode}
                                                isCorrect={mark}
                                                onToggleCorrect={handleToggleCorrect}
                                                readOnly={alreadyDone || readOnly || sectionDone}
                                                showSolution={showSolution}
                                                solutionValue={p.answers?.[0] || ''}
                                                draftValue={draftAnswers[gi]}
                                                onDraftChange={handleDraftChange}
                                                assignment={assignment}
                                                maxStars={rewardCaps.maxStars}
                                                maxCoins={rewardCaps.maxCoins}
                                                rewardStars={(gapRewards[String(gi)] || gapRewards[gi] || {}).stars ?? 0}
                                                rewardCoins={(gapRewards[String(gi)] || gapRewards[gi] || {}).coins ?? 0}
                                                onRewardChange={handleGapRewardChange}
                                            />
                                            {fb ? (
                                                <span style={{
                                                    display: 'block', marginTop: 6, fontSize: '0.75rem', color: '#3730a3',
                                                    padding: '8px 10px', background: '#eef2ff', borderRadius: 8,
                                                    border: '1px solid #c7d2fe', maxWidth: 340, lineHeight: 1.5, whiteSpace: 'pre-wrap',
                                                }}>
                                                    <span style={{ display: 'block', fontWeight: 700, marginBottom: 4 }}>🤖 AI Feedback</span>
                                                    {fb}
                                                </span>
                                            ) : null}
                                        </span>
                                    );
                                },
                            })}
                            {renderAiCheckButton(scopeForPiece(pieceIndex))}
                        </React.Fragment>
                    );
                })}
            </div>

            {aiError && (
                <div style={{ marginTop: '0.75rem', padding: '0.65rem 0.85rem', background: '#fef2f2', color: '#991b1b', borderRadius: 10, fontSize: '0.85rem', fontWeight: 600 }}>
                    {aiError}
                </div>
            )}

            {!preview && (
                <ExerciseActionBar style={{ marginTop: '1.25rem' }}>
                    {canComplete && (
                        <button
                            type="button"
                            className={COMPLETE_BTN}
                            onClick={() => {
                                const payload = buildPayload(activeAnswers, {
                                    ai_feedback: activeAiFeedback,
                                    ai_section_checks: activeSectionChecks,
                                    ai_checked: true,
                                    ai_feedback_at: readFillGapsAiMeta(assignment?.answers || {}).ai_feedback_at || new Date().toISOString(),
                                }, assignment?.answers);
                                setCompleted(true);
                                persist({
                                    answers: payload,
                                    locked: activeLocked,
                                    status: 'completed',
                                    is_correct: activeIsCorrect,
                                    score,
                                    total: gaps.length,
                                });
                                if (onComplete) {
                                    onComplete({
                                        answers: payload,
                                        score,
                                        total: gaps.length,
                                        is_correct: activeIsCorrect,
                                        status: 'completed',
                                    });
                                }
                            }}
                        >
                            {(alreadyDone || completed) ? COMPLETE_DONE_LABEL : COMPLETE_LABEL}
                        </button>
                    )}
                    {(!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                        activeAiChecked || completed || reviewMode ? (
                            <ExerciseScoreBox submitted={completed || alreadyDone || reviewMode}>
                                Score: {score} / {gaps.length}
                            </ExerciseScoreBox>
                        ) : (
                            <AssignmentStatusPill assignment={assignment} completed={completed} isOnlineLesson={isOnlineLesson} fallback="Fill the gaps, then Check with AI"  practiceMode={practiceMode} />
                        )
                    )}
                </ExerciseActionBar>
            )}
            <TeacherComment exercise={exercise} assignment={assignment} />
        </div>
    );
}
FillGapsAiInteractive.displayName = 'FillGapsAiInteractive';

/* ═══════════════════════════════════════════════════════════════════════════
   FILL IN THE GAPS WITH GIVEN WORDS (drag-and-drop)
═══════════════════════════════════════════════════════════════════════════ */
function FillGapsWordsInteractive({ exercise, assignment, preview, onComplete, reviewMode, onUpdateAssignment, onUpdate, readOnly, isOnlineLesson, updateSlideState, slideState, user, isTeacher, showSolution, practiceMode }) {
    'use no memo';
    const [parts, setParts] = useState([]);
    useEffect(() => {
        const raw = parseFillGapsStructuredBody(exercise.body || '');
        setParts(normalizeFillGapsWordsParts(raw));
    }, [exercise.body]);
    const gaps = useMemo(() => collectGapParts(parts), [parts]);
    
    const wordPool = useMemo(() => {
        let all = [];
        gaps.forEach(g => { all = all.concat(g.answers); });
        return seededShuffle(all, `fill_words_${exercise?.id || 'ex'}`);
    }, [gaps, exercise?.id]);

    const alreadyDone = isAlreadyDone(assignment) && !reviewMode;
    const [answers, setAnswers] = useState(assignment?.answers || {});
    const [locked, setLocked] = useState(
        readOnly || reviewMode ? Object.fromEntries(gaps.map((_, i) => [i, true])) :
        alreadyDone ? Object.fromEntries(gaps.map((_, i) => [i, true])) : {}
    );
    const [isCorrectMap, setIsCorrectMap] = useState(assignment?.is_correct || {});
    const [gapAttempts, setGapAttempts] = useState(assignment?.gap_attempts || {});
    const [gapRewards, setGapRewards] = useState(() => (
        assignment?.gap_rewards
        || buildGapRewardsFromIsCorrect(exercise, assignment?.is_correct || {}, gaps.length)
    ));
    const [completed, setCompleted] = useState(readOnly || reviewMode || alreadyDone);
    const rewardCaps = useMemo(() => getPerGapRewardCaps(exercise), [exercise]);
    const threeAttempts = isThreeAttemptsEnabled(exercise);
    const [localUnblurred, setLocalUnblurred] = useState({});
    const currentUnblurred = isOnlineLesson ? (slideState?.unblurred || {}) : localUnblurred;

    // CLICK-TO-FILL: State to manage selection
    const [selectedWord, setSelectedWord] = useState(null);

    const activeAnswers = answers;
    const activeLocked = locked;

    const handleSubmitAnswer = (idx, val) => {
        // Clearing a gap (return word to pool) — skip attempt accounting
        if (val === '') {
            const nextAnswers = { ...activeAnswers, [idx]: '' };
            const nextLocked = { ...activeLocked, [idx]: false };
            const nextIsCorrect = { ...isCorrectMap };
            delete nextIsCorrect[idx];
            delete nextIsCorrect[String(idx)];
            setAnswers(nextAnswers);
            setLocked(nextLocked);
            setIsCorrectMap(nextIsCorrect);
            persist({
                answers: nextAnswers,
                locked: nextLocked,
                status: 'working',
                is_correct: nextIsCorrect,
                ...(threeAttempts ? { gap_attempts: gapAttempts } : {}),
            });
            return;
        }

        const gap = gaps[idx];
        const hasKey = Array.isArray(gap?.answers) && gap.answers.some((a) => String(a || '').trim());
        const ok = hasKey ? gap.answers.some(a => normalizeAnswer(a) === normalizeAnswer(val)) : null;

        let nextAnswers = { ...activeAnswers, [idx]: val };
        let nextLocked = { ...activeLocked, [idx]: true };
        let nextIsCorrect = { ...isCorrectMap };
        if (ok === null) {
            delete nextIsCorrect[idx];
            delete nextIsCorrect[String(idx)];
        } else {
            nextIsCorrect = { ...isCorrectMap, [idx]: ok, [String(idx)]: ok };
        }
        let nextGapAttempts = gapAttempts;

        if (threeAttempts && hasKey) {
            const applied = applyGapAttempt(getGapAttemptResults(gapAttempts, idx), ok);
            nextGapAttempts = setGapAttemptResults(gapAttempts, idx, applied.results);
            nextLocked = { ...activeLocked, [idx]: applied.lock };
            if (applied.isCorrect === null) {
                const { [idx]: _a, [String(idx)]: _b, ...rest } = nextIsCorrect;
                nextIsCorrect = rest;
            } else {
                nextIsCorrect = { ...isCorrectMap, [idx]: applied.isCorrect, [String(idx)]: applied.isCorrect };
            }
            if (applied.clearAnswer) {
                nextAnswers = { ...activeAnswers, [idx]: '' };
            }
            setGapAttempts(nextGapAttempts);
        }

        setAnswers(nextAnswers);
        setLocked(nextLocked);
        setIsCorrectMap(nextIsCorrect);
        persist({
            answers: nextAnswers,
            locked: nextLocked,
            status: 'working',
            is_correct: nextIsCorrect,
            ...(threeAttempts ? { gap_attempts: nextGapAttempts } : {}),
        });
    };

    const handleGapClick = (idx) => {
        if (completed || alreadyDone || readOnly) return;
        if (activeLocked[idx]) return;

        if (selectedWord && !activeAnswers[idx]) {
            handleSubmitAnswer(idx, selectedWord);
            setSelectedWord(null);
            return;
        }

        if (activeAnswers[idx]) {
            handleSubmitAnswer(idx, '');
        }
    };

    const computeScore = (ansMap) => {
        return gaps.filter((g, i) => {
            if (!(g.answers || []).some((a) => String(a || '').trim())) return false;
            return g.answers.some(a => normalizeAnswer(a) === normalizeAnswer(ansMap[i]));
        }).length;
    };

    useEffect(() => {
        if (!isOnlineLesson && (readOnly || reviewMode || alreadyDone) && assignment) {
            if (assignment.answers) setAnswers(assignment.answers);
            if (assignment.locked) setLocked(assignment.locked);
            if (assignment.is_correct) setIsCorrectMap(assignment.is_correct);
            if (assignment.status === 'completed' || assignment.status === 'checked') setCompleted(true);
        }
    }, [readOnly, reviewMode, alreadyDone, assignment, isOnlineLesson]);

    useEffect(() => {
        if (!isOnlineLesson) return;
        setAnswers(slideState?.answers || {});
        setLocked(slideState?.locked || {});
        setIsCorrectMap(slideState?.is_correct || {});
    }, [isOnlineLesson, slideState?.answers, slideState?.locked, slideState?.is_correct]);

    const { syncStatus, persist, getLocalData } = useExercisePersistence(
        assignment?.id,
        { answers: activeAnswers, locked: activeLocked, status: completed ? 'completed' : 'working', is_correct: isCorrectMap },
        onUpdate,
        readOnly || alreadyDone,
        assignment,
        isOnlineLesson,
        updateSlideState,
        slideState
    );

    useEffect(() => {
        if (isOnlineLesson) return;
        const local = getLocalData();
        if (local && !alreadyDone && !readOnly) {
            if (local.answers) setAnswers(local.answers);
            if (local.locked) setLocked(local.locked);
        }
    }, [getLocalData, alreadyDone, readOnly, isOnlineLesson]);

    const handleToggleCorrect = (idx) => {
        if (!reviewMode || !onUpdateAssignment) return;
        const k = String(idx);
        const cur = isCorrectMap[idx] === true || isCorrectMap[k] === true;
        const nextOk = !cur;
        const newMap = { ...isCorrectMap, [idx]: nextOk, [k]: nextOk };
        const newRewards = {
            ...gapRewards,
            [k]: {
                stars: nextOk ? rewardCaps.maxStars : 0,
                coins: nextOk ? rewardCaps.maxCoins : 0,
            },
        };
        const newScore = Object.values(newMap).filter(v => v === true).length;
        const rewards = sumGapRewards(newRewards, exercise);
        setIsCorrectMap(newMap);
        setGapRewards(newRewards);
        onUpdateAssignment(assignment.id, {
            is_correct: newMap,
            gap_rewards: newRewards,
            score: newScore,
            awarded_stars: rewards.awarded_stars,
            awarded_coins: rewards.awarded_coins,
        });
    };

    const handleGapRewardChange = (idx, partial) => {
        if (!reviewMode || !onUpdateAssignment) return;
        const k = String(idx);
        const prev = gapRewards[k] || gapRewards[idx] || { stars: 0, coins: 0 };
        let stars = partial.stars !== undefined ? Number(partial.stars) : (parseFloat(prev.stars) || 0);
        let coins = partial.coins !== undefined ? Number(partial.coins) : (parseInt(prev.coins, 10) || 0);
        if (Number.isNaN(stars)) stars = 0;
        if (Number.isNaN(coins)) coins = 0;
        if (rewardCaps.maxStars > 0) stars = Math.max(0, Math.min(rewardCaps.maxStars, stars));
        else stars = 0;
        if (rewardCaps.maxCoins > 0) coins = Math.max(0, Math.min(rewardCaps.maxCoins, Math.round(coins)));
        else coins = 0;
        const ok = isMaxGapReward(stars, coins, rewardCaps.maxStars, rewardCaps.maxCoins);
        const newMap = { ...isCorrectMap, [idx]: ok, [k]: ok };
        const newRewards = { ...gapRewards, [k]: { stars, coins } };
        const newScore = Object.values(newMap).filter(v => v === true).length;
        const rewards = sumGapRewards(newRewards, exercise);
        setIsCorrectMap(newMap);
        setGapRewards(newRewards);
        onUpdateAssignment(assignment.id, {
            is_correct: newMap,
            gap_rewards: newRewards,
            score: newScore,
            awarded_stars: rewards.awarded_stars,
            awarded_coins: rewards.awarded_coins,
        });
    };

    const handleBlurClick = (e) => {
        const target = e.target;
        const blurTarget = target.closest('.blurred-text');
        if (blurTarget) {
            e.stopPropagation();
            const blurId = blurTarget.getAttribute('data-blur-id');
            const nextUnblurred = { ...currentUnblurred, [blurId]: !currentUnblurred[blurId] };
            if (isOnlineLesson && isTeacher && updateSlideState) {
                updateSlideState({ ...slideState, unblurred: nextUnblurred });
            } else if (isOnlineLesson && !isTeacher) {
                setLocalUnblurred(nextUnblurred);
            } else if (!isOnlineLesson) {
                setLocalUnblurred(nextUnblurred);
                persist({ unblurred: nextUnblurred, status: 'working' });
            }
        }
    };

    const score = (completed || readOnly) ? computeScore(activeAnswers) : 0;
    const usedWords = Object.values(activeAnswers).filter(Boolean);
    
    // Proper multiset difference for duplicate words in pool
    const usedCounts = {};
    usedWords.forEach(w => { usedCounts[w] = (usedCounts[w] || 0) + 1; });
    
    const pool = wordPool.filter(w => {
        if (usedCounts[w] > 0) {
            usedCounts[w]--;
            return false;
        }
        return true;
    });
    
    const gapIdxRef = useRef(0);
    gapIdxRef.current = 0;

    return (
        <div className="exercise-card-shell" style={CARD_STYLE}>
            <ExerciseLead exercise={exercise} />
            
            <div style={{ background: '#f8fafc', padding: '1.25rem', borderRadius: '16px', border: '2px solid #e2e8f0', marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'center' }}>
                    {pool.map((w, i) => {
                        const isSelected = selectedWord === w;
                        const canMove = !alreadyDone && !readOnly && !completed;
                        return (
                            <div
                                key={i}
                                draggable={canMove}
                                onDragStart={(e) => {
                                    if (!canMove) return;
                                    e.dataTransfer.setData('text/plain', w);
                                    e.dataTransfer.effectAllowed = 'copy';
                                    setSelectedWord(w);
                                }}
                                onClick={() => canMove && setSelectedWord(isSelected ? null : w)}
                                style={{
                                    padding: '6px 16px', background: isSelected ? '#ffedd5' : '#4361ee', 
                                    color: isSelected ? '#f97316' : 'white', borderRadius: '20px', 
                                    fontSize: '0.9rem', fontWeight: 800, cursor: canMove ? 'grab' : 'default',
                                    border: isSelected ? '2px solid #f97316' : '2px solid transparent',
                                    boxShadow: isSelected ? '0 4px 12px rgba(249, 115, 22, 0.25)' : '0 2px 4px rgba(67, 97, 238, 0.2)',
                                    transform: isSelected ? 'scale(1.1)' : 'none',
                                    transition: 'all 0.2s',
                                    userSelect: 'none'
                                }}
                            >
                                {w}
                            </div>
                        );
                    })}
                    {pool.length === 0 && <span style={{ color: '#94a3b8', fontSize: '0.85rem', fontStyle: 'italic' }}>All words placed</span>}
                </div>
            </div>

            <div className="exercise-gap-content" onClick={handleBlurClick}>
                {renderFillGapsStructuredParts(parts, {
                    idPrefix: 'fgw',
                    nextGapIdx: () => gapIdxRef.current++,
                    renderText: (p, key) => (
                        <InlineAudioRenderer key={key} inline html={renderMarkdownToHtml(p.text, currentUnblurred, key, true)} />
                    ),
                    renderGap: (p, gi, key) => (
                        <GapInput key={key} gapIdx={gi} prompt={p.prompt} answers={p.answers}
                            globalAnswers={activeAnswers} onSubmit={handleSubmitAnswer}
                            onClick={() => handleGapClick(gi)}
                            locked={activeLocked} draggable={!alreadyDone && !readOnly}
                            reviewMode={reviewMode} isCorrect={isCorrectMap[gi] ?? isCorrectMap[String(gi)]}
                            onToggleCorrect={handleToggleCorrect}
                            readOnly={alreadyDone || readOnly}
                            showSolution={showSolution}
                            solutionValue={p.answers?.[0] || ''}
                            assignment={assignment}
                            maxStars={rewardCaps.maxStars}
                            maxCoins={rewardCaps.maxCoins}
                            rewardStars={(gapRewards[String(gi)] || gapRewards[gi] || {}).stars ?? 0}
                            rewardCoins={(gapRewards[String(gi)] || gapRewards[gi] || {}).coins ?? 0}
                            onRewardChange={handleGapRewardChange}
                            showAttempts={threeAttempts}
                            attemptResults={getGapAttemptResults(gapAttempts, gi)}
                        />
                    ),
                })}
            </div>

            {!preview && (
                    <ExerciseActionBar>
                        {!readOnly && !reviewMode && (!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && <button type="button" className={(alreadyDone || completed) ? COMPLETE_DONE_CLASS : COMPLETE_BTN} disabled={!!(alreadyDone || completed)} onClick={(alreadyDone || completed) ? undefined : (() => {
                            setCompleted(true);
                            if (onComplete) {
                                const isCorrectMapFinal = Object.fromEntries(gaps.map((g, i) => {
                                    const hasKey = (g.answers || []).some((a) => String(a || '').trim());
                                    if (!hasKey) return [i, null];
                                    return [i, g.answers.some(a => normalizeAnswer(a) === normalizeAnswer(activeAnswers[i]))];
                                }));
                                const finalScore = Object.values(isCorrectMapFinal).filter((v) => v === true).length;
                                onComplete({ answers: activeAnswers, score: finalScore, total: gaps.length, is_correct: isCorrectMapFinal, status: 'completed' });
                            }
                        })}>{(alreadyDone || completed) ? COMPLETE_DONE_LABEL : COMPLETE_LABEL}</button>}
                        {(!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                            <AssignmentStatusPill assignment={assignment} completed={completed} isOnlineLesson={isOnlineLesson}  practiceMode={practiceMode} />
                        )}
                    </ExerciseActionBar>
            )}
            <TeacherComment exercise={exercise} assignment={assignment} />
        </div>
    );
}
FillGapsWordsInteractive.displayName = 'FillGapsWordsInteractive';

/* ═══════════════════════════════════════════════════════════════════════════
   MIXED — [gap:…], [multiple:…], [checkbox:…], [note:anchor/tooltip]
   Each gap, dropdown, and checkbox is one scored item (one reward).
   Notes highlight preceding matching text and show a click tooltip (not scored).
═══════════════════════════════════════════════════════════════════════════ */
function mixedItemIsCorrect(item, val) {
    if (!item) return null;
    if (item.type === 'choice') {
        const correct = (item.options || []).find((o) => o.correct)?.label || '';
        if (!String(correct).trim()) return null;
        return normalizeAnswer(val || '') === normalizeAnswer(correct);
    }
    if (item.type === 'check') {
        if (item.expected !== true && item.expected !== false) return null;
        const on = val === true || val === 'true' || val === 1 || val === '1';
        return on === item.expected;
    }
    const keys = (item.answers || []).filter((a) => String(a || '').trim());
    if (!keys.length) return null;
    return keys.some((a) => normalizeAnswer(a) === normalizeAnswer(val || ''));
}

function mixedItemHasAutoKey(item) {
    if (!item) return false;
    if (item.type === 'choice') return !!(item.options || []).some((o) => o.correct && String(o.label || '').trim());
    if (item.type === 'check') return item.expected === true || item.expected === false;
    return (item.answers || []).some((a) => String(a || '').trim());
}

function FillGapsChoiceInteractive({
    exercise, assignment, preview, onComplete, reviewMode, onUpdateAssignment, onUpdate,
    readOnly, isOnlineLesson, updateSlideState, slideState, user, isTeacher, showSolution, practiceMode,
}) {
    'use no memo';
    const parts = useMemo(() => parseFillGapsChoiceStructuredBody(exercise.body || ''), [exercise.body]);
    const items = useMemo(() => collectMixedGapChoiceItems(parts), [parts]);
    const alreadyDone = isAlreadyDone(assignment) && !reviewMode;
    const [answers, setAnswers] = useState(assignment?.answers || {});
    const [locked, setLocked] = useState(
        readOnly || reviewMode ? Object.fromEntries(items.map((it) => [it.idx, true])) :
        alreadyDone ? Object.fromEntries(items.map((it) => [it.idx, true])) : {}
    );
    const [isCorrectMap, setIsCorrectMap] = useState(assignment?.is_correct || {});
    const [gapAttempts, setGapAttempts] = useState(assignment?.gap_attempts || {});
    const [gapRewards, setGapRewards] = useState(() => (
        assignment?.gap_rewards
        || buildGapRewardsFromIsCorrect(exercise, assignment?.is_correct || {}, items.length)
    ));
    const [completed, setCompleted] = useState(readOnly || reviewMode || alreadyDone);
    const rewardCaps = useMemo(() => getPerGapRewardCaps(exercise), [exercise]);
    const threeAttempts = isThreeAttemptsEnabled(exercise);
    const [localUnblurred, setLocalUnblurred] = useState({});
    const [draftAnswers, setDraftAnswers] = useState({});
    const [peekMap, setPeekMap] = useState({});
    const [checkLocal, setCheckLocal] = useState({});
    const currentUnblurred = isOnlineLesson ? (slideState?.unblurred || {}) : localUnblurred;

    useEffect(() => {
        if (!(readOnly || reviewMode || alreadyDone) || items.length === 0) return;
        setLocked((prev) => {
            const next = { ...prev };
            let changed = false;
            items.forEach((it) => {
                if (!next[it.idx]) {
                    next[it.idx] = true;
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [items, readOnly, reviewMode, alreadyDone]);

    const activeAnswers = isOnlineLesson ? (slideState?.answers || answers) : answers;
    const activeLocked = isOnlineLesson ? (slideState?.locked || locked) : locked;
    const activeIsCorrect = isOnlineLesson ? (slideState?.is_correct || isCorrectMap) : isCorrectMap;
    const activeGapAttempts = isOnlineLesson ? (slideState?.gap_attempts || gapAttempts) : gapAttempts;
    const effectiveAnswers = useMemo(
        () => ({ ...activeAnswers, ...draftAnswers, ...checkLocal }),
        [activeAnswers, draftAnswers, checkLocal]
    );
    const DRAFT_KEY = useMemo(() => `ex_draft_${assignment?.id}`, [assignment?.id]);

    useEffect(() => {
        if (isOnlineLesson || !assignment?.id || alreadyDone || readOnly) return;
        try {
            const raw = localStorage.getItem(DRAFT_KEY);
            if (raw) {
                const draft = JSON.parse(raw);
                if (draft && typeof draft === 'object') setDraftAnswers(draft);
            }
        } catch (e) {
            console.warn('[FillGapsChoice] Failed to load draft answers:', e);
        }
    }, [DRAFT_KEY, isOnlineLesson, assignment?.id, alreadyDone, readOnly]);

    const hasAutoCheck = items.some((it) => {
        if (it.type === 'choice') return (it.options || []).some((o) => o.correct);
        if (it.type === 'check') return it.expected === true || it.expected === false;
        return (it.answers || []).length > 0;
    });
    const { persist, getLocalData } = useExercisePersistence(
        assignment?.id,
        { answers: activeAnswers, locked: activeLocked, status: completed ? 'completed' : 'working', is_correct: activeIsCorrect },
        onUpdate, readOnly || alreadyDone, assignment, isOnlineLesson, updateSlideState, slideState
    );

    const draftAnswersRef = useRef(draftAnswers);
    draftAnswersRef.current = draftAnswers;
    const activeAnswersRef = useRef(activeAnswers);
    activeAnswersRef.current = activeAnswers;
    const activeLockedRef = useRef(activeLocked);
    activeLockedRef.current = activeLocked;
    const isCorrectMapRef = useRef(activeIsCorrect);
    isCorrectMapRef.current = activeIsCorrect;
    const itemsRef = useRef(items);
    itemsRef.current = items;
    const checkLocalRef = useRef(checkLocal);
    checkLocalRef.current = checkLocal;

    const toggleCheck = (idx) => {
        if (showSolution || alreadyDone || readOnly || reviewMode || completed || activeLocked[idx]) return;
        setCheckLocal((prev) => {
            const merged = { ...activeAnswersRef.current, ...draftAnswersRef.current, ...prev };
            return { ...prev, [idx]: !isChecklistBoxChecked(merged, idx) };
        });
    };

    const handleDraftChange = useCallback((idx, val) => {
        const nextDrafts = { ...draftAnswersRef.current, [idx]: val };
        draftAnswersRef.current = nextDrafts;
        setDraftAnswers(nextDrafts);
        try { localStorage.setItem(DRAFT_KEY, JSON.stringify(nextDrafts)); } catch (e) { /* ignore */ }
        const mergedAnswers = { ...activeAnswersRef.current, ...checkLocalRef.current };
        Object.entries(nextDrafts).forEach(([draftIdx, draftVal]) => {
            if (draftVal != null && String(draftVal).trim() !== '') mergedAnswers[draftIdx] = draftVal;
        });
        persist({ answers: mergedAnswers, locked: activeLockedRef.current, status: 'working', is_correct: isCorrectMapRef.current });
    }, [DRAFT_KEY, persist]);

    const handleSubmitAnswer = (idx, val) => {
        const item = itemsRef.current.find((it) => it.idx === idx) || itemsRef.current[idx];
        const hasKey = mixedItemHasAutoKey(item);
        const ok = mixedItemIsCorrect(item, val);

        let nextAnswers = { ...activeAnswers, ...checkLocalRef.current, [idx]: val };
        let nextLocked = { ...activeLocked, [idx]: true };
        let nextIsCorrect = { ...activeIsCorrect };
        if (ok === null) {
            delete nextIsCorrect[idx];
            delete nextIsCorrect[String(idx)];
        } else {
            nextIsCorrect = { ...activeIsCorrect, [idx]: ok, [String(idx)]: ok };
        }
        let nextGapAttempts = activeGapAttempts;

        if (threeAttempts && hasKey && ok !== null) {
            const applied = applyGapAttempt(getGapAttemptResults(activeGapAttempts, idx), ok);
            nextGapAttempts = setGapAttemptResults(activeGapAttempts, idx, applied.results);
            nextLocked = { ...activeLocked, [idx]: applied.lock };
            if (applied.isCorrect === null) {
                const { [idx]: _a, [String(idx)]: _b, ...rest } = nextIsCorrect;
                nextIsCorrect = rest;
            } else {
                nextIsCorrect = { ...activeIsCorrect, [idx]: applied.isCorrect, [String(idx)]: applied.isCorrect };
            }
            if (applied.clearAnswer) {
                nextAnswers = { ...activeAnswers, ...checkLocalRef.current, [idx]: '' };
            }
        }

        const nextDrafts = { ...draftAnswersRef.current };
        if (threeAttempts && nextAnswers[idx] === '') nextDrafts[idx] = '';
        else delete nextDrafts[idx];
        draftAnswersRef.current = nextDrafts;
        setDraftAnswers(nextDrafts);
        if (!isOnlineLesson) {
            setAnswers(nextAnswers);
            setLocked(nextLocked);
            setIsCorrectMap(nextIsCorrect);
            if (threeAttempts) setGapAttempts(nextGapAttempts);
        }
        try { localStorage.setItem(DRAFT_KEY, JSON.stringify(nextDrafts)); } catch (e) { /* ignore */ }
        persist({
            answers: nextAnswers,
            locked: nextLocked,
            status: 'working',
            is_correct: nextIsCorrect,
            ...(threeAttempts ? { gap_attempts: nextGapAttempts } : {}),
        });
    };

    const setChoiceAnswer = (idx, val) => {
        const next = { ...activeAnswers, ...checkLocalRef.current, [idx]: val };
        if (!isOnlineLesson) setAnswers(next);
        persist({
            answers: next,
            locked: activeLocked,
            is_correct: activeIsCorrect,
            status: 'working',
            ...(threeAttempts ? { gap_attempts: activeGapAttempts } : {}),
        });
    };

    const checkChoice = (idx) => {
        const item = itemsRef.current.find((it) => it.idx === idx) || itemsRef.current[idx];
        const selected = activeAnswers[idx];
        const hasKey = mixedItemHasAutoKey(item);
        const ok = mixedItemIsCorrect(item, selected);

        let nextLocked = { ...activeLocked, [idx]: true };
        let nextIsCorrect = { ...activeIsCorrect };
        if (ok === null) {
            delete nextIsCorrect[idx];
            delete nextIsCorrect[String(idx)];
        } else {
            nextIsCorrect = { ...activeIsCorrect, [idx]: ok, [String(idx)]: ok };
        }
        let nextAnswers = { ...activeAnswers, ...checkLocalRef.current };
        let nextGapAttempts = activeGapAttempts;

        if (threeAttempts && hasKey && ok !== null) {
            const applied = applyGapAttempt(getGapAttemptResults(activeGapAttempts, idx), ok);
            nextGapAttempts = setGapAttemptResults(activeGapAttempts, idx, applied.results);
            nextLocked = { ...activeLocked, [idx]: applied.lock };
            if (applied.isCorrect === null) {
                const { [idx]: _a, [String(idx)]: _b, ...rest } = nextIsCorrect;
                nextIsCorrect = rest;
            } else {
                nextIsCorrect = { ...activeIsCorrect, [idx]: applied.isCorrect, [String(idx)]: applied.isCorrect };
            }
            if (applied.clearAnswer) {
                nextAnswers = { ...nextAnswers, [idx]: '' };
            }
        }

        if (!isOnlineLesson) {
            setLocked(nextLocked);
            setIsCorrectMap(nextIsCorrect);
            setAnswers(nextAnswers);
            if (threeAttempts) setGapAttempts(nextGapAttempts);
        }
        persist({
            answers: nextAnswers,
            locked: nextLocked,
            is_correct: nextIsCorrect,
            status: 'working',
            ...(threeAttempts ? { gap_attempts: nextGapAttempts } : {}),
        });
    };

    const handleToggleCorrect = (idx) => {
        if (!reviewMode || !onUpdateAssignment) return;
        const k = String(idx);
        const cur = isCorrectMap[idx] === true || isCorrectMap[k] === true;
        const nextOk = !cur;
        const newMap = { ...isCorrectMap, [idx]: nextOk, [k]: nextOk };
        const newRewards = {
            ...gapRewards,
            [k]: { stars: nextOk ? rewardCaps.maxStars : 0, coins: nextOk ? rewardCaps.maxCoins : 0 },
        };
        const score = Object.values(newMap).filter((v) => v === true).length;
        const rewards = sumGapRewards(newRewards, exercise);
        setIsCorrectMap(newMap);
        setGapRewards(newRewards);
        onUpdateAssignment(assignment.id, {
            is_correct: newMap, gap_rewards: newRewards, score,
            awarded_stars: rewards.awarded_stars, awarded_coins: rewards.awarded_coins,
        });
    };

    const handleGapRewardChange = (idx, partial) => {
        if (!reviewMode || !onUpdateAssignment) return;
        const k = String(idx);
        const prev = gapRewards[k] || gapRewards[idx] || { stars: 0, coins: 0 };
        let stars = partial.stars !== undefined ? Number(partial.stars) : (parseFloat(prev.stars) || 0);
        let coins = partial.coins !== undefined ? Number(partial.coins) : (parseInt(prev.coins, 10) || 0);
        if (Number.isNaN(stars)) stars = 0;
        if (Number.isNaN(coins)) coins = 0;
        if (rewardCaps.maxStars > 0) stars = Math.max(0, Math.min(rewardCaps.maxStars, stars));
        else stars = 0;
        if (rewardCaps.maxCoins > 0) coins = Math.max(0, Math.min(rewardCaps.maxCoins, Math.round(coins)));
        else coins = 0;
        const ok = isMaxGapReward(stars, coins, rewardCaps.maxStars, rewardCaps.maxCoins);
        const newMap = { ...isCorrectMap, [idx]: ok, [k]: ok };
        const newRewards = { ...gapRewards, [k]: { stars, coins } };
        const score = Object.values(newMap).filter((v) => v === true).length;
        const rewards = sumGapRewards(newRewards, exercise);
        setIsCorrectMap(newMap);
        setGapRewards(newRewards);
        onUpdateAssignment(assignment.id, {
            is_correct: newMap, gap_rewards: newRewards, score,
            awarded_stars: rewards.awarded_stars, awarded_coins: rewards.awarded_coins,
        });
    };

    const handleBlurClick = (e) => {
        const blurTarget = e.target.closest('.blurred-text');
        if (!blurTarget) return;
        e.stopPropagation();
        const blurId = blurTarget.getAttribute('data-blur-id');
        const nextUnblurred = { ...currentUnblurred, [blurId]: !currentUnblurred[blurId] };
        if (isOnlineLesson && isTeacher && updateSlideState) updateSlideState({ ...slideState, unblurred: nextUnblurred });
        else if (isOnlineLesson && !isTeacher) setLocalUnblurred(nextUnblurred);
        else if (!isOnlineLesson) {
            setLocalUnblurred(nextUnblurred);
            persist({ answers: { ...activeAnswers, ...checkLocalRef.current, unblurred: nextUnblurred }, locked: activeLocked, status: 'working', is_correct: isCorrectMap });
        }
    };

    useEffect(() => {
        if (!isOnlineLesson && (readOnly || reviewMode || alreadyDone) && assignment) {
            if (assignment.answers) setAnswers(assignment.answers);
            if (assignment.locked) setLocked(assignment.locked);
            if (assignment.is_correct) setIsCorrectMap(assignment.is_correct);
            if (assignment.status === 'completed' || assignment.status === 'checked') setCompleted(true);
        }
    }, [readOnly, reviewMode, alreadyDone, assignment, isOnlineLesson]);

    useEffect(() => {
        if (!reviewMode || !assignment || !exercise) return;
        const stored = assignment.is_correct;
        let nextMap = stored && typeof stored === 'object' ? stored : {};
        if (stored && typeof stored === 'object' && Object.keys(stored).length > 0) setIsCorrectMap(stored);
        else if (assignment.answers) {
            const graded = computeAutoGrade(exercise, assignment.answers);
            nextMap = graded.is_correct;
            setIsCorrectMap(graded.is_correct);
        }
        if (assignment.gap_rewards && typeof assignment.gap_rewards === 'object' && Object.keys(assignment.gap_rewards).length > 0) {
            setGapRewards(assignment.gap_rewards);
        } else {
            setGapRewards(buildGapRewardsFromIsCorrect(exercise, nextMap || {}, items.length));
        }
    }, [reviewMode, assignment, exercise, items.length]);

    useEffect(() => {
        if (isOnlineLesson) return;
        const local = getLocalData();
        if (local && !alreadyDone && !readOnly) {
            if (local.answers) setAnswers(local.answers);
            if (local.locked) setLocked(local.locked);
        }
    }, [getLocalData, alreadyDone, readOnly, isOnlineLesson]);

    useEffect(() => {
        if (completed && !isOnlineLesson && assignment?.id) {
            try { localStorage.removeItem(DRAFT_KEY); setDraftAnswers({}); } catch (e) { /* ignore */ }
        }
    }, [completed, isOnlineLesson, assignment?.id, DRAFT_KEY]);

    const shuffledOptions = useMemo(() => {
        const doShuffle = exercise?.assessment?.shuffleOptions !== false;
        return Object.fromEntries(items.filter((it) => it.type === 'choice').map((c) => [
            c.idx,
            doShuffle ? seededShuffle(c.options || [], `fgc_${exercise?.id || 'ex'}_${c.idx}`) : [...(c.options || [])],
        ]));
    }, [items, exercise?.id, exercise?.assessment?.shuffleOptions]);

    const finishExercise = () => {
        const mergedAnswers = { ...activeAnswers, ...checkLocalRef.current };
        Object.entries(draftAnswers || {}).forEach(([draftIdx, draftVal]) => {
            if (draftVal != null && String(draftVal).trim() !== '') mergedAnswers[draftIdx] = draftVal;
        });
        Object.assign(mergedAnswers, checkLocalRef.current);
        const nextLocked = Object.fromEntries(items.map((it) => [it.idx, true]));
        const isCorrectMapFinal = Object.fromEntries(items.map((it) => [it.idx, mixedItemIsCorrect(it, mergedAnswers[it.idx])]));
        const finalScore = Object.values(isCorrectMapFinal).filter((v) => v === true).length;
        if (!isOnlineLesson) {
            setAnswers(mergedAnswers);
            setLocked(nextLocked);
            setDraftAnswers({});
            try { localStorage.removeItem(DRAFT_KEY); } catch (e) { /* ignore */ }
        }
        setCompleted(true);
        setIsCorrectMap(isCorrectMapFinal);
        persist({ answers: mergedAnswers, locked: nextLocked, status: 'completed', is_correct: isCorrectMapFinal });
        if (onComplete) onComplete({ answers: mergedAnswers, score: finalScore, total: items.length, is_correct: isCorrectMapFinal, status: 'completed' });
    };

    return (
        <div className="exercise-card-shell" style={CARD_STYLE}>
            <ExerciseLead exercise={exercise} />
            <div style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                <div className="exercise-gap-content" onClick={handleBlurClick}>
                    {(() => {
                        let n = 0;
                        return renderFillGapsStructuredParts(parts, {
                        idPrefix: 'fgc',
                        nextGapIdx: () => n++,
                        renderText: (p, key) => (
                            <InlineAudioRenderer key={key} inline html={renderMarkdownToHtml(p.text, currentUnblurred, key, true)} />
                        ),
                        renderNote: (p, key) => (
                            <MixedNoteHighlight key={key} text={p.text} tooltip={p.tooltip} />
                        ),
                        renderGap: (p, gi, key) => {
                            const idx = p.idx ?? gi;
                            return (
                                <GapInput key={key} gapIdx={idx} prompt={p.prompt} answers={p.answers}
                                    globalAnswers={effectiveAnswers} onSubmit={handleSubmitAnswer}
                                    locked={activeLocked} draggable={alreadyDone || readOnly}
                                    reviewMode={reviewMode} isCorrect={activeIsCorrect[idx] ?? activeIsCorrect[String(idx)]}
                                    onToggleCorrect={handleToggleCorrect}
                                    readOnly={alreadyDone || readOnly}
                                    showSolution={showSolution}
                                    solutionValue={p.answers?.[0] || ''}
                                    draftValue={draftAnswers[idx]}
                                    onDraftChange={handleDraftChange}
                                    assignment={assignment}
                                    maxStars={rewardCaps.maxStars}
                                    maxCoins={rewardCaps.maxCoins}
                                    rewardStars={(gapRewards[String(idx)] || gapRewards[idx] || {}).stars ?? 0}
                                    rewardCoins={(gapRewards[String(idx)] || gapRewards[idx] || {}).coins ?? 0}
                                    onRewardChange={handleGapRewardChange}
                                    showAttempts={threeAttempts}
                                    attemptResults={getGapAttemptResults(activeGapAttempts, idx)}
                                />
                            );
                        },
                        renderChoice: (p, gi, key) => {
                            const idx = p.idx ?? gi;
                            const selected = effectiveAnswers[idx];
                            const correctLabel = (p.options || []).find((o) => o.correct)?.label || '';
                            const result = activeIsCorrect[idx] ?? activeIsCorrect[String(idx)];
                            const mcPeeking = !!peekMap[idx];
                            return (
                                <ChoiceInput
                                    key={key}
                                    choiceIdx={idx}
                                    options={shuffledOptions[idx] || p.options || []}
                                    selected={selected}
                                    correctLabel={correctLabel}
                                    locked={activeLocked[idx]}
                                    isCorrect={result}
                                    showSolution={showSolution}
                                    reviewMode={reviewMode}
                                    readOnly={readOnly}
                                    alreadyDone={alreadyDone}
                                    assignment={assignment}
                                    onSelect={setChoiceAnswer}
                                    onCheck={checkChoice}
                                    onToggleCorrect={handleToggleCorrect}
                                    peeking={mcPeeking}
                                    onPeekToggle={() => setPeekMap((m) => ({ ...m, [idx]: !m[idx] }))}
                                    showAttempts={threeAttempts}
                                    attemptResults={getGapAttemptResults(activeGapAttempts, idx)}
                                    maxStars={rewardCaps.maxStars}
                                    maxCoins={rewardCaps.maxCoins}
                                    rewardStars={(gapRewards[String(idx)] || gapRewards[idx] || {}).stars ?? 0}
                                    rewardCoins={(gapRewards[String(idx)] || gapRewards[idx] || {}).coins ?? 0}
                                    onRewardChange={handleGapRewardChange}
                                />
                            );
                        },
                        renderCheck: (p, gi, key) => {
                            const idx = p.idx ?? gi;
                            const studentOn = isChecklistBoxChecked(effectiveAnswers, idx);
                            const result = activeIsCorrect[idx] ?? activeIsCorrect[String(idx)];
                            const hasKey = p.expected === true || p.expected === false;
                            const checkPeekEnabled = canUseAnswerPeek({
                                assignment, reviewMode, isWrong: result === false, hasKey,
                            });
                            const checkPeeking = !!peekMap[idx];
                            const displayOn = (showSolution || (checkPeeking && checkPeekEnabled)) && hasKey
                                ? p.expected
                                : studentOn;
                            const boxLocked = showSolution || completed || alreadyDone || readOnly || reviewMode;
                            return (
                                <span key={key} style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px', position: 'relative', margin: '0 0.15rem', verticalAlign: 'middle' }}>
                                    <button
                                        type="button"
                                        role="checkbox"
                                        aria-checked={displayOn}
                                        disabled={(alreadyDone || completed) || (boxLocked)}
                                        onClick={(alreadyDone || completed) ? undefined : (() => toggleCheck(idx))}
                                        title={hasKey ? undefined : 'Teacher will check this box'}
                                        style={{
                                            width: 22,
                                            height: 22,
                                            borderRadius: 6,
                                            border: `2px solid ${displayOn ? '#4361ee' : '#c7d2fe'}`,
                                            background: displayOn ? '#4361ee' : 'white',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: boxLocked ? 'default' : 'pointer',
                                            verticalAlign: 'middle',
                                            color: 'white',
                                            fontSize: 13,
                                            fontWeight: 900,
                                            padding: 0,
                                            lineHeight: 1,
                                            flexShrink: 0,
                                        }}
                                    >
                                        {displayOn ? '✓' : ''}
                                    </button>
                                    {(showSolution || completed || alreadyDone || reviewMode) && (
                                        <span
                                            onClick={() => !showSolution && reviewMode && handleToggleCorrect(idx)}
                                            style={{ cursor: (!showSolution && reviewMode) ? 'pointer' : 'default', fontSize: '0.9rem' }}
                                            title={!hasKey ? 'Teacher will check this' : (reviewMode ? 'Click to toggle mark' : undefined)}
                                        >
                                            {answerMarkEmoji(result, { hasAutoKey: hasKey, reviewMode: reviewMode || !!showSolution })}
                                        </span>
                                    )}
                                    {checkPeekEnabled && (
                                        <ExerciseAnswerPeekButton
                                            peeking={checkPeeking}
                                            onToggle={() => setPeekMap((m) => ({ ...m, [idx]: !m[idx] }))}
                                        />
                                    )}
                                    {reviewMode && (
                                        <GapRewardEditors
                                            maxStars={rewardCaps.maxStars}
                                            maxCoins={rewardCaps.maxCoins}
                                            stars={(gapRewards[String(idx)] || gapRewards[idx] || {}).stars ?? 0}
                                            coins={(gapRewards[String(idx)] || gapRewards[idx] || {}).coins ?? 0}
                                            onChange={(next) => handleGapRewardChange(idx, next)}
                                        />
                                    )}
                                </span>
                            );
                        },
                    });
                    })()}
                </div>
            </div>
            {!preview && (
                <ExerciseActionBar>
                    {!readOnly && !reviewMode && (!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                        <button type="button" className={(alreadyDone || completed) ? COMPLETE_DONE_CLASS : COMPLETE_BTN} disabled={!!(alreadyDone || completed)} onClick={(alreadyDone || completed) ? undefined : finishExercise}>{(alreadyDone || completed) ? COMPLETE_DONE_LABEL : COMPLETE_LABEL}</button>
                    )}
                    {(!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                        <>
                            {hasAutoCheck && !reviewMode ? (
                                <ExerciseScoreBox submitted={completed}>
                                    Score: {Object.values(activeIsCorrect).filter((v) => v === true).length} / {items.length}
                                </ExerciseScoreBox>
                            ) : !hasAutoCheck ? (
                                <AssignmentStatusPill assignment={assignment} completed={completed} isOnlineLesson={isOnlineLesson}  practiceMode={practiceMode} />
                            ) : null}
                        </>
                    )}
                </ExerciseActionBar>
            )}
            <TeacherComment exercise={exercise} assignment={assignment} />
        </div>
    );
}
FillGapsChoiceInteractive.displayName = 'FillGapsChoiceInteractive';

/* ═══════════════════════════════════════════════════════════════════════════
   CHECKLIST — [+] must be checked, [-] must stay empty, [] teacher-graded
   Submitted as a whole; each checkbox is one scored item.
═══════════════════════════════════════════════════════════════════════════ */
function ChecklistInteractive({
    exercise, assignment, preview, onComplete, reviewMode, onUpdateAssignment, onUpdate,
    readOnly, isOnlineLesson, updateSlideState, slideState, user, isTeacher, showSolution, practiceMode,
}) {
    'use no memo';
    const parts = useMemo(() => parseChecklistStructuredBody(exercise.body || ''), [exercise.body]);
    const items = useMemo(() => collectChecklistItems(parts), [parts]);
    const hasAutoKeys = items.some((it) => it.expected === true || it.expected === false);
    const alreadyDone = isAlreadyDone(assignment) && !reviewMode;
    const [answers, setAnswers] = useState(assignment?.answers || {});
    const [completed, setCompleted] = useState(readOnly || reviewMode || alreadyDone);
    const [isCorrectMap, setIsCorrectMap] = useState(assignment?.is_correct || {});
    const [gapRewards, setGapRewards] = useState(() => (
        assignment?.gap_rewards
        || buildGapRewardsFromIsCorrect(exercise, assignment?.is_correct || {}, items.length)
    ));
    const rewardCaps = useMemo(() => getPerGapRewardCaps(exercise), [exercise]);
    const [localUnblurred, setLocalUnblurred] = useState({});
    const [peekMap, setPeekMap] = useState({});
    const emptyUnblurredRef = useRef({});
    const currentUnblurred = isOnlineLesson ? (slideState?.unblurred || emptyUnblurredRef.current) : localUnblurred;
    const locked = readOnly || reviewMode || alreadyDone || completed || showSolution;

    const { persist } = useExercisePersistence(
        assignment?.id,
        { answers, status: completed ? 'completed' : 'working', is_correct: isCorrectMap },
        onUpdate, readOnly || alreadyDone, assignment, isOnlineLesson, updateSlideState, slideState
    );

    const countCorrect = (map) => items.filter((it) => map[it.idx] === true || map[String(it.idx)] === true).length;

    const toggleBox = (idx) => {
        if (locked) return;
        setAnswers((prev) => ({ ...prev, [idx]: !isChecklistBoxChecked(prev, idx) }));
    };

    const handleToggleCorrect = (idx) => {
        if (!reviewMode || !onUpdateAssignment) return;
        const k = String(idx);
        const cur = isCorrectMap[idx] === true || isCorrectMap[k] === true;
        const nextOk = !cur;
        const newMap = { ...isCorrectMap, [idx]: nextOk, [k]: nextOk };
        const newRewards = {
            ...gapRewards,
            [k]: { stars: nextOk ? rewardCaps.maxStars : 0, coins: nextOk ? rewardCaps.maxCoins : 0 },
        };
        const score = countCorrect(newMap);
        const rewards = sumGapRewards(newRewards, exercise);
        setIsCorrectMap(newMap);
        setGapRewards(newRewards);
        onUpdateAssignment(assignment.id, {
            is_correct: newMap, gap_rewards: newRewards, score, total: items.length,
            awarded_stars: rewards.awarded_stars, awarded_coins: rewards.awarded_coins,
        });
    };

    const handleGapRewardChange = (idx, partial) => {
        if (!reviewMode || !onUpdateAssignment) return;
        const k = String(idx);
        const prev = gapRewards[k] || gapRewards[idx] || { stars: 0, coins: 0 };
        let stars = partial.stars !== undefined ? Number(partial.stars) : (parseFloat(prev.stars) || 0);
        let coins = partial.coins !== undefined ? Number(partial.coins) : (parseInt(prev.coins, 10) || 0);
        if (Number.isNaN(stars)) stars = 0;
        if (Number.isNaN(coins)) coins = 0;
        if (rewardCaps.maxStars > 0) stars = Math.max(0, Math.min(rewardCaps.maxStars, stars));
        else stars = 0;
        if (rewardCaps.maxCoins > 0) coins = Math.max(0, Math.min(rewardCaps.maxCoins, Math.round(coins)));
        else coins = 0;
        const ok = isMaxGapReward(stars, coins, rewardCaps.maxStars, rewardCaps.maxCoins);
        const newMap = { ...isCorrectMap, [idx]: ok, [k]: ok };
        const newRewards = { ...gapRewards, [k]: { stars, coins } };
        const score = countCorrect(newMap);
        const rewards = sumGapRewards(newRewards, exercise);
        setIsCorrectMap(newMap);
        setGapRewards(newRewards);
        onUpdateAssignment(assignment.id, {
            is_correct: newMap, gap_rewards: newRewards, score, total: items.length,
            awarded_stars: rewards.awarded_stars, awarded_coins: rewards.awarded_coins,
        });
    };

    const handleBlurClick = (e) => {
        const blurTarget = e.target.closest('.blurred-text');
        if (!blurTarget) return;
        e.stopPropagation();
        const blurId = blurTarget.getAttribute('data-blur-id');
        const nextUnblurred = { ...currentUnblurred, [blurId]: !currentUnblurred[blurId] };
        if (isOnlineLesson && isTeacher && updateSlideState) updateSlideState({ ...slideState, unblurred: nextUnblurred });
        else if (isOnlineLesson && !isTeacher) setLocalUnblurred(nextUnblurred);
        else if (!isOnlineLesson) setLocalUnblurred(nextUnblurred);
    };

    useEffect(() => {
        if (!isOnlineLesson && (readOnly || reviewMode || alreadyDone) && assignment) {
            if (assignment.answers) setAnswers(assignment.answers);
            if (assignment.is_correct) setIsCorrectMap(assignment.is_correct);
            if (assignment.status === 'completed' || assignment.status === 'checked') setCompleted(true);
        }
    }, [readOnly, reviewMode, alreadyDone, assignment, isOnlineLesson]);

    useEffect(() => {
        if (!reviewMode || !assignment || !exercise) return;
        const stored = assignment.is_correct;
        const keys = stored && typeof stored === 'object' ? Object.keys(stored) : [];
        const isLegacyWhole = items.length > 1 && keys.length > 0 && keys.every((k) => String(k) === '0');
        if (stored && typeof stored === 'object' && keys.length > 0 && !isLegacyWhole) {
            setIsCorrectMap(stored);
        } else {
            const graded = computeAutoGrade(exercise, assignment.answers || {});
            setIsCorrectMap(graded.is_correct);
            if (isLegacyWhole) {
                setGapRewards(buildGapRewardsFromIsCorrect(exercise, graded.is_correct, items.length));
            }
        }
        if (!isLegacyWhole && assignment.gap_rewards && typeof assignment.gap_rewards === 'object' && Object.keys(assignment.gap_rewards).length > 0) {
            setGapRewards(assignment.gap_rewards);
        }
    }, [reviewMode, assignment, exercise, hasAutoKeys, items.length]);

    const finishExercise = () => {
        const nextAnswers = { ...(answers || {}) };
        items.forEach((it) => {
            nextAnswers[it.idx] = isChecklistBoxChecked(nextAnswers, it.idx);
        });
        const graded = computeAutoGrade(exercise, nextAnswers);
        setCompleted(true);
        setAnswers(nextAnswers);
        setIsCorrectMap(graded.is_correct);
        persist({ answers: nextAnswers, status: 'completed', is_correct: graded.is_correct });
        if (onComplete) onComplete({ answers: nextAnswers, score: graded.score, total: items.length, is_correct: graded.is_correct, status: 'completed' });
    };

    const correctCount = countCorrect(isCorrectMap);

    return (
        <div className="exercise-card-shell" style={CARD_STYLE}>
            <ExerciseLead exercise={exercise} />
            <div style={{ width: '100%', maxWidth: '100%', overflowX: 'auto' }}>
                <div className="exercise-gap-content" onClick={handleBlurClick} style={{ lineHeight: 1.85 }}>
                    {(() => {
                        let n = 0;
                        return renderFillGapsStructuredParts(parts, {
                            idPrefix: 'chk',
                            nextGapIdx: () => n++,
                            renderText: (p, key) => (
                                <InlineAudioRenderer key={key} inline html={renderMarkdownToHtml(p.text, currentUnblurred, key, true)} />
                            ),
                            renderCheck: (p, gi, key) => {
                                const idx = p.idx ?? gi;
                                const studentOn = isChecklistBoxChecked(answers, idx);
                                const result = isCorrectMap[idx] ?? isCorrectMap[String(idx)];
                                const hasKey = p.expected === true || p.expected === false;
                                const checkPeekEnabled = canUseAnswerPeek({
                                    assignment, reviewMode, isWrong: result === false, hasKey,
                                });
                                const checkPeeking = !!peekMap[idx];
                                const displayOn = (showSolution || (checkPeeking && checkPeekEnabled)) && hasKey
                                    ? p.expected
                                    : studentOn;
                                return (
                                    <span key={key} style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px', position: 'relative', margin: '0 0.15rem', verticalAlign: 'middle' }}>
                                        <button
                                            type="button"
                                            role="checkbox"
                                            aria-checked={displayOn}
                                            disabled={(alreadyDone || completed) || (locked)}
                                            onClick={(alreadyDone || completed) ? undefined : (() => toggleBox(idx))}
                                            title={hasKey ? undefined : 'Teacher will check this box'}
                                            style={{
                                                width: 22,
                                                height: 22,
                                                borderRadius: 6,
                                                border: `2px solid ${displayOn ? '#4361ee' : '#c7d2fe'}`,
                                                background: displayOn ? '#4361ee' : 'white',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: locked ? 'default' : 'pointer',
                                                verticalAlign: 'middle',
                                                color: 'white',
                                                fontSize: 13,
                                                fontWeight: 900,
                                                padding: 0,
                                                lineHeight: 1,
                                                flexShrink: 0,
                                            }}
                                        >
                                            {displayOn ? '✓' : ''}
                                        </button>
                                        {(showSolution || completed || alreadyDone || reviewMode) && (
                                            <span
                                                onClick={() => !showSolution && reviewMode && handleToggleCorrect(idx)}
                                                style={{ cursor: (!showSolution && reviewMode) ? 'pointer' : 'default', fontSize: '0.9rem' }}
                                                title={!hasKey ? 'Teacher will check this' : (reviewMode ? 'Click to toggle mark' : undefined)}
                                            >
                                                {answerMarkEmoji(result, { hasAutoKey: hasKey, reviewMode: reviewMode || !!showSolution })}
                                            </span>
                                        )}
                                        {checkPeekEnabled && (
                                            <ExerciseAnswerPeekButton
                                                peeking={checkPeeking}
                                                onToggle={() => setPeekMap((m) => ({ ...m, [idx]: !m[idx] }))}
                                            />
                                        )}
                                        {reviewMode && (
                                            <GapRewardEditors
                                                maxStars={rewardCaps.maxStars}
                                                maxCoins={rewardCaps.maxCoins}
                                                stars={(gapRewards[String(idx)] || gapRewards[idx] || {}).stars ?? 0}
                                                coins={(gapRewards[String(idx)] || gapRewards[idx] || {}).coins ?? 0}
                                                onChange={(next) => handleGapRewardChange(idx, next)}
                                            />
                                        )}
                                    </span>
                                );
                            },
                        });
                    })()}
                </div>
            </div>
            {!preview && (
                <ExerciseActionBar>
                    {!readOnly && !reviewMode && (!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                        <button type="button" className={(alreadyDone || completed) ? COMPLETE_DONE_CLASS : COMPLETE_BTN} disabled={!!(alreadyDone || completed)} onClick={(alreadyDone || completed) ? undefined : finishExercise}>{(alreadyDone || completed) ? COMPLETE_DONE_LABEL : COMPLETE_LABEL}</button>
                    )}
                    {(!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                        reviewMode ? (
                            <ExerciseScoreBox submitted>
                                Score: {correctCount} / {items.length}
                            </ExerciseScoreBox>
                        ) : hasAutoKeys ? (
                            <ExerciseScoreBox submitted={completed}>
                                Score: {correctCount} / {items.length}
                            </ExerciseScoreBox>
                        ) : (
                            <AssignmentStatusPill assignment={assignment} completed={completed} isOnlineLesson={isOnlineLesson}  practiceMode={practiceMode} />
                        )
                    )}
                </ExerciseActionBar>
            )}
            <TeacherComment exercise={exercise} assignment={assignment} />
        </div>
    );
}
ChecklistInteractive.displayName = 'ChecklistInteractive';

/* ═══════════════════════════════════════════════════════════════════════════
   ORDER THE WORDS
═══════════════════════════════════════════════════════════════════════════ */
function OrderWordsInteractive({ exercise, assignment, preview, onComplete, reviewMode, onUpdateAssignment, onUpdate, readOnly, isOnlineLesson, updateSlideState, slideState, user, isTeacher, showSolution, practiceMode }) {
    const sentences = useMemo(() => parseOrderWords(exercise.body || '', exercise?.id), [exercise.body, exercise?.id]);
    const alreadyDone = isAlreadyDone(assignment) && !reviewMode;
    const [arrangements, setArrangements] = useState(assignment?.answers || {});
    const [locked, setLocked] = useState(
        readOnly || reviewMode ? Object.fromEntries(sentences.map((_, i) => [i, true])) :
        alreadyDone ? Object.fromEntries(sentences.map((_, i) => [i, true])) : {}
    );
    const [isCorrectMap, setIsCorrectMap] = useState(assignment?.is_correct || {});
    const [gapRewards, setGapRewards] = useState(() => (
        assignment?.gap_rewards
        || buildGapRewardsFromIsCorrect(exercise, assignment?.is_correct || {}, sentences.length)
    ));
    const [completed, setCompleted] = useState(readOnly || reviewMode || alreadyDone);
    const rewardCaps = useMemo(() => getPerGapRewardCaps(exercise), [exercise]);
    const [peekMap, setPeekMap] = useState({});
    const [localUnblurred, setLocalUnblurred] = useState({});
    const currentUnblurred = isOnlineLesson ? (slideState?.unblurred || {}) : localUnblurred;

    const activeArrangements = isOnlineLesson ? (slideState?.answers || arrangements) : arrangements;
    const activeLocked = isOnlineLesson ? (slideState?.locked || locked) : locked;

    const setGlobalArrangement = (idx, val) => {
        const next = { ...activeArrangements, [idx]: val };
        if (!isOnlineLesson) setArrangements(next);
        persist({ answers: next, locked: activeLocked, status: 'working', is_correct: isCorrectMap });
    };

    const setGlobalLocked = (idx, val) => {
        const next = { ...activeLocked, [idx]: val };
        if (!isOnlineLesson) setLocked(next);
        persist({ answers: activeArrangements, locked: next, status: 'working', is_correct: isCorrectMap });
    };

    const checkSentence = (idx, currentArrs = activeArrangements) => {
        const arranged = currentArrs[idx] || [];
        const userAnswer = arranged.map(w => typeof w === 'string' ? w : w.text).join(' ');
        const correct = sentences[idx].original;
        return normalizeAnswer(userAnswer) === normalizeAnswer(correct);
    };

    useEffect(() => {
        if (!isOnlineLesson && (readOnly || reviewMode || alreadyDone) && assignment) {
            if (assignment.answers) setArrangements(assignment.answers);
            if (assignment.locked) setLocked(assignment.locked);
            if (assignment.is_correct) setIsCorrectMap(assignment.is_correct);
            if (assignment.status === 'completed' || assignment.status === 'checked') setCompleted(true);
        }
    }, [readOnly, reviewMode, alreadyDone, assignment, isOnlineLesson]);

    const handleBlurClick = (e) => {
        const bt = e.target.closest('.blurred-text');
        if (!bt) return;
        e.stopPropagation();
        const blurId = bt.getAttribute('data-blur-id');
        const next = { ...currentUnblurred, [blurId]: !currentUnblurred[blurId] };
        if (isOnlineLesson && isTeacher && updateSlideState) {
            updateSlideState({ ...slideState, unblurred: next });
        } else if (isOnlineLesson && !isTeacher) {
            setLocalUnblurred(next);
        } else if (!isOnlineLesson) {
            setLocalUnblurred(next);
            persist({ answers: activeArrangements, locked: activeLocked, status: 'working', is_correct: isCorrectMap, unblurred: next });
        }
    };

    const { syncStatus, persist, getLocalData } = useExercisePersistence(assignment?.id, { answers: activeArrangements, locked: activeLocked, status: completed ? 'completed' : 'working', is_correct: isCorrectMap }, onUpdate, readOnly || alreadyDone, assignment, isOnlineLesson, updateSlideState, slideState);

    useEffect(() => {
        if (isOnlineLesson) return;
        const local = getLocalData();
        if (local && !alreadyDone && !readOnly) {
            if (local.answers) setArrangements(local.answers);
            if (local.locked) setLocked(local.locked);
        }
    }, [getLocalData, alreadyDone, readOnly, isOnlineLesson]);

    const moveWord = (si, w, toAnswer) => {
        if (activeLocked[si] || alreadyDone || readOnly) return;
        const current = activeArrangements[si] || [];
        let next;
        if (toAnswer) next = [...current, w];
        else next = current.filter(x => (x.id || x) !== (w.id || w));
        if (preview) {
            setArrangements((prev) => ({ ...prev, [si]: next }));
            return;
        }
        setGlobalArrangement(si, next);
        persist({ answers: { ...activeArrangements, [si]: next }, locked: activeLocked, status: 'working', is_correct: isCorrectMap });
    };

    const handleToggleCorrect = (idx) => {
        if (!reviewMode || !onUpdateAssignment) return;
        const k = String(idx);
        const cur = isCorrectMap[idx] === true || isCorrectMap[k] === true;
        const nextOk = !cur;
        const newMap = { ...isCorrectMap, [idx]: nextOk, [k]: nextOk };
        const newRewards = {
            ...gapRewards,
            [k]: {
                stars: nextOk ? rewardCaps.maxStars : 0,
                coins: nextOk ? rewardCaps.maxCoins : 0,
            },
        };
        const newScore = Object.values(newMap).filter(v => v === true).length;
        const rewards = sumGapRewards(newRewards, exercise);
        setIsCorrectMap(newMap);
        setGapRewards(newRewards);
        onUpdateAssignment(assignment.id, {
            is_correct: newMap,
            gap_rewards: newRewards,
            score: newScore,
            awarded_stars: rewards.awarded_stars,
            awarded_coins: rewards.awarded_coins,
        });
    };

    const handleGapRewardChange = (idx, partial) => {
        if (!reviewMode || !onUpdateAssignment) return;
        const k = String(idx);
        const prev = gapRewards[k] || gapRewards[idx] || { stars: 0, coins: 0 };
        let stars = partial.stars !== undefined ? Number(partial.stars) : (parseFloat(prev.stars) || 0);
        let coins = partial.coins !== undefined ? Number(partial.coins) : (parseInt(prev.coins, 10) || 0);
        if (Number.isNaN(stars)) stars = 0;
        if (Number.isNaN(coins)) coins = 0;
        if (rewardCaps.maxStars > 0) stars = Math.max(0, Math.min(rewardCaps.maxStars, stars));
        else stars = 0;
        if (rewardCaps.maxCoins > 0) coins = Math.max(0, Math.min(rewardCaps.maxCoins, Math.round(coins)));
        else coins = 0;
        const ok = isMaxGapReward(stars, coins, rewardCaps.maxStars, rewardCaps.maxCoins);
        const newMap = { ...isCorrectMap, [idx]: ok, [k]: ok };
        const newRewards = { ...gapRewards, [k]: { stars, coins } };
        const newScore = Object.values(newMap).filter(v => v === true).length;
        const rewards = sumGapRewards(newRewards, exercise);
        setIsCorrectMap(newMap);
        setGapRewards(newRewards);
        onUpdateAssignment(assignment.id, {
            is_correct: newMap,
            gap_rewards: newRewards,
            score: newScore,
            awarded_stars: rewards.awarded_stars,
            awarded_coins: rewards.awarded_coins,
        });
    };

    const score = completed ? sentences.filter((_, i) => checkSentence(i)).length : 0;
    return (
        <div className="exercise-card-shell" style={CARD_STYLE} onClick={handleBlurClick}>
            <ExerciseLead exercise={exercise} unblurred={currentUnblurred} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                {sentences.map((s, si) => {
                    const arranged = activeArrangements[si] || [];
                    const pool = s.shuffled.filter(w => !arranged.some(a => (a.id || a) === (w.id || w)));
                    const isLocked = showSolution || activeLocked[si] || alreadyDone || readOnly;
                    const correct = showSolution ? true : ((reviewMode && isCorrectMap[si] !== undefined) ? isCorrectMap[si] : (isLocked ? checkSentence(si, activeArrangements) : null));
                    const peekEnabled = canUseAnswerPeek({
                        assignment,
                        reviewMode,
                        isWrong: correct === false,
                        hasKey: !!s.original,
                    });
                    const peeking = !!peekMap[si];
                    const displayWords = showSolution
                        ? s.original.split(' ')
                        : (peeking && peekEnabled ? s.original.split(' ') : (activeArrangements[si] || []));

                    return (
                        <div
                            key={si}
                            className="order-words-sentence-card"
                            style={{ background: 'white', borderRadius: '14px', padding: '1rem', border: `2px solid ${isLocked ? (correct ? '#6ee7b7' : '#fca5a5') : '#e2e8f0'}` }}
                        >
                            {!showSolution && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.75rem', minHeight: '36px', padding: '0.5rem', background: '#f8fafc', borderRadius: '8px' }}>
                                    {pool.map((w, wi) => (
                                        <span key={wi} onClick={() => !isLocked && moveWord(si, w, true)}
                                            style={{ padding: '4px 10px', borderRadius: '16px', background: '#dde1ff', color: '#4361ee', fontWeight: 600, fontSize: '0.85rem', cursor: isLocked ? 'default' : 'pointer' }}>
                                            {renderBold(typeof w === 'string' ? w : w.text, currentUnblurred)}
                                        </span>
                                    ))}
                                </div>
                            )}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', minHeight: '36px', padding: '0.5rem', background: '#f0f4ff', borderRadius: '8px', border: '2px dashed #c7d2fe' }}>
                                {!showSolution && displayWords.length === 0 && <span style={{ color: '#c7d2fe', fontSize: '0.85rem', fontStyle: 'italic' }}>Drop words here</span>}
                                {displayWords.map((w, wi) => (
                                    <span key={wi} onClick={() => !showSolution && !isLocked && moveWord(si, w, false)}
                                        style={{ padding: '4px 10px', borderRadius: '16px', background: showSolution ? '#059669' : '#4361ee', color: 'white', fontWeight: 600, fontSize: '0.85rem', cursor: showSolution || isLocked ? 'default' : 'pointer' }}>
                                        {renderBold(typeof w === 'string' ? w : (w.text || w), currentUnblurred)}
                                    </span>
                                ))}
                            </div>
                            {!isLocked && !alreadyDone && (
                                <button disabled={!!(alreadyDone || completed)} onClick={(alreadyDone || completed) ? undefined : (() => {
                                    if (preview) {
                                        setLocked((prev) => ({ ...prev, [si]: true }));
                                        return;
                                    }
                                    setGlobalLocked(si, true);
                                })} style={{ marginTop: '0.5rem', border: 'none', background: '#4361ee18', color: '#4361ee', borderRadius: '8px', padding: '4px 12px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700 }}>👆 Check</button>
                            )}
                            {(isLocked || reviewMode) && correct !== null && (
                                <div style={{ marginTop: '0.4rem', display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                                    <span onClick={() => reviewMode && handleToggleCorrect(si)} style={{ fontSize: '0.85rem', display: 'inline-block', cursor: reviewMode ? 'pointer' : 'default' }}>{correct ? '✅ Correct!' : '❌ Wrong'}</span>
                                    {reviewMode && (
                                        <GapRewardEditors
                                            maxStars={rewardCaps.maxStars}
                                            maxCoins={rewardCaps.maxCoins}
                                            stars={(gapRewards[String(si)] || gapRewards[si] || {}).stars ?? 0}
                                            coins={(gapRewards[String(si)] || gapRewards[si] || {}).coins ?? 0}
                                            onChange={(next) => handleGapRewardChange(si, next)}
                                        />
                                    )}
                                </div>
                            )}
                            {peekEnabled && (
                                <div style={{ marginTop: '0.35rem' }}>
                                    <ExerciseAnswerPeekButton
                                        peeking={peeking}
                                        onToggle={() => setPeekMap((m) => ({ ...m, [si]: !m[si] }))}
                                    />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            {!preview && (
                    <ExerciseActionBar>
                        {!readOnly && !reviewMode && (!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && <button type="button" className={(alreadyDone || completed) ? COMPLETE_DONE_CLASS : COMPLETE_BTN} onClick={() => {
                            const finalLocked = Object.fromEntries(sentences.map((_, i) => [i, true]));
                            if (!isOnlineLesson) setLocked(finalLocked);
                            setCompleted(true);
                            if (onComplete) {
                                const finalScore = sentences.filter((_, i) => checkSentence(i, activeArrangements)).length;
                                const finalIsCorrectMap = Object.fromEntries(sentences.map((_, i) => [i, checkSentence(i, activeArrangements)]));
                                onComplete({ answers: activeArrangements, score: finalScore, total: sentences.length, is_correct: finalIsCorrectMap, status: 'completed' });
                            }
                        }}>{(alreadyDone || completed) ? COMPLETE_DONE_LABEL : COMPLETE_LABEL}</button>}
                        {(!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                            <AssignmentStatusPill assignment={assignment} completed={completed} isOnlineLesson={isOnlineLesson}  practiceMode={practiceMode} />
                        )}
                    </ExerciseActionBar>
            )}
            <TeacherComment exercise={exercise} assignment={assignment} />
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   ORDER (LINES)
═══════════════════════════════════════════════════════════════════════════ */
function OrderLinesInteractive({ exercise, assignment, preview, onComplete, reviewMode, onUpdateAssignment, onUpdate, readOnly, isOnlineLesson, updateSlideState, slideState, user, isTeacher, showSolution, practiceMode }) {
    const items = useMemo(() => parseOrderLinesItems(exercise.body || ''), [exercise.body]);
    const itemById = useMemo(() => Object.fromEntries(items.map((it) => [it.id, it])), [items]);
    const defaultOrder = useMemo(
        () => buildOrderLinesShuffle(items, exercise?.id),
        [items, exercise?.id]
    );

    const alreadyDone = isAlreadyDone(assignment) && !reviewMode;
    const savedOrder = getOrderLinesOrder(assignment?.answers);
    const [order, setOrder] = useState(() => (savedOrder?.length === items.length ? savedOrder : defaultOrder));
    const [isCorrectMap, setIsCorrectMap] = useState(assignment?.is_correct || {});
    const [gapRewards, setGapRewards] = useState(() => (
        assignment?.gap_rewards
        || buildGapRewardsFromIsCorrect(exercise, assignment?.is_correct || {}, items.length)
    ));
    const [completed, setCompleted] = useState(readOnly || reviewMode || alreadyDone);
    const [dragFrom, setDragFrom] = useState(null);
    const [peekMap, setPeekMap] = useState({});
    const [localUnblurred, setLocalUnblurred] = useState({});
    const rewardCaps = useMemo(() => getPerGapRewardCaps(exercise), [exercise]);
    const currentUnblurred = isOnlineLesson ? (slideState?.unblurred || {}) : localUnblurred;

    const activeOrder = isOnlineLesson
        ? (getOrderLinesOrder(slideState?.answers) || order)
        : order;
    const locked = completed || alreadyDone || readOnly || reviewMode || showSolution;

    const answersPayload = (ord) => ({ order: ord });

    const { syncStatus, persist, getLocalData } = useExercisePersistence(
        assignment?.id,
        { answers: answersPayload(activeOrder), status: completed ? 'completed' : 'working', is_correct: isCorrectMap },
        onUpdate,
        readOnly || alreadyDone,
        assignment,
        isOnlineLesson,
        updateSlideState,
        slideState
    );

    useEffect(() => {
        if (!isOnlineLesson && (readOnly || reviewMode || alreadyDone) && assignment) {
            const next = getOrderLinesOrder(assignment.answers);
            if (next?.length === items.length) setOrder(next);
            if (assignment.is_correct) setIsCorrectMap(assignment.is_correct);
            if (assignment.status === 'completed' || assignment.status === 'checked') setCompleted(true);
        }
    }, [readOnly, reviewMode, alreadyDone, assignment, isOnlineLesson, items.length]);

    useEffect(() => {
        if (isOnlineLesson) return;
        const local = getLocalData();
        if (local && !alreadyDone && !readOnly) {
            const next = getOrderLinesOrder(local.answers);
            if (next?.length === items.length) setOrder(next);
        }
    }, [getLocalData, alreadyDone, readOnly, isOnlineLesson, items.length]);

    const setGlobalOrder = (next) => {
        if (!isOnlineLesson) setOrder(next);
        persist({ answers: answersPayload(next), status: 'working', is_correct: isCorrectMap });
    };

    const moveItem = (fromIdx, toIdx) => {
        if (locked || fromIdx === toIdx || fromIdx < 0 || toIdx < 0 || fromIdx >= activeOrder.length || toIdx >= activeOrder.length) return;
        const next = [...activeOrder];
        const [moved] = next.splice(fromIdx, 1);
        next.splice(toIdx, 0, moved);
        if (preview) {
            setOrder(next);
            return;
        }
        setGlobalOrder(next);
    };

    const handleBlurClick = (e) => {
        const bt = e.target.closest('.blurred-text');
        if (!bt) return;
        e.stopPropagation();
        const blurId = bt.getAttribute('data-blur-id');
        const next = { ...currentUnblurred, [blurId]: !currentUnblurred[blurId] };
        if (isOnlineLesson && isTeacher && updateSlideState) {
            updateSlideState({ ...slideState, unblurred: next });
        } else if (!isOnlineLesson) {
            setLocalUnblurred(next);
            persist({ answers: answersPayload(activeOrder), status: 'working', is_correct: isCorrectMap, unblurred: next });
        } else {
            setLocalUnblurred(next);
        }
    };

    const gradeOrder = (ord) => computeAutoGrade(exercise, answersPayload(ord));

    const handleComplete = () => {
        const graded = gradeOrder(activeOrder);
        setIsCorrectMap(graded.is_correct);
        setCompleted(true);
        if (preview) return;
        if (onComplete) {
            onComplete({
                answers: answersPayload(activeOrder),
                score: graded.score,
                total: graded.total,
                is_correct: graded.is_correct,
                status: 'completed',
            });
        }
    };

    const handleToggleCorrect = (idx) => {
        if (!reviewMode || !onUpdateAssignment) return;
        const k = String(idx);
        const cur = isCorrectMap[idx] === true || isCorrectMap[k] === true;
        const nextOk = !cur;
        const newMap = { ...isCorrectMap, [idx]: nextOk, [k]: nextOk };
        const newRewards = {
            ...gapRewards,
            [k]: {
                stars: nextOk ? rewardCaps.maxStars : 0,
                coins: nextOk ? rewardCaps.maxCoins : 0,
            },
        };
        const newScore = Object.values(newMap).filter((v) => v === true).length;
        const rewards = sumGapRewards(newRewards, exercise);
        setIsCorrectMap(newMap);
        setGapRewards(newRewards);
        onUpdateAssignment(assignment.id, {
            is_correct: newMap,
            gap_rewards: newRewards,
            score: newScore,
            awarded_stars: rewards.awarded_stars,
            awarded_coins: rewards.awarded_coins,
        });
    };

    const handleGapRewardChange = (idx, partial) => {
        if (!reviewMode || !onUpdateAssignment) return;
        const k = String(idx);
        const prev = gapRewards[k] || gapRewards[idx] || { stars: 0, coins: 0 };
        let stars = partial.stars !== undefined ? Number(partial.stars) : (parseFloat(prev.stars) || 0);
        let coins = partial.coins !== undefined ? Number(partial.coins) : (parseInt(prev.coins, 10) || 0);
        if (Number.isNaN(stars)) stars = 0;
        if (Number.isNaN(coins)) coins = 0;
        if (rewardCaps.maxStars > 0) stars = Math.max(0, Math.min(rewardCaps.maxStars, stars));
        else stars = 0;
        if (rewardCaps.maxCoins > 0) coins = Math.max(0, Math.min(rewardCaps.maxCoins, Math.round(coins)));
        else coins = 0;
        const ok = isMaxGapReward(stars, coins, rewardCaps.maxStars, rewardCaps.maxCoins);
        const newMap = { ...isCorrectMap, [idx]: ok, [k]: ok };
        const newRewards = { ...gapRewards, [k]: { stars, coins } };
        const newScore = Object.values(newMap).filter((v) => v === true).length;
        const rewards = sumGapRewards(newRewards, exercise);
        setIsCorrectMap(newMap);
        setGapRewards(newRewards);
        onUpdateAssignment(assignment.id, {
            is_correct: newMap,
            gap_rewards: newRewards,
            score: newScore,
            awarded_stars: rewards.awarded_stars,
            awarded_coins: rewards.awarded_coins,
        });
    };

    const displayOrder = showSolution ? items.map((it) => it.id) : activeOrder;
    const gradedLive = (completed || reviewMode || alreadyDone || showSolution) ? gradeOrder(displayOrder) : null;
    const score = gradedLive ? gradedLive.score : 0;

    if (!items.length) {
        return (
            <div className="exercise-card-shell" style={CARD_STYLE}>
                <ExerciseLead exercise={exercise} unblurred={currentUnblurred} />
                <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
                    No lines yet. Add one phrase, word, or sentence per line in the correct order.
                </div>
            </div>
        );
    }

    return (
        <div className="exercise-card-shell" style={CARD_STYLE} onClick={handleBlurClick}>
            {!readOnly && !preview && !isOnlineLesson && <SyncStatus status={syncStatus} />}
            <ExerciseLead exercise={exercise} unblurred={currentUnblurred} />
            <p style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: '#64748b' }}>
                Drag lines or use ▲ ▼ to put them in the correct order, then Done.
            </p>
            <div className="order-lines-list" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {displayOrder.map((id, pos) => {
                    const item = itemById[id] || items[id];
                    const text = item?.text || '';
                    const showResult = showSolution || completed || reviewMode || alreadyDone;
                    const correct = showSolution
                        ? true
                        : (reviewMode && (isCorrectMap[pos] !== undefined || isCorrectMap[String(pos)] !== undefined)
                            ? (isCorrectMap[pos] === true || isCorrectMap[String(pos)] === true)
                            : (showResult ? (activeOrder[pos] === pos) : null));
                    const border = showResult && correct !== null
                        ? (correct ? '2px solid #6ee7b7' : '2px solid #fca5a5')
                        : '2px solid #e2e8f0';
                    const bg = showResult && correct === false ? '#fef2f2' : (showResult && correct ? '#ecfdf5' : '#fff');
                    const peekEnabled = canUseAnswerPeek({
                        assignment,
                        reviewMode,
                        isWrong: correct === false,
                        hasKey: true,
                    });
                    const peeking = !!peekMap[pos];
                    const shownText = (peeking && peekEnabled && items[pos]) ? items[pos].text : text;

                    return (
                        <div
                            key={id}
                            className="order-lines-row"
                            draggable={!locked}
                            onDragStart={() => setDragFrom(pos)}
                            onDragEnd={() => setDragFrom(null)}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => {
                                e.preventDefault();
                                if (dragFrom != null) moveItem(dragFrom, pos);
                                setDragFrom(null);
                            }}
                            style={{
                                display: 'flex',
                                alignItems: 'stretch',
                                gap: 8,
                                padding: '0.65rem 0.75rem',
                                borderRadius: 12,
                                border,
                                background: bg,
                                boxShadow: dragFrom === pos ? '0 4px 14px rgba(15,23,42,0.12)' : '0 1px 2px rgba(15,23,42,0.04)',
                                cursor: locked ? 'default' : 'grab',
                                opacity: dragFrom === pos ? 0.75 : 1,
                            }}
                        >
                            <div style={{
                                display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2,
                                color: '#94a3b8', fontSize: '0.7rem', fontWeight: 800, minWidth: 28, userSelect: 'none',
                            }}>
                                <button
                                    type="button"
                                    disabled={(alreadyDone || completed) || (locked || pos === 0)}
                                    onClick={(alreadyDone || completed) ? undefined : ((e) => { e.stopPropagation(); moveItem(pos, pos - 1); })}
                                    style={{
                                        border: 'none', background: 'transparent', cursor: locked || pos === 0 ? 'default' : 'pointer',
                                        color: locked || pos === 0 ? '#cbd5e1' : '#4361ee', fontWeight: 900, lineHeight: 1, padding: 2,
                                    }}
                                    aria-label="Move up"
                                >▲</button>
                                <span style={{ textAlign: 'center' }}>{pos + 1}</span>
                                <button
                                    type="button"
                                    disabled={locked || pos === displayOrder.length - 1}
                                    onClick={(e) => { e.stopPropagation(); moveItem(pos, pos + 1); }}
                                    style={{
                                        border: 'none', background: 'transparent', cursor: locked || pos === displayOrder.length - 1 ? 'default' : 'pointer',
                                        color: locked || pos === displayOrder.length - 1 ? '#cbd5e1' : '#4361ee', fontWeight: 900, lineHeight: 1, padding: 2,
                                    }}
                                    aria-label="Move down"
                                >▼</button>
                            </div>
                            <div style={{ flex: 1, fontSize: '0.95rem', color: '#0f172a', lineHeight: 1.45, display: 'flex', alignItems: 'center' }}>
                                {renderBold(shownText, currentUnblurred, `ol_${exercise?.id || 'ex'}_${pos}`)}
                            </div>
                            {showResult && correct !== null && (
                                <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                                    <span
                                        onClick={() => reviewMode && handleToggleCorrect(pos)}
                                        style={{ fontSize: '0.8rem', fontWeight: 700, cursor: reviewMode ? 'pointer' : 'default', color: correct ? '#059669' : '#dc2626' }}
                                    >
                                        {correct ? '✅' : '❌'}
                                    </span>
                                    {reviewMode && (
                                        <GapRewardEditors
                                            maxStars={rewardCaps.maxStars}
                                            maxCoins={rewardCaps.maxCoins}
                                            stars={(gapRewards[String(pos)] || gapRewards[pos] || {}).stars ?? 0}
                                            coins={(gapRewards[String(pos)] || gapRewards[pos] || {}).coins ?? 0}
                                            onChange={(next) => handleGapRewardChange(pos, next)}
                                        />
                                    )}
                                </div>
                            )}
                            {peekEnabled && (
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <ExerciseAnswerPeekButton
                                        peeking={peeking}
                                        onToggle={() => setPeekMap((m) => ({ ...m, [pos]: !m[pos] }))}
                                    />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            <ExerciseActionBar style={{ marginTop: '1.25rem' }}>
                {!readOnly && !reviewMode && !alreadyDone && !completed && (!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open') || preview) && (
                    <button type="button" className={(alreadyDone || completed) ? COMPLETE_DONE_CLASS : COMPLETE_BTN} disabled={!!(alreadyDone || completed)} onClick={(alreadyDone || completed) ? undefined : handleComplete}>{(alreadyDone || completed) ? COMPLETE_DONE_LABEL : COMPLETE_LABEL}</button>
                )}
                {!preview && (!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                    completed && !reviewMode ? (
                        <>
                            <ExerciseScoreBox submitted>
                                Score: {score} / {items.length}
                            </ExerciseScoreBox>
                            <AssignmentStatusPill assignment={assignment} completed={completed} isOnlineLesson={isOnlineLesson}  practiceMode={practiceMode} />
                        </>
                    ) : !reviewMode ? (
                        <AssignmentStatusPill assignment={assignment} completed={completed} isOnlineLesson={isOnlineLesson}  practiceMode={practiceMode} />
                    ) : null
                )}
                {preview && completed && (
                    <ExerciseScoreBox submitted>
                        Score: {score} / {items.length}
                    </ExerciseScoreBox>
                )}
            </ExerciseActionBar>
            <TeacherComment exercise={exercise} assignment={assignment} />
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   TEXT
═══════════════════════════════════════════════════════════════════════════ */
function TextInteractive({ exercise, assignment, preview, onComplete, reviewMode, onUpdateAssignment, onUpdate, readOnly, isOnlineLesson, updateSlideState, slideState, user, isTeacher, practiceMode }) {
    const alreadyDone = isAlreadyDone(assignment) && !reviewMode;
    const [completed, setCompleted] = useState(readOnly || reviewMode || alreadyDone);
    const [localUnblurred, setLocalUnblurred] = useState({});
    const currentUnblurred = isOnlineLesson ? (slideState?.unblurred || {}) : localUnblurred;

    const pages = React.useMemo(() => parseTextSlidePages(exercise.body), [exercise.body]);

    const [localPage, setLocalPage] = useState(0);
    const pageIdx = isOnlineLesson && slideState?.page !== undefined ? slideState.page : localPage;
    const safeIndex = Math.min(pageIdx, Math.max(0, pages.length - 1));

    const handlePageChange = (newPage) => {
        setLocalPage(newPage);
        if (updateSlideState) updateSlideState({ ...slideState, page: newPage });
    };

    useEffect(() => {
        if (!isOnlineLesson && !readOnly && onUpdate) {
            onUpdate({ status: completed ? 'completed' : 'working' });
        }
    }, [completed, onUpdate, readOnly, isOnlineLesson]);

    const html = React.useMemo(() => renderMarkdownToHtml(pages[safeIndex] || '', currentUnblurred, `pg${safeIndex}`), [pages, safeIndex, currentUnblurred]);

        const { syncStatus, persist, getLocalData } = useExercisePersistence(
        assignment?.id,
        { unblurred: localUnblurred, status: completed ? 'completed' : 'working' },
        onUpdate,
        readOnly || alreadyDone,
        assignment,
        isOnlineLesson,
        updateSlideState,
        slideState
    );

    useEffect(() => {
        if (isOnlineLesson) return;
        const local = getLocalData();
        if (local && !readOnly) {
            if (local.unblurred) setLocalUnblurred(local.unblurred);
        }
    }, [getLocalData, readOnly, isOnlineLesson]);

    const handleBlurClick = (e) => {
        const target = e.target;
        const blurTarget = target.closest('.blurred-text');
        if (blurTarget) {
            e.stopPropagation();
            const blurId = blurTarget.getAttribute('data-blur-id');
            const nextUnblurred = { ...currentUnblurred, [blurId]: !currentUnblurred[blurId] };
            if (isOnlineLesson && isTeacher && updateSlideState) {
                updateSlideState({ ...slideState, unblurred: nextUnblurred });
            } else if (isOnlineLesson && !isTeacher) {
                setLocalUnblurred(nextUnblurred);
            } else if (!isOnlineLesson) {
                setLocalUnblurred(nextUnblurred);
                persist({ unblurred: nextUnblurred, status: 'working' });
            }
        }
    };
return (
        <div className="exercise-card-shell" style={CARD_STYLE}>
            <ExerciseLead exercise={exercise} />
            
            <RichTextSlideRenderer
                content={html}
                style={{ lineHeight: 1.8, fontSize: '1rem', color: '#1e293b' }}
            />
            
            {!preview ? (
                <>
                    <ExerciseActionBar>
                        {!readOnly && !reviewMode && (!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && <button type="button" className={(alreadyDone || completed) ? COMPLETE_DONE_CLASS : COMPLETE_BTN} disabled={!!(alreadyDone || completed)} onClick={(alreadyDone || completed) ? undefined : (() => {
                            setCompleted(true);
                            if (onComplete) onComplete({ status: 'completed', score: 1, total: 1 });
                        })}>{(alreadyDone || completed) ? COMPLETE_DONE_LABEL : COMPLETE_LABEL}</button>}
                        {(!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                            <AssignmentStatusPill assignment={assignment} completed={completed} isOnlineLesson={isOnlineLesson}  practiceMode={practiceMode} />
                        )}
                    </ExerciseActionBar>

                    {pages.length > 1 && (
                        <div className="pagination-subtle" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', opacity: 0.5, transition: 'opacity 0.2s', marginTop: '1rem', justifyContent: 'flex-end' }}>
                            <button 
                                onClick={() => handlePageChange(safeIndex - 1)} 
                                disabled={safeIndex === 0 || (!isTeacher && isOnlineLesson)} 
                                className="pagination-btn-subtle"
                                style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '3px 8px', color: '#64748b', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700 }}
                            >←</button>
                            <span style={{ fontSize: '0.72rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase' }}>Page {safeIndex + 1} / {pages.length}</span>
                            <button 
                                onClick={() => handlePageChange(safeIndex + 1)} 
                                disabled={safeIndex === pages.length - 1 || (!isTeacher && isOnlineLesson)} 
                                className="pagination-btn-subtle"
                                style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '3px 8px', color: '#64748b', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700 }}
                            >→</button>
                        </div>
                    )}
                    <style>{`
                        .pagination-subtle:hover { opacity: 1 !important; }
                        .pagination-btn-subtle:hover { border-color: #4361ee !important; color: #4361ee !important; background: #eff6ff !important; }
                        .pagination-btn-subtle:disabled { opacity: 0.3; cursor: not-allowed !important; }
                    `}</style>
                </>
            ) : (
                <>
                    {pages.length > 1 && (
                        <div className="pagination-subtle" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', opacity: 0.5, transition: 'opacity 0.2s', marginTop: '1rem', justifyContent: 'flex-end' }}>
                            <button 
                                onClick={() => handlePageChange(safeIndex - 1)} 
                                disabled={safeIndex === 0} 
                                className="pagination-btn-subtle"
                                style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '3px 8px', color: '#64748b', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700 }}
                            >←</button>
                            <span style={{ fontSize: '0.72rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase' }}>Page {safeIndex + 1} / {pages.length}</span>
                            <button 
                                onClick={() => handlePageChange(safeIndex + 1)} 
                                disabled={safeIndex === pages.length - 1} 
                                className="pagination-btn-subtle"
                                style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '3px 8px', color: '#64748b', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700 }}
                            >→</button>
                        </div>
                    )}
                    <style>{`
                        .pagination-subtle:hover { opacity: 1 !important; }
                        .pagination-btn-subtle:hover { border-color: #4361ee !important; color: #4361ee !important; background: #eff6ff !important; }
                        .pagination-btn-subtle:disabled { opacity: 0.3; cursor: not-allowed !important; }
                    `}</style>
                </>
            )}
            <TeacherComment exercise={exercise} assignment={assignment} />
        </div>
    );
}

function TextSlidesInteractive({ exercise, assignment, preview, onComplete, reviewMode, onUpdate, readOnly, isOnlineLesson, updateSlideState, slideState, user, isTeacher, practiceMode, studentLabel }) {
    const alreadyDone = isAlreadyDone(assignment) && !reviewMode;
    const [completed, setCompleted] = useState(readOnly || reviewMode || alreadyDone);
    const [localUnblurred, setLocalUnblurred] = useState({});
    const currentUnblurred = isOnlineLesson ? (slideState?.unblurred || {}) : localUnblurred;
    const pages = useMemo(() => parseTextSlidePages(exercise.body), [exercise.body]);
    const differentStudents = isTextSlidesDifferentStudents(exercise) && isOnlineLesson;
    const audiences = useMemo(
        () => (differentStudents ? getTextSlideAudiences(exercise, pages.length) : []),
        [differentStudents, exercise, pages.length]
    );
    const [localPage, setLocalPage] = useState(0);
    // Teacher page is authoritative in the room; students remap letter groups in parallel.
    const teacherPageIdx = isOnlineLesson && slideState?.page !== undefined ? slideState.page : localPage;
    const teacherSafeIndex = Math.min(Math.max(0, teacherPageIdx), Math.max(0, pages.length - 1));
    const studentViewPage = differentStudents && !isTeacher
        ? resolveStudentTextSlidePage(audiences, teacherSafeIndex, studentLabel)
        : teacherSafeIndex;
    const displayIndex = studentViewPage == null
        ? teacherSafeIndex
        : Math.min(Math.max(0, studentViewPage), Math.max(0, pages.length - 1));
    const studentLocked = !isTeacher && isOnlineLesson && practiceMode !== 'individual';
    const touchX = useRef(null);

    const { steps: logicalSteps, stepIndex: logicalStepIndex, step: logicalStep } = useMemo(
        () => (differentStudents
            ? findTextSlideLogicalStep(audiences, teacherSafeIndex)
            : { steps: pages.map((_, i) => ({ type: 'all', indices: [i] })), stepIndex: teacherSafeIndex, step: { type: 'all', indices: [teacherSafeIndex] } }),
        [differentStudents, audiences, teacherSafeIndex, pages]
    );
    const teacherAudience = differentStudents ? (audiences[teacherSafeIndex] || 'all') : 'all';
    const displayAudience = differentStudents ? (audiences[displayIndex] || 'all') : 'all';
    // Waiting only when teacher is in a letter group and this student has no matching slide.
    const canSeeCurrent = isTeacher || !differentStudents || studentViewPage != null;

    const handlePageChange = (newPage) => {
        const next = Math.max(0, Math.min(pages.length - 1, newPage));
        setLocalPage(next);
        if (updateSlideState) updateSlideState({ ...slideState, page: next });
    };

    useEffect(() => {
        if (!isOnlineLesson && !readOnly && onUpdate) {
            onUpdate({ status: completed ? 'completed' : 'working' });
        }
    }, [completed, onUpdate, readOnly, isOnlineLesson]);

    const html = useMemo(
        () => renderMarkdownToHtml(pages[displayIndex] || '', currentUnblurred, `ts${displayIndex}`),
        [pages, displayIndex, currentUnblurred]
    );

    const { persist, getLocalData } = useExercisePersistence(
        assignment?.id,
        { unblurred: localUnblurred, page: localPage, status: completed ? 'completed' : 'working' },
        onUpdate,
        readOnly || alreadyDone,
        assignment,
        isOnlineLesson,
        updateSlideState,
        slideState
    );

    useEffect(() => {
        if (isOnlineLesson) return;
        const local = getLocalData();
        if (local && !readOnly) {
            if (local.unblurred) setLocalUnblurred(local.unblurred);
            if (typeof local.page === 'number') setLocalPage(local.page);
        }
    }, [getLocalData, readOnly, isOnlineLesson]);

    const handleBlurClick = (e) => {
        const blurTarget = e.target.closest('.blurred-text');
        if (!blurTarget) return;
        e.stopPropagation();
        const blurId = blurTarget.getAttribute('data-blur-id');
        const nextUnblurred = { ...currentUnblurred, [blurId]: !currentUnblurred[blurId] };
        if (isOnlineLesson && isTeacher && updateSlideState) {
            updateSlideState({ ...slideState, unblurred: nextUnblurred });
        } else if (isOnlineLesson && !isTeacher) {
            setLocalUnblurred(nextUnblurred);
        } else if (!isOnlineLesson) {
            setLocalUnblurred(nextUnblurred);
            persist({ unblurred: nextUnblurred, page: displayIndex, status: 'working' });
        }
    };

    const canNav = !studentLocked;
    const atFirstStep = differentStudents && isTeacher
        ? logicalStepIndex <= 0
        : teacherSafeIndex === 0;
    const atLastStep = differentStudents && isTeacher
        ? logicalStepIndex < 0 || logicalStepIndex >= logicalSteps.length - 1
        : teacherSafeIndex >= pages.length - 1;

    const go = (delta) => {
        if (!canNav) return;
        let next = teacherSafeIndex + delta;
        if (differentStudents && isTeacher) {
            const logical = resolveTeacherLogicalPage(audiences, teacherSafeIndex, delta);
            if (logical == null) return;
            next = logical;
        }
        handlePageChange(next);
        if (!isOnlineLesson) {
            persist({
                unblurred: currentUnblurred,
                page: Math.max(0, Math.min(pages.length - 1, next)),
                status: completed ? 'completed' : 'working',
            });
        }
    };

    const stepLabel = differentStudents && isTeacher
        ? `${Math.max(1, logicalStepIndex + 1)} / ${Math.max(1, logicalSteps.length)}`
        : `${displayIndex + 1} / ${pages.length}`;

    const navRow = pages.length > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginTop: '0.85rem', flexWrap: 'wrap' }}>
            <button
                type="button"
                onClick={(alreadyDone || completed) ? undefined : (() => go(-1))}
                disabled={(alreadyDone || completed) || (!canNav || atFirstStep)}
                style={{
                    width: 36, height: 36, borderRadius: '50%', border: 'none',
                    background: (!canNav || atFirstStep) ? '#e2e8f0' : '#4361ee',
                    color: (!canNav || atFirstStep) ? '#94a3b8' : '#fff',
                    cursor: (!canNav || atFirstStep) ? 'not-allowed' : 'pointer',
                    fontSize: '1.2rem', fontWeight: 700,
                }}
            >‹</button>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                {pages.map((_, i) => {
                    const aud = differentStudents ? audiences[i] : 'all';
                    const badge = aud && aud !== 'all' ? aud : null;
                    const inActiveGroup = Boolean(
                        differentStudents
                        && logicalStep?.type === 'group'
                        && logicalStep.indices.includes(i)
                    );
                    const activeDot = isTeacher
                        ? (i === teacherSafeIndex || inActiveGroup)
                        : (i === displayIndex);
                    const selectedDot = isTeacher ? i === teacherSafeIndex : i === displayIndex;
                    return (
                        <button
                            key={i}
                            type="button"
                            aria-label={`Slide ${i + 1}${badge ? ` (${badge})` : ''}`}
                            onClick={() => {
                                if (!canNav) return;
                                // Students in individual mode: jump only to All or their letter.
                                if (differentStudents && !isTeacher) {
                                    const targetAud = audiences[i] || 'all';
                                    if (targetAud !== 'all' && targetAud !== studentLabel) return;
                                }
                                handlePageChange(i);
                                if (!isOnlineLesson) persist({ page: i, status: completed ? 'completed' : 'working' });
                            }}
                            disabled={!canNav}
                            title={badge
                                ? (isTeacher
                                    ? `Preview group ${badge} (students with ${badge} see this in parallel)`
                                    : `Group ${badge}`)
                                : undefined}
                            style={{
                                width: selectedDot ? 18 : 8,
                                height: 8,
                                borderRadius: 8,
                                border: 'none',
                                padding: 0,
                                background: selectedDot
                                    ? (badge ? '#d97706' : '#4361ee')
                                    : (activeDot
                                        ? (badge ? '#fbbf24' : '#93c5fd')
                                        : (badge ? '#fcd34d' : '#cbd5e1')),
                                cursor: canNav ? 'pointer' : 'default',
                                transition: 'width 0.15s',
                                opacity: (differentStudents && !isTeacher && badge && badge !== studentLabel) ? 0.35 : 1,
                            }}
                        />
                    );
                })}
            </div>
            <span style={{ fontSize: '0.75rem', fontWeight: 800, color: '#94a3b8' }}>{stepLabel}</span>
            {isTeacher && differentStudents && logicalStep?.type === 'group' && (
                <span style={{ fontSize: '0.7rem', fontWeight: 900, background: '#fef3c7', color: '#92400e', borderRadius: 6, padding: '2px 7px' }}>
                    {teacherAudience === 'all' ? 'ABC…' : `Preview ${teacherAudience}`}
                </span>
            )}
            {!isTeacher && differentStudents && displayAudience !== 'all' && canSeeCurrent && (
                <span style={{ fontSize: '0.7rem', fontWeight: 900, background: '#fef3c7', color: '#92400e', borderRadius: 6, padding: '2px 7px' }}>
                    {displayAudience}
                </span>
            )}
            <button
                type="button"
                onClick={() => go(1)}
                disabled={!canNav || atLastStep}
                style={{
                    width: 36, height: 36, borderRadius: '50%', border: 'none',
                    background: (!canNav || atLastStep) ? '#e2e8f0' : '#4361ee',
                    color: (!canNav || atLastStep) ? '#94a3b8' : '#fff',
                    cursor: (!canNav || atLastStep) ? 'not-allowed' : 'pointer',
                    fontSize: '1.2rem', fontWeight: 700,
                }}
            >›</button>
        </div>
    );

    const stageShellStyle = {
        width: '100%',
        aspectRatio: '1 / 1',
        background: '#fff',
        borderRadius: 16,
        border: '1.5px solid #e2e8f0',
        boxSizing: 'border-box',
        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
        // Minimal inset — slide uses the full card width under the lead.
        padding: 6,
        display: 'flex',
        flexDirection: 'column',
        containerType: 'size',
    };

    if (!canSeeCurrent) {
        return (
            <div className="exercise-card-shell" style={CARD_STYLE}>
                <ExerciseLead exercise={exercise} />
                <div style={{
                    ...stageShellStyle,
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    color: '#64748b',
                    padding: '1rem',
                }}
                >
                    <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>⏳</div>
                    <div style={{ fontWeight: 800, fontSize: '1.15rem', color: '#1e293b', marginBottom: '0.35rem' }}>
                        Waiting for your group…
                    </div>
                    <div style={{ fontSize: '0.9rem', maxWidth: 320, lineHeight: 1.45 }}>
                        {studentLabel
                            ? `No slide for group ${studentLabel} in this section.`
                            : 'Ask your teacher to assign you A–F to see your slide.'}
                    </div>
                </div>
                {navRow}
            </div>
        );
    }

    return (
        <div className="exercise-card-shell" style={CARD_STYLE}>
            <ExerciseLead exercise={exercise} />
            <div
                className="text-slides-stage"
                onClick={handleBlurClick}
                onTouchStart={(e) => { touchX.current = e.changedTouches[0]?.clientX ?? null; }}
                onTouchEnd={(e) => {
                    if (touchX.current == null || !canNav) return;
                    const dx = (e.changedTouches[0]?.clientX ?? touchX.current) - touchX.current;
                    if (dx > 50) go(-1);
                    else if (dx < -50) go(1);
                    touchX.current = null;
                }}
                style={stageShellStyle}
            >
                <div className="text-slides-stage__inner">
                    <RichTextSlideRenderer
                        content={html}
                        style={{ lineHeight: 1.7, fontSize: '1rem', color: '#1e293b', width: '100%' }}
                    />
                </div>
            </div>
            <style>{`
                .text-slides-stage__inner {
                    flex: 1;
                    min-height: 0;
                    min-width: 0;
                    width: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    overflow: auto;
                }
                .text-slides-stage__inner .rich-text-content {
                    width: 100%;
                    max-height: 100%;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    text-align: center;
                }
                .text-slides-stage__inner .rich-text-content img {
                    max-width: 100%;
                    max-height: calc(100cqh - 12px);
                    width: auto;
                    height: auto;
                    object-fit: contain;
                    margin: 0 auto;
                    display: block;
                }
            `}</style>
            {navRow}
            {!preview ? (
                <ExerciseActionBar>
                    {!readOnly && !reviewMode && (!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                        <button type="button" className={(alreadyDone || completed) ? COMPLETE_DONE_CLASS : COMPLETE_BTN} onClick={() => {
                            setCompleted(true);
                            if (onComplete) onComplete({ status: 'completed', score: 1, total: 1 });
                        }}>{(alreadyDone || completed) ? COMPLETE_DONE_LABEL : COMPLETE_LABEL}</button>
                    )}
                    {(!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                        <AssignmentStatusPill assignment={assignment} completed={completed} isOnlineLesson={isOnlineLesson}  practiceMode={practiceMode} />
                    )}
                </ExerciseActionBar>
            ) : null}
            <TeacherComment exercise={exercise} assignment={assignment} />
        </div>
    );
}
function EssayInteractive({ exercise, assignment, preview, onComplete, reviewMode, onUpdateAssignment, onUpdate, readOnly, isOnlineLesson, updateSlideState, slideState, user, isTeacher, practiceMode }) {
    const alreadyDone = isAlreadyDone(assignment) && !reviewMode;
    const [text, setText] = useState(assignment?.answers?.text || '');
    const [completed, setCompleted] = useState(readOnly || reviewMode || alreadyDone);
    const [localUnblurred, setLocalUnblurred] = useState({});
    const currentUnblurred = isOnlineLesson ? (slideState?.unblurred || {}) : localUnblurred;
    
    // LOCAL AUTHORITY: Use a purely local state for typing to ensure 0ms lag
    const [localText, setLocalText] = useState(text);
    const isFocusedRef = useRef(false);
    const lastTypedRef = useRef(0);

    // Synchronize localText with assignment text on mount/initial load
    useEffect(() => {
        if (assignment?.answers?.text && !localText) {
            setLocalText(assignment.answers.text);
        }
    }, [assignment?.id]);

    const activeText = isOnlineLesson ? (slideState?.answers?.text || text) : text;
    
    // Sync incoming server state to localText ONLY if user is not actively typing
    useEffect(() => {
        if (!isOnlineLesson || !slideState?.answers?.text) return;
        
        const now = Date.now();
        const hasRecentActivity = (now - lastTypedRef.current) < 3000; // 3 second buffer
        
        if (!isFocusedRef.current && !hasRecentActivity) {
            setLocalText(slideState.answers.text);
        }
    }, [slideState?.answers?.text, isOnlineLesson]);

    const { syncStatus, persist, getLocalData } = useExercisePersistence(assignment?.id, { answers: { text: localText }, status: completed ? 'completed' : 'working' }, onUpdate, readOnly || alreadyDone, assignment, isOnlineLesson, updateSlideState, slideState);

    // Sync optimization: trigger immediate on space/newline, debounce otherwise
    const syncTimeoutRef = useRef(null);
    const lastSyncedTextRef = useRef(localText);


    const performSync = useCallback((val, unblurredState) => {
        const nextUnblurred = unblurredState || currentUnblurred;
        if (val === lastSyncedTextRef.current) {
             // Only update unblurred if text hasn't changed? Actually, always use both.
        }
        persist({ answers: { text: val, unblurred: nextUnblurred }, status: completed ? 'completed' : 'working' });
        lastSyncedTextRef.current = val;
    }, [persist, completed, currentUnblurred]);

    const handleBlurClick = (e) => {
        const target = e.target;
        const blurTarget = target.closest('.blurred-text');
        if (blurTarget) {
            e.stopPropagation();
            const blurId = blurTarget.getAttribute('data-blur-id');
            const nextUnblurred = { ...currentUnblurred, [blurId]: !currentUnblurred[blurId] };
            if (isOnlineLesson && isTeacher && updateSlideState) {
                updateSlideState({ ...slideState, unblurred: nextUnblurred });
            } else if (isOnlineLesson && !isTeacher) {
                setLocalUnblurred(nextUnblurred);
            } else if (!isOnlineLesson) {
                setLocalUnblurred(nextUnblurred);
                persist({ answers: { text: localText, unblurred: nextUnblurred }, status: 'working' });
            }
        }
    };

    const handleTextChange = (val) => {
        if (completed || alreadyDone || readOnly) return;
        
        setLocalText(val); // UI updates INSTANTLY
        lastTypedRef.current = Date.now();

        // Background synchronization logic
        if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
        const lastChar = val.slice(-1);
        if (/\s/.test(lastChar)) {
            performSync(val);
        } else {
            syncTimeoutRef.current = setTimeout(() => {
                performSync(val);
            }, 1000);
        }
    };

    // Final sync and cleanup
    useEffect(() => {
        return () => {
            if (syncTimeoutRef.current) {
                clearTimeout(syncTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!isOnlineLesson && (readOnly || reviewMode || alreadyDone) && assignment) {
            if (assignment.answers?.text) {
                setText(assignment.answers.text);
                setLocalText(assignment.answers.text);
            }
            if (assignment.status === 'completed' || assignment.status === 'checked') setCompleted(true);
        }
    }, [readOnly, reviewMode, alreadyDone, assignment, isOnlineLesson]);

    useEffect(() => {
        if (isOnlineLesson) return;
        const local = getLocalData();
        if (local && !alreadyDone && !readOnly) {
            if (local.answers?.text) {
                setText(local.answers.text);
                setLocalText(local.answers.text);
            }
        }
    }, [getLocalData, alreadyDone, readOnly, isOnlineLesson]);

    const wordCount = localText.trim() ? localText.trim().split(/\s+/).length : 0;
    
    // Get max points from assessment config
    const maxPoints = exercise.assessment?.pointsPerGap || null;
    
    return (
        <div className="exercise-card-shell" style={CARD_STYLE} onClick={handleBlurClick}>
            <ExerciseLead exercise={exercise} />
            {exercise.body && <RichTextSlideRenderer content={renderMarkdownToHtml(exercise.body, currentUnblurred)} style={{ lineHeight: 1.8, color: '#1e293b', marginBottom: '1rem' }} />}
            <div style={{ flexShrink: 0 }}>
            <textarea
                value={localText} 
                onChange={e => handleTextChange(e.target.value)}
                onFocus={() => { isFocusedRef.current = true; }}
                onBlur={() => { 
                    isFocusedRef.current = false; 
                    performSync(localText); // Final sync on blur
                }}
                readOnly={completed || alreadyDone}
                placeholder="Write your answer here..."
                style={{
                    width: '100%', minHeight: '160px', border: '2px solid #c7d2fe', borderRadius: '14px',
                    padding: '0.85rem', fontSize: '0.95rem', fontFamily: 'inherit', lineHeight: 1.7,
                    resize: 'vertical', outline: 'none', boxSizing: 'border-box', flexShrink: 0,
                    background: (completed || alreadyDone) ? '#f8fafc' : 'white',
                    transition: 'border-color 0.2s',
                }}
            />
            </div>
            <ExerciseActionBar style={{ justifyContent: 'flex-start' }}>
                <span style={{ fontSize: '0.8rem', color: '#94a3b8', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em' }}>📝 {wordCount} word{wordCount !== 1 ? 's' : ''}</span>
                {!preview && !readOnly && !reviewMode && (!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && <button type="button" className={(alreadyDone || completed) ? COMPLETE_DONE_CLASS : COMPLETE_BTN} disabled={!!(alreadyDone || completed)} onClick={(alreadyDone || completed) ? undefined : (() => {
                    setCompleted(true);
                    if (onComplete) onComplete({ answers: { text: localText }, status: 'completed', score: 1, total: 1 });
                })}>{(alreadyDone || completed) ? COMPLETE_DONE_LABEL : COMPLETE_LABEL}</button>}
                {reviewMode && onUpdateAssignment && (
                     <button type="button" className="exercise-game-btn exercise-game-btn--secondary" onClick={() => {
                         const maxLabel = maxPoints ? ` (max ${maxPoints})` : '';
                         const score = window.prompt(`Enter score for this essay${maxLabel}:`, assignment?.score || "0");
                         if (score !== null) {
                             let val = parseFloat(score);
                             if (maxPoints && val > maxPoints) {
                                 alert(`Maximum allowed score is ${maxPoints}. Setting to ${maxPoints}.`);
                                 val = maxPoints;
                             }
                             onUpdateAssignment(assignment.id, { score: val, status: 'checked' });
                         }
                     }}>Update Score ✏</button>
                )}
                {!preview && (!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                    reviewMode ? (
                        <ExerciseScoreBadge>
                            Score: {assignment?.score || 0}{maxPoints ? ` / ${maxPoints}` : ''}
                        </ExerciseScoreBadge>
                    ) : (
                        <AssignmentStatusPill assignment={assignment} completed={completed} isOnlineLesson={isOnlineLesson}  practiceMode={practiceMode} />
                    )
                )}
            </ExerciseActionBar>
            <TeacherComment exercise={exercise} assignment={assignment} />
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   WORDLIST
═══════════════════════════════════════════════════════════════════════════ */
function WordlistCompactTable({ words, onSpeak }) {
    let stripe = 0;
    return (
        <table className="wordlist-compact">
            <tbody>
                {(words || []).map((w, i) => {
                    if (isWordlistHeadingRow(w)) {
                        const title = w.text || w.english || '';
                        return (
                            <tr key={`h-${i}-${title}`} className="wordlist-compact-heading">
                                <td colSpan={3}>{title}</td>
                            </tr>
                        );
                    }
                    const ipaDisplay = formatIpaInBrackets(w.ipa);
                    const rowClass = stripe % 2 === 0 ? 'is-even' : 'is-odd';
                    stripe += 1;
                    return (
                        <tr key={w.id || i} className={rowClass}>
                            <td className="wordlist-compact-en">
                                {renderBold(`**${w.english}**`)}
                                {ipaDisplay ? (
                                    <span className="wordlist-compact-ipa">{` ${ipaDisplay}`}</span>
                                ) : null}
                            </td>
                            <td>
                                <button type="button" className="wordlist-compact-audio" onClick={() => onSpeak(w.english)} title="Play pronunciation">🔊</button>
                            </td>
                            <td className="wordlist-compact-ru">{w.russian}</td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}

function WordlistInteractive({ exercise, assignment, preview, onComplete, reviewMode, onUpdate, readOnly, isOnlineLesson, updateSlideState, slideState, user, isTeacher, practiceMode }) {
    const alreadyDone = isAlreadyDone(assignment) && !reviewMode;
    const [completed, setCompleted] = useState(readOnly || reviewMode || alreadyDone);
    const [localUnblurred, setLocalUnblurred] = useState({});
    const currentUnblurred = isOnlineLesson ? (slideState?.unblurred || {}) : localUnblurred;

    useEffect(() => {
        if (!isOnlineLesson && !readOnly && onUpdate) {
            onUpdate({ status: completed ? 'completed' : 'working' });
        }
    }, [completed, onUpdate, readOnly, isOnlineLesson]);
    const words = useMemo(() => {
        if (exercise.wordlist_data && exercise.wordlist_data.length > 0) return exercise.wordlist_data;
        // Parse from body if data is empty (decoupled mode)
        return parseWordlistExerciseBody(exercise.body);
    }, [exercise.wordlist_data, exercise.body]);

    const speak = (val) => speakEnglish(val);

    return (
        <div className="exercise-card-shell" style={CARD_STYLE}>
            <ExerciseLead exercise={exercise} />
            <div className="wordlist-compact-wrap">
                {words.length === 0 && <div style={{ color: '#94a3b8', textAlign: 'center', padding: '2rem', fontStyle: 'italic' }}>No words yet. Edit exercise to import or add words.</div>}
                {words.length > 0 && <WordlistCompactTable words={words} onSpeak={speak} />}
            </div>
            {!preview && (
                    <ExerciseActionBar>
                        {!readOnly && !reviewMode && (!isOnlineLesson || isTeacher) && <button type="button" className={COMPLETE_BTN} onClick={() => {
                            setCompleted(true);
                            if (onComplete) onComplete({ status: 'completed' });
                        }}>{(alreadyDone || completed) ? COMPLETE_DONE_LABEL : COMPLETE_LABEL}</button>}
                        {(!isOnlineLesson || isTeacher) && (
                            <AssignmentStatusPill assignment={assignment} completed={completed} isOnlineLesson={isOnlineLesson}  practiceMode={practiceMode} />
                        )}
                    </ExerciseActionBar>
            )}
            <TeacherComment exercise={exercise} assignment={assignment} />
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   AUTO WORDLIST (synced with Global Words Database)
═══════════════════════════════════════════════════════════════════════════ */
function AutoWordlistInteractive({ exercise, assignment, preview, onComplete, reviewMode, onUpdate, readOnly, isOnlineLesson, updateSlideState, slideState, user, isTeacher, practiceMode }) {
    const alreadyDone = isAlreadyDone(assignment) && !reviewMode;
    const [completed, setCompleted] = useState(readOnly || reviewMode || alreadyDone);
    const [localUnblurred, setLocalUnblurred] = useState({});
    const currentUnblurred = isOnlineLesson ? (slideState?.unblurred || {}) : localUnblurred;

    useEffect(() => {
        if (!isOnlineLesson && !readOnly && onUpdate) {
            onUpdate({ status: completed ? 'completed' : 'working' });
        }
    }, [completed, onUpdate, readOnly, isOnlineLesson]);

    // Read words from exercise.wordlist_data (saved by teacher in edit mode)
    const words = useMemo(() => {
        if (exercise.wordlist_data && exercise.wordlist_data.length > 0) return exercise.wordlist_data;
        return [];
    }, [exercise.wordlist_data]);

    const groupBySubtheme = exercise?.assessment?.auto_wordlist_group_by_subtheme === true;
    const displayRows = useMemo(
        () => (groupBySubtheme ? groupWordlistRowsBySubtheme(words) : words),
        [words, groupBySubtheme],
    );

    const speak = (val) => speakEnglish(val);

    return (
        <div className="exercise-card-shell" style={CARD_STYLE}>
            <ExerciseLead exercise={exercise} />
            <div className="wordlist-compact-wrap">
                {words.length === 0 && <div style={{ color: '#94a3b8', textAlign: 'center', padding: '2rem', fontStyle: 'italic' }}>
                    {exercise.course ? 'No words match the current filters. Please ask your teacher to refresh this exercise.' : 'No course selected. Please ask your teacher to configure this exercise.'}
                </div>}
                {words.length > 0 && <WordlistCompactTable words={displayRows} onSpeak={speak} />}
            </div>
            {!preview && (
                    <ExerciseActionBar>
                        {!readOnly && !reviewMode && (!isOnlineLesson || isTeacher) && <button type="button" className={(alreadyDone || completed) ? COMPLETE_DONE_CLASS : COMPLETE_BTN} disabled={!!(alreadyDone || completed)} onClick={(alreadyDone || completed) ? undefined : (() => {
                            setCompleted(true);
                            if (onComplete) onComplete({ status: 'completed' });
                        })}>{(alreadyDone || completed) ? COMPLETE_DONE_LABEL : COMPLETE_LABEL}</button>}
                        {(!isOnlineLesson || isTeacher) && (
                            <AssignmentStatusPill assignment={assignment} completed={completed} isOnlineLesson={isOnlineLesson}  practiceMode={practiceMode} />
                        )}
                    </ExerciseActionBar>
            )}
            <TeacherComment exercise={exercise} assignment={assignment} />
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   WORDLIST AS SLIDES (flashcards)
═══════════════════════════════════════════════════════════════════════════ */
function WordlistSlidesInteractive({ exercise, assignment, preview, onComplete, reviewMode, onUpdate, readOnly, isOnlineLesson, updateSlideState, slideState, user, isTeacher, showSolution, practiceMode }) {
    const alreadyDone = isAlreadyDone(assignment) && !reviewMode;
    const [completed, setCompleted] = useState(readOnly || reviewMode || alreadyDone);
    const [localUnblurred, setLocalUnblurred] = useState({});
    const currentUnblurred = isOnlineLesson ? (slideState?.unblurred || {}) : localUnblurred;

    useEffect(() => {
        if (!isOnlineLesson && !readOnly && onUpdate) {
            onUpdate({ status: completed ? 'completed' : 'working' });
        }
    }, [completed, onUpdate, readOnly, isOnlineLesson]);
    const words = useMemo(() => {
        if (exercise.wordlist_data && exercise.wordlist_data.length > 0) return exercise.wordlist_data;
        // Parse from body Front // Back format (decoupled mode)
        return (exercise.body || '').split(/\r?\n/).filter(l => l.trim()).map(line => {
            const [front, back] = line.split('//').map(s => s.trim());
            const parsed = parseWordlistExerciseLine(front || '');
            return { english: parsed?.english || '', russian: (back || '').trim(), ipa: parsed?.ipa || '' };
        });
    }, [exercise.wordlist_data, exercise.body]);

    const [current, setCurrent] = useState(0);
    const [flipped, setFlipped] = useState(false);
    const activeCurrent = isOnlineLesson ? (slideState?.current || 0) : current;
    const activeFlipped = showSolution ? true : (isOnlineLesson ? (slideState?.flipped || false) : flipped);

    const setGlobalIndex = (idx) => {
        if (isOnlineLesson && updateSlideState) {
            if (updateSlideState) updateSlideState({ current: idx, flipped: false });
        } else {
            setCurrent(idx);
            setFlipped(false);
        }
    };

    const toggleFlipped = () => {
        if (isOnlineLesson && updateSlideState) {
            if (updateSlideState) updateSlideState({ flipped: !activeFlipped });
        } else {
            setFlipped(f => !f);
        }
    };
    const speak = (text) => speakEnglish(text);
    const w = words[activeCurrent];
    const isLast = activeCurrent === words.length - 1;

    const handleBlurClick = (e) => {
        const target = e.target;
        const blurTarget = target.closest('.blurred-text');
        if (blurTarget) {
            e.stopPropagation();
            const blurId = blurTarget.getAttribute('data-blur-id');
            const nextUnblurred = { ...currentUnblurred, [blurId]: !currentUnblurred[blurId] };
            if (isOnlineLesson && isTeacher && updateSlideState) {
                updateSlideState({ ...slideState, unblurred: nextUnblurred });
            } else if (isOnlineLesson && !isTeacher) {
                setLocalUnblurred(nextUnblurred);
            } else if (!isOnlineLesson) {
                setLocalUnblurred(nextUnblurred);
                persist({ unblurred: nextUnblurred, status: 'working' });
            }
        }
    };

    return (
        <div className="exercise-card-shell" style={CARD_STYLE} onClick={handleBlurClick}>
            <ExerciseLead exercise={exercise} />
            {words.length === 0 ? (
                <div style={{ color: '#94a3b8', textAlign: 'center', padding: '2rem', fontStyle: 'italic' }}>No words selected yet.</div>
            ) : (
                <>
                    <div style={{ textAlign: 'center', marginBottom: '0.5rem', fontSize: '0.8rem', color: '#94a3b8' }}>
                        Card {activeCurrent + 1} of {words.length}
                    </div>
                    <div onClick={toggleFlipped} style={{
                        minHeight: '160px', background: activeFlipped ? 'linear-gradient(135deg,#4361ee,#7c3aed)' : 'linear-gradient(135deg,#f0f4ff,#e0e7ff)',
                        borderRadius: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', padding: '1.5rem', gap: '0.5rem', transition: 'background 0.3s',
                    }}>
                        {!activeFlipped ? (
                            <div style={{ fontWeight: 800, fontSize: '1.5rem', color: '#1e293b' }}>{renderBold(w?.russian || '—', currentUnblurred)}</div>
                        ) : (
                            <>
                                <div style={{ fontWeight: 800, fontSize: '1.4rem', color: 'white' }}>{renderBold(w?.english, currentUnblurred)}</div>
                                {w?.ipa && <div style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.7)' }}>{formatIpaInBrackets(w.ipa)}</div>}
                                <button onClick={(alreadyDone || completed) ? undefined : ((e) => { e.stopPropagation(); speak(w?.english || ''); })}
                                    style={{ border: 'none', background: 'rgba(255,255,255,0.2)', color: 'white', borderRadius: '8px', padding: '5px 12px', cursor: 'pointer', marginTop: '0.5rem' }}>🔊</button>
                            </>
                        )}
                    </div>
                        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', marginTop: '1rem' }}>
                            <button onClick={() => setGlobalIndex(Math.max(0, activeCurrent - 1))}
                                disabled={(alreadyDone || completed) || (activeCurrent === 0)} style={{ ...navBtn, opacity: activeCurrent === 0 ? 0.4 : 1 }}>◀ Prev</button>
                            <button onClick={() => { setGlobalIndex(Math.min(words.length - 1, activeCurrent + 1)); }}
                                disabled={isLast} style={{ ...navBtn, opacity: isLast ? 0.4 : 1 }}>Next ▶</button>
                        </div>
                    {/* Complete / Start Again buttons at last slide */}
                    <ExerciseActionBar>
                        {!preview && isLast && !reviewMode && (!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                            <>
                                <button type="button" className="exercise-game-btn exercise-game-btn--secondary" onClick={() => setGlobalIndex(0)}>🔄 Start Again</button>
                                <button type="button" className={(alreadyDone || completed) ? COMPLETE_DONE_CLASS : COMPLETE_BTN} onClick={() => {
                                    setCompleted(true);
                                    if (onComplete) onComplete({ status: 'completed', score: 1, total: 1 });
                                }}>{(alreadyDone || completed) ? COMPLETE_DONE_LABEL : COMPLETE_LABEL}</button>
                            </>
                        )}
                        {!preview && (!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                            <AssignmentStatusPill assignment={assignment} completed={completed} isOnlineLesson={isOnlineLesson}  practiceMode={practiceMode} />
                        )}
                    </ExerciseActionBar>
                </>
            )}
            <TeacherComment exercise={exercise} assignment={assignment} />
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SLIDES (Front // Back)
═══════════════════════════════════════════════════════════════════════════ */
function SlidesInteractive({ exercise, assignment, preview, onComplete, reviewMode, onUpdate, readOnly, isOnlineLesson, updateSlideState, slideState, user, isTeacher, showSolution, practiceMode }) {
    const alreadyDone = isAlreadyDone(assignment) && !reviewMode;
    const [completed, setCompleted] = useState(readOnly || reviewMode || alreadyDone);
    const [localUnblurred, setLocalUnblurred] = useState({});
    const currentUnblurred = isOnlineLesson ? (slideState?.unblurred || {}) : localUnblurred;

    useEffect(() => {
        if (!isOnlineLesson && !readOnly && onUpdate) {
            onUpdate({ status: completed ? 'completed' : 'working' });
        }
    }, [completed, onUpdate, readOnly, isOnlineLesson]);
    const slides = useMemo(() => {
        return (exercise.body || '').split('\n').filter(l => l.trim()).map(line => {
            const [front, back] = line.split('//').map(s => s.trim());
            return { front: front || '', back: back || '' };
        });
    }, [exercise.body]);
    const [current, setCurrent] = useState(0);
    const [flipped, setFlipped] = useState(false);
    const activeCurrent = isOnlineLesson ? (slideState?.current || 0) : current;
    const activeFlipped = showSolution ? true : (isOnlineLesson ? (slideState?.flipped || false) : flipped);

    const setGlobalIndex = (idx) => {
        if (isOnlineLesson && updateSlideState) {
            if (updateSlideState) updateSlideState({ current: idx, flipped: false });
        } else {
            setCurrent(idx);
            setFlipped(false);
        }
    };

    const toggleFlipped = () => {
        if (isOnlineLesson && updateSlideState) {
            updateSlideState({ flipped: !activeFlipped });
        } else {
            setFlipped(f => !f);
        }
    };
    const handleBlurClick = (e) => {
        const target = e.target;
        const blurTarget = target.closest('.blurred-text');
        if (blurTarget) {
            e.stopPropagation();
            const blurId = blurTarget.getAttribute('data-blur-id');
            const nextUnblurred = { ...currentUnblurred, [blurId]: !currentUnblurred[blurId] };
            if (isOnlineLesson && isTeacher && updateSlideState) {
                updateSlideState({ ...slideState, unblurred: nextUnblurred });
            } else if (isOnlineLesson && !isTeacher) {
                setLocalUnblurred(nextUnblurred);
            } else if (!isOnlineLesson) {
                setLocalUnblurred(nextUnblurred);
                persist({ unblurred: nextUnblurred, status: 'working' });
            }
        }
    };

    const s = slides[activeCurrent];
    const isLast = activeCurrent === slides.length - 1;
    
    return (
        <div className="exercise-card-shell" style={CARD_STYLE} onClick={handleBlurClick}>
            <ExerciseLead exercise={exercise} />
            {slides.length === 0 ? (
                <div style={{ color: '#94a3b8', textAlign: 'center', padding: '2rem', fontStyle: 'italic' }}>No slides yet. Edit content using Front // Back format.</div>
            ) : (
                <>
                    {(!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                        <div style={{ textAlign: 'center', marginBottom: '0.5rem', fontSize: '0.8rem', color: '#94a3b8' }}>
                            Slide {activeCurrent + 1} of {slides.length}
                        </div>
                    )}
                    <div onClick={toggleFlipped} style={{
                        minHeight: '160px', background: activeFlipped ? 'linear-gradient(135deg,#4361ee,#7c3aed)' : 'linear-gradient(135deg,#f0f4ff,#e0e7ff)',
                        borderRadius: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', padding: '1.5rem', textAlign: 'center', transition: 'background 0.3s',
                        fontSize: '1.2rem', fontWeight: 600, lineHeight: 1.6,
                        color: activeFlipped ? 'white' : '#1e293b',
                    }}>
                        {renderBold(activeFlipped ? (s?.back || '') : (s?.front || ''), currentUnblurred)}
                    </div>
                    {(!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', marginTop: '1rem' }}>
                            <button onClick={(alreadyDone || completed) ? undefined : (() => setGlobalIndex(Math.max(0, activeCurrent - 1)))} disabled={(alreadyDone || completed) || (activeCurrent === 0)} style={{ ...navBtn, opacity: activeCurrent === 0 ? 0.4 : 1 }}>◀ Prev</button>
                            <button onClick={() => setGlobalIndex(Math.min(slides.length - 1, activeCurrent + 1))} disabled={isLast} style={{ ...navBtn, opacity: isLast ? 0.4 : 1 }}>Next ▶</button>
                        </div>
                    )}
                    {/* Complete / Start Again buttons at last slide */}
                    <ExerciseActionBar>
                        {!preview && isLast && !reviewMode && (!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                            <>
                                <button type="button" className="exercise-game-btn exercise-game-btn--secondary" onClick={() => setGlobalIndex(0)}>🔄 Start Again</button>
                                <button type="button" className={(alreadyDone || completed) ? COMPLETE_DONE_CLASS : COMPLETE_BTN} onClick={() => {
                                    setCompleted(true);
                                    if (onComplete) onComplete({ status: 'completed', score: 1, total: 1 });
                                }}>{(alreadyDone || completed) ? COMPLETE_DONE_LABEL : COMPLETE_LABEL}</button>
                            </>
                        )}
                        {!preview && (!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                            <AssignmentStatusPill assignment={assignment} completed={completed} isOnlineLesson={isOnlineLesson}  practiceMode={practiceMode} />
                        )}
                    </ExerciseActionBar>
                </>
            )}
            <TeacherComment exercise={exercise} assignment={assignment} />
        </div>
    );
}

const navBtn = { border: '1.5px solid #e2e8f0', borderRadius: '10px', padding: '6px 18px', cursor: 'pointer', background: 'white', fontWeight: 600, fontSize: '0.88rem', color: '#1e293b' };

/* ═══════════════════════════════════════════════════════════════════════════
   JUMBLED LETTERS — carousel of scrambled word/phrase items
═══════════════════════════════════════════════════════════════════════════ */
function JumbledLettersInteractive({ exercise, assignment, preview, onComplete, reviewMode, onUpdateAssignment, onUpdate, readOnly, isOnlineLesson, updateSlideState, slideState = {}, user, isTeacher, showSolution, practiceMode }) {
    const items = useMemo(() => {
        const lines = (exercise.body || '').split('\n').filter(l => l.trim());
        return lines.map((line, idx) => {
            const parts = [];
            for (const m of line.matchAll(/\((.*?)\)|([^()\n\r]+)/g)) {
                if (m[1] !== undefined) {
                    parts.push({ text: m[1], fixed: true });
                } else if (m[2] !== undefined) {
                    parts.push({ text: m[2].trim(), fixed: false });
                }
            }
            const letters = [];
            parts.forEach(p => {
                if (p.fixed) { letters.push({ char: p.text, fixed: true, id: `fixed_${idx}_${letters.length}` }); }
                else {
                    const chars = p.text.split('');
                    const shuffled = seededShuffle(chars, `jumble_${exercise?.id || 'ex'}_${idx}_${letters.length}`);
                    shuffled.forEach((c, ci) => letters.push({ char: c, fixed: false, id: `char_${idx}_${letters.length}_${ci}` }));
                }
            });
            const answer = parts.map(p => p.text).join('');
            return { original: line, letters, answer, parts };
        });
    }, [exercise.body, exercise?.id]);

    const alreadyDone = isAlreadyDone(assignment) && !reviewMode;
    const current = isOnlineLesson ? (slideState.current || 0) : 0;
    const [localCurrent, setLocalCurrent] = useState(0);
    const setSlideIndex = (idx) => {
        if (isOnlineLesson && updateSlideState) { if (updateSlideState) updateSlideState({ current: idx }); } else { setLocalCurrent(idx); }
    };
    const activeCurrent = isOnlineLesson ? current : localCurrent;
    const [arrangements, setArrangements] = useState(assignment?.answers || {});
    const [results, setResults] = useState(assignment?.is_correct || {});
    const [completed, setCompleted] = useState(readOnly || reviewMode || alreadyDone);
    const [peekMap, setPeekMap] = useState({});
    const [localUnblurred, setLocalUnblurred] = useState({});
    const currentUnblurred = isOnlineLesson ? (slideState?.unblurred || {}) : localUnblurred;

    const activeArrangements = isOnlineLesson ? (slideState?.answers || arrangements) : arrangements;
    const activeResults = isOnlineLesson ? (slideState?.is_correct || results) : results;

    const { syncStatus, persist, getLocalData } = useExercisePersistence(assignment?.id, { answers: activeArrangements, is_correct: activeResults, status: completed ? 'completed' : 'working' }, onUpdate, readOnly || alreadyDone, assignment, isOnlineLesson, updateSlideState, slideState);

    const setGlobalArrangement = (idx, val) => {
        const next = { ...activeArrangements, [idx]: val };
        if (!isOnlineLesson) setArrangements(next);
        persist({ answers: next, is_correct: activeResults, status: 'working' });
    };

    const setGlobalResults = (idx, val) => {
        const next = { ...activeResults, [idx]: val };
        if (!isOnlineLesson) setResults(next);
        persist({ answers: activeArrangements, is_correct: next, status: 'working' });
    };

    const handleToggleCorrect = (idx) => {
        if (!reviewMode || !onUpdateAssignment) return;
        const newMap = { ...activeResults, [idx]: !activeResults[idx] };
        setGlobalResults(idx, !activeResults[idx]);
        const newScore = Object.values(newMap).filter(v => v === true).length;
        onUpdateAssignment(assignment.id, { is_correct: newMap, score: newScore });
    };

    useEffect(() => {
        if (isOnlineLesson) return;
        const local = getLocalData();
        if (local && !alreadyDone && !readOnly) {
            if (local.answers) setArrangements(local.answers);
            if (local.is_correct) setResults(local.is_correct);
        }
    }, [getLocalData, alreadyDone, readOnly, isOnlineLesson]);

    const item = items[activeCurrent];
    const rawArr = activeArrangements[activeCurrent];
    const jumblePeekEnabled = canUseAnswerPeek({
        assignment,
        reviewMode,
        isWrong: activeResults[activeCurrent] === false,
        hasKey: !!item?.answer,
    });
    const jumblePeeking = !!peekMap[activeCurrent];
    const solutionLetterArr = item?.answer
        ? item.answer.split('').map((c, i) => ({ char: c, fixed: false, id: `sol_${activeCurrent}_${i}` }))
        : [];
    const currentArr = showSolution
        ? solutionLetterArr
        : (jumblePeeking && jumblePeekEnabled
            ? solutionLetterArr
            : ((Array.isArray(rawArr) ? rawArr : null) || item?.letters || []));
    const isLast = activeCurrent === items.length - 1;

    const canInteract = !showSolution && !jumblePeeking && !readOnly && !alreadyDone && activeResults[activeCurrent] === undefined;

    const checkCurrent = () => {
        const userAnswer = currentArr.map(l => l.char).join('');
        const isCorrect = normalizeAnswer(userAnswer) === normalizeAnswer(item.answer);
        setGlobalResults(activeCurrent, isCorrect);
        persist({ answers: activeArrangements, is_correct: { ...activeResults, [activeCurrent]: isCorrect }, status: 'working' });
        return isCorrect;
    };

    const [dragIdx, setDragIdx] = useState(null);
    const [overIdx, setOverIdx] = useState(null);
    const [dragPos, setDragPos] = useState(null);
    const dragRef = useRef({ active: false, from: null, startX: 0, startY: 0, moved: false, pointerId: null });
    const currentArrRef = useRef(currentArr);
    currentArrRef.current = currentArr;
    const overIdxRef = useRef(overIdx);
    overIdxRef.current = overIdx;
    const persistCtxRef = useRef({ activeCurrent, activeArrangements, activeResults, canInteract });
    persistCtxRef.current = { activeCurrent, activeArrangements, activeResults, canInteract };

    useEffect(() => {
        setDragIdx(null);
        setOverIdx(null);
        setDragPos(null);
        dragRef.current = { active: false, from: null, startX: 0, startY: 0, moved: false, pointerId: null };
    }, [activeCurrent]);

    const applyArrangement = useCallback((next) => {
        const { activeCurrent: idx, activeArrangements: ans, activeResults: res } = persistCtxRef.current;
        setGlobalArrangement(idx, next);
        persist({ answers: { ...ans, [idx]: next }, is_correct: res, status: 'working' });
    }, [persist, setGlobalArrangement]);

    const resolveTileIdx = (clientX, clientY) => {
        const el = document.elementFromPoint(clientX, clientY);
        const tile = el?.closest?.('[data-jl-idx]');
        if (!tile) return null;
        const n = Number(tile.getAttribute('data-jl-idx'));
        return Number.isFinite(n) ? n : null;
    };

    const finishDrag = useCallback((clientX, clientY) => {
        const d = dragRef.current;
        if (!d.active || d.from == null) return;
        const from = d.from;
        const moved = d.moved;
        const arr = currentArrRef.current;
        const hit = resolveTileIdx(clientX, clientY);
        const toCand = (hit != null && !arr[hit]?.fixed) ? hit : overIdxRef.current;
        dragRef.current = { active: false, from: null, startX: 0, startY: 0, moved: false, pointerId: null };
        setDragIdx(null);
        setOverIdx(null);
        setDragPos(null);
        if (!moved || toCand == null || toCand === from || arr[toCand]?.fixed) return;
        const movableIdxs = [];
        arr.forEach((l, i) => { if (!l.fixed) movableIdxs.push(i); });
        const fromPos = movableIdxs.indexOf(from);
        const toPos = movableIdxs.indexOf(toCand);
        if (fromPos < 0 || toPos < 0) return;
        const next = [...arr];
        const chars = movableIdxs.map((i) => next[i]);
        const [m] = chars.splice(fromPos, 1);
        chars.splice(toPos, 0, m);
        movableIdxs.forEach((slot, i) => { next[slot] = chars[i]; });
        applyArrangement(next);
    }, [applyArrangement]);

    useEffect(() => {
        if (dragIdx == null) return undefined;
        const onMove = (e) => {
            const d = dragRef.current;
            if (!d.active) return;
            e.preventDefault();
            const dx = e.clientX - d.startX;
            const dy = e.clientY - d.startY;
            if (!d.moved && (dx * dx + dy * dy) > 36) {
                d.moved = true;
            }
            setDragPos({ x: e.clientX, y: e.clientY });
            const hit = resolveTileIdx(e.clientX, e.clientY);
            const arr = currentArrRef.current;
            if (hit != null && !arr[hit]?.fixed) setOverIdx(hit);
        };
        const onUp = (e) => finishDrag(e.clientX, e.clientY);
        const onCancel = () => {
            dragRef.current = { active: false, from: null, startX: 0, startY: 0, moved: false, pointerId: null };
            setDragIdx(null);
            setOverIdx(null);
            setDragPos(null);
        };
        window.addEventListener('pointermove', onMove, { passive: false });
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onCancel);
        return () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('pointercancel', onCancel);
        };
    }, [dragIdx, finishDrag]);

    const onLetterPointerDown = (e, idx) => {
        if (!canInteract || currentArr[idx]?.fixed) return;
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        try {
            e.currentTarget.setPointerCapture?.(e.pointerId);
        } catch (_) { /* ignore */ }
        dragRef.current = {
            active: true,
            from: idx,
            startX: e.clientX,
            startY: e.clientY,
            moved: false,
            pointerId: e.pointerId,
        };
        setDragIdx(idx);
        setOverIdx(idx);
        setDragPos({ x: e.clientX, y: e.clientY });
    };

    /** Keep each jumbled word on one line; fixed phrase text can wrap around it. */
    const letterRuns = useMemo(() => {
        const runs = [];
        currentArr.forEach((l, i) => {
            const kind = l.fixed ? 'fixed' : 'jumble';
            const last = runs[runs.length - 1];
            if (last && last.kind === kind) last.items.push({ letter: l, idx: i });
            else runs.push({ kind, items: [{ letter: l, idx: i }] });
        });
        return runs;
    }, [currentArr]);

    const boardTone = (showSolution || jumblePeeking || activeResults[activeCurrent] !== undefined)
        ? ((showSolution || jumblePeeking || activeResults[activeCurrent]) ? 'ok' : 'bad')
        : 'idle';

    const renderLetterTile = (l, i) => {
        const isDragging = dragIdx === i;
        const isOver = overIdx === i && dragIdx != null && dragIdx !== i;
        const locked = showSolution || l.fixed || !canInteract;
        return (
            <button
                type="button"
                key={l.id || `jl_${i}`}
                data-jl-idx={i}
                tabIndex={locked ? -1 : 0}
                aria-label={l.fixed ? undefined : `Letter ${l.char === ' ' ? 'space' : l.char}`}
                disabled={(alreadyDone || completed) || (locked)}
                onPointerDown={(e) => onLetterPointerDown(e, i)}
                onContextMenu={(e) => e.preventDefault()}
                onDragStart={(e) => e.preventDefault()}
                className={[
                    'jl-letter',
                    l.fixed ? 'jl-letter--fixed' : 'jl-letter--movable',
                    isDragging ? 'jl-letter--dragging' : '',
                    isOver ? 'jl-letter--over' : '',
                    showSolution ? 'jl-letter--solution' : '',
                ].filter(Boolean).join(' ')}
            >
                <span className="jl-letter__char" aria-hidden="true">
                    {l.char === ' ' ? '\u00a0' : l.char}
                </span>
            </button>
        );
    };

    const dragGhostChar = dragIdx != null ? currentArr[dragIdx]?.char : null;
    const showDragGhost = dragIdx != null && dragPos && dragGhostChar != null && !currentArr[dragIdx]?.fixed;

    const totalScore = Object.values(activeResults).filter(v => v === true).length;
    return (
        <div className="exercise-card-shell" style={CARD_STYLE}>
            <ExerciseLead exercise={exercise} />
            {items.length === 0 ? (
                <div style={{ color: '#94a3b8', textAlign: 'center', padding: '2rem', fontStyle: 'italic' }}>No items yet.</div>
            ) : (
                <>
                    {activeResults[activeCurrent] !== undefined && (
                        <div style={{ textAlign: 'center', marginTop: '0.5rem', fontSize: '0.9rem', fontWeight: 700, color: activeResults[activeCurrent] ? '#059669' : '#dc2626', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                             <span onClick={(alreadyDone || completed) ? undefined : (() => reviewMode && handleToggleCorrect(activeCurrent))} style={{ cursor: reviewMode ? 'pointer' : 'default' }}>{activeResults[activeCurrent] ? '✅ Correct!' : '❌ Incorrect'}</span>
                             {jumblePeekEnabled && (
                                 <ExerciseAnswerPeekButton
                                     peeking={jumblePeeking}
                                     onToggle={() => setPeekMap((m) => ({ ...m, [activeCurrent]: !m[activeCurrent] }))}
                                 />
                             )}
                        </div>
                    )}
                    <div style={{ textAlign: 'center', marginBottom: '0.5rem', fontSize: '0.8rem', color: '#94a3b8', position: 'relative' }}>Word {activeCurrent + 1} of {items.length}</div>
                    {canInteract && (
                        <div className="jl-hint">Drag letters to rearrange</div>
                    )}
                    <div
                        className={`jl-board jl-board--${boardTone}${dragIdx != null ? ' jl-board--dragging' : ''}`}
                        onContextMenu={(e) => e.preventDefault()}
                    >
                        {letterRuns.map((run, ri) => (
                            run.kind === 'fixed' ? (
                                <span key={`fixed_${ri}`} className="jl-fixed-run">
                                    {run.items.map(({ letter, idx }) => renderLetterTile(letter, idx))}
                                </span>
                            ) : (
                                <span key={`jumble_${ri}`} className="jl-word">
                                    {run.items.map(({ letter, idx }) => renderLetterTile(letter, idx))}
                                </span>
                            )
                        ))}
                    </div>
                    {showDragGhost && (
                        <div
                            className="jl-letter jl-letter--movable jl-letter--ghost"
                            style={{
                                position: 'fixed',
                                left: dragPos.x,
                                top: dragPos.y,
                                transform: 'translate(-50%, -50%)',
                                pointerEvents: 'none',
                                zIndex: 10000,
                            }}
                            aria-hidden="true"
                        >
                            <span className="jl-letter__char">
                                {dragGhostChar === ' ' ? '\u00a0' : dragGhostChar}
                            </span>
                        </div>
                    )}
                    {!alreadyDone && !reviewMode && !readOnly && (!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                        <div style={{ textAlign: 'center', marginTop: '1.25rem' }}>
                            <button onClick={checkCurrent} style={{ border: 'none', background: '#4361ee18', color: '#4361ee', borderRadius: '10px', padding: '8px 20px', cursor: 'pointer', fontWeight: 800 }}>👆 Check</button>
                        </div>
                    )}
                    {(!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', marginTop: '1.5rem' }}>
                            <button onClick={() => setSlideIndex(Math.max(0, activeCurrent - 1))} disabled={activeCurrent === 0} style={{ ...navBtn, opacity: activeCurrent === 0 ? 0.4 : 1 }}>◀ Prev</button>
                            <button onClick={() => setSlideIndex(Math.min(items.length - 1, activeCurrent + 1))} disabled={isLast} style={{ ...navBtn, opacity: isLast ? 0.4 : 1 }}>Next ▶</button>
                        </div>
                    )}
                    {!preview && isLast && !reviewMode && (!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                        <ExerciseActionBar style={{ marginTop: '1.5rem', borderTop: 'none', paddingTop: 0 }}>
                            <button type="button" className={(alreadyDone || completed) ? COMPLETE_DONE_CLASS : COMPLETE_BTN} onClick={() => {
                                setCompleted(true);
                                if (onComplete) onComplete({ answers: activeArrangements, score: totalScore, total: items.length, is_correct: activeResults, status: 'completed' });
                            }}>{(alreadyDone || completed) ? COMPLETE_DONE_LABEL : COMPLETE_LABEL}</button>
                        </ExerciseActionBar>
                    )}
                    {completed && (!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                        <ExerciseActionBar style={{ marginTop: '1rem', borderTop: 'none', paddingTop: 0 }}>
                            <ExerciseScoreBox submitted>
                                {`Score: ${totalScore} / ${items.length}`}
                            </ExerciseScoreBox>
                        </ExerciseActionBar>
                    )}
                </>
            )}
            <TeacherComment exercise={exercise} assignment={assignment} />
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   DICTATION — carousel of listen-and-type items
═══════════════════════════════════════════════════════════════════════════ */
function DictationInteractive({ exercise, assignment, preview, onComplete, reviewMode, onUpdateAssignment, onUpdate, readOnly, isOnlineLesson, updateSlideState, slideState = {}, user, isTeacher, showSolution, practiceMode }) {
    const items = useMemo(() => {
        return (exercise.body || '').split('\n').filter(l => l.trim()).map((line, idx) => {
            const m = line.match(/\[(.*?)\]/);
            const answer = m && m[1] ? m[1].trim() : line.trim();
            return { original: line, answer: answer };
        });
    }, [exercise.body]);

    const alreadyDone = isAlreadyDone(assignment) && !reviewMode;
    const current = isOnlineLesson ? (slideState.current || 0) : 0;
    const [localCurrent, setLocalCurrent] = useState(0);
    const setSlideIndex = (idx) => {
        if (isOnlineLesson && updateSlideState) { if (updateSlideState) updateSlideState({ current: idx }); } else { setLocalCurrent(idx); }
    };
    const activeCurrent = isOnlineLesson ? current : localCurrent;
    const [answers, setAnswers] = useState(assignment?.answers || {});
    const [results, setResults] = useState(assignment?.is_correct || {});
    const [completed, setCompleted] = useState(readOnly || reviewMode || alreadyDone);
    const [peekMap, setPeekMap] = useState({});
    const [localUnblurred, setLocalUnblurred] = useState({});
    const currentUnblurred = isOnlineLesson ? (slideState?.unblurred || {}) : localUnblurred;

    const activeAnswers = isOnlineLesson ? (slideState?.answers || answers) : answers;
    const activeResults = isOnlineLesson ? (slideState?.is_correct || results) : results;

    const { syncStatus, persist, getLocalData } = useExercisePersistence(assignment?.id, { answers: activeAnswers, is_correct: activeResults, status: completed ? 'completed' : 'working' }, onUpdate, readOnly || alreadyDone, assignment, isOnlineLesson, updateSlideState, slideState);

    const setGlobalAnswer = (idx, val) => {
        const next = { ...activeAnswers, [idx]: val };
        if (!isOnlineLesson) setAnswers(next);
        persist({ answers: next, is_correct: activeResults, status: 'working' });
    };

    const setGlobalResults = (idx, val) => {
        const next = { ...activeResults, [idx]: val };
        if (!isOnlineLesson) setResults(next);
        persist({ answers: activeAnswers, is_correct: next, status: 'working' });
    };

    const handleToggleCorrect = (idx) => {
        if (!reviewMode || !onUpdateAssignment) return;
        const newMap = { ...activeResults, [idx]: !activeResults[idx] };
        setGlobalResults(idx, !activeResults[idx]);
        const newScore = Object.values(newMap).filter(v => v === true).length;
        onUpdateAssignment(assignment.id, { is_correct: newMap, score: newScore });
    };

    useEffect(() => {
        if (isOnlineLesson) return;
        const local = getLocalData();
        if (local && !alreadyDone && !readOnly) {
            if (local.answers) setAnswers(local.answers);
            if (local.is_correct) setResults(local.is_correct);
        }
    }, [getLocalData, alreadyDone, readOnly, isOnlineLesson]);

    const item = items[activeCurrent];
    const isLast = activeCurrent === items.length - 1;
    const dictPeekEnabled = canUseAnswerPeek({
        assignment,
        reviewMode,
        isWrong: activeResults[activeCurrent] === false,
        hasKey: !!item?.answer,
    });
    const dictPeeking = !!peekMap[activeCurrent];
    const dictDisplayText = showSolution
        ? item.answer
        : (dictPeeking && dictPeekEnabled ? item.answer : (activeAnswers[activeCurrent] || ''));

    const checkCurrent = () => {
        const userVal = activeAnswers[activeCurrent] || '';
        const isCorrect = normalizeAnswer(userVal) === normalizeAnswer(item.answer);
        setGlobalResults(activeCurrent, isCorrect);
        persist({ answers: activeAnswers, is_correct: { ...activeResults, [activeCurrent]: isCorrect }, status: 'working' });
        return isCorrect;
    };

    const totalScore = Object.values(activeResults).filter(v => v === true).length;
    return (
        <div className="exercise-card-shell" style={CARD_STYLE}>
            <ExerciseLead exercise={exercise} />
            {items.length === 0 ? (
                <div style={{ color: '#94a3b8', textAlign: 'center', padding: '2rem', fontStyle: 'italic' }}>No items yet.</div>
            ) : (
                <>
                    {activeResults[activeCurrent] !== undefined && (
                        <div style={{ textAlign: 'center', marginTop: '0.5rem', fontSize: '0.9rem', fontWeight: 700, color: activeResults[activeCurrent] ? '#059669' : '#dc2626', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                             <span onClick={() => reviewMode && handleToggleCorrect(activeCurrent)} style={{ cursor: reviewMode ? 'pointer' : 'default' }}>{activeResults[activeCurrent] ? '✅ Correct!' : '❌ Incorrect'}</span>
                             {dictPeekEnabled && (
                                 <ExerciseAnswerPeekButton
                                     peeking={dictPeeking}
                                     onToggle={() => setPeekMap((m) => ({ ...m, [activeCurrent]: !m[activeCurrent] }))}
                                 />
                             )}
                        </div>
                    )}
                    <div style={{ textAlign: 'center', marginBottom: '0.5rem', fontSize: '0.8rem', color: '#94a3b8', position: 'relative' }}>Sentence {activeCurrent + 1} of {items.length}</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', alignItems: 'center' }}>
                        <button onClick={(alreadyDone || completed) ? undefined : (() => window.speakText(item.answer))} style={{ width: '60px', height: '60px', borderRadius: '50%', border: 'none', background: '#4361ee', color: 'white', fontSize: '1.5rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(67,97,238,0.3)' }}>🔊</button>
                        <textarea value={dictDisplayText} onChange={(e) => setGlobalAnswer(activeCurrent, e.target.value)} disabled={(alreadyDone || completed) || (showSolution || dictPeeking || alreadyDone || readOnly || activeResults[activeCurrent] !== undefined)} placeholder="Type what you hear..." style={{ width: '100%', minHeight: '100px', padding: '1rem', borderRadius: '12px', border: `2px solid ${showSolution || dictPeeking ? '#6ee7b7' : '#e2e8f0'}`, fontSize: '1rem', outline: 'none', resize: 'none', background: showSolution || dictPeeking ? '#ecfdf5' : 'white' }} />
                    </div>
                    {!alreadyDone && !reviewMode && !readOnly && (!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                        <div style={{ textAlign: 'center', marginTop: '1.25rem' }}>
                            <button onClick={checkCurrent} style={{ border: 'none', background: '#4361ee18', color: '#4361ee', borderRadius: '10px', padding: '8px 20px', cursor: 'pointer', fontWeight: 800 }}>👆 Check</button>
                        </div>
                    )}
                    {(!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', marginTop: '1.5rem' }}>
                            <button onClick={() => setSlideIndex(Math.max(0, activeCurrent - 1))} disabled={activeCurrent === 0} style={{ ...navBtn, opacity: activeCurrent === 0 ? 0.4 : 1 }}>◀ Prev</button>
                            <button onClick={() => setSlideIndex(Math.min(items.length - 1, activeCurrent + 1))} disabled={isLast} style={{ ...navBtn, opacity: isLast ? 0.4 : 1 }}>Next ▶</button>
                        </div>
                    )}
                    {!preview && isLast && !reviewMode && (!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                        <ExerciseActionBar style={{ marginTop: '1.5rem', borderTop: 'none', paddingTop: 0 }}>
                            <button type="button" className={(alreadyDone || completed) ? COMPLETE_DONE_CLASS : COMPLETE_BTN} onClick={() => {
                                setCompleted(true);
                                if (onComplete) onComplete({ answers: activeAnswers, score: totalScore, total: items.length, is_correct: activeResults, status: 'completed' });
                            }}>{(alreadyDone || completed) ? COMPLETE_DONE_LABEL : COMPLETE_LABEL}</button>
                        </ExerciseActionBar>
                    )}
                    {(!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                        <ExerciseActionBar style={{ marginTop: '1rem', borderTop: 'none', paddingTop: 0 }}>
                            {completed ? (
                                <ExerciseScoreBox submitted>
                                    Score: {totalScore} / {items.length}
                                </ExerciseScoreBox>
                            ) : (
                                <AssignmentStatusPill assignment={assignment} completed={completed} isOnlineLesson={isOnlineLesson}  practiceMode={practiceMode} />
                            )}
                            {completed && (
                                <AssignmentStatusPill assignment={assignment} completed={completed} isOnlineLesson={isOnlineLesson}  practiceMode={practiceMode} />
                            )}
                        </ExerciseActionBar>
                    )}
                </>
            )}
            <TeacherComment exercise={exercise} assignment={assignment} />
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MULTIPLE CHOICE (SHORT) — inline options in text
   Format: I [go/went/will go*] home tomorrow.
═══════════════════════════════════════════════════════════════════════════ */
const parseMultipleChoiceShort = parseMultipleChoiceShortBody;

function MultipleChoiceShortInteractive({ exercise, assignment, preview, onComplete, reviewMode, onUpdateAssignment, onUpdate, readOnly, isOnlineLesson, updateSlideState, slideState = {}, user, isTeacher, showSolution, practiceMode }) {
    const parts = useMemo(() => parseMultipleChoiceShort(exercise.body || ''), [exercise.body]);
    const choices = parts.filter(p => p.type === 'choice');
    const alreadyDone = isAlreadyDone(assignment) && !reviewMode;
    const [answers, setAnswers] = useState(assignment?.answers || {});
    const [locked, setLocked] = useState(
        readOnly || reviewMode ? Object.fromEntries(choices.map(c => [c.idx, true])) :
        alreadyDone ? Object.fromEntries(choices.map(c => [c.idx, true])) : {}
    );
    const [isCorrectMap, setIsCorrectMap] = useState(assignment?.is_correct || {});
    const [gapAttempts, setGapAttempts] = useState(assignment?.gap_attempts || {});
    const [completed, setCompleted] = useState(readOnly || reviewMode || alreadyDone);
    const [peekMap, setPeekMap] = useState({});
    const [localUnblurred, setLocalUnblurred] = useState({});
    const currentUnblurred = isOnlineLesson ? (slideState?.unblurred || {}) : localUnblurred;
    const threeAttempts = isThreeAttemptsEnabled(exercise);

    const activeAnswers = isOnlineLesson ? (slideState?.answers || answers) : answers;
    const activeLocked = isOnlineLesson ? (slideState?.locked || locked) : locked;
    const activeResults = isOnlineLesson ? (slideState?.is_correct || isCorrectMap) : isCorrectMap;
    const activeGapAttempts = isOnlineLesson ? (slideState?.gap_attempts || gapAttempts) : gapAttempts;

    const { syncStatus, persist, getLocalData } = useExercisePersistence(assignment?.id, { answers: activeAnswers, locked: activeLocked, is_correct: activeResults, status: completed ? 'completed' : 'working' }, onUpdate, readOnly || alreadyDone, assignment, isOnlineLesson, updateSlideState, slideState);

    const setGlobalAnswer = (idx, val) => {
        const next = { ...activeAnswers, [idx]: val };
        if (!isOnlineLesson) setAnswers(next);
        persist({ answers: next, locked: activeLocked, is_correct: activeResults, status: 'working' });
    };

    const setGlobalLocked = (idx, val) => {
        const next = { ...activeLocked, [idx]: val };
        if (!isOnlineLesson) setLocked(next);
        persist({ answers: activeAnswers, locked: next, is_correct: activeResults, status: 'working' });
    };

    const setGlobalResults = (idx, val) => {
        const next = { ...activeResults, [idx]: val };
        if (!isOnlineLesson) setIsCorrectMap(next);
        persist({ answers: activeAnswers, locked: activeLocked, is_correct: next, status: 'working' });
    };


    const handleBlurClick = (e) => {
        const target = e.target;
        const blurTarget = target.closest('.blurred-text');
        if (blurTarget) {
            e.stopPropagation();
            const blurId = blurTarget.getAttribute('data-blur-id');
            const nextUnblurred = { ...currentUnblurred, [blurId]: !currentUnblurred[blurId] };
            if (isOnlineLesson && isTeacher && updateSlideState) {
                updateSlideState({ ...slideState, unblurred: nextUnblurred });
            } else if (isOnlineLesson && !isTeacher) {
                setLocalUnblurred(nextUnblurred);
            } else if (!isOnlineLesson) {
                setLocalUnblurred(nextUnblurred);
                persist({ answers: { ...activeAnswers, unblurred: nextUnblurred }, status: 'working' });
            }
        }
    };

    const computeScore = useCallback((resMap) => Object.values(resMap).filter(v => v === true).length, []);

    useEffect(() => {
        if (!isOnlineLesson && (readOnly || reviewMode || alreadyDone) && assignment) {
            if (assignment.answers) setAnswers(assignment.answers);
            if (assignment.locked) setLocked(assignment.locked);
            if (assignment.is_correct) setIsCorrectMap(assignment.is_correct);
            if (assignment.status === 'completed' || assignment.status === 'checked') setCompleted(true);
        }
    }, [readOnly, reviewMode, alreadyDone, assignment, isOnlineLesson]);



    useEffect(() => {
        if (isOnlineLesson) return;
        const local = getLocalData();
        if (local && !alreadyDone && !readOnly) {
            if (local.answers) setAnswers(local.answers);
            if (local.locked) setLocked(local.locked);
            if (local.is_correct) setIsCorrectMap(local.is_correct);
        }
    }, [getLocalData, alreadyDone, readOnly, isOnlineLesson]);

    const shuffledOptions = useMemo(() => {
        const doShuffle = exercise?.assessment?.shuffleOptions !== false;
        return Object.fromEntries(choices.map(c => [
            c.idx,
            doShuffle
                ? seededShuffle(c.options, `mc_short_${exercise?.id || 'ex'}_${c.idx}`)
                : [...(c.options || [])],
        ]));
    }, [choices, exercise?.id, exercise?.assessment?.shuffleOptions]);

    const checkChoice = (choiceIdx) => {
        const choice = choices.find(c => c.idx === choiceIdx);
        const selected = activeAnswers[choiceIdx];
        const correct = choice?.options.find(o => o.correct)?.label || '';
        const ok = normalizeAnswer(selected) === normalizeAnswer(correct);

        let nextLocked = { ...activeLocked, [choiceIdx]: true };
        let nextResults = { ...activeResults, [choiceIdx]: ok };
        let nextAnswers = { ...activeAnswers };
        let nextGapAttempts = activeGapAttempts;

        if (threeAttempts) {
            const applied = applyGapAttempt(getGapAttemptResults(activeGapAttempts, choiceIdx), ok);
            nextGapAttempts = setGapAttemptResults(activeGapAttempts, choiceIdx, applied.results);
            nextLocked = { ...activeLocked, [choiceIdx]: applied.lock };
            if (applied.isCorrect === null) {
                const { [choiceIdx]: _a, [String(choiceIdx)]: _b, ...rest } = nextResults;
                nextResults = rest;
            } else {
                nextResults = { ...activeResults, [choiceIdx]: applied.isCorrect, [String(choiceIdx)]: applied.isCorrect };
            }
            if (applied.clearAnswer) {
                nextAnswers = { ...activeAnswers, [choiceIdx]: '' };
            }
            if (!isOnlineLesson) setGapAttempts(nextGapAttempts);
        }

        if (!isOnlineLesson) {
            setLocked(nextLocked);
            setIsCorrectMap(nextResults);
            setAnswers(nextAnswers);
        }
        persist({
            answers: nextAnswers,
            locked: nextLocked,
            is_correct: nextResults,
            status: 'working',
            ...(threeAttempts ? { gap_attempts: nextGapAttempts } : {}),
        });
    };

    const totalScore = computeScore(activeResults);

    return (
        <div className="exercise-card-shell" style={CARD_STYLE}>
            <ExerciseLead exercise={exercise} />
            <div className="exercise-gap-content" style={{ lineHeight: 2.2, fontSize: '1rem', color: '#1e293b' }} onClick={handleBlurClick}>
                {(() => {
                    let n = 0;
                    return renderFillGapsStructuredParts(parts, {
                        idPrefix: 'mcs',
                        nextGapIdx: () => n++,
                        renderText: (p, key) => (
                            <InlineAudioRenderer key={key} inline html={renderMarkdownToHtml(p.text, currentUnblurred, key, true)} />
                        ),
                        renderChoice: (p, gi, key) => {
                            const idx = p.idx ?? gi;
                            const selected = activeAnswers[idx];
                            const isLocked = activeLocked[idx];
                            const correctLabel = (p.options || []).find((o) => o.correct)?.label || '';
                            const mcPeeking = !!peekMap[idx];
                            return (
                                <ChoiceInput
                                    key={key}
                                    choiceIdx={idx}
                                    options={shuffledOptions[idx] || p.options || []}
                                    selected={selected}
                                    correctLabel={correctLabel}
                                    locked={isLocked}
                                    isCorrect={activeResults[idx]}
                                    showSolution={showSolution}
                                    reviewMode={reviewMode}
                                    readOnly={readOnly}
                                    alreadyDone={alreadyDone}
                                    assignment={assignment}
                                    onSelect={setGlobalAnswer}
                                    onCheck={checkChoice}
                                    onToggleCorrect={(i) => setGlobalResults(i, !activeResults[i])}
                                    peeking={mcPeeking}
                                    onPeekToggle={() => setPeekMap((m) => ({ ...m, [idx]: !m[idx] }))}
                                    showAttempts={threeAttempts}
                                    attemptResults={getGapAttemptResults(activeGapAttempts, idx)}
                                />
                            );
                        },
                    });
                })()}
            </div>
            {!preview && (
                 <ExerciseActionBar>
                    {!readOnly && !reviewMode && (!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                        <button type="button" className={(alreadyDone || completed) ? COMPLETE_DONE_CLASS : COMPLETE_BTN} disabled={!!(alreadyDone || completed)} onClick={(alreadyDone || completed) ? undefined : (() => {
                            setCompleted(true);
                            if (onComplete) onComplete({ answers: activeAnswers, score: totalScore, total: choices.length, is_correct: activeResults, status: 'completed' });
                        })}>{(alreadyDone || completed) ? COMPLETE_DONE_LABEL : COMPLETE_LABEL}</button>
                    )}
                    {(!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                        <AssignmentStatusPill assignment={assignment} completed={completed} isOnlineLesson={isOnlineLesson}
                            completedLabel={`Score: ${totalScore} / ${choices.length}`}  practiceMode={practiceMode} />
                    )}
                </ExerciseActionBar>
            )}
            <TeacherComment exercise={exercise} assignment={assignment} />
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MULTIPLE CHOICE (LONG) — question + option blocks
   Format: Question?\n[Option A]\n[Option B *]\n[Option C]
═══════════════════════════════════════════════════════════════════════════ */
function parseMultipleChoiceLong(body) {
    const lines = (body || '').split('\n').filter(l => l.trim());
    const questions = [];
    let current = null;
    lines.forEach(line => {
        const optMatch = line.match(/^\[(.+)\]$/);
        if (optMatch) {
            if (!current) current = { question: '', options: [] };
            current.options.push(parseMcOptionText(optMatch[1]));
        } else {
            if (current && current.options.length > 0) {
                questions.push(current);
                current = null;
            }
            if (!current) current = { question: line.trim(), options: [] };
            else current.question += '\n' + line.trim();
        }
    });
    if (current && current.options.length > 0) questions.push(current);
    return questions;
}

function MultipleChoiceLongInteractive({ exercise, assignment, preview, onComplete, reviewMode, onUpdateAssignment, onUpdate, readOnly, isOnlineLesson, updateSlideState, slideState = {}, user, isTeacher, showSolution, practiceMode }) {
    const questions = useMemo(() => parseMultipleChoiceLong(exercise.body || ''), [exercise.body]);
    const alreadyDone = isAlreadyDone(assignment) && !reviewMode;
    const current = isOnlineLesson ? (slideState.current || 0) : 0;
    const [localCurrent, setLocalCurrent] = useState(0);
    const setSlideIndex = (idx) => {
        if (isOnlineLesson && updateSlideState) {
            if (updateSlideState) updateSlideState({ current: idx });
        } else {
            setLocalCurrent(idx);
        }
    };
    const activeCurrent = isOnlineLesson ? current : localCurrent;
    const [answers, setAnswers] = useState(assignment?.answers || {});
    const [results, setResults] = useState(assignment?.is_correct || {});
    const [completed, setCompleted] = useState(readOnly || reviewMode || alreadyDone);
    const [peekMap, setPeekMap] = useState({});
    const [localUnblurred, setLocalUnblurred] = useState({});
    const currentUnblurred = isOnlineLesson ? (slideState?.unblurred || {}) : localUnblurred;

    const activeAnswers = isOnlineLesson ? (slideState?.answers || answers) : answers;
    const activeResults = isOnlineLesson ? (slideState?.is_correct || results) : results;

    const setGlobalAnswer = (idx, val) => {
        const next = { ...activeAnswers, [idx]: val };
        if (!isOnlineLesson) setAnswers(next);
        persist({ answers: next, is_correct: activeResults, status: 'working' });
    };

    const setGlobalResults = (idx, val) => {
        const next = { ...activeResults, [idx]: val };
        if (!isOnlineLesson) setResults(next);
        persist({ answers: activeAnswers, is_correct: next, status: 'working' });
    };

    const handleToggleCorrect = (idx) => {
        if (!reviewMode || !onUpdateAssignment) return;
        const newMap = { ...activeResults, [idx]: !activeResults[idx] };
        setGlobalResults(idx, !activeResults[idx]);
        const newScore = Object.values(newMap).filter(v => v === true).length;
        onUpdateAssignment(assignment.id, { is_correct: newMap, score: newScore });
    };

    const shuffledOpts = useMemo(() => {
        const doShuffle = exercise?.assessment?.shuffleOptions !== false;
        return Object.fromEntries(questions.map((q, i) => [
            i,
            doShuffle
                ? seededShuffle(q.options, `mc_long_${exercise?.id || 'ex'}_${i}`)
                : [...(q.options || [])],
        ]));
    }, [questions, exercise?.id, exercise?.assessment?.shuffleOptions]);

    const { syncStatus, persist, getLocalData } = useExercisePersistence(assignment?.id, { answers: activeAnswers, is_correct: activeResults, status: completed ? 'completed' : 'working' }, onUpdate, readOnly || alreadyDone, assignment, isOnlineLesson, updateSlideState, slideState);

    useEffect(() => {
        if (isOnlineLesson) return;
        const local = getLocalData();
        if (local && !alreadyDone && !readOnly) {
            if (local.answers) setAnswers(local.answers);
            if (local.is_correct) setResults(local.is_correct);
        }
    }, [getLocalData, alreadyDone, readOnly, isOnlineLesson]);

    const q = (questions && questions.length > 0) ? questions[activeCurrent] : null;
    if (!q && questions.length > 0) return <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>Loading question {activeCurrent + 1}...</div>;
    if (questions.length === 0) return <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>No questions found in this exercise.</div>;

    const isLast = activeCurrent === questions.length - 1;
    const mcLongCorrectLabel = q.options.find(o => o.correct)?.label || '';
    const mcLongPeekEnabled = canUseAnswerPeek({
        assignment,
        reviewMode,
        isWrong: activeResults[activeCurrent] === false,
        hasKey: !!mcLongCorrectLabel,
    });
    const mcLongPeeking = !!peekMap[activeCurrent];

    const checkCurrent = () => {
        const selected = activeAnswers[activeCurrent];
        const correct = q.options.find(o => o.correct)?.label || '';
        const isCorrect = normalizeAnswer(selected) === normalizeAnswer(correct);
        setGlobalResults(activeCurrent, isCorrect);
        persist({ answers: activeAnswers, is_correct: { ...activeResults, [activeCurrent]: isCorrect }, status: 'working' });
        return isCorrect;
    };

    const totalScore = Object.values(activeResults).filter(v => v === true).length;
    
    const handleBlurClick = (e) => {
        const target = e.target;
        const blurTarget = target.closest('.blurred-text');
        if (blurTarget) {
            e.stopPropagation();
            const blurId = blurTarget.getAttribute('data-blur-id');
            const nextUnblurred = { ...currentUnblurred, [blurId]: !currentUnblurred[blurId] };
            if (isOnlineLesson && isTeacher && updateSlideState) {
                updateSlideState({ ...slideState, unblurred: nextUnblurred });
            } else if (isOnlineLesson && !isTeacher) {
                setLocalUnblurred(nextUnblurred);
            } else if (!isOnlineLesson) {
                setLocalUnblurred(nextUnblurred);
                persist({ answers: { ...activeAnswers, unblurred: nextUnblurred }, is_correct: activeResults, status: 'working' });
            }
        }
    };
return (
        <div className="exercise-card-shell" style={CARD_STYLE}>
            <ExerciseLead exercise={exercise} />
            {questions.length === 0 ? (
                <div style={{ color: '#94a3b8', textAlign: 'center', padding: '2rem', fontStyle: 'italic' }}>No questions yet.</div>
            ) : (
                <>
                    {(showSolution || activeResults[activeCurrent] !== undefined) && (
                        <div style={{ textAlign: 'center', marginTop: '0.5rem', fontSize: '0.9rem', fontWeight: 700, color: (showSolution || activeResults[activeCurrent]) ? '#059669' : '#dc2626', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                             <span onClick={() => !showSolution && reviewMode && handleToggleCorrect(activeCurrent)} style={{ cursor: (!showSolution && reviewMode) ? 'pointer' : 'default' }}>
                                {(showSolution || activeResults[activeCurrent]) ? '✅ Correct!' : '❌ Incorrect'}
                             </span>
                             {mcLongPeekEnabled && (
                                 <ExerciseAnswerPeekButton
                                     peeking={mcLongPeeking}
                                     onToggle={() => setPeekMap((m) => ({ ...m, [activeCurrent]: !m[activeCurrent] }))}
                                 />
                             )}
                        </div>
                    )}
                    <div style={{ textAlign: 'center', marginBottom: '0.5rem', fontSize: '0.8rem', color: '#94a3b8', position: 'relative' }}>
                        Question {activeCurrent + 1} of {questions.length}
                    </div>
                    <RichTextSlideRenderer content={renderMarkdownToHtml(q?.question || '', currentUnblurred, `mcl_${activeCurrent}`)} style={{ fontWeight: 800, fontSize: '1.15rem', color: '#1e293b', marginBottom: '1rem', textAlign: 'center', lineHeight: 1.5 }} />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                        {(shuffledOpts[activeCurrent] || q.options).map((o, oi) => {
                            const isSelected = activeAnswers[activeCurrent] === o.label;
                            const hasResult = showSolution || mcLongPeeking || activeResults[activeCurrent] !== undefined;
                            const isCorrectOpt = o.correct;
                            let optBg = 'white'; let optBorder = '#e2e8f0'; let optColor = '#4361ee';
                            if ((showSolution || mcLongPeeking) && isCorrectOpt) {
                                optBg = '#d1fae5'; optBorder = '#6ee7b7'; optColor = '#065f46';
                            } else if (hasResult && isSelected) {
                                optBg = activeResults[activeCurrent] ? '#d1fae5' : '#fee2e2';
                                optBorder = activeResults[activeCurrent] ? '#6ee7b7' : '#fca5a5';
                                optColor = activeResults[activeCurrent] ? '#065f46' : '#991b1b';
                            } else if (hasResult && isCorrectOpt) {
                                optBg = '#d1fae5'; optBorder = '#6ee7b7'; optColor = '#065f46';
                            } else if (isSelected) {
                                optBg = '#eff6ff'; optBorder = '#4361ee';
                            }
                            return (
                                <button key={oi}
                                    onClick={(alreadyDone || completed) ? undefined : (() => {
                                        if (!showSolution && !hasResult && !alreadyDone && !readOnly) {
                                            setGlobalAnswer(activeCurrent, o.label);
                                        }
                                    })}
                                    style={{
                                        width: '100%', padding: '0.8rem 1.25rem', borderRadius: '14px', border: '2.5px solid ' + optBorder,
                                        background: optBg, color: optColor, fontSize: '0.95rem', fontWeight: 700, textAlign: 'left',
                                        cursor: showSolution || hasResult || alreadyDone || readOnly ? 'default' : 'pointer', transition: 'all 0.2s'
                                    }}
                                >
                                    {renderBold(o.label, currentUnblurred, `mcl_opt_${activeCurrent}_${oi}`)}
                                </button>
                            );
                        })}
                    </div>
                    {activeResults[activeCurrent] === undefined && activeAnswers[activeCurrent] && !alreadyDone && !readOnly && (
                        <div style={{ textAlign: 'center', marginTop: '1.25rem' }}>
                            <button onClick={checkCurrent} style={{ border: 'none', background: '#4361ee20', color: '#4361ee', borderRadius: '10px', padding: '8px 20px', cursor: 'pointer', fontWeight: 800 }}>👆🏻 Check Answer</button>
                        </div>
                    )}
                    {(!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', marginTop: '1.5rem' }}>
                            <button onClick={() => setSlideIndex(Math.max(0, activeCurrent - 1))} disabled={(alreadyDone || completed) || (activeCurrent === 0)} style={{ ...navBtn, opacity: activeCurrent === 0 ? 0.4 : 1 }}>◀ Prev</button>
                            <button onClick={() => setSlideIndex(Math.min(questions.length - 1, activeCurrent + 1))} disabled={isLast} style={{ ...navBtn, opacity: isLast ? 0.4 : 1 }}>Next ▶</button>
                        </div>
                    )}
                    {!preview && isLast && !reviewMode && (!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                        <ExerciseActionBar style={{ marginTop: '1.5rem', borderTop: 'none', paddingTop: 0 }}>
                            <button type="button" className={(alreadyDone || completed) ? COMPLETE_DONE_CLASS : COMPLETE_BTN} onClick={() => {
                                setCompleted(true);
                                if (onComplete) onComplete({ answers: activeAnswers, score: totalScore, total: questions.length, is_correct: activeResults, status: 'completed' });
                            }}>{(alreadyDone || completed) ? COMPLETE_DONE_LABEL : COMPLETE_LABEL}</button>
                        </ExerciseActionBar>
                    )}
                    {(!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                        <ExerciseActionBar style={{ marginTop: '1rem', borderTop: 'none', paddingTop: 0 }}>
                            {completed ? (
                                <ExerciseScoreBox submitted>
                                    Score: {totalScore} / {questions.length}
                                </ExerciseScoreBox>
                            ) : (
                                <AssignmentStatusPill assignment={assignment} completed={completed} isOnlineLesson={isOnlineLesson}  practiceMode={practiceMode} />
                            )}
                            {completed && (
                                <AssignmentStatusPill assignment={assignment} completed={completed} isOnlineLesson={isOnlineLesson}  practiceMode={practiceMode} />
                            )}
                        </ExerciseActionBar>
                    )}
                </>
            )}
            <TeacherComment exercise={exercise} assignment={assignment} />
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SENTENCE SLIDES — example sentences with gap (two modes)
   Format: I [live/;живу] in a house. // Я [живу] в доме.
═══════════════════════════════════════════════════════════════════════════ */
function parseSentenceSlide(line, mode = 'hints') {
    const [engPart, rusPart] = line.split('//').map(s => (s || '').trim());
    // Parse English: find all [word/hint] then strip others
    const engBracketMatch = engPart.match(/\[([^\]]+)\]/);
    let engWord = '', ruHint = '', engFront = engPart, engBack = engPart;
    
    if (engBracketMatch) {
        const inner = engBracketMatch[1];
        let slashIdx = inner.indexOf('/;');
        let sepLen = 2;
        if (slashIdx < 0) {
            slashIdx = inner.indexOf('/');
            sepLen = 1;
        }

        if (slashIdx >= 0) {
            engWord = inner.slice(0, slashIdx).trim();
            ruHint = inner.slice(slashIdx + sepLen).trim();
        } else {
            engWord = inner.trim();
        }

        // Front sentence formatting based on practiceMode
        let replacement = '___';
        if (mode === 'hints' && ruHint) {
            replacement = `___HINT:${ruHint}___`;
        } else if (mode === 'first_letter' && engWord) {
            replacement = engWord[0] + '___';
        } else {
            replacement = '___';
        }

        engFront = engPart.replace(engBracketMatch[0], replacement);

        // Back sentence: replace the TARGET bracket with Bold word, and strip other brackets if any
        engBack = engPart.replace(engBracketMatch[0], `**${engWord}**`);
    }

    // Clean up any remaining brackets in back sentence
    engBack = engBack.replace(/\[([^\]]+)\]/g, '$1');

    // Parse Russian: strip all brackets and bold the one that was bracketed
    let rusBack = (rusPart || '').replace(/\[([^\]]+)\]/g, '**$1**');
    
    return { engFront, engBack, ruHint, rusBack, engWord, rusPart };
}

function SentenceSlidesInteractive({ exercise, assignment, preview, onComplete, reviewMode, onUpdate, readOnly, isOnlineLesson, updateSlideState, slideState = {}, user, isTeacher, showSolution, practiceMode }) {
    const alreadyDone = isAlreadyDone(assignment) && !reviewMode;
    
    // AUTHORITATIVE STATE: drive from slideState if in online lesson
    const current = isOnlineLesson ? (slideState.current || 0) : 0;
    const flipped = isOnlineLesson ? (slideState.flipped || false) : false;
    const [localCurrent, setLocalCurrent] = useState(0);
    const [localFlipped, setLocalFlipped] = useState(false);

    // Helper to update state (authority depends on being in online lesson)
    const setSlideIndex = (idx) => {
        if (isOnlineLesson && updateSlideState) {
            if (updateSlideState) updateSlideState({ current: idx, flipped: false });
        } else {
            setLocalCurrent(idx);
            setLocalFlipped(false);
        }
    };

    const toggleFlip = () => {
        if (isOnlineLesson && updateSlideState) {
            if (updateSlideState) updateSlideState({ flipped: !flipped });
        } else {
            setLocalFlipped(!localFlipped);
        }
    };

    const activeCurrent = isOnlineLesson ? current : localCurrent;
    const activeFlipped = showSolution ? true : (isOnlineLesson ? flipped : localFlipped);

    const [completed, setCompleted] = useState(readOnly || reviewMode || alreadyDone);
    const [localUnblurred, setLocalUnblurred] = useState({});
    const currentUnblurred = isOnlineLesson ? (slideState?.unblurred || {}) : localUnblurred;

    const handleBlurClick = (e) => {
        const bt = e.target.closest('.blurred-text');
        if (!bt) return;
        e.stopPropagation();
        const blurId = bt.getAttribute('data-blur-id');
        const next = { ...currentUnblurred, [blurId]: !currentUnblurred[blurId] };
        if (isOnlineLesson && isTeacher && updateSlideState) {
            updateSlideState({ ...slideState, unblurred: next });
        } else {
            setLocalUnblurred(next);
        }
    };

    const { slides, mode, config } = useMemo(() => {
        let text = exercise.body || '';
        let config = { mode: 'hints', randomize: false, direction: 'en_first' };
        try {
            if (text.trim().startsWith('{')) {
                const parsed = JSON.parse(text);
                text = parsed.text || '';
                config = { ...config, ...(parsed.config || {}) };
            }
        } catch(e) {}
        
        let slideData = text.split('\n').filter(l => l.trim()).map(line => parseSentenceSlide(line, config.mode));
        
        if (config.randomize) {
            slideData = seededShuffle(slideData, `dictation_${exercise?.id || 'ex'}`);
        }

        return { slides: slideData, mode: config.mode, config };
    }, [exercise.body, exercise?.id]);

    const { syncStatus, persist, getLocalData } = useExercisePersistence(
        assignment?.id,
        { current: activeCurrent, status: completed ? 'completed' : 'working' },
        onUpdate,
        readOnly || alreadyDone,
        assignment,
        isOnlineLesson,
        updateSlideState,
        slideState
    );

    useEffect(() => {
        if (!isOnlineLesson && (readOnly || reviewMode || alreadyDone) && assignment) {
            if (assignment.answers?.current !== undefined) setSlideIndex(assignment.answers.current);
            if (assignment.status === 'completed' || assignment.status === 'checked') setCompleted(true);
        }
    }, [readOnly, reviewMode, alreadyDone, assignment, isOnlineLesson]);

    useEffect(() => {
        if (isOnlineLesson) return;
        const local = getLocalData();
        if (local && !alreadyDone && !readOnly) {
            if (local.current !== undefined) setSlideIndex(local.current);
        }
    }, [getLocalData, alreadyDone, readOnly, isOnlineLesson]);

    const handleNextPrev = (val) => {
        const next = Math.max(0, Math.min(slides.length - 1, val));
        setSlideIndex(next);
        persist({ current: next, status: completed ? 'completed' : 'working' });
    };

    const s = slides[activeCurrent];
    const isLast = activeCurrent === slides.length - 1;

    return (
        <div className="exercise-card-shell" style={CARD_STYLE}>
            <ExerciseLead exercise={exercise} />
            {!readOnly && !preview && !isOnlineLesson && <SyncStatus status={syncStatus} />}
            {slides.length === 0 ? (
                <div style={{ color: '#94a3b8', textAlign: 'center', padding: '2rem', fontStyle: 'italic' }}>No sentences yet.</div>
            ) : (
                <>
                    {(!isOnlineLesson || isTeacher) && (
                        <div style={{ textAlign: 'center', marginBottom: '0.5rem', fontSize: '0.8rem', color: '#94a3b8' }}>
                            Sentence {activeCurrent + 1} of {slides.length}
                        </div>
                    )}
                    <div onClick={(e) => {
                        const bt = e.target.closest('.blurred-text');
                        if (bt) {
                            handleBlurClick(e);
                        } else {
                            toggleFlip();
                        }
                    }} style={{
                        minHeight: '140px', borderRadius: '20px', display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: '1.5rem',
                        gap: '0.75rem', transition: 'background 0.3s', textAlign: 'center',
                        background: activeFlipped ? 'linear-gradient(135deg,#4361ee,#7c3aed)' : 'linear-gradient(135deg,#f0f4ff,#e0e7ff)',
                        boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                    }}>
                        {config.direction === 'ru_first' ? (
                            /* Russian -> English */
                            !activeFlipped ? (
                                <div style={{ fontSize: '1.15rem', fontWeight: 600, color: '#1e293b', lineHeight: 1.7 }}>
                                    {renderBold(s?.rusBack || '', currentUnblurred)}
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: '10px' }}>
                                    <div style={{ fontSize: '1.15rem', fontWeight: 600, color: 'white', lineHeight: 1.7 }}>
                                        {renderBold(s?.engBack || '', currentUnblurred)}
                                    </div>
                                    <button 
                                        onClick={(alreadyDone || completed) ? undefined : ((e) => { 
                                            e.stopPropagation(); 
                                            window.speakText(s?.engBack.replace(/\*\*/g, '').replace(/\*/g, '')); 
                                        })} 
                                        style={{ 
                                            background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', 
                                            borderRadius: '50%', width: '42px', height: '42px', cursor: 'pointer', 
                                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', 
                                            fontSize: '1.2rem', transition: 'all 0.2s', 
                                            color: 'white', backdropFilter: 'blur(4px)'
                                        }}
                                        className="tts-hover-btn"
                                        title="Listen to pronunciation"
                                    >
                                        🔊
                                    </button>
                                </div>
                            )
                        ) : (
                            /* English -> Russian (Default) */
                            !activeFlipped ? (
                                <div style={{ fontSize: '1.15rem', fontWeight: 600, color: '#1e293b', lineHeight: 1.7 }}>
                                    {s?.engFront.split(/(___HINT:.*?___|___)/).map((part, i) => {
                                        if (part === '___') {
                                            return <span key={i} style={{ borderBottom: '3px solid #4361ee', padding: '0 12px', boxSizing: 'border-box' }}>&nbsp;</span>;
                                        }
                                        if (part.startsWith('___HINT:') && part.endsWith('___')) {
                                            const h = part.slice(8, -3);
                                            return <span key={i} style={{ color: '#64748b', fontStyle: 'italic', fontWeight: 400 }}>{h}</span>;
                                        }
                                        return <span key={i}>{renderBold(part)}</span>;
                                    })}
                                </div>
                            ) : (
                                <>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: '10px' }}>
                                        <div style={{ fontSize: '1.15rem', fontWeight: 600, color: 'white', lineHeight: 1.7 }}>
                                            {renderBold(s?.engBack || '', currentUnblurred)}
                                        </div>
                                        <button 
                                            onClick={(e) => { 
                                                e.stopPropagation(); 
                                                window.speakText(s?.engBack.replace(/\*\*/g, '').replace(/\*/g, '')); 
                                            }} 
                                            style={{ 
                                                background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', 
                                                borderRadius: '50%', width: '42px', height: '42px', cursor: 'pointer', 
                                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', 
                                                fontSize: '1.2rem', transition: 'all 0.2s', 
                                                color: 'white', backdropFilter: 'blur(4px)'
                                            }}
                                            className="tts-hover-btn"
                                            title="Listen to pronunciation"
                                        >
                                            🔊
                                        </button>
                                    </div>
                                    {s?.rusBack && (
                                        <div style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.85)', marginTop: '0.25rem', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '0.5rem' }}>
                                            {renderBold(s.rusBack)}
                                        </div>
                                    )}
                                </>
                            )
                        )}
                    </div>
                    {(!isOnlineLesson || isTeacher) && (
                        <div style={{ textAlign: 'center', marginTop: '0.5rem', fontSize: '0.72rem', color: '#94a3b8' }}>
                            Tap to {flipped ? 'see front' : 'reveal answer'}
                        </div>
                    )}
                    {/* Navigation */}
                    {(!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', marginTop: '1rem' }}>
                            <button onClick={() => handleNextPrev(activeCurrent - 1)} disabled={(alreadyDone || completed) || (activeCurrent === 0)} style={{ ...navBtn, opacity: activeCurrent === 0 ? 0.4 : 1 }}>◀ Prev</button>
                            <button onClick={() => handleNextPrev(activeCurrent + 1)} disabled={isLast} style={{ ...navBtn, opacity: isLast ? 0.4 : 1 }}>Next ▶</button>
                        </div>
                    )}
                    {/* Complete / Start Again */}
                    <ExerciseActionBar>
                        {!preview && isLast && !reviewMode && (!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                            <>
                                <button type="button" className="exercise-game-btn exercise-game-btn--secondary" onClick={() => { handleNextPrev(0); }}>🔄 Start Again</button>
                                <button type="button" className={(alreadyDone || completed) ? COMPLETE_DONE_CLASS : COMPLETE_BTN} onClick={() => {
                                    setCompleted(true);
                                    if (onComplete) onComplete({ status: 'completed', score: 1, total: 1 });
                                }}>{(alreadyDone || completed) ? COMPLETE_DONE_LABEL : COMPLETE_LABEL}</button>
                            </>
                        )}
                        {!preview && (!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                            <AssignmentStatusPill assignment={assignment} completed={completed} isOnlineLesson={isOnlineLesson}  practiceMode={practiceMode} />
                        )}
                    </ExerciseActionBar>
                </>
            )}
            <TeacherComment exercise={exercise} assignment={assignment} />
        </div>
    );
}


/* ═══════════════════════════════════════════════════════════════════════════
   RANDOMIZER
   A slot machine-style training mode for words/phrases.
═══════════════════════════════════════════════════════════════════════════ */
function RandomizerInteractive({ exercise, assignment, preview, onComplete, reviewMode, onUpdate, readOnly, isOnlineLesson, updateSlideState, slideState, user, isTeacher, practiceMode }) {
    let config = { slotCount: 1, slotConfigs: [{ source: 'DATABASE', customText: '', words: [] }, { source: 'DATABASE', customText: '', words: [] }, { source: 'DATABASE', customText: '', words: [] }] };
    try {
        if (exercise.body) {
            const parsed = JSON.parse(exercise.body);
            if (parsed.slotConfigs) config = parsed;
        }
    } catch (e) {
        // Not JSON
    }

    const [slotPools, setSlotPools] = useState(() => {
        return config.slotConfigs.map(sc => {
            if (sc.source === 'CUSTOM') {
                return (sc.customText || '').split('\n').map(s => s.trim()).filter(Boolean);
            }
            return sc.words || [];
        });
    });

    const [currentSlots, setCurrentSlots] = useState(() => {
        return slotPools.map((pool, i) => pool.length > 0 ? seededPick(pool, `randomizer_init_${exercise?.id || 'ex'}_${i}`) : '---');
    });

    const [isSpinning, setIsSpinning] = useState(false);
    const alreadyDone = isAlreadyDone(assignment) && !reviewMode;
    const [completed, setCompleted] = useState(readOnly || reviewMode || alreadyDone);
    const [localUnblurred, setLocalUnblurred] = useState({});
    const currentUnblurred = isOnlineLesson ? (slideState?.unblurred || {}) : localUnblurred;

    const { syncStatus, persist, getLocalData } = useExercisePersistence(
        assignment?.id,
        { currentSlots, slotPools, status: completed ? 'completed' : 'working' },
        onUpdate,
        readOnly || alreadyDone,
        assignment,
        isOnlineLesson,
        updateSlideState,
        slideState
    );

    useEffect(() => {
        if (isOnlineLesson) return;
        const local = getLocalData();
        if (local && !alreadyDone && !readOnly) {
            if (local.currentSlots) setCurrentSlots(local.currentSlots);
            if (local.slotPools) setSlotPools(local.slotPools);
        }
    }, [getLocalData, alreadyDone, readOnly]);

    const activeSlots = isOnlineLesson ? (slideState?.currentSlots || currentSlots) : currentSlots;


    const handleBlurClick = (e) => {
        const bt = e.target.closest('.blurred-text');
        if (!bt) return;
        e.stopPropagation();
        const blurId = bt.getAttribute('data-blur-id');
        const next = { ...currentUnblurred, [blurId]: !currentUnblurred[blurId] };
        if (isOnlineLesson && isTeacher && updateSlideState) {
            updateSlideState({ ...slideState, unblurred: next });
        } else if (isOnlineLesson && !isTeacher) {
            setLocalUnblurred(next);
        } else if (!isOnlineLesson) {
            setLocalUnblurred(next);
            persist({ currentSlots, slotPools, unblurred: next, status: 'working' });
        }
    };

    const spinAll = () => {
        if (isSpinning || preview || readOnly || alreadyDone) return;

        const next = activeSlots.map((curr, i) => {
            const pool = slotPools[i];
            if (pool && pool.length > 0) {
                return pool[Math.floor(Math.random() * pool.length)];
            }
            return curr;
        });

        if (isOnlineLesson && updateSlideState) {
            // updateSlideState via persist will be called after timeout to match animation
        } else {
            setCurrentSlots(next);
        }
        setIsSpinning(true);
        setTimeout(() => {
            setIsSpinning(false);
            persist({ currentSlots: next, slotPools, status: 'working' });
        }, 600);
    };

    const removeValueFromPool = (slotIdx, val) => {
        if (preview || readOnly || alreadyDone) return;
        setSlotPools(prev => {
            const next = [...prev];
            next[slotIdx] = next[slotIdx].filter(v => v !== val);
            persist({ currentSlots, slotPools: next, status: 'working' });
            return next;
        });
    };

    return (
        <div className="exercise-card-shell" style={{ ...CARD_STYLE, padding: '2rem', position: 'relative' }} onClick={handleBlurClick}>
            <ExerciseLead exercise={exercise} style={instructionStyleForPad('2rem', { marginBottom: '2rem' }, !!exercise?.is_info)} notesStyle={notesStyleForPad('2rem', !!exercise?.is_info)} unblurred={currentUnblurred} />
            {!readOnly && !preview && !isOnlineLesson && <SyncStatus status={syncStatus} />}

            <div style={{
                display: 'flex',
                justifyContent: 'center',
                flexWrap: 'wrap',
                gap: '1rem',
                marginBottom: '2.5rem',
            }}>
                {[...Array(config.slotCount)].map((_, i) => (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
                        <div style={{
                            width: config.slotCount === 1 ? '380px' : config.slotCount === 2 ? '300px' : '240px',
                            aspectRatio: '1.2/1',
                            background: 'white',
                            borderRadius: '24px',
                            boxShadow: '0 10px 30px -10px rgba(0,0,0,0.15)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '1rem',
                            textAlign: 'center',
                            position: 'relative',
                            overflow: 'hidden',
                            border: '5px solid #1e293b',
                            transition: 'all 0.3s'
                        }}>
                             <div style={{
                                position: 'absolute',
                                top: 0, left: 0, right: 0, height: '2.5rem',
                                background: 'linear-gradient(to bottom, rgba(255,255,255,0.9), transparent)',
                                zIndex: 2
                            }} />
                            <div style={{
                                fontSize: config.slotCount === 1 ? '2.2rem' : config.slotCount === 2 ? '1.8rem' : '1.4rem',
                                fontWeight: 900,
                                color: '#1e293b',
                                transition: 'all 0.15s',
                                filter: isSpinning ? 'blur(8px)' : 'none',
                                transform: isSpinning ? 'translateY(10px)' : 'none',
                                opacity: isSpinning ? 0.4 : 1,
                                lineHeight: 1.2,
                                wordBreak: 'break-word',
                                zIndex: 1
                            }}>
                                {renderBold(activeSlots[i], currentUnblurred)}
                            </div>
                            <div style={{
                                position: 'absolute',
                                bottom: 0, left: 0, right: 0, height: '2.5rem',
                                background: 'linear-gradient(to top, rgba(255,255,255,0.9), transparent)',
                                zIndex: 2
                            }} />
                        </div>

                        {/* Per-Slot Remove Button */}
                        {(!isOnlineLesson || isTeacher) && (
                            <button
                                onClick={(alreadyDone || completed) ? undefined : (() => removeValueFromPool(i, activeSlots[i]))}
                                disabled={(alreadyDone || completed) || (isSpinning || preview || readOnly || alreadyDone || activeSlots[i] === '---')}
                                title="Remove this word from the pool"
                                style={{
                                    border: 'none',
                                    background: '#fee2e2',
                                    color: '#ef4444',
                                    borderRadius: '10px',
                                    padding: '6px 14px',
                                    fontSize: '0.72rem',
                                    fontWeight: 800,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    opacity: (isSpinning || preview || readOnly || alreadyDone || activeSlots[i] === '---') ? 0.5 : 1
                                }}
                            >
                                🗑️ Remove
                            </button>
                        )}
                    </div>
                ))}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem' }}>
                {(!isOnlineLesson || isTeacher) && (
                    <button
                        onClick={spinAll}
                        disabled={isSpinning || preview || readOnly || alreadyDone}
                        style={{
                            background: isSpinning ? '#9472e0' : 'linear-gradient(135deg, #7c3aed 0%, #4361ee 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '16px',
                            padding: '0.8rem 2.2rem',
                            fontSize: '1.25rem',
                            fontWeight: 900,
                            letterSpacing: '0.04em',
                            cursor: 'pointer',
                            boxShadow: '0 10px 20px -5px rgba(67, 97, 238, 0.4)',
                            transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                            transform: isSpinning ? 'scale(0.95)' : 'none',
                        }}
                    >
                        {isSpinning ? 'SPINNING...' : '🎰 Spin'}
                    </button>
                )}

                {!preview && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '1rem' }}>
                        {!readOnly && !reviewMode && (!isOnlineLesson || isTeacher) && (
                            <button type="button" className={(alreadyDone || completed) ? COMPLETE_DONE_CLASS : COMPLETE_BTN} onClick={() => {
                                setCompleted(true);
                                if (onComplete) onComplete({ status: 'completed', score: 1, total: 1 });
                            }}>{(alreadyDone || completed) ? COMPLETE_DONE_LABEL : COMPLETE_LABEL}</button>
                        )}
                        {(!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                            <AssignmentStatusPill assignment={assignment} completed={completed} isOnlineLesson={isOnlineLesson}
                                fallback="— In Progress —" completedLabel="Session Completed"  practiceMode={practiceMode} />
                        )}
                    </div>
                )}
            </div>

            <TeacherComment exercise={exercise} assignment={assignment} />
        </div>
    );
}

/** Local-only pending pair fields — typing does not sync until + Add Pair. */
function ReadingCartPendingPair({
    initialOrg = '',
    initialTrn = '',
    primaryLang = 'en',
    hasCompletePairSelection,
    onAdd,
    onCancel,
}) {
    const [org, setOrg] = useState(initialOrg);
    const [trn, setTrn] = useState(initialTrn);
    useEffect(() => { setOrg(initialOrg); }, [initialOrg]);
    useEffect(() => { setTrn(initialTrn); }, [initialTrn]);
    const canAdd = hasCompletePairSelection && !!org.trim() && !!trn.trim();
    return (
        <div style={{ marginBottom: '1rem', background: '#eff6ff', border: '1.5px solid #bfdbfe', padding: '0.75rem', borderRadius: '8px' }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 800, color: '#3b82f6', marginBottom: '8px', textTransform: 'uppercase' }}>
                {hasCompletePairSelection ? 'Ready to Add (Editable):' : 'Select the other side, then Add Pair'}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <strong style={{ minWidth: '24px' }}>{primaryLang === 'en' ? '🇬🇧' : '🇷🇺'}</strong>
                <input value={org} onChange={(e) => setOrg(e.target.value)} placeholder="Original…" style={{ flex: 1, padding: '6px 10px', border: '1.5px solid #bfdbfe', borderRadius: '8px', fontSize: '0.95rem', outline: 'none', background: 'white' }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <strong style={{ minWidth: '24px' }}>{primaryLang === 'en' ? '🇷🇺' : '🇬🇧'}</strong>
                <input value={trn} onChange={(e) => setTrn(e.target.value)} placeholder="Translation…" style={{ flex: 1, padding: '6px 10px', border: '1.5px solid #bfdbfe', borderRadius: '8px', fontSize: '0.95rem', outline: 'none', background: 'white' }} />
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                <button
                    type="button"
                    onClick={() => onAdd?.(org.trim(), trn.trim())}
                    disabled={!canAdd}
                    style={{
                        flex: 1, background: canAdd ? '#3b82f6' : '#94a3b8', color: 'white', border: 'none',
                        padding: '0.6rem', borderRadius: '8px', cursor: canAdd ? 'pointer' : 'not-allowed',
                        fontWeight: 800, fontSize: '0.9rem', transition: 'background 0.2s', opacity: canAdd ? 1 : 0.75,
                    }}
                >+ Add Pair</button>
                <button
                    type="button"
                    onClick={onCancel}
                    style={{ background: 'white', color: '#64748b', border: '1.5px solid #cbd5e1', padding: '0.6rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 700, fontSize: '0.9rem' }}
                >Cancel</button>
            </div>
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   READING PARALLEL TEXT COMPONENT
═══════════════════════════════════════════════════════════════════════════ */
function ReadingInteractive({ exercise, assignment, onUpdate, readOnly, reviewMode, preview, onComplete, updateSlideState, slideState = {}, isTeacher, isOnlineLesson, groupId, showSolution, practiceMode }) {
    const alreadyDone = isAlreadyDone(assignment) && !reviewMode;
    const [completed, setCompleted] = useState(readOnly || reviewMode || alreadyDone);
    const config = React.useMemo(() => {
        try {
            return JSON.parse(exercise.body || '{}');
        } catch(e) {
            return { theme: 'modern', primaryLang: 'en', original: '', translation: '' };
        }
    }, [exercise.body]);

    const orgPagesRaw = React.useMemo(() => {
        let chunks = (config.original || '').split(/(?:\r?\n)?======(?:\r?\n)?/);
        chunks = chunks.filter(p => !p.match(/^=+$/) && p.trim() !== '');
        return chunks.length > 0 ? chunks : [''];
    }, [config.original]);

    const trnPagesRaw = React.useMemo(() => {
        let chunks = (config.translation || '').split(/(?:\r?\n)?======(?:\r?\n)?/);
        chunks = chunks.filter(p => !p.match(/^=+$/) && p.trim() !== '');
        return chunks.length > 0 ? chunks : [''];
    }, [config.translation]);

    const maxPagesCount = Math.max(orgPagesRaw.length, trnPagesRaw.length);

    const [localPage, setLocalPage] = useState(0);
    const [localUnblurred, setLocalUnblurred] = useState({});
    /** Preview / offline reveal toggles (online lesson uses slideState.paraVisible_*). */
    const [localParaVisible, setLocalParaVisible] = useState({});
    const currentUnblurred = isOnlineLesson ? (slideState?.unblurred || {}) : localUnblurred;

    
    const { syncStatus, persist, getLocalData } = useExercisePersistence(assignment?.id, { unblurred: localUnblurred }, onUpdate, readOnly, assignment, isOnlineLesson, updateSlideState, slideState);
    useEffect(() => {
        if (isOnlineLesson) return;
        const local = getLocalData();
        if (local && !readOnly) {
            if (local.unblurred) setLocalUnblurred(local.unblurred);
            if (local.answers?.unblurred) setLocalUnblurred(local.answers.unblurred);
        }
    }, [getLocalData, readOnly, isOnlineLesson]);
const safePageIdx = Math.min(
        isOnlineLesson && slideState?.page !== undefined ? slideState.page : localPage, 
        Math.max(0, maxPagesCount - 1)
    );

    const splitParas = (text) => {
        const raw = (text || '').split('\n\n').filter(p => p.trim() !== '');
        const merged = [];
        for (let i = 0; i < raw.length; i++) {
            const p = raw[i];
            // If it's a standalone image and not the last paragraph, merge it with the next one
            // so it doesn't create a mismatched row in the two-column layout
            if (p.trim().startsWith('![') && p.trim().endsWith(')') && i < raw.length - 1) {
                merged.push(p + '\n' + raw[i + 1]);
                i++;
            } else {
                merged.push(p);
            }
        }
        return merged;
    };

    const orgParas = splitParas(orgPagesRaw[safePageIdx]);
    const trnParas = splitParas(trnPagesRaw[safePageIdx]);
    const maxParas = Math.max(orgParas.length, trnParas.length);

    const handlePageChange = (newPage) => {
        setLocalPage(newPage);
        if (updateSlideState) updateSlideState({ ...slideState, page: newPage });
    };

    const [cartMode, setCartMode] = useState('mini'); // 'floating' | 'mini' | 'full' | 'test'
    const [testIdx, setTestIdx] = useState(0);
    const [testFlipped, setTestFlipped] = useState(false);

    /** Persist cart edits only on blur / Add — not every keystroke (avoids room sync lag). */
    const handleUpdateCartItem = (id, field, val) => {
        const cart = slideState.readingCart || [];
        const next = cart.map(p => p.id === id ? { ...p, [field]: val } : p);
        if (updateSlideState) updateSlideState({ ...slideState, readingCart: next });
    };
    /** Draft OS selection tooltip — not committed until "Add to cart". Teacher + Online Lesson only. */
    const [selTooltip, setSelTooltip] = useState(null);
    // { mode: 'add'|'clear', type: 'Org'|'Trn', text?, paraIdx?, top, left }

    const canUseReadingCart = !!(isTeacher && isOnlineLesson && !preview && updateSlideState);

    // Auto-suggest Theme
    const autoTheme = useMemo(() => {
        if (!config.original) return '';
        const words = config.original.replace(/\n/g, ' ').trim().split(/\s+/);
        return words.slice(0, 4).join(' ') + (words.length > 4 ? '...' : '');
    }, [config.original]);
    
    const [cartTheme, setCartTheme] = useState(autoTheme);

    useEffect(() => {
        if (!selTooltip) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') setSelTooltip(null);
        };
        const onDown = (e) => {
            if (e.target.closest?.('.reading-sel-tooltip')) return;
            // Draft Add tooltip stays until Add / Escape / new selection; Clear tip dismisses on outside click
            if (selTooltip.mode === 'clear') setSelTooltip(null);
        };
        window.addEventListener('keydown', onKey);
        window.addEventListener('mousedown', onDown);
        return () => {
            window.removeEventListener('keydown', onKey);
            window.removeEventListener('mousedown', onDown);
        };
    }, [selTooltip]);

    const clearTeacherSelections = useCallback((sides = ['Org', 'Trn']) => {
        if (!updateSlideState) return;
        const patch = { ...slideState };
        if (sides.includes('Org')) patch.teacherSelectionOrg = null;
        if (sides.includes('Trn')) patch.teacherSelectionTrn = null;
        updateSlideState(patch);
        setSelTooltip(null);
    }, [slideState, updateSlideState]);

    const handleSelection = useCallback((type, paraIdx, e) => {
        if (!canUseReadingCart) return;

        const sel = window.getSelection();
        const text = (sel?.toString() || '').trim();

        if (text) {
            let top = 0;
            let left = 0;
            if (sel.rangeCount > 0) {
                const rect = sel.getRangeAt(0).getBoundingClientRect();
                top = rect.top;
                left = rect.left + rect.width / 2;
            }
            setSelTooltip({
                mode: 'add',
                type,
                text,
                paraIdx,
                top,
                left,
            });
            return;
        }

        // Empty selection: click on a committed yellow highlight → Clear tooltip
        const hl = e?.target?.closest?.('[data-reading-sel]');
        if (hl) {
            const side = hl.getAttribute('data-reading-sel');
            if (side !== 'Org' && side !== 'Trn') return;
            const committed = side === 'Org' ? slideState.teacherSelectionOrg : slideState.teacherSelectionTrn;
            if (!committed?.text) return;
            const rect = hl.getBoundingClientRect();
            setSelTooltip({
                mode: 'clear',
                type: side,
                top: rect.top,
                left: rect.left + rect.width / 2,
            });
        }
    }, [canUseReadingCart, slideState.teacherSelectionOrg, slideState.teacherSelectionTrn]);

    const commitDraftToCartField = useCallback(() => {
        if (!selTooltip || selTooltip.mode !== 'add' || !updateSlideState) return;
        const key = selTooltip.type === 'Trn' ? 'teacherSelectionTrn' : 'teacherSelectionOrg';
        updateSlideState({
            ...slideState,
            [key]: { text: selTooltip.text, paraIdx: selTooltip.paraIdx },
        });
        setSelTooltip(null);
        try { window.getSelection()?.removeAllRanges(); } catch { /* ignore */ }
        setCartMode('mini');
    }, [selTooltip, slideState, updateSlideState]);

    const handleAddPairToCart = (orgRaw, trnRaw) => {
        const org = String(orgRaw || '').trim();
        const trn = String(trnRaw || '').trim();
        if (!org || !trn) return;

        const cart = slideState.readingCart || [];
        const isOrgEn = config.primaryLang === 'en';
        const pair = {
            id: crypto.randomUUID(),
            english: isOrgEn ? org : trn,
            russian: isOrgEn ? trn : org,
        };

        if (updateSlideState) updateSlideState({
            ...slideState,
            readingCart: [...cart, pair],
            teacherSelectionOrg: null,
            teacherSelectionTrn: null
        });
        setSelTooltip(null);
    };

    const handleRemoveFromCart = (id) => {
        const cart = slideState.readingCart || [];
        if (updateSlideState) updateSlideState({ ...slideState, readingCart: cart.filter(p => p.id !== id) });
    };

    const handleSaveCartToDB = async () => {
        const cart = slideState.readingCart || [];
        if (cart.length === 0) return;

        try {
            await db.saveReadingCart(cart, cartTheme, groupId);
            // Keep cart in this lesson so teacher can keep practicing / testing.
            alert(`Saved ${cart.length} words to database, group, and students! Cart stays for this lesson.`);
            setCartMode('mini');
        } catch(e) {
            alert('Failed to save words: ' + e.message);
        }
    };

    const applyHighlight = (text, selectionState, currentParaIdx) => {
        if (!text || !selectionState || selectionState.paraIdx !== currentParaIdx) return text;
        const word = selectionState.text;
        const safeWord = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`(${safeWord})`, 'i');
        return text.replace(regex, '{bg:#fef08a}$1{/bg}');
    };

    const tagReadingHighlightHtml = (html, side) => {
        if (!html || !side) return html;
        return String(html).replace(
            /(<span style="background-color:#fef08a;)([^"]*")(>)/gi,
            `$1$2 data-reading-sel="${side}" class="reading-cart-hl"$3`
        );
    };

    const handleBlurClick = (e) => {
        const target = e.target;
        const blurTarget = target.closest('.blurred-text');
        if (blurTarget) {
            e.stopPropagation();
            const blurId = blurTarget.getAttribute('data-blur-id');
            const nextUnblurred = { ...currentUnblurred, [blurId]: !currentUnblurred[blurId] };
            if (isOnlineLesson && isTeacher && updateSlideState) {
                updateSlideState({ ...slideState, unblurred: nextUnblurred });
            } else if (isOnlineLesson && !isTeacher) {
                setLocalUnblurred(nextUnblurred);
            } else if (!isOnlineLesson) {
                setLocalUnblurred(nextUnblurred);
                persist({ unblurred: nextUnblurred });
            }
        }
    };

    const paraVisibleKey = (pageIdx, paraIdx) => `paraVisible_${pageIdx}_${paraIdx}`;

    const isParaVisible = (pageIdx, paraIdx) => {
        const key = paraVisibleKey(pageIdx, paraIdx);
        if (isOnlineLesson) return !!slideState?.[key];
        return !!localParaVisible[key];
    };

    const toggleParaVisible = (pageIdx, paraIdx) => {
        const key = paraVisibleKey(pageIdx, paraIdx);
        if (isOnlineLesson && updateSlideState) {
            updateSlideState({ ...slideState, [key]: !slideState?.[key] });
            return;
        }
        setLocalParaVisible((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const isClassic = config.theme === 'classic';
    const containerStyle = isClassic ? {
        background: '#fdf6e3', 
        color: '#3e2723',
        fontFamily: '"Georgia", serif',
        border: '1px solid #d7ccc8',
        borderRadius: '4px',
        padding: '2rem',
        boxShadow: 'inset 0 0 50px rgba(139, 69, 19, 0.05), 0 5px 15px rgba(0,0,0,0.05)'
    } : {
        background: 'white',
        color: '#1e293b',
        fontFamily: 'inherit',
        border: '1px solid #e2e8f0',
        borderRadius: '16px',
        padding: '1.5rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
    };

    const cartItems = slideState.readingCart || [];
    const cartCount = cartItems.length;
    const hasPendingOrg = !!slideState.teacherSelectionOrg?.text;
    const hasPendingTrn = !!slideState.teacherSelectionTrn?.text;
    const hasPendingPairFields = hasPendingOrg || hasPendingTrn;
    const hasCompletePairSelection = hasPendingOrg && hasPendingTrn;
    const showCartWidget = (canUseReadingCart && hasPendingPairFields) || cartCount > 0;

    const CartOverlay = () => {
        if (!showCartWidget || cartMode === 'floating') return null;

        if (cartMode === 'mini') {
            return (
                <div style={{ position: 'fixed', bottom: '1.5rem', right: '1.5rem', background: 'white', padding: '1rem', borderRadius: '20px', boxShadow: '0 15px 40px rgba(0,0,0,0.2)', zIndex: 1000, border: '2.5px solid #4361ee', width: '320px' }}>
                    <div style={{ fontWeight: 900, marginBottom: '0.5rem', fontSize: '1.05rem', color: '#1e293b', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        🛒 Wordlist Cart
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <span style={{ background: '#e2e8f0', color: '#475569', padding: '2px 8px', borderRadius: '12px', fontSize: '0.81rem' }}>{cartCount}</span>
                            <button onClick={() => setCartMode('full')} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1.1rem' }} title="Full View">🔲</button>
                            <button onClick={() => setCartMode('floating')} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: '#94a3b8' }} title="Minimize">🔽</button>
                        </div>
                    </div>

                    {canUseReadingCart && hasPendingPairFields && (
                        <ReadingCartPendingPair
                            key={`pending-${slideState.teacherSelectionOrg?.text || ''}|${slideState.teacherSelectionTrn?.text || ''}`}
                            initialOrg={slideState.teacherSelectionOrg?.text || ''}
                            initialTrn={slideState.teacherSelectionTrn?.text || ''}
                            primaryLang={config.primaryLang}
                            hasCompletePairSelection={hasCompletePairSelection}
                            onAdd={handleAddPairToCart}
                            onCancel={() => clearTeacherSelections(['Org', 'Trn'])}
                        />
                    )}

                    {cartCount > 0 && !hasPendingPairFields && (
                        <div style={{ fontSize: '0.88rem', color: '#64748b', marginBottom: '0.75rem', maxHeight: '180px', overflowY: 'auto' }} className="custom-scrollbar">
                            {cartItems.slice(-5).reverse().map((pair) => (
                                <div key={pair.id} style={{ borderBottom: '1px solid #f1f5f9', padding: '6px 0', display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <div style={{flex: 1, wordBreak: 'break-word'}}><strong>{pair.english}</strong></div>
                                    <div style={{flex: 1, color: '#94a3b8', wordBreak: 'break-word'}}>{pair.russian}</div>
                                    {isTeacher && <button onClick={() => handleRemoveFromCart(pair.id)} style={{background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '4px', fontSize: '1.1rem'}} title="Remove">✕</button>}
                                </div>
                            ))}
                            <button onClick={() => setCartMode('full')} style={{ width: '100%', marginTop: '8px', background: '#f8fafc', border: '1.5px dashed #e2e8f0', borderRadius: '8px', padding: '6px', fontSize: '0.8rem', color: '#64748b', cursor: 'pointer' }}>View All ({cartCount})</button>
                        </div>
                    )}

                    {cartCount > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '10px' }}>
                            {isTeacher && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <label style={{ fontSize: '0.72rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>Theme / Topic</label>
                                    <input value={cartTheme} onChange={e => setCartTheme(e.target.value)} style={{ padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '0.85rem', outline: 'none' }} placeholder="E.g. Vocabulary..." />
                                </div>
                            )}
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button onClick={() => { setTestIdx(0); setTestFlipped(false); setCartMode('test'); }} style={{ flex: 1, background: 'linear-gradient(135deg, #7c3aed, #4361ee)', color: 'white', border: 'none', padding: '0.65rem', borderRadius: '10px', cursor: 'pointer', fontWeight: 800, fontSize: '0.85rem' }}>⚡ Test</button>
                                {isTeacher && <button onClick={handleSaveCartToDB} style={{ flex: 1, background: '#10b981', color: 'white', border: 'none', padding: '0.65rem', borderRadius: '10px', cursor: 'pointer', fontWeight: 800, fontSize: '0.85rem' }}>💾 Save</button>}
                            </div>
                        </div>
                    )}
                </div>
            );
        }

        if (cartMode === 'full' || cartMode === 'test') {
            return (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '2rem' }}>
                    <div style={{ background: 'white', width: '100%', maxWidth: '800px', maxHeight: '90vh', borderRadius: '24px', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)' }}>
                        <div style={{ padding: '1.5rem 2rem', background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 900, color: '#1e293b' }}>
                                    {cartMode === 'full' ? '📝 Manage Wordlist' : '⚡ Quick Test'}
                                </h2>
                                <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748b', fontWeight: 600 }}>{cartCount} items in cart</p>
                            </div>
                            <div style={{ display: 'flex', gap: '0.75rem' }}>
                                {cartMode === 'test' ? (
                                    <button onClick={() => setCartMode('full')} style={{ background: '#f1f5f9', border: 'none', padding: '0.6rem 1.2rem', borderRadius: '10px', fontWeight: 800, color: '#475569', cursor: 'pointer' }}>Back to List</button>
                                ) : (
                                    <button onClick={() => { setTestIdx(0); setTestFlipped(false); setCartMode('test'); }} disabled={cartCount === 0} style={{ background: 'linear-gradient(135deg, #7c3aed, #4361ee)', border: 'none', padding: '0.6rem 1.2rem', borderRadius: '10px', fontWeight: 800, color: 'white', cursor: 'pointer' }}>⚡ Start Test</button>
                                )}
                                <button onClick={() => setCartMode('mini')} style={{ background: '#1e293b', color: 'white', border: 'none', width: '36px', height: '36px', borderRadius: '50%', cursor: 'pointer', fontWeight: 900 }}>✕</button>
                            </div>
                        </div>

                        <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem 2rem' }}>
                            {cartMode === 'full' ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: isTeacher ? '1fr 1fr 40px' : '1fr 1fr', gap: '1rem', padding: '0 0.5rem', marginBottom: '0.25rem' }}>
                                        <span style={{ fontSize: '0.72rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase' }}>English</span>
                                        <span style={{ fontSize: '0.72rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase' }}>Russian</span>
                                        {isTeacher && <span></span>}
                                    </div>
                                    {cartItems.map((pair) => (
                                        <div key={pair.id} style={{ display: 'grid', gridTemplateColumns: isTeacher ? '1fr 1fr 40px' : '1fr 1fr', gap: '1rem', alignItems: 'center', background: '#f8fafc', padding: '0.5rem 0.75rem', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                            {isTeacher ? (
                                                <>
                                                    <input
                                                        key={`${pair.id}-en`}
                                                        defaultValue={pair.english}
                                                        onFocus={(e) => { e.target.style.borderColor = '#4361ee'; }}
                                                        onBlur={(e) => {
                                                            e.target.style.borderColor = 'transparent';
                                                            const v = e.target.value;
                                                            if (v !== pair.english) handleUpdateCartItem(pair.id, 'english', v);
                                                        }}
                                                        style={{ padding: '6px 10px', borderRadius: '8px', border: '1.5px solid transparent', background: 'white', fontSize: '0.9rem', fontWeight: 700, outline: 'none' }}
                                                    />
                                                    <input
                                                        key={`${pair.id}-ru`}
                                                        defaultValue={pair.russian}
                                                        onFocus={(e) => { e.target.style.borderColor = '#4361ee'; }}
                                                        onBlur={(e) => {
                                                            e.target.style.borderColor = 'transparent';
                                                            const v = e.target.value;
                                                            if (v !== pair.russian) handleUpdateCartItem(pair.id, 'russian', v);
                                                        }}
                                                        style={{ padding: '6px 10px', borderRadius: '8px', border: '1.5px solid transparent', background: 'white', fontSize: '0.9rem', color: '#475569', outline: 'none' }}
                                                    />
                                                    <button onClick={() => handleRemoveFromCart(pair.id)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '1.1rem' }}>✕</button>
                                                </>
                                            ) : (
                                                <>
                                                    <div style={{ padding: '6px 10px', fontSize: '0.9rem', fontWeight: 700 }}>{pair.english}</div>
                                                    <div style={{ padding: '6px 10px', fontSize: '0.9rem', color: '#475569' }}>{pair.russian}</div>
                                                </>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '300px', gap: '2rem' }}>
                                    <div 
                                        key={`cart-qt-${testIdx}`}
                                        onClick={() => setTestFlipped(!testFlipped)}
                                        style={{
                                            width: '100%', maxWidth: '400px', height: '260px', borderRadius: '32px',
                                            cursor: 'pointer', perspective: '1000px', position: 'relative',
                                            overflow: 'hidden'
                                        }}
                                    >
                                        <div style={{
                                            position: 'relative', width: '100%', height: '100%', textAlign: 'center', transition: 'transform 0.6s',
                                            transformStyle: 'preserve-3d', transform: testFlipped ? 'rotateY(180deg)' : 'none',
                                            borderRadius: '32px'
                                        }}>
                                            <div style={{
                                                position: 'absolute', inset: 0, backfaceVisibility: 'hidden',
                                                background: 'linear-gradient(135deg, #f0f4ff, #e0e7ff)', border: '4px solid white',
                                                borderRadius: '32px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem',
                                                boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
                                            }}>
                                                <span style={{ fontSize: '0.75rem', fontWeight: 800, color: '#4361ee', textTransform: 'uppercase', marginBottom: '1rem', letterSpacing: '0.1em' }}>Russian</span>
                                                <div style={{ fontSize: '1.8rem', fontWeight: 900, color: '#1e293b' }}>{cartItems[testIdx]?.russian}</div>
                                                <div style={{ marginTop: '1.5rem', fontSize: '0.8rem', color: '#64748b' }}>Tap to reveal translation</div>
                                            </div>
                                            <div style={{
                                                position: 'absolute', inset: 0, backfaceVisibility: 'hidden', transform: 'rotateY(180deg)',
                                                background: 'linear-gradient(135deg, #4361ee, #7c3aed)', border: '4px solid rgba(255,255,255,0.2)',
                                                borderRadius: '32px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem',
                                                boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
                                            }}>
                                                <span style={{ fontSize: '0.75rem', fontWeight: 800, color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', marginBottom: '1rem', letterSpacing: '0.1em' }}>English</span>
                                                <div style={{ fontSize: '1.8rem', fontWeight: 900, color: 'white' }}>{cartItems[testIdx]?.english}</div>
                                                <button 
                                                    onClick={(e) => { e.stopPropagation(); window.speakText(cartItems[testIdx]?.english); }}
                                                    style={{ marginTop: '1rem', background: 'rgba(255,255,255,0.2)', border: 'none', width: '44px', height: '44px', borderRadius: '50%', color: 'white', fontSize: '1.2rem', cursor: 'pointer' }}
                                                >📢</button>
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                                        <button onClick={() => { setTestIdx(Math.max(0, testIdx - 1)); setTestFlipped(false); }} disabled={testIdx === 0} style={{ background: '#f1f5f9', border: 'none', padding: '10px 20px', borderRadius: '12px', fontWeight: 800, color: '#475569', cursor: 'pointer', opacity: testIdx === 0 ? 0.3 : 1 }}>← Prev</button>
                                        <span style={{ fontWeight: 800, color: '#94a3b8' }}>{testIdx + 1} / {cartCount}</span>
                                        <button onClick={() => { setTestIdx(Math.min(cartCount - 1, testIdx + 1)); setTestFlipped(false); }} disabled={testIdx === cartCount - 1} style={{ background: '#f1f5f9', border: 'none', padding: '10px 20px', borderRadius: '12px', fontWeight: 800, color: '#475569', cursor: 'pointer', opacity: testIdx === cartCount - 1 ? 0.3 : 1 }}>Next →</button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {cartMode === 'full' && (
                            <div style={{ padding: '1.5rem 2rem', background: '#f8fafc', borderTop: '1.5px solid #e2e8f0', display: 'flex', gap: '1rem', alignItems: 'center' }}>
                                <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                    <label style={{ fontSize: '0.75rem', fontWeight: 800, color: '#64748b' }}>THEME:</label>
                                    {isTeacher ? (
                                        <input value={cartTheme} onChange={e => setCartTheme(e.target.value)} style={{ flex: 1, padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '0.9rem', outline: 'none' }} />
                                    ) : (
                                        <span style={{ fontWeight: 700, color: '#1e293b' }}>{cartTheme || 'General'}</span>
                                    )}
                                </div>
                                {isTeacher && (
                                    <button onClick={handleSaveCartToDB} disabled={cartCount === 0} style={{ background: '#10b981', color: 'white', border: 'none', padding: '0.8rem 2rem', borderRadius: '12px', fontWeight: 800, cursor: 'pointer', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)' }}>Save to Database</button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            );
        }
        return null;
    };

    return (
        <div style={{ position: 'relative' }}>
            {showCartWidget && cartMode === 'floating' && (
                <button 
                    onClick={() => setCartMode('mini')} 
                    style={{ position: 'fixed', bottom: '2.5rem', right: '2.5rem', background: 'white', padding: '12px 24px', borderRadius: '30px', boxShadow: '0 15px 35px rgba(0,0,0,0.2)', zIndex: 1000, border: '2.5px solid #4361ee', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px', fontWeight: 900, color: '#1e293b', fontSize: '1rem', transition: 'all 0.3s' }}
                >
                    🛒 Cart 
                    {cartCount > 0 && <span style={{ background: '#4361ee', color: 'white', minWidth: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', padding: '0 4px' }}>{cartCount}</span>}
                </button>
            )}

            <CartOverlay />

            <div style={containerStyle} onClick={handleBlurClick}>
                {React.useMemo(() => {
                    const isOnlyImage = (p) => p && p.trim().startsWith('![') && p.trim().endsWith(')');
                    const items = [];
                    let oIdx = 0;
                    let tIdx = 0;

                    const orgParas = (orgPagesRaw[safePageIdx] || '').split('\n\n').filter(p => p.trim() !== '');
                    const trnParas = (trnPagesRaw[safePageIdx] || '').split('\n\n').filter(p => p.trim() !== '');

                    while (oIdx < orgParas.length || tIdx < trnParas.length) {
                        const o = orgParas[oIdx];
                        const t = trnParas[tIdx];

                        if (o && isOnlyImage(o)) {
                            items.push({ type: 'media', content: o });
                            oIdx++;
                            continue;
                        }
                        if (t && isOnlyImage(t)) {
                            items.push({ type: 'media', content: t });
                            tIdx++;
                            continue;
                        }

                        items.push({
                            type: 'para',
                            original: o || '',
                            translation: t || '',
                            oIdx,
                            tIdx
                        });
                        oIdx++;
                        tIdx++;
                    }

                    return items.map((item, i) => {
                        if (item.type === 'media') {
                            return (
                                <div key={`m-${i}`} style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
                                    <InlineAudioRenderer html={formatInline(item.content, { prefix: `med_${safePageIdx}_${i}`, unblurredMap: currentUnblurred })} />
                                </div>
                            );
                        }

                        const originalRaw = applyHighlight(item.original, slideState.teacherSelectionOrg, item.oIdx);
                        const transRaw = applyHighlight(item.translation, slideState.teacherSelectionTrn, item.tIdx);
                        const orgHtml = tagReadingHighlightHtml(
                            formatInline(originalRaw, { prefix: `org_${safePageIdx}_${item.oIdx}`, unblurredMap: currentUnblurred }),
                            'Org',
                        );
                        const trnHtml = tagReadingHighlightHtml(
                            formatInline(transRaw, { prefix: `trn_${safePageIdx}_${item.tIdx}`, unblurredMap: currentUnblurred }),
                            'Trn',
                        );
                        const isVisible = isParaVisible(safePageIdx, item.oIdx);
                        // Preview: hide translation until revealed (local toggle). Online teacher: dimmed peek + eye.
                        // Online/student: placeholder until teacher reveals.
                        const showHiddenPlaceholder = !isVisible && !showSolution && (!isTeacher || preview);
                        const canToggleVisibility = (isTeacher || preview) && !showSolution;

                        return (
                            <div key={`p-${i}`} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.5rem', paddingBottom: '0.5rem' }} className="reading-para-row">
                                <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
                                    <div
                                        style={{ flex: '1 1 300px', fontSize: isClassic ? '1.2rem' : '1.1rem', lineHeight: 1.8 }}
                                        onMouseUp={(e) => handleSelection('Org', item.oIdx, e)}
                                    >
                                        <InlineAudioRenderer html={orgHtml} style={{ display: 'contents' }} />
                                    </div>
                                    
                                    {showHiddenPlaceholder ? (
                                        <div style={{ flex: '1 1 300px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, color: '#94a3b8', fontStyle: 'italic', background: 'rgba(0,0,0,0.02)', borderRadius: '12px', minHeight: '100px', border: '2px dashed #e2e8f0', padding: '12px' }}>
                                            <span>{preview ? 'Translation hidden' : 'Translation hidden by teacher'}</span>
                                            {canToggleVisibility && (
                                                <button
                                                    type="button"
                                                    onClick={(alreadyDone || completed) ? undefined : ((e) => { e.stopPropagation(); toggleParaVisible(safePageIdx, item.oIdx); })}
                                                    title="Reveal translation"
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: 6,
                                                        padding: '8px 14px',
                                                        borderRadius: 999,
                                                        border: '1.5px solid #cbd5e1',
                                                        background: 'white',
                                                        color: '#475569',
                                                        fontWeight: 800,
                                                        fontSize: 13,
                                                        fontStyle: 'normal',
                                                        cursor: 'pointer',
                                                        boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                                                    }}
                                                >
                                                    🙈 Reveal
                                                </button>
                                            )}
                                        </div>
                                    ) : (
                                        <div style={{ position: 'relative', flex: '1 1 300px', fontSize: isClassic ? '1.2rem' : '1.1rem', lineHeight: 1.8, opacity: isTeacher && !preview && !isVisible && !showSolution ? 0.35 : 1, transition: 'opacity 0.2s', color: isClassic ? '#5d4037' : '#475569' }} onMouseUp={(e) => handleSelection('Trn', item.tIdx, e)}>
                                            {canToggleVisibility && (
                                                <button 
                                                    type="button"
                                                    onClick={(e) => { e.stopPropagation(); toggleParaVisible(safePageIdx, item.oIdx); }}
                                                    title={isVisible ? (preview ? 'Hide translation' : 'Hide from Students') : (preview ? 'Reveal translation' : 'Reveal to Students')}
                                                    style={{ position: 'absolute', top: '-10px', right: '-10px', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: isVisible ? 'white' : '#f8fafc', color: isVisible ? '#10b981' : '#94a3b8', border: isVisible ? '2px solid #10b981' : '1.5px solid #cbd5e1', borderRadius: '50%', cursor: 'pointer', fontSize: '1rem', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', transition: 'all 0.2s', zIndex: 10 }}
                                                >
                                                    {isVisible ? '👁️' : '🙈'}
                                                </button>
                                            )}
                                            <InlineAudioRenderer html={trnHtml} style={{ display: 'contents' }} />
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    });
                }, [orgPagesRaw, trnPagesRaw, safePageIdx, slideState, isTeacher, preview, showSolution, isClassic, localParaVisible, handleSelection, currentUnblurred])}
            </div>

            {canUseReadingCart && selTooltip && typeof document !== 'undefined' && createPortal(
                <div
                    className="reading-sel-tooltip"
                    style={{
                        position: 'fixed',
                        top: Math.max(8, selTooltip.top - 44),
                        left: selTooltip.left,
                        transform: 'translateX(-50%)',
                        zIndex: 5000,
                        pointerEvents: 'auto',
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                >
                    {selTooltip.mode === 'add' ? (
                        <button
                            type="button"
                            onClick={commitDraftToCartField}
                            style={{
                                background: '#4361ee',
                                color: 'white',
                                border: 'none',
                                borderRadius: 10,
                                padding: '8px 12px',
                                fontWeight: 800,
                                fontSize: 12,
                                cursor: 'pointer',
                                boxShadow: '0 8px 20px rgba(67, 97, 238, 0.35)',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            Add to cart
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={() => clearTeacherSelections([selTooltip.type])}
                            style={{
                                background: '#1e293b',
                                color: 'white',
                                border: 'none',
                                borderRadius: 10,
                                padding: '8px 12px',
                                fontWeight: 800,
                                fontSize: 12,
                                cursor: 'pointer',
                                boxShadow: '0 8px 20px rgba(15, 23, 42, 0.3)',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            Clear
                        </button>
                    )}
                </div>,
                document.body,
            )}

            {maxPagesCount > 1 && (
                <div className="pagination-subtle" style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginTop: '1.5rem', opacity: 0.4, transition: 'opacity 0.2s', justifyContent: 'center' }}>
                    <button 
                        onClick={() => handlePageChange(safePageIdx - 1)} 
                        disabled={(alreadyDone || completed) || (safePageIdx === 0 || (!isTeacher && isOnlineLesson))} 
                        className="pagination-btn-subtle"
                        style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '4px 10px', color: '#64748b', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 800 }}
                    >← Prev</button>
                    <span style={{ fontSize: '0.75rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Page {safePageIdx + 1} / {maxPagesCount}</span>
                    <button 
                        onClick={() => handlePageChange(safePageIdx + 1)} 
                        disabled={safePageIdx === maxPagesCount - 1 || (!isTeacher && isOnlineLesson)} 
                        className="pagination-btn-subtle"
                        style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '4px 10px', color: '#64748b', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 800 }}
                    >Next →</button>
                </div>
            )}

            {!preview && (
                <ExerciseActionBar>
                    {!readOnly && !reviewMode && (!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && <button type="button" className={(alreadyDone || completed) ? COMPLETE_DONE_CLASS : COMPLETE_BTN} onClick={() => {
                        setCompleted(true);
                        if (onComplete) onComplete({ status: 'completed', score: 1, total: 1 });
                    }}>{(alreadyDone || completed) ? COMPLETE_DONE_LABEL : COMPLETE_LABEL}</button>}
                    {(!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                        <AssignmentStatusPill assignment={assignment} completed={completed} isOnlineLesson={isOnlineLesson}  practiceMode={practiceMode} />
                    )}
                </ExerciseActionBar>
            )}

            <style>{`
                @media (min-width: 768px) {
                    .reading-para-row > div:first-child {
                        flex-wrap: nowrap !important;
                    }
                }
                .pagination-subtle:hover { opacity: 1 !important; }
                .pagination-btn-subtle:hover { border-color: #4361ee !important; color: #4361ee !important; background: #eff6ff !important; }
                .pagination-btn-subtle:disabled { opacity: 0.25; cursor: not-allowed !important; }
            `}</style>
        </div>
    );
}
/* ═══════════════════════════════════════════════════════════════════════════
   MAIN DISPATCHER
═══════════════════════════════════════════════════════════════════════════ */

function PowerPointPresentationInteractive({ exercise, assignment, preview, onComplete, reviewMode, onUpdate, readOnly, isOnlineLesson, user, isTeacher, practiceMode }) {
    const alreadyDone = isAlreadyDone(assignment) && !reviewMode;
    const [completed, setCompleted] = useState(readOnly || reviewMode || alreadyDone);
    const pptData = useMemo(() => parsePowerpointBody(exercise.body), [exercise.body]);

    useEffect(() => {
        if (!isOnlineLesson && !readOnly && onUpdate) {
            onUpdate({ status: completed ? 'completed' : 'working' });
        }
    }, [completed, onUpdate, readOnly, isOnlineLesson]);

    return (
        <div className="exercise-card-shell" style={CARD_STYLE}>
            <ExerciseLead exercise={exercise} />
            <React.Suspense fallback={<div style={{ color: '#64748b', textAlign: 'center', padding: '2rem' }}>Loading…</div>}>
                <PowerPointPresentationViewer url={pptData?.url} fileName={pptData?.fileName} />
            </React.Suspense>
            {!pptData?.url && (
                <div style={{ color: '#94a3b8', textAlign: 'center', padding: '1rem 0', fontSize: '0.9rem' }}>
                    The teacher has not uploaded a presentation yet.
                </div>
            )}
            {!preview && (
                <ExerciseActionBar>
                    {!readOnly && !reviewMode && (!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                        <button
                            type="button"
                            className={(alreadyDone || completed) ? COMPLETE_DONE_CLASS : COMPLETE_BTN}
                            disabled={(alreadyDone || completed) || (!pptData?.url)}
                            onClick={(alreadyDone || completed) ? undefined : (() => {
                                setCompleted(true);
                                if (onComplete) onComplete({ status: 'completed', score: 1, total: 1 });
                            })}
                        >
                            {(alreadyDone || completed) ? COMPLETE_DONE_LABEL : COMPLETE_LABEL}
                        </button>
                    )}
                    {(!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                        <AssignmentStatusPill assignment={assignment} completed={completed} isOnlineLesson={isOnlineLesson}  practiceMode={practiceMode} />
                    )}
                </ExerciseActionBar>
            )}
            <TeacherComment exercise={exercise} assignment={assignment} />
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   DISAPPEARING TEXT
═══════════════════════════════════════════════════════════════════════════ */
function DisappearingTextInteractive({
    exercise, assignment, preview, onComplete, reviewMode, onUpdate,
    readOnly, isOnlineLesson, updateSlideState, slideState, isTeacher, practiceMode,
}) {
    const alreadyDone = isAlreadyDone(assignment) && !reviewMode;
    const [completed, setCompleted] = useState(readOnly || reviewMode || alreadyDone);
    const [localRevealed, setLocalRevealed] = useState({});
    const { expandedSrc, openImage, closeImage } = useImageLightbox();
    const currentRevealed = isOnlineLesson ? (slideState?.revealed || {}) : localRevealed;

    const config = useMemo(() => parseDisappearingTextBody(exercise.body), [exercise.body]);
    const pages = useMemo(() => generateDisappearingPages(config.text || ''), [config.text]);

    const [localPage, setLocalPage] = useState(0);
    const pageIdx = isOnlineLesson && slideState?.page !== undefined ? slideState.page : localPage;
    const safeIndex = Math.min(pageIdx, Math.max(0, pages.length - 1));

    const handlePageChange = (newPage) => {
        setLocalPage(newPage);
        if (updateSlideState) updateSlideState({ ...slideState, page: newPage });
    };

    const { persist, getLocalData } = useExercisePersistence(
        assignment?.id,
        { revealed: localRevealed, status: completed ? 'completed' : 'working' },
        onUpdate,
        readOnly || alreadyDone,
        assignment,
        isOnlineLesson,
        updateSlideState,
        slideState
    );

    useEffect(() => {
        if (isOnlineLesson) return;
        const local = getLocalData();
        if (local && !readOnly) {
            if (local.revealed) setLocalRevealed(local.revealed);
            if (local.answers?.revealed) setLocalRevealed(local.answers.revealed);
        }
    }, [getLocalData, readOnly, isOnlineLesson]);

    useEffect(() => {
        if (!isOnlineLesson && !readOnly && onUpdate) {
            onUpdate({ status: completed ? 'completed' : 'working' });
        }
    }, [completed, onUpdate, readOnly, isOnlineLesson]);

    const toggleReveal = (gapId) => {
        const next = { ...currentRevealed, [gapId]: !currentRevealed[gapId] };
        if (isOnlineLesson && isTeacher && updateSlideState) {
            updateSlideState({ ...slideState, revealed: next });
        } else if (isOnlineLesson && !isTeacher) {
            setLocalRevealed(next);
        } else if (!isOnlineLesson) {
            setLocalRevealed(next);
            persist({ revealed: next, status: 'working' });
        }
    };

    const parts = pages[safeIndex] || [];
    const imgPct = normalizeImageWidthPercent(config.imageWidth);
    const isLastSlide = pages.length <= 1 || safeIndex === pages.length - 1;
    const canComplete = !readOnly && !reviewMode && (!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open'));

    return (
        <div className="exercise-card-shell" style={CARD_STYLE}>
            <ExerciseLead exercise={exercise} />

            {config.image ? (
                <img
                    src={config.image}
                    alt=""
                    className="embedded-image"
                    loading="lazy"
                    decoding="async"
                    onClick={(e) => {
                        e.stopPropagation();
                        openImage(config.image);
                    }}
                    style={{
                        width: `${imgPct}%`,
                        maxWidth: '100%',
                        height: 'auto',
                        borderRadius: 12,
                        margin: '0 auto 1rem',
                        display: 'block',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                        cursor: 'zoom-in',
                    }}
                />
            ) : null}

            <div
                style={{ lineHeight: 1.85, fontSize: '1.05rem', color: '#1e293b', whiteSpace: 'pre-wrap' }}
                onClick={(e) => {
                    const gap = e.target.closest?.('.disappear-gap');
                    if (!gap) return;
                    e.stopPropagation();
                    const id = gap.getAttribute('data-gap-id');
                    if (id) toggleReveal(id);
                }}
            >
                {parts.map((p, i) => {
                    if (p.type === 'text') {
                        return <React.Fragment key={`t-${safeIndex}-${i}`}>{p.value}</React.Fragment>;
                    }
                    const gapId = `${safeIndex}_${i}`;
                    const revealed = !!currentRevealed[gapId];
                    return (
                        <span
                            key={`g-${gapId}`}
                            className={`disappear-gap${revealed ? ' revealed' : ''}`}
                            data-gap-id={gapId}
                            role="button"
                            tabIndex={0}
                            title={revealed ? 'Hide word' : 'Reveal word'}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    toggleReveal(gapId);
                                }
                            }}
                        >
                            {revealed ? p.value : disappearingBlankForWord(p.value)}
                        </span>
                    );
                })}
            </div>

            {!preview ? (
                <ExerciseActionBar>
                    {canComplete && isLastSlide && (
                        <button
                            type="button"
                            className={(alreadyDone || completed) ? COMPLETE_DONE_CLASS : COMPLETE_BTN}
                            disabled={!!(alreadyDone || completed)} onClick={(alreadyDone || completed) ? undefined : (() => {
                                setCompleted(true);
                                if (onComplete) onComplete({ status: 'completed', score: 1, total: 1 });
                            })}
                        >
                            {(alreadyDone || completed) ? COMPLETE_DONE_LABEL : COMPLETE_LABEL}
                        </button>
                    )}
                    {(!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                        <AssignmentStatusPill assignment={assignment} completed={completed} isOnlineLesson={isOnlineLesson}  practiceMode={practiceMode} />
                    )}
                </ExerciseActionBar>
            ) : null}

            {pages.length > 1 && (
                <div className="pagination-subtle" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', opacity: 0.5, transition: 'opacity 0.2s', marginTop: '1rem', justifyContent: 'flex-end' }}>
                    <button
                        type="button"
                        onClick={() => handlePageChange(safeIndex - 1)}
                        disabled={safeIndex === 0 || (!isTeacher && isOnlineLesson && !preview)}
                        className="pagination-btn-subtle"
                        style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '3px 8px', color: '#64748b', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700 }}
                    >←</button>
                    <span style={{ fontSize: '0.72rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase' }}>
                        Slide {safeIndex + 1} / {pages.length}
                    </span>
                    <button
                        type="button"
                        onClick={() => handlePageChange(safeIndex + 1)}
                        disabled={safeIndex === pages.length - 1 || (!isTeacher && isOnlineLesson && !preview)}
                        className="pagination-btn-subtle"
                        style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '3px 8px', color: '#64748b', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700 }}
                    >→</button>
                </div>
            )}
            <style>{`
                .pagination-subtle:hover { opacity: 1 !important; }
                .pagination-btn-subtle:hover { border-color: #4361ee !important; color: #4361ee !important; background: #eff6ff !important; }
                .pagination-btn-subtle:disabled { opacity: 0.3; cursor: not-allowed !important; }
            `}</style>

            <TeacherComment exercise={exercise} assignment={assignment} />
            <ImageLightboxPortal src={expandedSrc} onClose={closeImage} />
        </div>
    );
}

function ExerciseAnswersFooter({
    exercise,
    assignment,
    reviewMode = false,
    isTeacher = false,
    isOnlineLesson = false,
    preview = false,
}) {
    if (!exerciseHasAnswersHtml(exercise)) return null;
    if (!canShowExerciseAnswers({
        assignment,
        reviewMode,
        isTeacher,
        isOnlineLesson,
        preview,
    })) return null;

    return (
        <div style={{ marginTop: '1.1rem' }}>
            <AnswersView html={exercise.answers_html || ''} />
        </div>
    );
}

/* ─── Hide setting: blur whole exercise until 👁 ─── */
function ExerciseHideGate({ active, revealed, onReveal, children }) {
    if (!active || revealed) return children;
    return (
        <div className="exercise-hide-gate">
            <div className="exercise-hide-gate__blurred" aria-hidden="true">
                {children}
            </div>
            <button
                type="button"
                className="exercise-hide-gate__reveal"
                onClick={onReveal}
                aria-label="Show exercise"
                title="Show exercise"
            >
                <span className="exercise-hide-gate__reveal-icon" aria-hidden>👁</span>
                <span className="exercise-hide-gate__reveal-label">Show</span>
            </button>
        </div>
    );
}

export default function ExerciseInteractiveMode({
    exercise, assignment, preview = false, onComplete, reviewMode = false,
    onUpdateAssignment, onUpdate, readOnly = false, isOnlineLesson = false,
    updateSlideState, slideState, user, isTeacher, showSolution = false,
    practiceMode = 'shared', groupId, studentLabel = null
}) {
    'use no memo';
    const hideEnabled = isExerciseHideEnabled(exercise);
    const forceVisible = isExerciseHideForcedVisible(assignment, { reviewMode, showSolution });
    const [localHideRevealed, setLocalHideRevealed] = useState(false);

    useEffect(() => {
        setLocalHideRevealed(false);
    }, [exercise?.id]);

    const hideRevealed = isOnlineLesson
        ? !!(slideState?.hide_revealed || localHideRevealed)
        : localHideRevealed;

    const revealHidden = useCallback(() => {
        if (isOnlineLesson && updateSlideState) {
            updateSlideState({ hide_revealed: true });
        }
        setLocalHideRevealed(true);
    }, [isOnlineLesson, updateSlideState]);

    if (!exercise) return <div style={{ padding: '2rem', color: '#94a3b8', textAlign: 'center' }}>No exercise data available.</div>;
    const type = exercise.type;
    const props = { 
        exercise, assignment, preview, onComplete, reviewMode, 
        onUpdateAssignment, onUpdate, readOnly, isOnlineLesson,
        updateSlideState, slideState, user, isTeacher, showSolution,
        practiceMode, groupId, studentLabel
    };
    let body = null;
    if (type === 'fill_gaps') body = <FillGapsInteractive {...props} />;
    else if (type === 'fill_gaps_self_check') body = <FillGapsSelfCheckInteractive {...props} />;
    else if (type === 'fill_gaps_ai') body = <FillGapsAiInteractive {...props} />;
    else if (type === 'fill_gaps_words') body = <FillGapsWordsInteractive {...props} />;
    else if (type === 'fill_gaps_choice') body = <FillGapsChoiceInteractive {...props} />;
    else if (type === 'checklist') body = <ChecklistInteractive {...props} />;
    else if (type === 'order_words') body = <OrderWordsInteractive {...props} />;
    else if (type === 'order_lines') body = <OrderLinesInteractive {...props} />;
    else if (type === 'jumbled_letters') body = <JumbledLettersInteractive {...props} />;
    else if (type === 'dictation') body = <DictationInteractive {...props} />;
    else if (type === 'voice_record') body = <VoiceRecordInteractive {...props} />;
    else if (type === 'voice_compare') body = <PronunciationCompareInteractive {...props} />;
    else if (type === 'multiple_choice_short') body = <MultipleChoiceShortInteractive {...props} />;
    else if (type === 'multiple_choice_long') body = <MultipleChoiceLongInteractive {...props} />;
    else if (type === 'text') body = <TextInteractive {...props} />;
    else if (type === 'text_slides') body = <TextSlidesInteractive {...props} />;
    else if (type === 'illustration') body = <IllustrationInteractive {...props} />;
    else if (type === 'disappearing_text') body = <DisappearingTextInteractive {...props} />;
    else if (type === 'essay') body = <EssayInteractive {...props} />;
    else if (type === 'wordlist') body = <WordlistInteractive {...props} />;
    else if (type === 'auto_wordlist') body = <AutoWordlistInteractive {...props} />;
    else if (type === 'wordlist_slides') body = <WordlistSlidesInteractive {...props} />;
    else if (type === 'slides') body = <SlidesInteractive {...props} />;
    else if (type === 'powerpoint_presentation') body = <PowerPointPresentationInteractive {...props} />;
    else if (type === 'sentence_slides') body = <SentenceSlidesInteractive {...props} />;
    else if (type === 'randomizer') body = <RandomizerInteractive {...props} />;
    else if (type === 'reading') body = <ReadingInteractive {...props} />;
    else if (type === 'image_fill_gaps') body = <ImageFillGapsInteractive {...props} />;
    else if (type === 'image_matching') body = <ImageMatchingInteractive {...props} />;
    else if (type === 'matching_columns') body = <MatchingColumnsInteractive {...props} />;
    else if (type === 'sort_boxes') body = <SortBoxesInteractive {...props} />;
    else if (type === 'error_correction') body = <ErrorCorrectionInteractive {...props} />;
    else body = <div style={{ padding: '2rem', color: '#94a3b8', textAlign: 'center' }}>Unknown exercise type: {type}</div>;

    const hideActive = hideEnabled && !forceVisible;

    return (
        <div className={exercise.is_info ? 'exercise-interactive--info' : undefined}>
            <ExerciseHideGate
                active={hideActive}
                revealed={hideRevealed}
                onReveal={revealHidden}
            >
                {body}
                <ExerciseAnswersFooter
                    exercise={exercise}
                    assignment={assignment}
                    reviewMode={reviewMode}
                    isTeacher={isTeacher}
                    isOnlineLesson={isOnlineLesson}
                    preview={preview}
                />
            </ExerciseHideGate>
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   IMAGE FILL GAPS
═══════════════════════════════════════════════════════════════════════════ */
function ImageFillGapsInteractive({ exercise, assignment, preview, onComplete, reviewMode, onUpdate, onUpdateAssignment, readOnly, isOnlineLesson, updateSlideState, slideState, user, isTeacher, practiceMode }) {
    const alreadyDone = isAlreadyDone(assignment) && !reviewMode;
    const [completed, setCompleted] = useState(readOnly || reviewMode || alreadyDone);
    const [answers, setAnswers] = useState(assignment?.answers || {});
    const [locked, setLocked] = useState({});
    const [isCorrectMap, setIsCorrectMap] = useState(assignment?.is_correct || {});
    const [gapAttempts, setGapAttempts] = useState(assignment?.gap_attempts || {});
    const [localUnblurred, setLocalUnblurred] = useState({});
    const [draftAnswers, setDraftAnswers] = useState({});
    const [currentIndex, setCurrentIndex] = useState(0);
    const [peekMap, setPeekMap] = useState({});
    const { expandedSrc, openImage, closeImage } = useImageLightbox();
    const currentUnblurred = isOnlineLesson ? (slideState?.unblurred || {}) : localUnblurred;
    const threeAttemptsOn = isThreeAttemptsEnabled(exercise);

    const DRAFT_KEY = useMemo(() => `ex_draft_${assignment?.id}`, [assignment?.id]);

    const activeAnswers = isOnlineLesson ? (slideState?.answers || answers) : answers;
    const activeGapAttempts = isOnlineLesson ? (slideState?.gap_attempts || gapAttempts) : gapAttempts;
    const effectiveAnswers = useMemo(() => ({ ...activeAnswers, ...draftAnswers }), [activeAnswers, draftAnswers]);

    useEffect(() => {
        if (isOnlineLesson || !assignment?.id || alreadyDone || readOnly) return;
        try {
            const raw = localStorage.getItem(DRAFT_KEY);
            if (raw) {
                const draft = JSON.parse(raw);
                if (draft && typeof draft === 'object') {
                    setDraftAnswers(draft);
                }
            }
        } catch (e) {
            console.warn('[ImageFillGaps] Failed to load draft answers:', e);
        }
    }, [DRAFT_KEY, isOnlineLesson, assignment?.id, alreadyDone, readOnly]);

    const { syncStatus, persist } = useExercisePersistence(assignment?.id, { answers: activeAnswers, locked, status: completed ? 'completed' : 'working', is_correct: isCorrectMap }, onUpdate, readOnly || alreadyDone, assignment, isOnlineLesson, updateSlideState, slideState);

    const draftAnswersRef = useRef(draftAnswers);
    draftAnswersRef.current = draftAnswers;

    const handleToggleCorrect = (idx) => {
        if (!reviewMode || !onUpdateAssignment) return;
        const newMap = { ...isCorrectMap, [idx]: !isCorrectMap[idx] };
        setIsCorrectMap(newMap);
        const newScore = Object.values(newMap).filter(v => v === true).length;
        onUpdateAssignment(assignment.id, { is_correct: newMap, score: newScore });
    };

    const handleDraftChange = useCallback((idx, val) => {
        const nextDrafts = { ...draftAnswers, [idx]: val };
        setDraftAnswers(nextDrafts);
        try {
            localStorage.setItem(DRAFT_KEY, JSON.stringify(nextDrafts));
        } catch (e) {
            console.warn('[ImageFillGaps] Failed to save draft answers:', e);
        }
        
        const mergedAnswers = { ...activeAnswers };
        Object.entries(nextDrafts).forEach(([draftIdx, draftVal]) => {
            if (draftVal && draftVal.trim()) {
                mergedAnswers[draftIdx] = draftVal;
            }
        });
        persist({ answers: mergedAnswers, locked, status: 'working', is_correct: isCorrectMap });
    }, [draftAnswers, DRAFT_KEY, activeAnswers, locked, isCorrectMap, persist]);

    const handleSubmitAnswer = (idx, val) => {
        const expectedWord = items[idx]?.word || '';
        const ok = normalizeAnswer(val) === normalizeAnswer(expectedWord);

        let nextAnswers = { ...activeAnswers, [idx]: val };
        let nextLocked = { ...locked, [idx]: true };
        let nextIsCorrect = { ...isCorrectMap, [idx]: ok };
        let nextGapAttempts = activeGapAttempts;
        const threeAttemptsOn = isThreeAttemptsEnabled(exercise);

        if (threeAttemptsOn) {
            const applied = applyGapAttempt(getGapAttemptResults(activeGapAttempts, idx), ok);
            nextGapAttempts = setGapAttemptResults(activeGapAttempts, idx, applied.results);
            nextLocked = { ...locked, [idx]: applied.lock };
            if (applied.isCorrect === null) {
                const { [idx]: _a, [String(idx)]: _b, ...rest } = nextIsCorrect;
                nextIsCorrect = rest;
            } else {
                nextIsCorrect = { ...isCorrectMap, [idx]: applied.isCorrect, [String(idx)]: applied.isCorrect };
            }
            if (applied.clearAnswer) {
                nextAnswers = { ...activeAnswers, [idx]: '' };
            }
        }

        const nextDrafts = { ...draftAnswersRef.current };
        if (threeAttemptsOn && nextAnswers[idx] === '') nextDrafts[idx] = '';
        else delete nextDrafts[idx];
        draftAnswersRef.current = nextDrafts;
        setDraftAnswers(nextDrafts);

        if (!isOnlineLesson) {
            setAnswers(nextAnswers);
            setLocked(nextLocked);
            setIsCorrectMap(nextIsCorrect);
            if (threeAttemptsOn) setGapAttempts(nextGapAttempts);
        }

        try {
            localStorage.setItem(DRAFT_KEY, JSON.stringify(nextDrafts));
        } catch (e) {
            console.warn('[ImageFillGaps] Failed to update draft answers:', e);
        }

        persist({
            answers: nextAnswers,
            locked: nextLocked,
            status: 'working',
            is_correct: nextIsCorrect,
            ...(threeAttemptsOn ? { gap_attempts: nextGapAttempts } : {}),
        });
    };

    const handleComplete = () => {
        setCompleted(true);
        const mergedAnswers = { ...activeAnswers };
        Object.entries(draftAnswers || {}).forEach(([draftIdx, draftVal]) => {
            if (draftVal != null && String(draftVal).trim() !== '') {
                mergedAnswers[draftIdx] = draftVal;
            }
        });
        if (!isOnlineLesson) {
            setAnswers(mergedAnswers);
            setDraftAnswers({});
            try { localStorage.removeItem(DRAFT_KEY); } catch (e) { /* ignore */ }
        }
        const score = Object.values(isCorrectMap).filter(v => v === true).length;
        const total = items.length;
        persist({ answers: mergedAnswers, locked, status: 'completed', is_correct: isCorrectMap });
        if (onComplete) onComplete({ status: 'completed', answers: mergedAnswers, score, total, is_correct: isCorrectMap });
    };

    useEffect(() => {
        if (!isOnlineLesson && !readOnly && onUpdate) {
            onUpdate({ status: completed ? 'completed' : 'working', answers: activeAnswers, is_correct: isCorrectMap });
        }
    }, [completed, activeAnswers, isCorrectMap, onUpdate, readOnly, isOnlineLesson]);

    useEffect(() => {
        if ((readOnly || reviewMode || alreadyDone) && assignment) {
            if (assignment.answers) setAnswers(assignment.answers);
            if (assignment.locked) setLocked(assignment.locked);
            if (assignment.status === 'completed' || assignment.status === 'checked') setCompleted(true);
        }
    }, [readOnly, reviewMode, alreadyDone, assignment]);

    useEffect(() => {
        if (!reviewMode || !assignment || !exercise) return;
        const stored = assignment.is_correct;
        if (stored && typeof stored === 'object' && Object.keys(stored).length > 0) {
            setIsCorrectMap(stored);
            return;
        }
        if (assignment.answers) {
            const { is_correct } = computeAutoGrade(exercise, assignment.answers);
            setIsCorrectMap(is_correct);
        }
    }, [reviewMode, assignment, exercise]);

    // Parse body: word // image_url
    const items = useMemo(() => {
        const lines = (exercise.body || '').split('\n').filter(l => l.trim());
        console.log('[ImageFillGapsInteractive] Parsed items:', lines.length, 'lines');
        const parsed = lines.map(line => {
            // Split only on the first // to handle URLs with https://
            const separatorIndex = line.indexOf('//');
            let word, imageUrl;
            if (separatorIndex === -1) {
                word = line.trim();
                imageUrl = '';
            } else {
                word = line.substring(0, separatorIndex).trim();
                imageUrl = line.substring(separatorIndex + 2).trim();
            }
            console.log('[ImageFillGapsInteractive] Item:', { word, imageUrl });
            return { word, imageUrl };
        });
        return parsed;
    }, [exercise.body]);

    const [failedImages, setFailedImages] = useState({});
    const [signedUrls, setSignedUrls] = useState({});
    const [urlsLoading, setUrlsLoading] = useState(true);

    // Resolve public media URLs to signed URLs (once per unique image URL)
    useEffect(() => {
        let cancelled = false;
        setUrlsLoading(true);
        const resolveUrls = async () => {
            const resolved = {};
            const seen = new Set();
            for (const item of items) {
                if (!item.imageUrl || seen.has(item.imageUrl)) continue;
                seen.add(item.imageUrl);
                if (extractMediaStoragePath(item.imageUrl)) {
                    try {
                        const signed = await db.getSignedMediaUrl(item.imageUrl);
                        resolved[item.imageUrl] = signed;
                    } catch (e) {
                        console.warn('[ImageFillGaps] Failed to get signed URL:', item.imageUrl, e);
                        resolved[item.imageUrl] = item.imageUrl;
                    }
                }
            }
            if (!cancelled) {
                setSignedUrls(resolved);
                setUrlsLoading(false);
            }
        };
        resolveUrls();
        return () => { cancelled = true; };
    }, [items]);

    const handleImageError = (idx) => {
        setFailedImages(prev => ({ ...prev, [idx]: true }));
    };

    const handleNext = () => {
        if (currentIndex < items.length - 1) {
            setCurrentIndex(currentIndex + 1);
        }
    };

    const handlePrev = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        }
    };

    const allAnswered = items.length > 0 && Object.keys(locked).length >= items.length;

    if (items.length === 0) {
        return (
            <div className="exercise-card-shell" style={CARD_STYLE}>
                <ExerciseLead exercise={exercise} />
                <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
                    No images to display
                </div>
            </div>
        );
    }

    const currentItem = items[currentIndex];
    const isLocked = locked[currentIndex] || alreadyDone;
    const currentAnswer = effectiveAnswers[currentIndex] || '';
    const isCorrect = isCorrectMap[currentIndex];
    const showCorrect = isLocked && isCorrect !== undefined;
    const imgPeekEnabled = canUseAnswerPeek({
        assignment,
        reviewMode,
        isWrong: isCorrect === false,
        hasKey: !!currentItem?.word,
    });
    const imgPeeking = !!peekMap[currentIndex];
    const displayAnswer = imgPeeking && imgPeekEnabled ? currentItem.word : currentAnswer;
    const imageSrc = signedUrls[currentItem.imageUrl] || currentItem.imageUrl;
    const totalScore = Object.values(isCorrectMap).filter(v => v === true).length;
    const isLast = currentIndex === items.length - 1;

    return (
        <div
            className="exercise-card-shell image-exercise-shell"
            style={{
                ...CARD_STYLE,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                padding: '1.5rem',
                borderRadius: '24px',
                position: 'relative',
            }}
        >
            {!readOnly && !preview && !isOnlineLesson && <SyncStatus status={syncStatus} />}
            <ExerciseLead exercise={exercise} />

            {/* Main Image Display */}
            <div
                className="image-exercise-inner"
                style={{
                background: 'white',
                borderRadius: '20px',
                padding: '1.5rem',
                marginBottom: '1.5rem',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
                position: 'relative'
            }}>
                {/* Progress Indicator */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1rem',
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    color: '#667eea'
                }}>
                    <span>Image {currentIndex + 1} of {items.length}</span>
                </div>

                {/* Image Container */}
                <div style={{
                    width: '100%',
                    height: '175px',
                    borderRadius: '16px',
                    border: '3px solid #e2e8f0',
                    background: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    marginBottom: '1.5rem',
                    position: 'relative'
                }}>
                    {currentItem.imageUrl && !failedImages[currentIndex] ? (
                        urlsLoading ? (
                            <div style={{ color: '#94a3b8', fontSize: '1rem', textAlign: 'center', padding: '1rem' }}>
                                Loading image...
                            </div>
                        ) : (
                            <img
                                key={signedUrls[currentItem.imageUrl] ? 'signed' : 'public'}
                                src={imageSrc}
                                alt={currentItem.word}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    openImage(imageSrc);
                                }}
                                onError={() => {
                                    console.error('[ImageFillGapsInteractive] Failed to load image for word:', currentItem.word, 'URL:', imageSrc);
                                    handleImageError(currentIndex);
                                }}
                                style={{ width: '100%', height: '100%', objectFit: 'contain', cursor: 'zoom-in' }}
                            />
                        )
                    ) : (
                        <div style={{ color: '#94a3b8', fontSize: '1rem', textAlign: 'center', padding: '1rem' }}>
                            {currentItem.imageUrl ? 'Failed to load image' : 'No image provided'}
                        </div>
                    )}

                    {/* Navigation Arrows */}
                    <button
                        onClick={(alreadyDone || completed) ? undefined : (handlePrev)}
                        disabled={(alreadyDone || completed) || (currentIndex === 0)}
                        style={{
                            position: 'absolute',
                            left: '10px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: '48px',
                            height: '48px',
                            borderRadius: '50%',
                            background: currentIndex === 0 ? 'rgba(255, 255, 255, 0.5)' : 'rgba(255, 255, 255, 0.95)',
                            border: 'none',
                            cursor: currentIndex === 0 ? 'not-allowed' : 'pointer',
                            fontSize: '1.5rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            transition: 'all 0.2s',
                            opacity: currentIndex === 0 ? 0.5 : 1
                        }}
                    >
                        ‹
                    </button>
                    <button
                        onClick={handleNext}
                        disabled={currentIndex === items.length - 1}
                        style={{
                            position: 'absolute',
                            right: '10px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: '48px',
                            height: '48px',
                            borderRadius: '50%',
                            background: currentIndex === items.length - 1 ? 'rgba(255, 255, 255, 0.5)' : 'rgba(255, 255, 255, 0.95)',
                            border: 'none',
                            cursor: currentIndex === items.length - 1 ? 'not-allowed' : 'pointer',
                            fontSize: '1.5rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            transition: 'all 0.2s',
                            opacity: currentIndex === items.length - 1 ? 0.5 : 1
                        }}
                    >
                        ›
                    </button>
                </div>

                {/* Input Field */}
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', position: 'relative' }}>
                    <input
                        type="text"
                        value={displayAnswer}
                        onChange={e => handleDraftChange(currentIndex, e.target.value)}
                        disabled={isLocked || readOnly || reviewMode || imgPeeking}
                        placeholder="Type the word..."
                        style={{
                            width: 'calc(100% - 60px)',
                            padding: '1rem 1.25rem',
                            border: (showCorrect || imgPeeking)
                                ? (isCorrect || imgPeeking ? '3px solid #10b981' : '3px solid #ef4444')
                                : '3px solid #e2e8f0',
                            borderRadius: '16px',
                            fontSize: '1.1rem',
                            outline: 'none',
                            background: isLocked || readOnly || reviewMode || imgPeeking ? (imgPeeking ? '#ecfdf5' : '#f8fafc') : 'white',
                            color: showCorrect && !isCorrect && !imgPeeking ? '#ef4444' : (imgPeeking ? '#065f46' : 'inherit'),
                            fontWeight: 600,
                            boxShadow: showCorrect ? (isCorrect ? '0 0 20px rgba(16, 185, 129, 0.3)' : '0 0 20px rgba(239, 68, 68, 0.3)') : 'none',
                            transition: 'all 0.2s'
                        }}
                    />
                    <GapAttemptDots
                        results={getGapAttemptResults(activeGapAttempts, currentIndex)}
                        visible={threeAttemptsOn}
                    />
                    {!isLocked && !readOnly && !reviewMode && (
                        <button
                            type="button"
                            onClick={() => handleSubmitAnswer(currentIndex, currentAnswer)}
                            disabled={!currentAnswer.trim()}
                            style={{
                                position: 'absolute',
                                right: 0,
                                width: '48px',
                                height: '48px',
                                padding: 0,
                                background: currentAnswer.trim() 
                                    ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' 
                                    : '#e2e8f0',
                                color: currentAnswer.trim() ? 'white' : '#94a3b8',
                                border: 'none',
                                borderRadius: '12px',
                                fontSize: '1.5rem',
                                fontWeight: 800,
                                cursor: currentAnswer.trim() ? 'pointer' : 'not-allowed',
                                boxShadow: currentAnswer.trim() ? '0 4px 16px rgba(16, 185, 129, 0.4)' : 'none',
                                transition: 'all 0.2s',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}
                        >
                            ✓
                        </button>
                    )}
                </div>
                {showCorrect && !isCorrect && (
                    <div style={{
                        marginTop: '0.75rem',
                        fontSize: '1rem',
                        color: '#ef4444',
                        fontWeight: 700,
                        textAlign: 'center',
                        padding: '0.75rem',
                        background: 'rgba(239, 68, 68, 0.1)',
                        borderRadius: '12px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '0.5rem',
                    }}>
                        <span
                            onClick={() => reviewMode && handleToggleCorrect(currentIndex)}
                            style={{ cursor: reviewMode ? 'pointer' : 'default' }}
                        >
                            ❌ Incorrect
                        </span>
                        {imgPeekEnabled && (
                            <ExerciseAnswerPeekButton
                                peeking={imgPeeking}
                                onToggle={() => setPeekMap((m) => ({ ...m, [currentIndex]: !m[currentIndex] }))}
                            />
                        )}
                    </div>
                )}
                {showCorrect && isCorrect && (
                    <div style={{
                        marginTop: '0.75rem',
                        fontSize: '1rem',
                        color: '#10b981',
                        fontWeight: 700,
                        textAlign: 'center',
                        padding: '0.75rem',
                        background: 'rgba(16, 185, 129, 0.1)',
                        borderRadius: '12px'
                    }}>
                        <span
                            onClick={() => reviewMode && handleToggleCorrect(currentIndex)}
                            style={{ cursor: reviewMode ? 'pointer' : 'default' }}
                        >
                            🎉 Correct!
                        </span>
                    </div>
                )}
            </div>

            {!preview && isLast && !readOnly && !reviewMode && (!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                <ExerciseActionBar style={{ marginTop: '0.5rem', borderTop: 'none', paddingTop: 0 }}>
                    <button type="button" className={(alreadyDone || completed) ? COMPLETE_DONE_CLASS : COMPLETE_BTN} disabled={!!(alreadyDone || completed)} onClick={(alreadyDone || completed) ? undefined : handleComplete}>{(alreadyDone || completed) ? COMPLETE_DONE_LABEL : COMPLETE_LABEL}</button>
                </ExerciseActionBar>
            )}
            {!preview && (!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                <ExerciseActionBar>
                    {completed && !reviewMode ? (
                        <>
                            <ExerciseScoreBox submitted>
                                Score: {totalScore} / {items.length}
                            </ExerciseScoreBox>
                            <AssignmentStatusPill assignment={assignment} completed={completed} isOnlineLesson={isOnlineLesson}  practiceMode={practiceMode} />
                        </>
                    ) : !reviewMode ? (
                        <AssignmentStatusPill
                            assignment={assignment}
                            completed={completed}
                            isOnlineLesson={isOnlineLesson}
                         practiceMode={practiceMode} />
                    ) : null}
                </ExerciseActionBar>
            )}
            <TeacherComment exercise={exercise} assignment={assignment} />
            <ImageLightboxPortal src={expandedSrc} onClose={closeImage} />
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   IMAGE MATCHING
═══════════════════════════════════════════════════════════════════════════ */
const IMAGE_MATCHING_ITEMS_PER_SLIDE = 9;
const IMAGE_MATCHING_GRID_COLS = 3;

function ImageMatchingInteractive({ exercise, assignment, preview, onComplete, reviewMode, onUpdate, onUpdateAssignment, readOnly, isOnlineLesson, updateSlideState, slideState, user, isTeacher, practiceMode }) {
    const alreadyDone = isAlreadyDone(assignment) && !reviewMode;
    const [completed, setCompleted] = useState(readOnly || reviewMode || alreadyDone);
    const [matches, setMatches] = useState(assignment?.answers || {});
    const [locked, setLocked] = useState({});
    const [isCorrectMap, setIsCorrectMap] = useState(assignment?.is_correct || {});
    const [failedImages, setFailedImages] = useState({});
    const [signedUrls, setSignedUrls] = useState({});
    const [urlsLoading, setUrlsLoading] = useState(true);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedWord, setSelectedWord] = useState(null);
    const [peekMap, setPeekMap] = useState({});
    const { expandedSrc, openImage, closeImage } = useImageLightbox();

    const DRAFT_KEY = useMemo(() => `ex_draft_${assignment?.id}`, [assignment?.id]);

    const activeAnswers = isOnlineLesson ? (slideState?.answers || matches) : matches;

    useEffect(() => {
        if (!isOnlineLesson && !readOnly && onUpdate) {
            onUpdate({ status: completed ? 'completed' : 'working', answers: activeAnswers, is_correct: isCorrectMap });
        }
    }, [completed, activeAnswers, isCorrectMap, onUpdate, readOnly, isOnlineLesson]);

    const { syncStatus, persist } = useExercisePersistence(assignment?.id, { answers: activeAnswers, locked, status: completed ? 'completed' : 'working', is_correct: isCorrectMap }, onUpdate, readOnly || alreadyDone, assignment, isOnlineLesson, updateSlideState, slideState);

    const handleToggleCorrect = (itemIdx) => {
        if (!reviewMode || !onUpdateAssignment) return;
        const newMap = { ...isCorrectMap, [itemIdx]: !isCorrectMap[itemIdx] };
        setIsCorrectMap(newMap);
        const newScore = Object.values(newMap).filter(v => v === true).length;
        onUpdateAssignment(assignment.id, { is_correct: newMap, score: newScore });
    };

    useEffect(() => {
        if ((readOnly || reviewMode || alreadyDone) && assignment) {
            if (assignment.answers) setMatches(assignment.answers);
            if (assignment.locked) setLocked(assignment.locked);
            if (assignment.status === 'completed' || assignment.status === 'checked') setCompleted(true);
        }
    }, [readOnly, reviewMode, alreadyDone, assignment]);

    useEffect(() => {
        if (!reviewMode || !assignment || !exercise) return;
        const stored = assignment.is_correct;
        if (stored && typeof stored === 'object' && Object.keys(stored).length > 0) {
            setIsCorrectMap(stored);
            return;
        }
        if (assignment.answers) {
            const { is_correct } = computeAutoGrade(exercise, assignment.answers);
            setIsCorrectMap(is_correct);
        }
    }, [reviewMode, assignment, exercise]);

    // Parse body: word // image_url
    const items = useMemo(() => {
        const lines = (exercise.body || '').split('\n').filter(l => l.trim());
        console.log('[ImageMatchingInteractive] Parsed items:', lines.length, 'lines');
        const parsed = lines.map(line => {
            const separatorIndex = line.indexOf('//');
            let word, imageUrl;
            if (separatorIndex === -1) {
                word = line.trim();
                imageUrl = '';
            } else {
                word = line.substring(0, separatorIndex).trim();
                imageUrl = line.substring(separatorIndex + 2).trim();
            }
            console.log('[ImageMatchingInteractive] Item:', { word, imageUrl });
            return { word, imageUrl };
        });
        return parsed;
    }, [exercise.body]);

    // Resolve public media URLs to signed URLs (once per unique image URL)
    useEffect(() => {
        let cancelled = false;
        setUrlsLoading(true);
        const resolveUrls = async () => {
            const resolved = {};
            const seen = new Set();
            for (const item of items) {
                if (!item.imageUrl || seen.has(item.imageUrl)) continue;
                seen.add(item.imageUrl);
                if (extractMediaStoragePath(item.imageUrl)) {
                    try {
                        const signed = await db.getSignedMediaUrl(item.imageUrl);
                        resolved[item.imageUrl] = signed;
                    } catch (e) {
                        console.warn('[ImageMatching] Failed to get signed URL:', item.imageUrl, e);
                        resolved[item.imageUrl] = item.imageUrl;
                    }
                }
            }
            if (!cancelled) {
                setSignedUrls(resolved);
                setUrlsLoading(false);
            }
        };
        resolveUrls();
        return () => { cancelled = true; };
    }, [items]);

    // Split items into slides (3 columns × 3 rows)
    const slides = useMemo(() => {
        const result = [];
        for (let i = 0; i < items.length; i += IMAGE_MATCHING_ITEMS_PER_SLIDE) {
            result.push(items.slice(i, i + IMAGE_MATCHING_ITEMS_PER_SLIDE));
        }
        return result;
    }, [items]);

    const currentSlide = slides[currentIndex] || [];

    // Create word pool from current slide only (shuffled)
    const wordPool = useMemo(() => {
        const words = currentSlide.map(i => i.word);
        return words.sort(() => Math.random() - 0.5);
    }, [currentSlide]);

    // Calculate used words for pool filtering (only from current slide items)
    const currentSlideStartIdx = currentIndex * IMAGE_MATCHING_ITEMS_PER_SLIDE;
    const currentSlideUsedWords = Object.entries(activeAnswers)
        .filter(([idx, val]) => {
            const idxNum = parseInt(idx);
            return val && idxNum >= currentSlideStartIdx && idxNum < currentSlideStartIdx + currentSlide.length;
        })
        .map(([_, val]) => val);
    
    const usedCounts = {};
    currentSlideUsedWords.forEach(w => { usedCounts[w] = (usedCounts[w] || 0) + 1; });

    const pool = wordPool.filter(w => {
        if (usedCounts[w] > 0) {
            usedCounts[w]--;
            return false;
        }
        return true;
    });

    const handleMatch = (itemIdx, selectedWord) => {
        const expectedWord = items[itemIdx]?.word || '';
        const isCorrect = selectedWord === expectedWord;
        
        // Only place and lock if correct - wrong answers return to pool
        if (!isCorrect) {
            return false; // Return false to indicate match failed
        }
        
        const nextMatches = { ...activeAnswers, [itemIdx]: selectedWord };
        const nextLocked = { ...locked, [itemIdx]: true };
        const nextIsCorrect = { ...isCorrectMap, [itemIdx]: true };

        if (!isOnlineLesson) {
            setMatches(nextMatches);
            setLocked(nextLocked);
            setIsCorrectMap(nextIsCorrect);
        }

        persist({ answers: nextMatches, locked: nextLocked, status: 'working', is_correct: nextIsCorrect });
        return true; // Return true to indicate match succeeded
    };

    const handleImageError = (idx) => {
        setFailedImages(prev => ({ ...prev, [idx]: true }));
    };

    const handleComplete = () => {
        setCompleted(true);
        const score = Object.values(isCorrectMap).filter(v => v === true).length;
        const total = items.length;
        if (onComplete) onComplete({ status: 'completed', answers: activeAnswers, score, total, is_correct: isCorrectMap });
    };

    const handleNext = () => {
        if (currentIndex < slides.length - 1) {
            setCurrentIndex(currentIndex + 1);
        }
    };

    const handlePrev = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        }
    };

    const handleGapClick = (globalIdx) => {
        if (completed || alreadyDone || readOnly || locked[globalIdx]) {
            if (activeAnswers[globalIdx]) {
                handleMatch(globalIdx, '');
            }
            return;
        }
        
        if (selectedWord) {
            const success = handleMatch(globalIdx, selectedWord);
            if (success) {
                setSelectedWord(null);
            }
            // If failed (wrong answer), word stays selected and returns to pool
        } else if (activeAnswers[globalIdx]) {
            handleMatch(globalIdx, '');
        }
    };

    if (items.length === 0) {
        return (
            <div className="exercise-card-shell" style={CARD_STYLE}>
                <ExerciseLead exercise={exercise} />
                <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
                    No images to display
                </div>
            </div>
        );
    }

    const totalScore = Object.values(isCorrectMap).filter(v => v === true).length;
    const isLast = currentIndex === slides.length - 1;

    return (
        <div
            className="exercise-card-shell image-exercise-shell"
            style={{
                ...CARD_STYLE,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                padding: '1.5rem',
                borderRadius: '24px',
                position: 'relative',
            }}
        >
            {!readOnly && !preview && !isOnlineLesson && <SyncStatus status={syncStatus} />}
            <ExerciseLead exercise={exercise} />

            {/* Word Pool - Sticky */}
            <div style={{
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '16px',
                padding: '1rem',
                marginBottom: '1.5rem',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                position: 'sticky',
                top: '10px',
                zIndex: 100
            }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'center' }}>
                    {pool.map((w, i) => {
                        const isSelected = selectedWord === w;
                        return (
                            <div
                                key={i}
                                onClick={() => !alreadyDone && !readOnly && !reviewMode && setSelectedWord(isSelected ? null : w)}
                                style={{
                                    padding: '6px 16px',
                                    background: isSelected ? '#ffedd5' : '#4361ee',
                                    color: isSelected ? '#f97316' : 'white',
                                    borderRadius: '20px',
                                    fontSize: '0.9rem',
                                    fontWeight: 800,
                                    cursor: 'pointer',
                                    border: isSelected ? '2px solid #f97316' : '2px solid transparent',
                                    boxShadow: isSelected ? '0 4px 12px rgba(249, 115, 22, 0.25)' : '0 2px 4px rgba(67, 97, 238, 0.2)',
                                    transform: isSelected ? 'scale(1.1)' : 'none',
                                    transition: 'all 0.2s',
                                    userSelect: 'none'
                                }}
                            >
                                {w}
                            </div>
                        );
                    })}
                    {pool.length === 0 && <span style={{ color: '#94a3b8', fontSize: '0.85rem', fontStyle: 'italic' }}>All words placed</span>}
                </div>
            </div>

            {/* Main Image Grid */}
            <div
                className="image-exercise-inner"
                style={{
                background: 'white',
                borderRadius: '20px',
                padding: '1.5rem',
                marginBottom: '1.5rem',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
                position: 'relative'
            }}>
                {/* Progress Indicator with Navigation */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginBottom: '1rem',
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    color: '#667eea',
                    gap: '12px'
                }}>
                    <button
                        onClick={(alreadyDone || completed) ? undefined : (handlePrev)}
                        disabled={(alreadyDone || completed) || (currentIndex === 0)}
                        style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '50%',
                            background: currentIndex === 0 ? '#e2e8f0' : '#667eea',
                            color: currentIndex === 0 ? '#94a3b8' : 'white',
                            border: 'none',
                            cursor: currentIndex === 0 ? 'not-allowed' : 'pointer',
                            fontSize: '1.2rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: currentIndex === 0 ? 'none' : '0 2px 8px rgba(102, 126, 234, 0.4)',
                            transition: 'all 0.2s'
                        }}
                    >
                        ‹
                    </button>
                    <span>Slide {currentIndex + 1} of {slides.length}</span>
                    <button
                        onClick={handleNext}
                        disabled={currentIndex === slides.length - 1}
                        style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '50%',
                            background: currentIndex === slides.length - 1 ? '#e2e8f0' : '#667eea',
                            color: currentIndex === slides.length - 1 ? '#94a3b8' : 'white',
                            border: 'none',
                            cursor: currentIndex === slides.length - 1 ? 'not-allowed' : 'pointer',
                            fontSize: '1.2rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: currentIndex === slides.length - 1 ? 'none' : '0 2px 8px rgba(102, 126, 234, 0.4)',
                            transition: 'all 0.2s'
                        }}
                    >
                        ›
                    </button>
                </div>

                {/* Image grid — 3 per row, up to 3 rows per slide */}
                <div
                    className="image-matching-grid"
                    style={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(${IMAGE_MATCHING_GRID_COLS}, minmax(0, 1fr))`,
                        gap: '0.5rem',
                        marginBottom: '1rem',
                    }}
                >
                    {currentSlide.map((item, slideIdx) => {
                        const globalIdx = currentIndex * IMAGE_MATCHING_ITEMS_PER_SLIDE + slideIdx;
                        const isLocked = locked[globalIdx] || alreadyDone;
                        const currentMatch = activeAnswers[globalIdx] || '';
                        const isCorrect = isCorrectMap[globalIdx];
                        const showCorrect = isLocked && isCorrect !== undefined;
                        const imgMatchPeekEnabled = canUseAnswerPeek({
                            assignment,
                            reviewMode,
                            isWrong: isCorrect === false,
                            hasKey: !!item.word,
                        });
                        const imgMatchPeeking = !!peekMap[globalIdx];
                        const displayMatch = imgMatchPeeking && imgMatchPeekEnabled ? item.word : currentMatch;
                        const imageSrc = signedUrls[item.imageUrl] || item.imageUrl;
                        
                        return (
                            <div key={globalIdx} className="image-matching-cell" style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', width: '100%', minWidth: 0 }}>
                                <div style={{ 
                                    width: '100%',
                                    aspectRatio: '1 / 1',
                                    borderRadius: '8px', 
                                    border: '2px solid #e2e8f0', 
                                    background: '#f8fafc', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    overflow: 'hidden' 
                                }}>
                                    {item.imageUrl && !failedImages[globalIdx] ? (
                                        urlsLoading ? (
                                            <div style={{ color: '#94a3b8', fontSize: '0.85rem', textAlign: 'center', padding: '1rem' }}>
                                                Loading image...
                                            </div>
                                        ) : (
                                            <img
                                                key={signedUrls[item.imageUrl] ? 'signed' : 'public'}
                                                src={imageSrc}
                                                alt={item.word}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    openImage(imageSrc);
                                                }}
                                                onError={() => {
                                                    console.error('[ImageMatchingInteractive] Failed to load image for word:', item.word, 'URL:', imageSrc);
                                                    handleImageError(globalIdx);
                                                }}
                                                style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'zoom-in' }}
                                            />
                                        )
                                    ) : (
                                        <div style={{ color: '#94a3b8', fontSize: '0.85rem', textAlign: 'center', padding: '1rem' }}>
                                            {item.imageUrl ? 'Failed to load image' : 'No image provided'}
                                        </div>
                                    )}
                                </div>
                                <div
                                    onClick={() => handleGapClick(globalIdx)}
                                    style={{
                                        width: '100%',
                                        padding: '0.35rem 0.4rem',
                                        border: showCorrect
                                            ? (isCorrect ? '2px solid #10b981' : '2px solid #ef4444')
                                            : (isLocked ? '2px solid #cbd5e1' : (selectedWord ? '2px solid #4361ee' : '2px solid #e2e8f0')),
                                        borderRadius: '8px',
                                        fontSize: '0.72rem',
                                        outline: 'none',
                                        background: isLocked || readOnly || reviewMode ? '#f8fafc' : (selectedWord ? '#e0e7ff' : 'white'),
                                        color: showCorrect && !isCorrect ? '#ef4444' : 'inherit',
                                        fontWeight: 600,
                                        boxShadow: showCorrect ? (isCorrect ? '0 0 12px rgba(16, 185, 129, 0.25)' : '0 0 12px rgba(239, 68, 68, 0.25)') : (selectedWord ? '0 0 12px rgba(67, 97, 238, 0.2)' : 'none'),
                                        transition: 'all 0.2s',
                                        textAlign: 'center',
                                        cursor: (!isLocked && !readOnly && !reviewMode) ? 'pointer' : 'default',
                                        minHeight: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        lineHeight: 1.2,
                                    }}
                                >
                                    {displayMatch || 'Tap to fill'}
                                </div>
                                {showCorrect && isCorrect && (
                                    <div
                                        style={{ fontSize: '0.75rem', color: '#10b981', fontWeight: 600, textAlign: 'center', cursor: reviewMode ? 'pointer' : 'default' }}
                                        onClick={() => reviewMode && handleToggleCorrect(globalIdx)}
                                    >
                                        ✓ Correct
                                    </div>
                                )}
                                {showCorrect && !isCorrect && (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.35rem' }}>
                                        <div
                                            style={{ fontSize: '0.75rem', color: '#ef4444', fontWeight: 600, textAlign: 'center', cursor: reviewMode ? 'pointer' : 'default' }}
                                            onClick={() => reviewMode && handleToggleCorrect(globalIdx)}
                                        >
                                            ❌ Incorrect
                                        </div>
                                        {imgMatchPeekEnabled && (
                                            <ExerciseAnswerPeekButton
                                                peeking={imgMatchPeeking}
                                                onToggle={() => setPeekMap((m) => ({ ...m, [globalIdx]: !m[globalIdx] }))}
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

            </div>

            {!preview && isLast && !readOnly && !reviewMode && (!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                <ExerciseActionBar style={{ marginTop: '0.5rem', borderTop: 'none', paddingTop: 0 }}>
                    <button type="button" className={(alreadyDone || completed) ? COMPLETE_DONE_CLASS : COMPLETE_BTN} disabled={!!(alreadyDone || completed)} onClick={(alreadyDone || completed) ? undefined : handleComplete}>{(alreadyDone || completed) ? COMPLETE_DONE_LABEL : COMPLETE_LABEL}</button>
                </ExerciseActionBar>
            )}
            {!preview && (!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                <ExerciseActionBar>
                    {completed && !reviewMode ? (
                        <>
                            <ExerciseScoreBox submitted>
                                Score: {totalScore} / {items.length}
                            </ExerciseScoreBox>
                            <AssignmentStatusPill assignment={assignment} completed={completed} isOnlineLesson={isOnlineLesson}  practiceMode={practiceMode} />
                        </>
                    ) : !reviewMode ? (
                        <AssignmentStatusPill
                            assignment={assignment}
                            completed={completed}
                            isOnlineLesson={isOnlineLesson}
                         practiceMode={practiceMode} />
                    ) : null}
                </ExerciseActionBar>
            )}
            <TeacherComment exercise={exercise} assignment={assignment} />
            <ImageLightboxPortal src={expandedSrc} onClose={closeImage} />
        </div>
    );
}
/* ═══════════════════════════════════════════════════════════════════════════
   MATCHING (COLUMNS)
═══════════════════════════════════════════════════════════════════════════ */
function MatchingColumnsInteractive({
    exercise, assignment, preview, onComplete, reviewMode, onUpdate, onUpdateAssignment,
    readOnly, isOnlineLesson, updateSlideState, slideState, user, isTeacher, practiceMode,
}) {
    const alreadyDone = isAlreadyDone(assignment) && !reviewMode;
    const [completed, setCompleted] = useState(readOnly || reviewMode || alreadyDone);
    const [matches, setMatches] = useState(assignment?.answers || {});
    const [locked, setLocked] = useState(assignment?.locked || {});
    const [isCorrectMap, setIsCorrectMap] = useState(assignment?.is_correct || {});
    const [selectedLeft, setSelectedLeft] = useState(null);
    const [mismatch, setMismatch] = useState(null);
    const [peekMap, setPeekMap] = useState({});

    const activeAnswers = isOnlineLesson ? (slideState?.answers || matches) : matches;

    useEffect(() => {
        if (!isOnlineLesson && !readOnly && onUpdate) {
            onUpdate({ status: completed ? 'completed' : 'working', answers: activeAnswers, is_correct: isCorrectMap });
        }
    }, [completed, activeAnswers, isCorrectMap, onUpdate, readOnly, isOnlineLesson]);

    const { syncStatus, persist } = useExercisePersistence(
        assignment?.id,
        { answers: activeAnswers, locked, status: completed ? 'completed' : 'working', is_correct: isCorrectMap },
        onUpdate,
        readOnly || alreadyDone,
        assignment,
        isOnlineLesson,
        updateSlideState,
        slideState
    );

    useEffect(() => {
        if ((readOnly || reviewMode || alreadyDone) && assignment) {
            if (assignment.answers) setMatches(assignment.answers);
            if (assignment.locked) setLocked(assignment.locked);
            if (assignment.status === 'completed' || assignment.status === 'checked') setCompleted(true);
        }
    }, [readOnly, reviewMode, alreadyDone, assignment]);

    useEffect(() => {
        if (!reviewMode || !assignment || !exercise) return;
        const stored = assignment.is_correct;
        if (stored && typeof stored === 'object' && Object.keys(stored).length > 0) {
            setIsCorrectMap(stored);
            return;
        }
        if (assignment.answers) {
            const { is_correct } = computeAutoGrade(exercise, assignment.answers);
            setIsCorrectMap(is_correct);
        }
    }, [reviewMode, assignment, exercise]);

    const pairs = useMemo(() => parseMatchingColumnsPairs(exercise.body || ''), [exercise.body]);

    const shuffledRights = useMemo(
        () => seededShuffle(pairs.map((p) => ({ idx: p.idx, text: p.right })), `match_cols_${exercise?.id || 'ex'}`),
        [pairs, exercise?.id]
    );

    const usedLeftIdxs = useMemo(() => {
        const set = new Set();
        Object.keys(activeAnswers || {}).forEach((k) => {
            const n = Number(k);
            if (Number.isFinite(n) && activeAnswers[k]) set.add(n);
        });
        return set;
    }, [activeAnswers]);

    const allMatched = pairs.every((p) => usedLeftIdxs.has(p.idx));
    // Author left order for review / done / fully matched; while playing, matched pairs sink to bottom (still sorted by left idx).
    const showSettledOrder = reviewMode || alreadyDone || readOnly || completed || allMatched;

    const displayRows = useMemo(() => {
        if (showSettledOrder) {
            return pairs.map((p) => ({
                left: p,
                right: { idx: p.idx, text: p.right },
                matched: usedLeftIdxs.has(p.idx),
            }));
        }

        const unmatchedLefts = pairs.filter((p) => !usedLeftIdxs.has(p.idx));
        const unmatchedRights = shuffledRights.filter((r) => !usedLeftIdxs.has(r.idx));
        const unmatchedRows = unmatchedLefts.map((p, i) => ({
            left: p,
            right: unmatchedRights[i] || null,
            matched: false,
        }));

        const matchedRows = pairs
            .filter((p) => usedLeftIdxs.has(p.idx))
            .sort((a, b) => a.idx - b.idx)
            .map((p) => ({
                left: p,
                right: { idx: p.idx, text: p.right },
                matched: true,
            }));

        return [...unmatchedRows, ...matchedRows];
    }, [pairs, shuffledRights, usedLeftIdxs, showSettledOrder]);

    const handleToggleCorrect = (itemIdx) => {
        if (!reviewMode || !onUpdateAssignment) return;
        const newMap = { ...isCorrectMap, [itemIdx]: !isCorrectMap[itemIdx] };
        setIsCorrectMap(newMap);
        const newScore = Object.values(newMap).filter((v) => v === true).length;
        onUpdateAssignment(assignment.id, { is_correct: newMap, score: newScore });
    };

    const tryMatch = (leftIdx, rightIdx) => {
        if (completed || alreadyDone || readOnly || reviewMode) return;
        if (usedLeftIdxs.has(leftIdx) || usedLeftIdxs.has(rightIdx)) return;

        const expected = pairs[leftIdx]?.right || '';
        const chosen = pairs[rightIdx]?.right || '';
        const ok = leftIdx === rightIdx && normalizeAnswer(expected) === normalizeAnswer(chosen);

        if (!ok) {
            setMismatch({ leftIdx, rightIdx });
            setSelectedLeft(null);
            setTimeout(() => setMismatch(null), 450);
            return;
        }

        const nextMatches = { ...activeAnswers, [leftIdx]: expected };
        const nextLocked = { ...locked, [leftIdx]: true };
        const nextIsCorrect = { ...isCorrectMap, [leftIdx]: true };

        if (!isOnlineLesson) {
            setMatches(nextMatches);
            setLocked(nextLocked);
            setIsCorrectMap(nextIsCorrect);
        }
        setSelectedLeft(null);
        persist({ answers: nextMatches, locked: nextLocked, status: 'working', is_correct: nextIsCorrect });
    };

    const handleLeftClick = (leftIdx) => {
        if (completed || alreadyDone || readOnly || reviewMode) return;
        if (usedLeftIdxs.has(leftIdx)) {
            const nextMatches = { ...activeAnswers };
            delete nextMatches[leftIdx];
            delete nextMatches[String(leftIdx)];
            const nextLocked = { ...locked };
            delete nextLocked[leftIdx];
            const nextIsCorrect = { ...isCorrectMap };
            delete nextIsCorrect[leftIdx];
            if (!isOnlineLesson) {
                setMatches(nextMatches);
                setLocked(nextLocked);
                setIsCorrectMap(nextIsCorrect);
            }
            persist({ answers: nextMatches, locked: nextLocked, status: 'working', is_correct: nextIsCorrect });
            setSelectedLeft(null);
            return;
        }
        setSelectedLeft((prev) => (prev === leftIdx ? null : leftIdx));
    };

    const handleRightClick = (rightIdx) => {
        if (completed || alreadyDone || readOnly || reviewMode) return;
        if (usedLeftIdxs.has(rightIdx)) return;
        if (selectedLeft == null) return;
        tryMatch(selectedLeft, rightIdx);
    };

    const handleComplete = () => {
        setCompleted(true);
        const score = Object.values(isCorrectMap).filter((v) => v === true).length;
        const total = pairs.length;
        if (onComplete) onComplete({ status: 'completed', answers: activeAnswers, score, total, is_correct: isCorrectMap });
    };

    if (pairs.length === 0) {
        return (
            <div className="exercise-card-shell" style={CARD_STYLE}>
                <ExerciseLead exercise={exercise} />
                <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
                    No matching pairs yet. Add lines like: Left // Right
                </div>
            </div>
        );
    }

    const totalScore = Object.values(isCorrectMap).filter((v) => v === true).length;

    const cellStyle = (opts) => ({
        width: '100%',
        textAlign: 'center',
        padding: '12px 14px',
        borderRadius: 12,
        border: opts.border,
        background: opts.bg,
        color: '#0f172a',
        fontWeight: 400,
        fontSize: '0.95rem',
        lineHeight: 1.35,
        cursor: opts.cursor,
        boxShadow: opts.shadow || 'none',
        transition: 'all 0.15s',
        userSelect: 'none',
        opacity: opts.opacity ?? 1,
        boxSizing: 'border-box',
    });

    return (
        <div className="exercise-card-shell" style={CARD_STYLE}>
            {!readOnly && !preview && !isOnlineLesson && <SyncStatus status={syncStatus} />}
            <ExerciseLead exercise={exercise} />

            <div className={`matching-columns-grid${reviewMode ? ' matching-columns-grid--review' : ''}`}>
                {displayRows.map((row, rowIndex) => {
                    const p = row.left;
                    const r = row.right;
                    const leftMatched = row.matched;
                    const isSelected = selectedLeft === p.idx;
                    const isWrongFlashLeft = mismatch?.leftIdx === p.idx;

                    let leftBorder = '1.5px solid #e2e8f0';
                    let leftBg = '#fff';
                    if (isWrongFlashLeft) { leftBorder = '2px solid #ef4444'; leftBg = '#fef2f2'; }
                    else if (leftMatched) { leftBorder = '2px solid #10b981'; leftBg = '#ecfdf5'; }
                    else if (isSelected) { leftBorder = '2px solid #0ea5e9'; leftBg = '#f0f9ff'; }

                    const rightMatched = !!(r && leftMatched && r.idx === p.idx);
                    const isWrongFlashRight = r && mismatch?.rightIdx === r.idx;
                    const canClick = !!(r && selectedLeft != null && !rightMatched && !completed && !alreadyDone && !readOnly && !reviewMode);
                    const isCorrect = r ? isCorrectMap[r.idx] === true : false;
                    const pair = r ? (pairs.find((x) => x.idx === r.idx) || pairs[r.idx]) : null;
                    const peekEnabled = r && canUseAnswerPeek({
                        assignment,
                        reviewMode,
                        isWrong: isCorrectMap[r.idx] === false,
                        hasKey: !!pair?.right,
                    });
                    const peeking = !!(r && peekMap[r.idx]);

                    let rightBorder = '1.5px solid #e2e8f0';
                    let rightBg = '#fff';
                    let rightOpacity = 1;
                    if (isWrongFlashRight) { rightBorder = '2px solid #ef4444'; rightBg = '#fef2f2'; }
                    else if (rightMatched) { rightBorder = '2px solid #10b981'; rightBg = '#ecfdf5'; rightOpacity = 0.85; }
                    else if (canClick) { rightBorder = '1.5px solid #bae6fd'; }

                    const gridRow = rowIndex + 1;

                    return (
                        <React.Fragment key={`row-${p.idx}`}>
                            <div className="matching-columns-left" style={{ gridRow, gridColumn: 1 }}>
                                <button
                                    type="button"
                                    className="matching-columns-tile"
                                    onClick={(alreadyDone || completed) ? undefined : (() => handleLeftClick(p.idx))}
                                    style={cellStyle({
                                        border: leftBorder,
                                        bg: leftBg,
                                        cursor: (completed || alreadyDone || readOnly || reviewMode) && !leftMatched ? 'default' : 'pointer',
                                        shadow: isSelected ? '0 4px 12px rgba(14,165,233,0.25)' : '0 1px 2px rgba(15,23,42,0.04)',
                                    })}
                                >
                                    {renderBold(String(p.left || '').replace(/(?<!:)\//g, '/\u200b'), {}, `mcL_${p.idx}`)}
                                </button>
                            </div>
                            {r && (
                                <div className="matching-columns-right" style={{ gridRow, gridColumn: 2 }}>
                                    <button
                                        type="button"
                                        className="matching-columns-tile"
                                        onClick={() => handleRightClick(r.idx)}
                                        disabled={(alreadyDone || completed) || (!canClick && !rightMatched)}
                                        style={cellStyle({
                                            border: rightBorder,
                                            bg: rightBg,
                                            opacity: rightOpacity,
                                            cursor: canClick ? 'pointer' : rightMatched ? 'default' : 'not-allowed',
                                            shadow: '0 1px 2px rgba(15,23,42,0.04)',
                                        })}
                                    >
                                        {renderBold(String(r.text || '—').replace(/(?<!:)\//g, '/\u200b'), {}, `mcR_${r.idx}`)}
                                    </button>
                                </div>
                            )}
                            {reviewMode && r && (
                                <div
                                    className="matching-columns-mark"
                                    style={{
                                        gridRow,
                                        gridColumn: 3,
                                        fontSize: '0.72rem',
                                        fontWeight: 700,
                                        color: isCorrect ? '#059669' : '#dc2626',
                                        textAlign: 'left',
                                        lineHeight: 1.25,
                                    }}
                                >
                                    {rightMatched && (
                                        <div
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => handleToggleCorrect(r.idx)}
                                        >
                                            {isCorrect ? '✓ Correct' : '❌ Incorrect'}
                                            {peekEnabled && (
                                                <div style={{ marginTop: 4 }}>
                                                    <ExerciseAnswerPeekButton
                                                        peeking={peeking}
                                                        onToggle={() => setPeekMap((m) => ({ ...m, [r.idx]: !m[r.idx] }))}
                                                    />
                                                    {peeking && (
                                                        <div style={{ marginTop: 4, color: '#0369a1' }}>
                                                            {renderBold(pair?.left || pair?.right || '', {}, `mcPeek_${r.idx}`)}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </React.Fragment>
                    );
                })}
            </div>

            {!preview && !readOnly && !reviewMode && (!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                <ExerciseActionBar style={{ marginTop: '1.25rem' }}>
                    <button
                        type="button"
                        className={(alreadyDone || completed) ? COMPLETE_DONE_CLASS : COMPLETE_BTN}
                        onClick={handleComplete}
                        disabled={!allMatched && Object.keys(activeAnswers || {}).length === 0}
                        style={!allMatched ? { opacity: 0.55 } : undefined}
                    >
                        {(alreadyDone || completed) ? COMPLETE_DONE_LABEL : COMPLETE_LABEL}
                    </button>
                </ExerciseActionBar>
            )}
            {!preview && (!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                <ExerciseActionBar>
                    {completed && !reviewMode ? (
                        <>
                            <ExerciseScoreBox submitted>
                                Score: {totalScore} / {pairs.length}
                            </ExerciseScoreBox>
                            <AssignmentStatusPill assignment={assignment} completed={completed} isOnlineLesson={isOnlineLesson}  practiceMode={practiceMode} />
                        </>
                    ) : !reviewMode ? (
                        <AssignmentStatusPill
                            assignment={assignment}
                            completed={completed}
                            isOnlineLesson={isOnlineLesson}
                         practiceMode={practiceMode} />
                    ) : null}
                </ExerciseActionBar>
            )}
            <TeacherComment exercise={exercise} assignment={assignment} />
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SORT INTO BOXES
   ═══════════════════════════════════════════════════════════════════════════ */
function SortBoxesInteractive({
    exercise, assignment, preview, onComplete, reviewMode, onUpdate, onUpdateAssignment,
    readOnly, isOnlineLesson, updateSlideState, slideState, user, isTeacher, practiceMode,
}) {
    const alreadyDone = isAlreadyDone(assignment) && !reviewMode;
    const [completed, setCompleted] = useState(readOnly || reviewMode || alreadyDone);
    const [placements, setPlacements] = useState(assignment?.answers || {});
    const [isCorrectMap, setIsCorrectMap] = useState(assignment?.is_correct || {});
    const [dragIdx, setDragIdx] = useState(null);
    const [selectedIdx, setSelectedIdx] = useState(null);

    const activeAnswers = isOnlineLesson ? (slideState?.answers || placements) : placements;
    const { boxes, items } = useMemo(() => parseSortBoxesBody(exercise.body || ''), [exercise.body]);

    useEffect(() => {
        if (!isOnlineLesson && !readOnly && onUpdate) {
            onUpdate({ status: completed ? 'completed' : 'working', answers: activeAnswers, is_correct: isCorrectMap });
        }
    }, [completed, activeAnswers, isCorrectMap, onUpdate, readOnly, isOnlineLesson]);

    const { syncStatus, persist } = useExercisePersistence(
        assignment?.id,
        { answers: activeAnswers, status: completed ? 'completed' : 'working', is_correct: isCorrectMap },
        onUpdate,
        readOnly || alreadyDone,
        assignment,
        isOnlineLesson,
        updateSlideState,
        slideState
    );

    useEffect(() => {
        if ((readOnly || reviewMode || alreadyDone) && assignment) {
            if (assignment.answers) setPlacements(assignment.answers);
            if (assignment.status === 'completed' || assignment.status === 'checked') setCompleted(true);
            if (assignment.is_correct) setIsCorrectMap(assignment.is_correct);
        }
    }, [readOnly, reviewMode, alreadyDone, assignment]);

    const poolOrder = useMemo(
        () => seededShuffle(items.map((it) => it.idx), `sort_boxes_${exercise?.id || 'ex'}`),
        [items, exercise?.id]
    );

    const placeItem = (itemIdx, boxName) => {
        if (completed || alreadyDone || readOnly || reviewMode) return;
        const next = { ...activeAnswers, [itemIdx]: boxName };
        if (!isOnlineLesson) setPlacements(next);
        const graded = computeAutoGrade(exercise, next);
        if (!isOnlineLesson) setIsCorrectMap(graded.is_correct);
        persist({ answers: next, status: 'working', is_correct: graded.is_correct });
    };

    const clearItem = (itemIdx) => {
        if (completed || alreadyDone || readOnly || reviewMode) return;
        const next = { ...activeAnswers };
        delete next[itemIdx];
        delete next[String(itemIdx)];
        if (!isOnlineLesson) setPlacements(next);
        const graded = computeAutoGrade(exercise, next);
        if (!isOnlineLesson) setIsCorrectMap(graded.is_correct);
        persist({ answers: next, status: 'working', is_correct: graded.is_correct });
    };

    const handleComplete = () => {
        setCompleted(true);
        const graded = computeAutoGrade(exercise, activeAnswers);
        setIsCorrectMap(graded.is_correct);
        if (onComplete) {
            onComplete({
                status: 'completed',
                answers: activeAnswers,
                score: graded.score,
                total: graded.total,
                is_correct: graded.is_correct,
            });
        }
    };

    const handleToggleCorrect = (itemIdx) => {
        if (!reviewMode || !onUpdateAssignment) return;
        const newMap = { ...isCorrectMap, [itemIdx]: !isCorrectMap[itemIdx] };
        setIsCorrectMap(newMap);
        const newScore = Object.values(newMap).filter((v) => v === true).length;
        onUpdateAssignment(assignment.id, { is_correct: newMap, score: newScore });
    };

    const chip = (item, inBox) => {
        const placed = activeAnswers[item.idx] ?? activeAnswers[String(item.idx)];
        const showResult = completed || reviewMode || alreadyDone;
        const ok = isCorrectMap[item.idx] === true || isCorrectMap[String(item.idx)] === true;
        let border = '1.5px solid #e2e8f0';
        let bg = '#fff';
        if (showResult && placed) {
            border = ok ? '2px solid #10b981' : '2px solid #ef4444';
            bg = ok ? '#ecfdf5' : '#fef2f2';
        }
        return (
            <button
                key={item.idx}
                type="button"
                draggable={!completed && !alreadyDone && !readOnly && !reviewMode}
                onDragStart={() => setDragIdx(item.idx)}
                onDragEnd={() => setDragIdx(null)}
                onClick={(alreadyDone || completed) ? undefined : (() => {
                if (completed || alreadyDone || readOnly || reviewMode) return;
                if (inBox) {
                    clearItem(item.idx);
                    setSelectedIdx(null);
                    return;
                }
                setSelectedIdx((prev) => (prev === item.idx ? null : item.idx));
            })}
                style={{
                    padding: '8px 12px',
                    borderRadius: 10,
                    border: selectedIdx === item.idx ? '2px solid #7c3aed' : border,
                    background: selectedIdx === item.idx ? '#f5f3ff' : bg,
                    cursor: (completed || alreadyDone || readOnly || reviewMode) ? 'default' : 'grab',
                    fontSize: '0.9rem',
                    color: '#0f172a',
                    boxShadow: '0 1px 2px rgba(15,23,42,0.06)',
                    textAlign: 'left',
                }}
                title={inBox && !completed && !readOnly && !reviewMode ? 'Click to return to pool' : (!inBox ? 'Select, then tap a box (or drag)' : undefined)}
            >
                {item.text}
                {reviewMode && placed && (
                    <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); handleToggleCorrect(item.idx); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); handleToggleCorrect(item.idx); } }}
                        style={{ marginLeft: 8, fontSize: '0.7rem', fontWeight: 800, color: ok ? '#059669' : '#dc2626', cursor: 'pointer' }}
                    >
                        {ok ? '✓' : '✕'}
                    </span>
                )}
            </button>
        );
    };

    const unplaced = poolOrder
        .map((idx) => items.find((it) => it.idx === idx))
        .filter((it) => it && !(activeAnswers[it.idx] ?? activeAnswers[String(it.idx)]));

    if (!boxes.length) {
        return (
            <div className="exercise-card-shell" style={CARD_STYLE}>
                <ExerciseLead exercise={exercise} />
                <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
                    No boxes yet. Add lines like: fruit // apples, bananas, oranges
                </div>
            </div>
        );
    }

    const totalScore = Object.values(isCorrectMap).filter((v) => v === true).length;

    return (
        <div className="exercise-card-shell" style={CARD_STYLE}>
            {!readOnly && !preview && !isOnlineLesson && <SyncStatus status={syncStatus} />}
            <ExerciseLead exercise={exercise} />

            <div style={{
                display: 'flex', flexWrap: 'wrap', gap: 8, minHeight: 52, padding: '0.75rem',
                borderRadius: 14, border: '1.5px dashed #cbd5e1', background: '#f8fafc', marginBottom: '1rem',
            }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                    e.preventDefault();
                    if (dragIdx != null) clearItem(dragIdx);
                    setDragIdx(null);
                }}
            >
                <span style={{ width: '100%', fontSize: '0.7rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase' }}>Pool</span>
                {unplaced.map((it) => chip(it, false))}
                {unplaced.length === 0 && (
                    <span style={{ fontSize: '0.85rem', color: '#94a3b8' }}>All items placed</span>
                )}
            </div>

            <div style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${Math.min(boxes.length, 3)}, minmax(0, 1fr))`,
                gap: 12,
            }}>
                {boxes.map((boxName) => {
                    const inBox = items.filter((it) => (activeAnswers[it.idx] ?? activeAnswers[String(it.idx)]) === boxName);
                    return (
                        <div
                            key={boxName}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => {
                                e.preventDefault();
                                if (dragIdx != null) placeItem(dragIdx, boxName);
                                setDragIdx(null);
                            }}
                            onClick={() => {
                                if (selectedIdx != null) {
                                    placeItem(selectedIdx, boxName);
                                    setSelectedIdx(null);
                                }
                            }}
                            style={{
                                minHeight: 140,
                                borderRadius: 16,
                                border: selectedIdx != null ? '1.5px solid #a78bfa' : '1.5px solid #e2e8f0',
                                background: '#fff',
                                padding: '0.75rem',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 8,
                                cursor: selectedIdx != null ? 'pointer' : 'default',
                            }}
                        >
                            <div style={{ fontWeight: 800, color: '#1e293b', fontSize: '0.95rem' }}>{boxName}</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
                                {inBox.map((it) => chip(it, true))}
                            </div>
                        </div>
                    );
                })}
            </div>

            {!preview && !readOnly && !reviewMode && (!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                <ExerciseActionBar style={{ marginTop: '1.25rem' }}>
                    <button
                        type="button"
                        className={(alreadyDone || completed) ? COMPLETE_DONE_CLASS : COMPLETE_BTN}
                        onClick={handleComplete}
                        disabled={(alreadyDone || completed) || (Object.keys(activeAnswers || {}).length === 0)}
                        style={Object.keys(activeAnswers || {}).length === 0 ? { opacity: 0.55 } : undefined}
                    >
                        {(alreadyDone || completed) ? COMPLETE_DONE_LABEL : COMPLETE_LABEL}
                    </button>
                </ExerciseActionBar>
            )}
            {!preview && (!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                <ExerciseActionBar>
                    {completed && !reviewMode ? (
                        <>
                            <ExerciseScoreBox submitted>
                                Score: {totalScore} / {items.length}
                            </ExerciseScoreBox>
                            <AssignmentStatusPill assignment={assignment} completed={completed} isOnlineLesson={isOnlineLesson}  practiceMode={practiceMode} />
                        </>
                    ) : !reviewMode ? (
                        <AssignmentStatusPill assignment={assignment} completed={completed} isOnlineLesson={isOnlineLesson}  practiceMode={practiceMode} />
                    ) : null}
                </ExerciseActionBar>
            )}
            <TeacherComment exercise={exercise} assignment={assignment} />
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   ERROR CORRECTION
   ═══════════════════════════════════════════════════════════════════════════ */
function ErrorCorrectionSlot({
    isOpen,
    canInteract,
    wrongText,
    onOpen,
    fieldLine,
}) {
    const fieldRef = useRef(null);
    const spacerRef = useRef(null);

    useLayoutEffect(() => {
        const field = fieldRef.current;
        const spacer = spacerRef.current;
        if (!spacer) return undefined;
        if (!isOpen || !field) {
            spacer.style.height = '0px';
            return undefined;
        }
        const syncHeight = () => {
            const h = field.getBoundingClientRect().height;
            // 4px matches field-line top offset (gap under the wrong word)
            spacer.style.height = h > 0 ? `${Math.ceil(h) + 4}px` : '0px';
        };
        syncHeight();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(syncHeight);
        ro.observe(field);
        return () => ro.disconnect();
    }, [isOpen]);

    return (
        <span className={`error-correction-slot${isOpen ? ' error-correction-slot--open' : ''}`}>
            <span className="error-correction-slot__anchor">
                {!isOpen ? (
                    <button
                        type="button"
                        className="error-correction-slot__wrong-btn"
                        onClick={onOpen}
                        disabled={!canInteract}
                        title={canInteract ? 'Click to correct' : undefined}
                    >
                        {wrongText}
                    </button>
                ) : (
                    <span className="error-correction-slot__wrong">{wrongText}</span>
                )}
                {isOpen && (
                    <span ref={fieldRef} className="error-correction-slot__field-line exercise-gap-row__field-line">
                        {fieldLine}
                    </span>
                )}
            </span>
            <span ref={spacerRef} className="error-correction-slot__spacer" aria-hidden="true" />
        </span>
    );
}

function ErrorCorrectionInteractive({
    exercise, assignment, preview, onComplete, reviewMode, onUpdate, onUpdateAssignment,
    readOnly, isOnlineLesson, updateSlideState, slideState, user, isTeacher, practiceMode,
}) {
    const alreadyDone = isAlreadyDone(assignment) && !reviewMode;
    const threeAttempts = isThreeAttemptsEnabled(exercise);
    const [completed, setCompleted] = useState(readOnly || reviewMode || alreadyDone);
    const [answers, setAnswers] = useState(assignment?.answers || {});
    const [locked, setLocked] = useState(assignment?.locked || {});
    const [isCorrectMap, setIsCorrectMap] = useState(assignment?.is_correct || {});
    const [gapAttempts, setGapAttempts] = useState(assignment?.gap_attempts || {});
    const [opened, setOpened] = useState(() => {
        const init = {};
        Object.keys(assignment?.answers || {}).forEach((k) => { init[k] = true; });
        return init;
    });
    const [peekMap, setPeekMap] = useState({});

    const activeAnswers = isOnlineLesson ? (slideState?.answers || answers) : answers;
    const activeLocked = isOnlineLesson ? (slideState?.locked || locked) : locked;
    const activeIsCorrect = isOnlineLesson ? (slideState?.is_correct || isCorrectMap) : isCorrectMap;
    const activeGapAttempts = isOnlineLesson ? (slideState?.gap_attempts || gapAttempts) : gapAttempts;
    const { segments } = useMemo(() => parseErrorCorrectionBody(exercise.body || ''), [exercise.body]);
    const errors = useMemo(() => collectErrorCorrectionItems(exercise.body || ''), [exercise.body]);

    const scoreFromMap = useCallback((map) => (
        errors.filter((e) => map?.[e.idx] === true || map?.[String(e.idx)] === true).length
    ), [errors]);

    const buildProgressFields = useCallback((map, status) => {
        const gradedMap = map || {};
        return {
            score: scoreFromMap(gradedMap),
            total: errors.length,
            status: status || 'working',
        };
    }, [errors.length, scoreFromMap]);

    const slideDone = isOnlineLesson && (slideState?.status === 'completed' || slideState?.status === 'checked');
    const effectivelyCompleted = completed || slideDone || alreadyDone;

    useEffect(() => {
        if (!isOnlineLesson && !readOnly && onUpdate) {
            onUpdate({
                status: effectivelyCompleted ? 'completed' : 'working',
                answers: activeAnswers,
                locked: activeLocked,
                is_correct: activeIsCorrect,
                ...buildProgressFields(activeIsCorrect, effectivelyCompleted ? 'completed' : 'working'),
                ...(threeAttempts ? { gap_attempts: activeGapAttempts } : {}),
            });
        }
    }, [effectivelyCompleted, activeAnswers, activeLocked, activeIsCorrect, activeGapAttempts, threeAttempts, onUpdate, readOnly, isOnlineLesson, buildProgressFields]);

    const { syncStatus, persist } = useExercisePersistence(
        assignment?.id,
        {
            answers: activeAnswers,
            locked: activeLocked,
            status: effectivelyCompleted ? 'completed' : 'working',
            is_correct: activeIsCorrect,
            ...buildProgressFields(activeIsCorrect, effectivelyCompleted ? 'completed' : 'working'),
            ...(threeAttempts ? { gap_attempts: activeGapAttempts } : {}),
        },
        onUpdate,
        readOnly || alreadyDone,
        assignment,
        isOnlineLesson,
        updateSlideState,
        slideState
    );

    useEffect(() => {
        if ((readOnly || reviewMode || alreadyDone) && assignment) {
            if (assignment.answers) {
                setAnswers(assignment.answers);
                const init = {};
                Object.keys(assignment.answers).forEach((k) => { init[k] = true; });
                setOpened((prev) => ({ ...prev, ...init }));
            }
            if (assignment.locked) setLocked(assignment.locked);
            if (assignment.gap_attempts) setGapAttempts(assignment.gap_attempts);
            if (assignment.status === 'completed' || assignment.status === 'checked') setCompleted(true);
            if (assignment.is_correct) setIsCorrectMap(assignment.is_correct);
        }
    }, [readOnly, reviewMode, alreadyDone, assignment]);

    // Online lesson: keep opened slots in sync with slide answers (teacher monitor / spectate)
    useEffect(() => {
        if (!isOnlineLesson || !activeAnswers) return;
        const init = {};
        Object.keys(activeAnswers).forEach((k) => {
            if (String(activeAnswers[k] ?? '').trim()) init[k] = true;
        });
        if (Object.keys(init).length) setOpened((prev) => ({ ...prev, ...init }));
    }, [isOnlineLesson, activeAnswers]);

    const openError = (errorIdx) => {
        if (effectivelyCompleted || alreadyDone || readOnly || reviewMode) return;
        setOpened((prev) => ({ ...prev, [errorIdx]: true, [String(errorIdx)]: true }));
    };

    const setAnswerDraft = (idx, value) => {
        if (effectivelyCompleted || alreadyDone || readOnly || activeLocked[idx] || activeLocked[String(idx)]) return;
        const next = { ...activeAnswers, [idx]: value };
        if (!isOnlineLesson) setAnswers(next);
        persist({
            answers: next,
            locked: activeLocked,
            is_correct: activeIsCorrect,
            ...buildProgressFields(activeIsCorrect, 'working'),
            ...(threeAttempts ? { gap_attempts: activeGapAttempts } : {}),
        });
    };

    const submitError = (idx, value) => {
        if (effectivelyCompleted || alreadyDone || readOnly || reviewMode) return;
        const item = errors.find((e) => e.idx === idx);
        const keys = item?.answers || [];
        const hasKey = keys.length > 0;
        const ok = hasKey
            && keys.some((a) => normalizeAnswer(a) === normalizeAnswer(String(value || '')));

        let nextAnswers = { ...activeAnswers, [idx]: value };
        let nextLocked = { ...activeLocked, [idx]: true, [String(idx)]: true };
        let nextIsCorrect = { ...activeIsCorrect };
        if (!hasKey) {
            delete nextIsCorrect[idx];
            delete nextIsCorrect[String(idx)];
        } else {
            nextIsCorrect = { ...activeIsCorrect, [idx]: ok, [String(idx)]: ok };
        }
        let nextGapAttempts = activeGapAttempts;

        if (threeAttempts && hasKey) {
            const applied = applyGapAttempt(getGapAttemptResults(activeGapAttempts, idx), ok);
            nextGapAttempts = setGapAttemptResults(activeGapAttempts, idx, applied.results);
            nextLocked = { ...activeLocked, [idx]: applied.lock, [String(idx)]: applied.lock };
            if (applied.isCorrect === null) {
                const { [idx]: _a, [String(idx)]: _b, ...rest } = nextIsCorrect;
                nextIsCorrect = rest;
            } else {
                nextIsCorrect = { ...activeIsCorrect, [idx]: applied.isCorrect, [String(idx)]: applied.isCorrect };
            }
            if (applied.clearAnswer) {
                nextAnswers = { ...activeAnswers, [idx]: '' };
            }
        }

        if (!isOnlineLesson) {
            setAnswers(nextAnswers);
            setLocked(nextLocked);
            setIsCorrectMap(nextIsCorrect);
            if (threeAttempts) setGapAttempts(nextGapAttempts);
        }
        setOpened((prev) => ({ ...prev, [idx]: true, [String(idx)]: true }));
        persist({
            answers: nextAnswers,
            locked: nextLocked,
            is_correct: nextIsCorrect,
            ...buildProgressFields(nextIsCorrect, 'working'),
            ...(threeAttempts ? { gap_attempts: nextGapAttempts } : {}),
        });
    };

    const handleComplete = () => {
        setCompleted(true);
        const graded = computeAutoGrade(exercise, activeAnswers);
        setIsCorrectMap(graded.is_correct);
        const nextLocked = { ...activeLocked };
        errors.forEach((e) => {
            nextLocked[e.idx] = true;
            nextLocked[String(e.idx)] = true;
        });
        if (!isOnlineLesson) setLocked(nextLocked);
        const allOpen = {};
        errors.forEach((e) => { allOpen[e.idx] = true; allOpen[String(e.idx)] = true; });
        setOpened((prev) => ({ ...prev, ...allOpen }));
        const progress = { score: graded.score, total: graded.total, status: 'completed' };
        persist({
            answers: activeAnswers,
            locked: nextLocked,
            is_correct: graded.is_correct,
            ...progress,
            ...(threeAttempts ? { gap_attempts: activeGapAttempts } : {}),
        });
        if (onComplete) {
            onComplete({
                answers: activeAnswers,
                locked: nextLocked,
                is_correct: graded.is_correct,
                ...progress,
                ...(threeAttempts ? { gap_attempts: activeGapAttempts } : {}),
            });
        }
    };

    const handleToggleCorrect = (itemIdx) => {
        if (!reviewMode) return;
        const cur = activeIsCorrect[itemIdx] === true || activeIsCorrect[String(itemIdx)] === true;
        const nextOk = !cur;
        const newMap = { ...activeIsCorrect, [itemIdx]: nextOk, [String(itemIdx)]: nextOk };
        const newScore = scoreFromMap(newMap);
        if (!isOnlineLesson) setIsCorrectMap(newMap);
        if (onUpdateAssignment && assignment?.id) {
            onUpdateAssignment(assignment.id, { is_correct: newMap, score: newScore });
        } else if (isOnlineLesson && updateSlideState) {
            updateSlideState({
                is_correct: newMap,
                score: newScore,
                total: errors.length,
            });
        }
    };

    if (!errors.length) {
        return (
            <div className="exercise-card-shell" style={CARD_STYLE}>
                <ExerciseLead exercise={exercise} />
                <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
                    No errors marked yet. Use [wrong/correct] in the sentence.
                </div>
            </div>
        );
    }

    const totalScore = scoreFromMap(activeIsCorrect);
    const showResults = effectivelyCompleted || reviewMode;
    const canInteract = !effectivelyCompleted && !alreadyDone && !readOnly && !reviewMode;

    return (
        <div className="exercise-card-shell" style={CARD_STYLE}>
            {!readOnly && !preview && !isOnlineLesson && <SyncStatus status={syncStatus} />}
            <ExerciseLead exercise={exercise} />

            <div
                className="exercise-gap-content"
                style={{
                    fontSize: '1.05rem',
                    lineHeight: 2.05,
                    color: '#0f172a',
                    whiteSpace: 'pre-wrap',
                }}
            >
                {segments.map((seg, i) => {
                    if (seg.type === 'text') {
                        return <span key={`t-${i}`}>{seg.text}</span>;
                    }

                    const idx = seg.idx;
                    const val = activeAnswers[idx] ?? activeAnswers[String(idx)] ?? '';
                    const isLocked = !!(activeLocked[idx] || activeLocked[String(idx)] || showResults);
                    const isOpen = !!(opened[idx] || opened[String(idx)] || String(val).trim() || showResults);
                    const ok = activeIsCorrect[idx] === true || activeIsCorrect[String(idx)] === true;
                    const wrongMark = activeIsCorrect[idx] === false || activeIsCorrect[String(idx)] === false;
                    const answerKey = (seg.answers || [])[0] || '';
                    const hasKey = (seg.answers || []).some((a) => String(a || '').trim());
                    const attemptResults = getGapAttemptResults(activeGapAttempts, idx);
                    const peekEnabled = canUseAnswerPeek({
                        assignment,
                        reviewMode,
                        isWrong: wrongMark,
                        hasKey: !!answerKey,
                    });
                    const peeking = !!peekMap[idx];
                    const displayVal = peekEnabled && peeking ? answerKey : val;

                    let borderColor = '#c7d2fe';
                    let background = '#fff';
                    if (isLocked || showResults) {
                        if (ok) { borderColor = '#6ee7b7'; background = '#ecfdf5'; }
                        else if (wrongMark) { borderColor = '#fca5a5'; background = '#fef2f2'; }
                    }

                    const markContent = ok ? '✅' : (wrongMark ? '❌' : '');
                    const showMark = (isLocked || reviewMode) && (!!markContent || reviewMode);

                    return (
                        <ErrorCorrectionSlot
                            key={`e-${idx}`}
                            isOpen={isOpen}
                            canInteract={canInteract}
                            wrongText={seg.wrong}
                            onOpen={() => openError(idx)}
                            fieldLine={(
                                <>
                                    <ExerciseGapField
                                        value={displayVal}
                                        readOnly={isLocked || (readOnly && !reviewMode) || (reviewMode && !onUpdateAssignment && !(isOnlineLesson && updateSlideState))}
                                        onChange={!isLocked && !readOnly && !reviewMode ? (e) => setAnswerDraft(idx, e.target.value) : undefined}
                                        placeholder="..."
                                        borderColor={borderColor}
                                        background={background}
                                        showSubmit={!isLocked && canInteract}
                                        onSubmit={() => submitError(idx, val)}
                                        showMark={showMark}
                                        markContent={markContent || (reviewMode ? (ok ? '✅' : '❌') : '')}
                                        markClickable={!!reviewMode}
                                        onMarkClick={() => handleToggleCorrect(idx)}
                                        markTitle={reviewMode ? 'Click to toggle mark' : (markContent === MARK_NEEDS_TEACHER ? 'Teacher will check this' : '')}
                                        peekEnabled={peekEnabled}
                                        peeking={peeking}
                                        onPeekToggle={() => setPeekMap((m) => ({ ...m, [idx]: !m[idx] }))}
                                    />
                                    <GapAttemptDots results={attemptResults} visible={threeAttempts && hasKey} />
                                </>
                            )}
                        />
                    );
                })}
            </div>

            {!preview && !readOnly && !reviewMode && (!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                <ExerciseActionBar style={{ marginTop: '1.25rem' }}>
                    <button
                        type="button"
                        className={(effectivelyCompleted || completed) ? COMPLETE_DONE_CLASS : COMPLETE_BTN}
                        onClick={(effectivelyCompleted || completed) ? undefined : (handleComplete)}
                        disabled={(effectivelyCompleted || completed) || (Object.keys(activeAnswers || {}).length === 0)}
                        style={Object.keys(activeAnswers || {}).length === 0 ? { opacity: 0.55 } : undefined}
                    >
                        {(effectivelyCompleted || completed) ? COMPLETE_DONE_LABEL : COMPLETE_LABEL}
                    </button>
                </ExerciseActionBar>
            )}
            {!preview && (!isOnlineLesson || isTeacher || (practiceMode === 'individual' || practiceMode === 'open')) && (
                <ExerciseActionBar>
                    {effectivelyCompleted && !reviewMode ? (
                        <>
                            <ExerciseScoreBox submitted>
                                Score: {totalScore} / {errors.length}
                            </ExerciseScoreBox>
                            <AssignmentStatusPill assignment={assignment} completed={effectivelyCompleted} isOnlineLesson={isOnlineLesson}  practiceMode={practiceMode} />
                        </>
                    ) : !reviewMode ? (
                        <AssignmentStatusPill assignment={assignment} completed={effectivelyCompleted} isOnlineLesson={isOnlineLesson}  practiceMode={practiceMode} />
                    ) : null}
                </ExerciseActionBar>
            )}
            <TeacherComment exercise={exercise} assignment={assignment} />
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   ILLUSTRATION
   ═══════════════════════════════════════════════════════════════════════════ */
function IllustrationInteractive({ exercise, assignment, preview, onComplete, reviewMode, onUpdateAssignment, onUpdate, readOnly, isOnlineLesson, updateSlideState, slideState, user, isTeacher, practiceMode }) {
    const [localUnblurred, setLocalUnblurred] = useState({});
    const autoMarkedRef = useRef(false);
    const contentRef = useRef(null);
    const onCompleteRef = useRef(onComplete);
    const onUpdateRef = useRef(onUpdate);
    onCompleteRef.current = onComplete;
    onUpdateRef.current = onUpdate;
    const currentUnblurred = isOnlineLesson ? (slideState?.unblurred || {}) : localUnblurred;
    const alreadyDone = isAlreadyDone(assignment) && !reviewMode;

    const html = React.useMemo(() => renderMarkdownToHtml(exercise.body || '', currentUnblurred, `ill`), [exercise.body, currentUnblurred]);

    // Auto Complete + Checked on first view (no Complete button; no rewards).
    // Callbacks are read from refs so parent re-renders (new onComplete identity) do not
    // cancel the timer and leave illustrations stuck at status "working".
    useEffect(() => {
        if (autoMarkedRef.current) return;
        if (preview || readOnly || reviewMode || isTeacher || isOnlineLesson) return;
        if (alreadyDone) return;
        if (!onCompleteRef.current && !onUpdateRef.current) return;

        let cancelled = false;
        const timer = setTimeout(() => {
            if (cancelled || autoMarkedRef.current) return;
            autoMarkedRef.current = true;
            const payload = {
                status: 'checked',
                autoViewed: true,
                score: 0,
                total: 0,
                awarded_stars: 0,
                awarded_coins: 0,
            };
            if (typeof onCompleteRef.current === 'function') onCompleteRef.current(payload);
            else if (typeof onUpdateRef.current === 'function') onUpdateRef.current(payload);
        }, 250);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [
        alreadyDone, preview, readOnly, reviewMode, isTeacher, isOnlineLesson,
        assignment?.id,
    ]);

    const handleBlurClick = (e) => {
        const blurTarget = e.target.closest('.blurred-text');
        if (!blurTarget) return;
        e.stopPropagation();
        const blurId = blurTarget.getAttribute('data-blur-id');
        const nextUnblurred = { ...currentUnblurred, [blurId]: !currentUnblurred[blurId] };
        if (isOnlineLesson && isTeacher && updateSlideState) {
            updateSlideState({ ...slideState, unblurred: nextUnblurred });
        } else if (isOnlineLesson && !isTeacher) {
            setLocalUnblurred(nextUnblurred);
        } else if (!isOnlineLesson) {
            setLocalUnblurred(nextUnblurred);
        }
    };

    return (
        <div className="exercise-card-shell illustration-exercise" style={{ ...CARD_STYLE, overflow: 'visible' }}>
            <ExerciseLead exercise={exercise} />
            
            <div ref={contentRef} onClick={handleBlurClick} className="illustration-exercise__content">
                <RichTextSlideRenderer
                    content={html}
                    style={{ lineHeight: 1.8, fontSize: '1.05rem', color: '#1e293b' }}
                />
            </div>

            <TeacherComment exercise={exercise} assignment={assignment} />
            <style>{`
                .illustration-exercise__content img.embedded-image,
                .illustration-exercise__content img {
                    max-width: 100% !important;
                    height: auto !important;
                    display: block;
                    margin-left: auto;
                    margin-right: auto;
                    cursor: zoom-in;
                }
            `}</style>
        </div>
    );
}

